import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ShoppingCart, Search, LogOut, Receipt, LayoutGrid, Plus, Minus, Trash2, X, Check, AlertCircle, Loader2, FileText, Euro, RefreshCw, Eye, Zap, Package, User, CreditCard, Banknote, ArrowLeft, Percent, Edit3, Printer, Download, Mail, RotateCcw, DollarSign, Wallet, TrendingUp, BarChart3, Settings, XCircle, CheckCircle, Archive, BookOpen, Save, PlusCircle, Building, Users, Clock, Send, Coffee, Move, Square, Circle, Armchair, ChevronRight, GripVertical, Maximize2, ToggleLeft, ToggleRight, Hash, Utensils, Home, Menu, WifiOff, Lock, Upload } from "lucide-react";

const API_BASE = (() => {
  const h = window.location.hostname, p = window.location.port, pr = window.location.protocol;
  // TEST BUILD — points at efis.cloud backend (mirror of efis.pro for testing).
  // Electron desktop (file:// protocol) — always use test API
  if (pr === "file:") return "https://efis.cloud/api";
  // Browser dev (localhost with port)
  if (h === "localhost" && p) return "http://localhost/fiscalization/api";
  // Capacitor native app (localhost, no port)
  if (h === "localhost" && !p) {
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return "http://10.0.2.2/fiscalization/api";
    return "http://localhost/fiscalization/api"; // iOS simulator
  }
  // TEST production — API on test domain
  return "https://efis.cloud/api";
})();

// ═══ OFFLINE CACHE SYSTEM ═══
const CACHE_PREFIX = "pos_cache_";
const OFFLINE_DRAFTS_KEY = "pos_offline_drafts";
function cacheSet(key, data) { try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() })); } catch(e) {} }
function cacheGet(key) { try { const raw = localStorage.getItem(CACHE_PREFIX + key); if (!raw) return null; return JSON.parse(raw).data; } catch { return null; } }
function getOfflineDrafts() { try { return JSON.parse(localStorage.getItem(OFFLINE_DRAFTS_KEY) || "[]"); } catch { return []; } }
function saveOfflineDraft(payload) {
  const drafts = getOfflineDrafts();
  drafts.push({ id: Date.now(), payload, created_at: new Date().toISOString() });
  localStorage.setItem(OFFLINE_DRAFTS_KEY, JSON.stringify(drafts));
  return drafts.length;
}
function removeOfflineDraft(id) {
  const drafts = getOfflineDrafts().filter(d => d.id !== id);
  localStorage.setItem(OFFLINE_DRAFTS_KEY, JSON.stringify(drafts));
}

async function api(path, opts = {}) {
  const token = localStorage.getItem("pos_token");
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  try {
    const fetchPromise = fetch(`${API_BASE}${path}`, { headers: h, ...opts });
    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 5000));
    const r = await Promise.race([fetchPromise, timeoutPromise]);
    if (r.status === 401) { localStorage.removeItem("pos_token"); localStorage.removeItem("pos_user"); window.location.reload(); return { success: false }; }
    const data = await r.json();
    // Cache GET responses
    if (!opts.method || opts.method === "GET") {
      if (path === "/products" && data.success) cacheSet("products", data.data);
      if (path.includes("/categories") && data.success) cacheSet("categories", data.data);
      if (path === "/partners" && data.success) cacheSet("partners", data.data);
      if (path === "/auth/company-config" && data.success) cacheSet("config", data.data);
      if (path === "/tables/settings" && data.success) cacheSet("tables_settings", data.data);
      if (path === "/tables" && data.success) cacheSet("tables", data.data);
    }
    return data;
  } catch (e) {
    // Offline: return cached data for GET requests
    if (!opts.method || opts.method === "GET") {
      if (path === "/products") { const c = cacheGet("products"); if (c) return { success: true, data: c, _offline: true }; }
      if (path.includes("/categories")) { const c = cacheGet("categories"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/partners") { const c = cacheGet("partners"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/auth/company-config") { const c = cacheGet("config"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/tables/settings") { const c = cacheGet("tables_settings"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/tables") { const c = cacheGet("tables"); if (c) return { success: true, data: c, _offline: true }; }
    }
    return { success: false, message: "Network error: " + e.message, _offline: true };
  }
}
function fmt(n) { return parseFloat(n || 0).toFixed(2); }

/**
 * Format a cart quantity for display.
 * Whole numbers → "1", "3", "12" (no trailing zeros — keeps existing receipts clean)
 * Decimals → "0,77" with comma separator (Montenegrin locale)
 * Mirrors the backend InvoicePDF::formatQty logic so receipts and on-screen UI match.
 */
function fmtQty(n) {
  const v = parseFloat(n || 0);
  if (Math.abs(v - Math.round(v)) < 0.0001) return String(Math.round(v));
  return v.toFixed(2).replace(".", ",");
}

// ═══ PIN LOCK SYSTEM ═══
const PIN_STORAGE_KEY_PREFIX = "pos_pin_system_";
function getPinKey(companyId) { return PIN_STORAGE_KEY_PREFIX + (companyId || "default"); }
const PIN_ENCRYPT_KEY = "efis_pro_2026_pin_lock";

// Simple XOR-based encryption for localStorage
function pinEncrypt(text) {
  const key = PIN_ENCRYPT_KEY;
  let result = "";
  for (let i = 0; i < text.length; i++) result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  return btoa(result);
}
function pinDecrypt(encoded) {
  try {
    const key = PIN_ENCRYPT_KEY;
    const text = atob(encoded);
    let result = "";
    for (let i = 0; i < text.length; i++) result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    return result;
  } catch { return null; }
}

// SHA-256 hash for PINs (async)
async function hashPin(pin) {
  const data = new TextEncoder().encode(pin + "_efis_salt_2026");
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, "0")).join("");
}

// Load/save PIN data
function loadPinData(companyId) {
  try {
    const raw = localStorage.getItem(getPinKey(companyId));
    if (!raw) return null;
    const decrypted = pinDecrypt(raw);
    if (!decrypted) return null;
    return JSON.parse(decrypted);
  } catch { return null; }
}
function savePinData(data, companyId) {
  try {
    const json = JSON.stringify(data);
    localStorage.setItem(getPinKey(companyId), pinEncrypt(json));
  } catch(e) { console.error("Failed to save PIN data", e); }
}

// Permission definitions
const ALL_PERMISSIONS = [
  { key: "sale_cash", label: "Gotovinski računi", group: "Prodaja" },
  { key: "sale_noncash", label: "Bezgotovinski (virman)", group: "Prodaja" },
  { key: "sale_tables", label: "Stolovi", group: "Prodaja" },
  { key: "sale_discount", label: "Primjena popusta", group: "Prodaja" },
  { key: "sale_storno", label: "Storno / refund", group: "Prodaja" },
  { key: "deposit_declare", label: "Polog u kasu", group: "Blagajna" },
  { key: "deposit_withdraw", label: "Podizanje iz kase", group: "Blagajna" },
  { key: "cashregister_close", label: "Zatvori kasu (Z izvještaj)", group: "Blagajna" },
  { key: "view_register_openings", label: "Istorija otvaranja kase", group: "Blagajna" },
  { key: "view_register_closings", label: "Istorija zatvaranja kase", group: "Blagajna" },
  { key: "internalday_open", label: "Otvori interni dan", group: "Interni dan" },
  { key: "internalday_close", label: "Zatvori interni dan", group: "Interni dan" },
  { key: "view_internal_openings", label: "Istorija otvaranja dana", group: "Interni dan" },
  { key: "view_internal_closings", label: "Istorija zatvaranja dana", group: "Interni dan" },
  { key: "view_racuni", label: "Pregled računa", group: "Pregled" },
  { key: "view_izvjestaji", label: "Izvještaji", group: "Pregled" },
  { key: "access_settings", label: "Podešavanja", group: "Sistem" },
  { key: "manage_pins", label: "Upravljanje PIN-ovima", group: "Sistem" },
];

const PERMISSION_PRESETS = {
  full: { label: "Pun pristup", permissions: ALL_PERMISSIONS.map(p => p.key) },
  manager: { label: "Menadžer", permissions: ALL_PERMISSIONS.map(p => p.key).filter(k => k !== "access_settings" && k !== "manage_pins") },
  cashier: { label: "Kasir", permissions: ["sale_cash", "sale_tables", "sale_discount", "deposit_declare", "view_racuni"] },
  viewer: { label: "Pregled", permissions: ["view_racuni", "view_izvjestaji"] },
  custom: { label: "Prilagođeno", permissions: [] },
};

function hasPermission(activeOperator, perm) {
  if (!activeOperator) return true; // no PIN system = full access
  if (activeOperator.is_main_admin) return true;
  return (activeOperator.permissions || []).includes(perm);
}

// Send kitchen-enabled items to kitchen API after invoice creation
async function sendToKitchen(cart, invoiceId, invoiceNumber, tableName) {
  if (localStorage.getItem("pos_kitchen_enabled_" + getCompanyId()) === "false") return;
  const kitchenItems = cart.filter(item => item.kitchen_enabled);
  if (kitchenItems.length === 0) return;
  try {
    await api("/kitchen/orders", { method: "POST", body: JSON.stringify({
      invoice_id: invoiceId,
      table_name: tableName || "",
      order_number: invoiceNumber || "",
      items: kitchenItems.map(item => ({
        product_id: item.id,
        product_name: item.name,
        quantity: item.quantity,
        unit: item.unit || "kom",
        tags: item.kitchen_tags || []
      }))
    }) });
  } catch(e) { console.error("Kitchen send failed:", e); }
}
function fmtDate(d) { if (!d) return "-"; return d.split(" ")[0].split("-").reverse().join("."); }
function fmtDateTime(d) { if (!d) return "-"; const [dt, tm] = d.split(" "); return dt.split("-").reverse().join(".") + " " + (tm || "").slice(0, 5); }
function todayLocal(offsetDays = 0) { const d = new Date(); if (offsetDays) d.setDate(d.getDate() + offsetDays); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
// Get company_id from stored user (for localStorage key scoping)
function getCompanyId() {
  try { return JSON.parse(localStorage.getItem("pos_user"))?.company_id || ""; }
  catch { return ""; }
}
function getTcr() { return localStorage.getItem("pos_tcr_" + getCompanyId()) || ""; }

// ═══ POS LAYOUT — Nova prodaja product grid customization ═══
// Per-device localStorage (4MB image budget). Only affects Nova prodaja grid.
// "Kartice" mode: simple — slider controls card size, no per-product custom.
// "Tabela" mode: per-product cell span, image, name/price toggles, bg color.

const POS_LAYOUT_KEY = "pos_layout_v1_";
const POS_TILES_KEY = "pos_tiles_v1_";
const POS_IMG_BUDGET_BYTES = 4 * 1024 * 1024; // 4 MB

// Card size presets — px width baseline for the auto-fill grid
const CARD_SIZES = {
  vrlo_male:   { px: 110, label: "Vrlo male", fontName: 11, fontPrice: 14, padding: 10 },
  male:        { px: 135, label: "Male",      fontName: 12, fontPrice: 15, padding: 12 },
  srednje:     { px: 160, label: "Srednje",   fontName: 13, fontPrice: 17, padding: 14 },
  velike:      { px: 200, label: "Velike",    fontName: 15, fontPrice: 20, padding: 16 },
  vrlo_velike: { px: 240, label: "Vrlo velike", fontName: 17, fontPrice: 22, padding: 18 },
};

// Cell span presets for "tabela" mode
const SPAN_PRESETS = [
  { x: 1, y: 1, label: "1×1", desc: "Standardna ploča" },
  { x: 2, y: 1, label: "2×1", desc: "Široka" },
  { x: 1, y: 2, label: "1×2", desc: "Visoka" },
  { x: 2, y: 2, label: "2×2", desc: "Velika" },
];

const DEFAULT_LAYOUT = {
  card_size: "srednje",
  style: "kartice",          // kartice | tabela
  show_default_name: true,   // for non-customized tiles in tabela mode
  show_default_price: true,
};

const DEFAULT_TILE = {
  span_x: 1,
  span_y: 1,
  show_name: true,
  show_price: true,
  image: null,
  bg_color: null,
};

function loadLayoutSettings() {
  try {
    const raw = localStorage.getItem(POS_LAYOUT_KEY + getCompanyId());
    if (!raw) return { ...DEFAULT_LAYOUT };
    return { ...DEFAULT_LAYOUT, ...JSON.parse(raw) };
  } catch { return { ...DEFAULT_LAYOUT }; }
}

function saveLayoutSettings(s) {
  try { localStorage.setItem(POS_LAYOUT_KEY + getCompanyId(), JSON.stringify(s)); return true; }
  catch { return false; }
}

function loadTileConfigs() {
  try {
    const raw = localStorage.getItem(POS_TILES_KEY + getCompanyId());
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch { return {}; }
}

function saveTileConfigs(tiles) {
  try { localStorage.setItem(POS_TILES_KEY + getCompanyId(), JSON.stringify(tiles)); return true; }
  catch (e) {
    if (e.name === "QuotaExceededError" || /quota/i.test(e.message)) {
      return false; // signal quota error to caller
    }
    return false;
  }
}

function getTileConfig(productId, allTiles = null) {
  const t = allTiles || loadTileConfigs();
  return { ...DEFAULT_TILE, ...(t[productId] || {}) };
}

function setTileConfig(productId, partial) {
  const all = loadTileConfigs();
  const current = { ...DEFAULT_TILE, ...(all[productId] || {}) };
  const next = { ...current, ...partial };
  // Drop tile entry if it's effectively default (saves space)
  const isDefault = next.span_x === 1 && next.span_y === 1
    && next.show_name === true && next.show_price === true
    && !next.image && !next.bg_color;
  if (isDefault) {
    delete all[productId];
  } else {
    all[productId] = next;
  }
  return { saved: saveTileConfigs(all), tiles: all };
}

// Estimate localStorage usage for tile images (rough)
function estimateImageStorageBytes() {
  try {
    const raw = localStorage.getItem(POS_TILES_KEY + getCompanyId()) || "";
    return raw.length; // 1 char ≈ 1 byte for ASCII (base64 is ASCII)
  } catch { return 0; }
}

// Process an uploaded File into a base64 JPEG, sized to tile dimensions.
// Returns { dataUrl, bytes, width, height } or throws.
// Target px = card-size-px × span × 1.5 (for retina-ish sharpness without bloat)
async function processProductImage(file, spanX = 1, spanY = 1, cardSizeKey = "srednje") {
  if (!file) throw new Error("Nema datoteke");
  const validTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!validTypes.includes(file.type)) throw new Error("Format mora biti JPEG, PNG ili WebP");
  if (file.size > 10 * 1024 * 1024) throw new Error("Slika je veća od 10MB");

  const baseSize = (CARD_SIZES[cardSizeKey] || CARD_SIZES.srednje).px;
  const targetW = Math.round(baseSize * spanX * 1.5); // 1.5× for sharpness
  const targetH = Math.round(baseSize * spanY * 1.5);

  // Read file as DataURL
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Greška pri čitanju datoteke"));
    r.readAsDataURL(file);
  });

  // Load into <img>
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error("Greška pri dekodiranju slike"));
    i.src = dataUrl;
  });

  // Cover-crop into target dimensions (preserve aspect ratio)
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const srcAspect = img.width / img.height;
  const dstAspect = targetW / targetH;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (srcAspect > dstAspect) {
    // src wider — crop sides
    sw = img.height * dstAspect;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / dstAspect;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);

  // Encode JPEG @ 0.78 (balanced)
  const out = canvas.toDataURL("image/jpeg", 0.78);
  const bytes = Math.round((out.length * 3) / 4); // base64 → byte estimate
  return { dataUrl: out, bytes, width: targetW, height: targetH };
}

// ─── Print / PDF / Email helpers ───
// ═══ UNIFIED PRINT SYSTEM ═══
// Detects platform: Electron (native printer) / Capacitor (system print) / Browser (popup print)
const isElectron = !!(window.electronAPI?.isElectron);
const isCapacitor = !!(window.Capacitor?.isNativePlatform?.());

async function openPdf(path) {
  const token = localStorage.getItem("pos_token");
  try {
    const r = await fetch(`${API_BASE}${path}`, { headers: { "Authorization": `Bearer ${token}` } });
    if (!r.ok) throw new Error("PDF error");
    const ct = r.headers.get("content-type") || "";
    if (ct.includes("text/html")) {
      const html = await r.text();
      const win = window.open("", "_blank", "width=400,height=700");
      if (win) { win.document.write(html); win.document.close(); }
    } else {
      const blob = await r.blob();
      if (isElectron) {
        // Electron: show PDF in full-screen overlay with iframe
        const reader = new FileReader();
        reader.onload = function() {
          const dataUrl = reader.result;
          const overlay = document.createElement("div");
          overlay.id = "pdf-overlay";
          overlay.style.cssText = "position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.9);display:flex;flex-direction:column";
          const bar = document.createElement("div");
          bar.style.cssText = "display:flex;justify-content:space-between;align-items:center;padding:10px 16px;background:#1e293b;flex-shrink:0";
          bar.innerHTML = '<span style="color:white;font-weight:600;font-size:15px">PDF Pregled</span>';
          const btnDiv = document.createElement("div");
          btnDiv.style.cssText = "display:flex;gap:8px";
          // Download button
          const dlBtn = document.createElement("a");
          dlBtn.href = dataUrl;
          dlBtn.download = path.includes("openingPdf") ? "otvaranje.pdf" : path.includes("closingPdf") ? "zatvaranje.pdf" : "racun.pdf";
          dlBtn.textContent = "⬇ Sačuvaj";
          dlBtn.style.cssText = "padding:8px 16px;background:#3b82f6;color:white;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;text-decoration:none";
          btnDiv.appendChild(dlBtn);
          // Close button
          const closeBtn = document.createElement("button");
          closeBtn.textContent = "✕ Zatvori";
          closeBtn.style.cssText = "padding:8px 16px;background:#ef4444;color:white;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer";
          closeBtn.onclick = function() { document.getElementById("pdf-overlay")?.remove(); };
          btnDiv.appendChild(closeBtn);
          bar.appendChild(btnDiv);
          overlay.appendChild(bar);
          const iframe = document.createElement("iframe");
          iframe.src = dataUrl;
          iframe.style.cssText = "flex:1;width:100%;border:none;background:white";
          overlay.appendChild(iframe);
          document.body.appendChild(overlay);
        };
        reader.readAsDataURL(blob);
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    }
  } catch (e) { return false; }
  return true;
}

// Read an HTTP response body as a binary string preserving bytes 0x00-0xFF.
// Use this when the response contains raw ESC/POS bytes (including the QR
// raster bitmap embedded by the PHP backend's escposQrBitmap()). The default
// response.text() would UTF-8-decode the body and corrupt any byte ≥ 0x80,
// turning the bitmap into garbage and the printer into a glyph-spewing mess.
//
// Output is a JS string where charCodeAt(i) === byte i. Pass directly to
// electronAPI.printText() — Electron main.js does Buffer.from(s, "binary")
// which reverses this and gets the original bytes back, byte-perfect.
//
// HISTORY: This helper was originally added 2026-04-29 during the QR raster
// bitmap rollout (Stage 2 fix). It was inadvertently removed in a later
// merge, causing fiscal receipts with QR codes to print garbled bitmaps
// (printer freezes on the QR portion). Restored 2026-05-10 to fix the
// regression.
async function readResponseAsBinaryString(r) {
  const buf = await r.arrayBuffer();
  const u8 = new Uint8Array(buf);
  // String.fromCharCode.apply has an arg-count limit on big buffers,
  // and concatenation in a tight loop is slow. Build in 8KB chunks for
  // speed and safety. 8KB stays well under apply()'s limit and keeps
  // memory pressure low even for ~10KB+ payloads with QR bitmap inside.
  let s = "";
  const chunkSize = 8192;
  for (let i = 0; i < u8.length; i += chunkSize) {
    s += String.fromCharCode.apply(null, u8.subarray(i, i + chunkSize));
  }
  return s;
}

// Print receipt — uses best available method per platform
async function printReceipt(invoiceId) {
  const token = localStorage.getItem("pos_token");

  // Method 1: Electron — native ESC/POS via IPC
  // Reads response body as binary string (preserves raster bitmap bytes
  // for QR codes). Without this, the QR portion of the receipt would be
  // UTF-8-corrupted and the printer would emit garbage glyphs after the QR.
  if (isElectron && window.electronAPI) {
    try {
      // When WinUSB direct-print is enabled, ask the backend for native QR
      // (GS(k command) instead of raster bitmap. The raster freezes cheap
      // thermal printers when sent via libusb — native QR sends only ~200
      // bytes total and the printer renders the QR itself. For other modes
      // (spooler / CUPS) the raster works fine and looks slightly better,
      // so default stays as raster.
      let qrModeParam = "";
      try {
        const s = await window.electronAPI.getSettings();
        if (s && s.useWinUsb) qrModeParam = "&qrMode=native";
      } catch {}
      const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text${qrModeParam}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        // CRITICAL: use binary-safe read, not r.text(). r.text() UTF-8-decodes
        // the response which corrupts the QR raster bitmap bytes.
        const text = await readResponseAsBinaryString(r);
        const result = await window.electronAPI.printText(text);
        return result.success;
      }
    } catch (e) { console.log("Electron print failed:", e); }
  }

  // Method 2: Network print server (for mobile/tablet)
  const printerType = localStorage.getItem("pos_printer_type");
  const printerAddr = localStorage.getItem("pos_printer_addr");
  if (printerType === "network" && printerAddr) {
    try {
      const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        const text = await r.text();
        const printUrl = (printerAddr || "").endsWith("/") ? printerAddr.slice(0,-1) + "/print/text" : printerAddr + "/print/text";
        const pr = await fetch(printUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text })
        });
        const result = await pr.json();
        return result.success;
      }
    } catch (e) { console.log("Network print failed:", e); }
  }

  // Method 3: All platforms — HTML receipt popup with system print dialog
  return printReceiptHTML(invoiceId);
}

// ═══ Print SELF kasa ticket ═══
// Mirrors printReceipt() above but fetches the SELF ticket ESC/POS bytes
// from /self-kasa/ticketEscPos/{id}. Used by sendToSelf() in POSView for
// auto-print on successful send.
//
// Routes:
//   1. Electron: fetch bytes → window.electronAPI.printText() → WinUSB or spooler
//      (whichever is configured in Postavke → Printer)
//   2. Web/Capacitor: not supported (no native printer access). Caller should
//      check isElectron and skip print on web.
//
// Returns true if printed, false on any error. Errors are logged but never
// thrown — auto-print failures should not block the SELF order flow.
async function printSelfTicket(selfOrderId) {
  if (!isElectron || !window.electronAPI) {
    // Web/Capacitor: no native printer. Caller already showed the success toast.
    return false;
  }
  const token = localStorage.getItem("pos_token");
  // Honor the user's paper width preference, same as fiscal receipts
  const paperWidth = localStorage.getItem("pos_paper_width") || "80";
  try {
    const r = await fetch(`${API_BASE}/self-kasa/ticketEscPos/${selfOrderId}?paperWidth=${paperWidth}`, {
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (!r.ok) {
      console.log("SELF ticket fetch failed:", r.status);
      return false;
    }
    // Use binary-safe read for consistency with fiscal printReceipt path.
    // SELF tickets are pure ASCII today (no QR raster, no logo bitmap), so
    // r.text() would work, but using the binary helper future-proofs in case
    // we ever add a company logo or QR to the ticket.
    const text = await readResponseAsBinaryString(r);
    const result = await window.electronAPI.printText(text);
    return result.success;
  } catch (e) {
    console.log("SELF ticket print failed:", e);
    return false;
  }
}

// HTML receipt printing — works everywhere (browser, Capacitor, Electron fallback)
async function printReceiptHTML(invoiceId) {
  const token = localStorage.getItem("pos_token");
  try {
    const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=html`, {
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (!r.ok) return false;
    const html = await r.text();

    if (isElectron && window.electronAPI?.printHTML) {
      await window.electronAPI.printHTML(html);
      return true;
    }

    // Browser / Capacitor — open print-styled popup
    const width = parseInt(localStorage.getItem("pos_paper_width") || localStorage.getItem("minipos_paper_width") || "80");
    const printWidth = width <= 58 ? "58mm" : "80mm";
    const win = window.open("", "print_receipt", `width=350,height=600`);
    if (win) {
      win.document.write(`<html><head><style>@page{size:${printWidth} auto;margin:2mm;}body{font-family:monospace;font-size:12px;margin:0;padding:4mm;width:${printWidth};}@media print{body{width:${printWidth};}}</style></head><body>${html}<script>setTimeout(function(){window.print();},300);<\/script></body></html>`);
      win.document.close();
    }
    return true;
  } catch (e) { return false; }
}

// Print cash register report (opening/closing)
async function printRegisterReport(sessionId, type) {
  const token = localStorage.getItem("pos_token");
  let endpoint;
  if (type === "opening") endpoint = "openingPdf";
  else if (type === "closing") endpoint = "closingPdf";
  else if (type === "internal_closing") endpoint = "closingPdf";
  else endpoint = "closingPdf";

  // Method 1: Electron — ESC/POS thermal via text endpoint
  if (isElectron && window.electronAPI) {
    try {
      const r = await fetch(`${API_BASE}/cashregister/${endpoint}/${sessionId}?type=text`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        const text = await r.text();
        await window.electronAPI.printText(text);
        return true;
      }
    } catch (e) { console.log("Thermal print failed:", e); }
  }

  // Method 2: HTML print fallback
  try {
    const r = await fetch(`${API_BASE}/cashregister/${endpoint}/${sessionId}?type=html`, {
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (!r.ok) return false;
    const html = await r.text();
    const win = window.open("", "print_report", "width=350,height=500");
    if (win) {
      win.document.write(`<html><head><style>@page{size:80mm auto;margin:2mm;}body{font-family:monospace;font-size:12px;margin:0;padding:4mm;}</style></head><body>${html}<script>setTimeout(function(){window.print();},300);<\/script></body></html>`);
      win.document.close();
    }
    return true;
  } catch { return false; }
}

// Legacy compatibility — used throughout the codebase
async function thermalPrint(endpoint, data) {
  // If Electron, try native print for register reports
  if (isElectron && window.electronAPI && endpoint === "/print/text" && data.text) {
    try { const r = await window.electronAPI.printText(data.text); return r.success; } catch {}
  }
  // Fallback: try old print server if configured
  const addr = localStorage.getItem("pos_printer_addr") || "http://localhost:9100";
  try {
    const r = await fetch(`${addr}${endpoint}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    });
    const d = await r.json();
    return d.success;
  } catch { return false; }
}

async function checkPrintServer() {
  if (isElectron && window.electronAPI) {
    try { const r = await window.electronAPI.testConnection(); return r.success; } catch {}
  }
  const addr = localStorage.getItem("pos_printer_addr") || "http://localhost:9100";
  try {
    const r = await fetch(`${addr}/status`, { signal: AbortSignal.timeout(2000) });
    const d = await r.json();
    return d.success;
  } catch { return false; }
}

const S = {
  font: "'DM Sans', -apple-system, sans-serif", mono: "'JetBrains Mono', monospace",
  bg: "#f6f6f7", dark: "#1a1a2e", accent: "#4f46e5", green: "#059669",
  red: "#dc2626", orange: "#ea580c", border: "#e5e7eb", muted: "#6b7280",
  input: { width: "100%", padding: "12px 14px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 15, outline: "none", background: "#f9fafb", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" },
  btn: (bg, color = "white") => ({ padding: "12px 20px", background: bg, color, border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif" }),
  btnSm: (bg, color = "white") => ({ padding: "9px 16px", background: bg, color, border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'DM Sans', sans-serif" }),
};
const th = { padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 13, borderBottom: "1px solid #e5e7eb" };
const td = { padding: "12px 14px", color: "#1a1a2e", fontSize: 14 };

function Row({ label, value, last }) {
  return <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: last ? 0 : 10 }}><span style={{ color: S.muted, fontSize: 14 }}>{label}</span><span style={{ fontSize: 14, color: S.dark }}>{value}</span></div>;
}
function CatTab({ active, onClick, label }) {
  return <button onClick={onClick} style={{ padding: "8px 18px", border: "1px solid", borderColor: active ? S.accent : S.border, background: active ? S.accent : "white", color: active ? "white" : S.muted, borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: S.font }}>{label}</button>;
}
function StatCard({ icon: Icon, label, value, color, hidden }) {
  return (
    <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={17} color={color} /></div>
        <span style={{ fontSize: 14, color: S.muted, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: S.dark }}>{hidden ? "••••••" : value}</div>
    </div>
  );
}
function Stat({ label, value }) { return <div><div style={{ fontSize: 13, color: S.muted }}>{label}</div><div style={{ fontSize: 16, fontWeight: 600, color: S.dark }}>{value}</div></div>; }
function Modal({ children, onClose, title, zIndex = 1000 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "white", borderRadius: 14, width: 440, maxHeight: "80vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 17 }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}><X size={18} color={S.muted} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}
/**
 * QtyNumpadModal — touch-friendly decimal quantity entry.
 *
 * Used when a product has is_decimal_unit=1 (e.g. fish/meat sold by 0.77 kg).
 * Caps input at 2 decimals — matches Poreska spec which mandates 2dp precision
 * for amount calculations (line 406 of Funkcionalna spec v5). Our DB column
 * invoice_items.quantity is decimal(10,3) so 2dp fits comfortably.
 *
 * Locale: Montenegrin users type comma decimal ("0,77"). We accept both comma
 * and dot, normalize to dot internally before saving — XMLGenerator already
 * formats Q with number_format($qty, 2, '.', '') for fiscal output.
 *
 * Props:
 *   - product: the cart line product object (has name, unit, current quantity)
 *   - onClose: dismisses without saving
 *   - onSave(qty): called with parsed float on confirm
 */
function QtyNumpadModal({ product, onClose, onSave }) {
  // Display string holds the user's typing — stays as a string so we can detect
  // trailing decimal separator before they finish typing (e.g. they tap "0", then
  // ".", then nothing yet — display "0." until they type a digit).
  const [display, setDisplay] = useState(String(product.quantity ?? "1").replace(".", ","));

  const append = (ch) => {
    setDisplay(prev => {
      // First digit when display is "0" replaces it (so users tapping 5 don't get "05")
      if (prev === "0") return ch;
      // Reject second decimal separator
      if ((ch === "," || ch === ".") && /[,.]/.test(prev)) return prev;
      // Cap at 2 decimals after separator
      const sepMatch = prev.match(/[,.]/);
      if (sepMatch) {
        const afterSep = prev.length - sepMatch.index - 1;
        if (afterSep >= 2 && ch !== "," && ch !== ".") return prev;
      }
      return prev + ch;
    });
  };
  const backspace = () => setDisplay(prev => prev.length <= 1 ? "0" : prev.slice(0, -1));
  const clear = () => setDisplay("0");

  const parseQty = () => {
    const normalized = display.replace(",", ".");
    const n = parseFloat(normalized);
    return isNaN(n) || n <= 0 ? 0 : n;
  };
  const handleSave = () => {
    const q = parseQty();
    if (q <= 0) return; // disabled state below should prevent this anyway
    onSave(q);
  };

  const valid = parseQty() > 0;

  // Numpad layout: 7 8 9 / 4 5 6 / 1 2 3 / , 0 ⌫
  const keys = [
    ["7","8","9"],
    ["4","5","6"],
    ["1","2","3"],
    [",","0","⌫"],
  ];

  return (
    // QtyNumpadModal can be opened FROM inside another modal (e.g. the Stolovi
    // "Nova narudžba" modal where the user is building a cart, or the Virman
    // partner modal). Use a higher zIndex so it always renders above whatever
    // triggered it. Standard Modal default is 1000.
    <Modal onClose={onClose} title="Količina" zIndex={1100}>
      <div style={{ marginBottom: 8, fontSize: 13, color: S.muted }}>
        {product.name}{product.unit ? ` (${product.unit})` : ""}
      </div>
      <div style={{
        background: "#f9fafb",
        borderRadius: 10,
        padding: "18px 20px",
        textAlign: "right",
        fontSize: 36,
        fontWeight: 700,
        fontFamily: S.font,
        color: valid ? S.dark : "#9ca3af",
        marginBottom: 14,
        minHeight: 60,
      }}>
        {display}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {keys.flat().map(k => (
          <button
            key={k}
            onClick={() => k === "⌫" ? backspace() : append(k)}
            style={{
              padding: "16px 0",
              fontSize: 22,
              fontWeight: 600,
              background: "white",
              border: `1px solid ${S.border}`,
              borderRadius: 10,
              cursor: "pointer",
              color: S.dark,
              fontFamily: S.font,
            }}
          >{k}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={clear} style={{ ...S.btn("#f3f4f6"), color: S.muted, flex: 1, justifyContent: "center" }}>Briši</button>
        <button onClick={handleSave} disabled={!valid} style={{ ...S.btn(S.green), flex: 2, justifyContent: "center", opacity: valid ? 1 : 0.5 }}>
          <Save size={15} />Sačuvaj
        </button>
      </div>
    </Modal>
  );
}

function Pagination({ pg, onChange }) {
  return (
    <div style={{ padding: "10px 20px", borderTop: `1px solid ${S.border}`, background: "white", display: "flex", justifyContent: "center", gap: 4 }}>
      {Array.from({ length: Math.min(pg.total_pages, 10) }, (_, i) => i + 1).map(p => (
        <button key={p} onClick={() => onChange(p)} style={{ width: 32, height: 32, border: "1px solid", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, borderColor: pg.page === p ? S.dark : S.border, background: pg.page === p ? S.dark : "white", color: pg.page === p ? "white" : S.muted, fontFamily: S.font }}>{p}</button>
      ))}
    </div>
  );
}
function Badge({ status }) {
  const m = { fiscalized: { bg: "#f0fdf4", c: "#15803d", l: "Fiskalizovano" }, draft: { bg: "#fefce8", c: "#a16207", l: "Draft" }, failed: { bg: "#fef2f2", c: S.red, l: "Neuspjelo" }, storno: { bg: "#f3f4f6", c: S.muted, l: "Storno" }, cancelled: { bg: "#f3f4f6", c: S.muted, l: "Otkazano" } };
  const s = m[status] || m.draft;
  return <span style={{ padding: "3px 10px", borderRadius: 5, fontSize: 12, fontWeight: 600, background: s.bg, color: s.c }}>{s.l}</span>;
}
function NumPad({ value, onChange, onClose, title }) {
  const [val, setVal] = useState(value || "");
  const press = (n) => setVal(v => v === "0" ? String(n) : v + String(n));
  const dot = () => setVal(v => v.includes(".") ? v : (v || "0") + ".");
  const del = () => setVal(v => v.slice(0, -1) || "");
  const clear = () => setVal("");
  const confirm = () => { onChange(val); onClose(); };
  const btnS = { width: "100%", padding: 16, border: `1px solid ${S.border}`, borderRadius: 10, fontSize: 22, fontWeight: 700, cursor: "pointer", background: "white", fontFamily: S.font, color: S.dark };
  return (
    <Modal onClose={onClose} title={title || "Unesite iznos"}>
      <div style={{ background: "#f9fafb", borderRadius: 10, padding: "14px 18px", marginBottom: 16, textAlign: "right" }}>
        <div style={{ fontSize: 32, fontWeight: 700, color: S.dark, minHeight: 44 }}>{val || "0"}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        {[1,2,3,4,5,6,7,8,9].map(n => <button key={n} onClick={() => press(n)} style={btnS}>{n}</button>)}
        <button onClick={dot} style={btnS}>.</button>
        <button onClick={() => press(0)} style={btnS}>0</button>
        <button onClick={del} style={{ ...btnS, color: S.red, fontSize: 18 }}>&#9003;</button>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button onClick={clear} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center", padding: 12, fontSize: 15 }}>Obriši</button>
        <button onClick={confirm} style={{ ...S.btn(S.green), flex: 2, justifyContent: "center", padding: 12, fontSize: 17 }}>Potvrdi</button>
      </div>
    </Modal>
  );
}
function DiscountForm({ current, onApply, onClose }) {
  const [type, setType] = useState(current.type === "none" ? "percentage" : current.type);
  const [value, setValue] = useState(current.value || "");
  const presets = [5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90];
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button onClick={() => setType("percentage")} style={{ ...S.btn(type === "percentage" ? S.accent : "#f3f4f6", type === "percentage" ? "white" : S.muted), flex: 1, justifyContent: "center" }}><Percent size={15} />Procenat</button>
        <button onClick={() => setType("fixed")} style={{ ...S.btn(type === "fixed" ? S.accent : "#f3f4f6", type === "fixed" ? "white" : S.muted), flex: 1, justifyContent: "center" }}><Euro size={15} />Iznos</button>
      </div>
      {type === "percentage" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 12 }}>
          {presets.map(p => (
            <button key={p} onClick={() => { setValue(p); onApply({ type: "percentage", value: p }); }} style={{ padding: "12px 4px", border: `2px solid ${Number(value) === p ? S.green : S.border}`, background: Number(value) === p ? "#f0fdf4" : "white", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: "pointer", color: Number(value) === p ? S.green : S.dark, fontFamily: S.font }}>{p}%</button>
          ))}
        </div>
      )}
      <input type="number" step="0.01" value={value} onChange={e => setValue(e.target.value)} placeholder={type === "percentage" ? "npr. 10" : "npr. 5.00"} style={{ ...S.input, marginBottom: 16 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
        <button onClick={() => onApply({ type, value: parseFloat(value) || 0 })} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>Primijeni</button>
      </div>
    </div>
  );
}
function StornoForm({ onStorno, onClose }) {
  const [reason, setReason] = useState("");
  const reasons = [{ id: "STORNO_OP", l: "Greška operatera" }, { id: "STORNO_BUYER", l: "Zahtjev kupca" }, { id: "STORNO_RETURN", l: "Povrat robe" }];
  return (
    <div>
      <p style={{ fontSize: 15, color: S.muted, marginBottom: 14 }}>Izaberite razlog storniranja:</p>
      {reasons.map(r => (
        <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, marginBottom: 5, cursor: "pointer", background: reason === r.id ? "#f0fdf4" : "#f9fafb", border: `1px solid ${reason === r.id ? S.green : S.border}` }}>
          <input type="radio" name="reason" checked={reason === r.id} onChange={() => setReason(r.id)} style={{ accentColor: S.green, width: 16, height: 16 }} />
          <span style={{ fontSize: 15 }}>{r.l}</span>
        </label>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
        <button onClick={() => reason && onStorno(reason)} disabled={!reason} style={{ ...S.btn(S.red), flex: 1, justifyContent: "center", opacity: reason ? 1 : 0.5 }}><RotateCcw size={15} />Storniraj</button>
      </div>
    </div>
  );
}
function EmailForm({ onSend, onClose }) {
  const [email, setEmail] = useState("");
  return (
    <div>
      <label style={{ fontSize: 14, fontWeight: 600 }}>Email adresa</label>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kupac@email.com" style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
        <button onClick={() => email && onSend(email)} disabled={!email} style={{ ...S.btn(S.accent), flex: 1, justifyContent: "center", opacity: email ? 1 : 0.5 }}><Mail size={15} />Pošalji</button>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    const data = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password, app: "pos" }) });
    setLoading(false);
    data.success ? (() => { onLogin(data.data.token, data.data.operator); })() : setError(data.message || "Login failed");
  };
  return (
    <div style={{ minHeight: "100vh", background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: S.font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <div style={{ width: 400, background: "white", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.08)", padding: "40px 32px", border: `1px solid ${S.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: S.dark, margin: 0 }}>EFIS.PRO <span style={{ color: S.accent, fontWeight: 600 }}>Fiscal POS</span></h1>
          <p style={{ color: S.muted, fontSize: 14, marginTop: 6 }}>Prijavite se na vaš nalog</p>
        </div>
        <form onSubmit={submit}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: S.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="operator@company.me" style={{ ...S.input, marginBottom: 14, padding: "12px 14px" }} />
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: S.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Lozinka</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && submit(e)} style={{ ...S.input, marginBottom: 20, padding: "12px 14px" }} />
          {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}><AlertCircle size={15} color={S.red} /><span style={{ color: S.red, fontSize: 14 }}>{error}</span></div>}
          <button type="submit" disabled={loading} style={{ ...S.btn(S.dark), width: "100%", padding: "14px", fontSize: 16, justifyContent: "center", opacity: loading ? 0.7 : 1, borderRadius: 10 }}>{loading ? "Prijava..." : "Prijavi se"}</button>
        </form>
      </div>
    </div>
  );
}

function POSApp({ user, onLogout, activeOperator, onLock }) {
  const [view, setView] = useState("home");
  const [toast, setToast] = useState(null);
  const [tablesEnabled, setTablesEnabled] = useState(false);
  const [tableForOrder, setTableForOrder] = useState(null); // {sessionId, tableName} when ordering for a table
  const [online, setOnline] = useState(navigator.onLine !== false);
  const [syncing, setSyncing] = useState(false);
  const suppressToastRef = useRef(false);

  // Nova prodaja layout config (loaded from per-device localStorage)
  const [posLayout, setPosLayout] = useState(() => loadLayoutSettings());
  const [posTiles, setPosTiles] = useState(() => loadTileConfigs());
  // Bump this to force re-read after Settings changes that may go via localStorage
  const reloadPosLayout = () => {
    setPosLayout(loadLayoutSettings());
    setPosTiles(loadTileConfigs());
  };

  // Permission helper for this operator
  const canAccess = (perm) => hasPermission(activeOperator, perm);
  const showToast = (msg, type = "success") => {
    if (suppressToastRef.current && msg.includes("Offline mod")) return; // Don't overwrite draft toast
    setToast({ message: msg, type }); setTimeout(() => setToast(null), msg.includes("Draft") || msg.includes("lokalno") ? 6000 : 4000);
  };

  // Online/offline detection + auto-sync
  const wasOfflineRef = useRef(false);
  const onlineRef = useRef(true);
  const syncDrafts = async () => {
    const drafts = getOfflineDrafts();
    if (drafts.length === 0) return;
    setSyncing(true);
    let synced = 0;
    for (const draft of drafts) {
      try {
        const d = await api("/invoices", { method: "POST", body: JSON.stringify(draft.payload) });
        if (d.success) { removeOfflineDraft(draft.id); synced++; }
      } catch(e) {}
    }
    setSyncing(false);
    const remaining = getOfflineDrafts().length;
    if (synced > 0) showToast(`Ponovo online! ${synced} draft račun${synced > 1 ? "a" : ""} sačuvan${synced > 1 ? "o" : ""} u bazi.`, "success");
    if (remaining > 0) showToast(`${remaining} draft nije uspjelo sinhronizovati.`, "error");
  };
  useEffect(() => {
    const goOnline = () => { setOnline(true); onlineRef.current = true; };
    const goOffline = () => { setOnline(false); onlineRef.current = false; wasOfflineRef.current = true; showToast("Offline mod - podaci iz keša", "error"); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const poll = setInterval(async () => {
      const token = localStorage.getItem("pos_token");
      if (!token) return;
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 4000);
        const r = await fetch(`${API_BASE}/ping`, { headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }, signal: controller.signal });
        clearTimeout(timeout);
        if (r.status !== 0) {
          if (!onlineRef.current || wasOfflineRef.current) {
            setOnline(true); onlineRef.current = true;
            if (wasOfflineRef.current) {
              wasOfflineRef.current = false;
              const drafts = getOfflineDrafts();
              if (drafts.length > 0) { showToast(`Ponovo online! Sinhronizujem ${drafts.length} draft${drafts.length > 1 ? "ova" : ""}...`, "success"); setTimeout(syncDrafts, 1000); }
              else showToast("Ponovo online!", "success");
            }
          }
        }
      } catch(e) {
        if (onlineRef.current) { setOnline(false); onlineRef.current = false; wasOfflineRef.current = true; showToast("Offline mod - podaci iz keša", "error"); }
      }
    }, 20000);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); clearInterval(poll); };
  }, []);

  // Check tables setting on mount
  useEffect(() => {
    api("/tables/settings").then(r => { if (r.success) setTablesEnabled(!!r.data?.tables_enabled); });
  }, []);

  // Auto-select TCR if none stored
  useEffect(() => {
    const stored = localStorage.getItem("pos_tcr_" + getCompanyId());
    if (stored) return; // already have one
    api("/auth/company-config").then(r => {
      if (r.success) {
        const allTcrs = r.data?.all_tcrs || [];
        const active = allTcrs.filter(t => t.status === "active");
        if (active.length > 0) {
          localStorage.setItem("pos_tcr_" + getCompanyId(), active[0].tcr_code);
        }
      }
    });
  }, []);

  // Auto-lock timer
  useEffect(() => {
    if (!onLock) return; // no PIN system
    const pinData = loadPinData(user.company_id);
    const timeout = (pinData?.auto_lock_minutes || 5) * 60 * 1000;
    let timer;
    const resetTimer = () => { clearTimeout(timer); timer = setTimeout(() => onLock(), timeout); };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, resetTimer)); };
  }, [onLock]);

  const navTop = [
    { id: "home", icon: Home, label: "Početna" },
    ...(tablesEnabled && canAccess("sale_tables") ? [{ id: "stolovi", icon: Coffee, label: "Stolovi" }] : []),
    ...(canAccess("sale_cash") || canAccess("sale_noncash") ? [{ id: "pos", icon: LayoutGrid, label: "Nova Prodaja" }] : []),
    ...(canAccess("view_racuni") ? [{ id: "invoices", icon: FileText, label: "Računi" }] : []),
  ];
  const navBottom = [
    ...(canAccess("deposit_declare") || canAccess("deposit_withdraw") || canAccess("cashregister_close") ? [{ id: "blagajna", icon: Wallet, label: "Blagajna" }] : []),
    ...(canAccess("sale_noncash") ? [{ id: "virman", icon: Building, label: "Virman" }] : []),
    ...(canAccess("view_izvjestaji") ? [{ id: "dashboard", icon: BarChart3, label: "Izvještaji" }] : []),
    ...(canAccess("access_settings") ? [{ id: "settings", icon: Settings, label: "Podešavanja" }] : []),
  ];

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // When returning from table order, go back to stolovi
  const handleTableOrderDone = (invoiceData) => {
    setTableForOrder(null);
    setView("stolovi");
  };

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: S.font, background: S.bg, paddingBottom: (!window.location.port && window.location.hostname === "localhost") ? 10 : 0 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}@keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}*{box-sizing:border-box}input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}::-webkit-scrollbar{width:6px}::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:3px}`}</style>

      {/* Sidebar overlay */}
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 900, animation: "fadeIn 0.2s" }} />}

      {/* Sidebar drawer */}
      <div style={{ position: "fixed", left: sidebarOpen ? 0 : -280, top: 0, bottom: 0, width: 280, background: S.dark, zIndex: 950, transition: "left 0.25s ease", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, background: "rgba(255,255,255,0.1)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}><Receipt size={17} color="#a5b4fc" /></div>
            <div><div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>EFIS.PRO</div><div style={{ color: "#6b6b80", fontSize: 12 }}>v3.0</div></div>
          </div>
          <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}><X size={18} color="#8c8c9a" /></button>
        </div>

        {/* Top nav items */}
        <nav style={{ flex: 1, padding: "10px 8px", display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1 }}>
            {navTop.map(item => (
              <button key={item.id} onClick={() => { setView(item.id); setSidebarOpen(false); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 14px", border: "none", borderRadius: 8, cursor: "pointer", marginBottom: 3, fontSize: 15, fontWeight: 500, background: view === item.id ? "rgba(165,180,252,0.12)" : "transparent", color: view === item.id ? "#a5b4fc" : "#8c8c9a", fontFamily: S.font }}>
                <item.icon size={19} />{item.label}
              </button>
            ))}
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 8 }}>
            {navBottom.map(item => (
              <button key={item.id} onClick={() => { setView(item.id); setSidebarOpen(false); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 14px", border: "none", borderRadius: 8, cursor: "pointer", marginBottom: 3, fontSize: 15, fontWeight: 500, background: view === item.id ? "rgba(165,180,252,0.12)" : "transparent", color: view === item.id ? "#a5b4fc" : "#8c8c9a", fontFamily: S.font }}>
                <item.icon size={19} />{item.label}
              </button>
            ))}
          </div>
        </nav>

        {/* User + logout */}
        <div style={{ padding: "14px 12px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 30, height: 30, background: "#2d2d44", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}><User size={14} color="#a5b4fc" /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "white", fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeOperator?.name || user.name}</div>
              <div style={{ color: "#6b6b80", fontSize: 11 }}>{user.company_name}</div>
              {activeOperator?.operator_code ? (
                <div style={{ color: "#6b6b80", fontSize: 10, fontFamily: "JetBrains Mono, monospace", marginTop: 2 }}>OP: {activeOperator.operator_code}</div>
              ) : (
                <div style={{ color: "#f87171", fontSize: 10, fontWeight: 600, marginTop: 2 }}>⚠ Operater nije postavljen</div>
              )}
            </div>
          </div>
          <button onClick={onLogout} style={{ ...S.btnSm("rgba(220,38,38,0.1)", "#f87171"), width: "100%", justifyContent: "center" }}><LogOut size={13} />Odjavi se</button>
        </div>
      </div>

      {/* Main content area — full width */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative", display: "flex", flexDirection: "column" }}>
        {/* Safe area spacer for Capacitor native apps */}
        {!window.location.port && window.location.hostname === "localhost" && <div style={{ height: 28, background: "white", flexShrink: 0 }} />}
        {/* Top bar with hamburger */}
        <div style={{ height: 48, background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", padding: "0 16px", gap: 12, flexShrink: 0 }}>
          <button onClick={() => setSidebarOpen(true)} style={{ background: "none", border: "none", cursor: "pointer", padding: 10, borderRadius: 8, display: "flex", alignItems: "center" }}>
            <Menu size={26} color={S.dark} />
          </button>
          <div style={{ fontWeight: 700, fontSize: 16, color: S.dark }}>
            {view === "home" ? "EFIS.PRO" : (navTop.concat(navBottom).find(n => n.id === view)?.label || "EFIS.PRO")}
          </div>
          <div style={{ flex: 1 }} />
          {/* Quick navigation shortcuts: Stolovi + Nova Prodaja. Saves one tap
              vs opening the hamburger menu, especially valuable on tablets where
              waiters jump between table view and POS many times per shift.
              
              Visibility rules per spec:
              - Stolovi: shown if operator has sale_tables permission. When
                company-level tables_enabled is OFF, button is greyed/disabled
                (admin can still click but feature isn't usable).
              - Nova Prodaja: shown if operator has sale_cash OR sale_noncash.
              - Both: highlighted (accent color background) when user is currently
                on that view; muted background otherwise. */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 6 }}>
            {canAccess("sale_tables") && (
              <button
                onClick={() => { if (tablesEnabled) setView("stolovi"); }}
                disabled={!tablesEnabled}
                title={tablesEnabled ? "Stolovi" : "Stolovi nisu omogućeni za ovu firmu"}
                style={{
                  background: view === "stolovi" ? S.accent : (tablesEnabled ? "#f3f4f6" : "#f9fafb"),
                  color: view === "stolovi" ? "white" : (tablesEnabled ? S.dark : "#cbd5e0"),
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: tablesEnabled ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  opacity: tablesEnabled ? 1 : 0.5,
                  transition: "background 0.1s",
                }}
              >
                <LayoutGrid size={18} />
              </button>
            )}
            {(canAccess("sale_cash") || canAccess("sale_noncash")) && (
              <button
                onClick={() => setView("pos")}
                title="Nova Prodaja"
                style={{
                  background: view === "pos" ? S.accent : "#f3f4f6",
                  color: view === "pos" ? "white" : S.dark,
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  transition: "background 0.1s",
                }}
              >
                <ShoppingCart size={18} />
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 12, color: S.muted, display: "flex", alignItems: "center", gap: 4 }}>
              <User size={12} />
              {activeOperator?.name || user.name}
              {activeOperator?.is_main_admin && <span style={{ fontSize: 10, color: "#3b82f6", fontWeight: 600 }}>ADMIN</span>}
              {activeOperator?.operator_code ? (
                <span style={{ fontSize: 10, color: "#9ca3af", fontFamily: "JetBrains Mono, monospace" }}>· {activeOperator.operator_code}</span>
              ) : (
                <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 700 }}>⚠ Bez koda</span>
              )}
            </div>
            {onLock && <button onClick={onLock} title="Zaključaj" style={{ background: "rgba(59,130,246,0.08)", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "#3b82f6", fontFamily: S.font }}>
              <Square size={13} />🔒
            </button>}
          </div>
        </div>

        {/* Offline indicator */}
        {(!online || syncing) && <div style={{ background: !online ? "#ef4444" : S.accent, color: "white", textAlign: "center", padding: "5px 12px", fontSize: 12, fontWeight: 600, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {syncing ? <><RefreshCw size={13} className="spin" />Sinhronizacija draft računa...</> : <><WifiOff size={13} />Offline — podaci iz keša</>}
        </div>}

        {/* Views */}
        <div style={{ flex: 1, overflow: "hidden" }}>
        {view === "home" && <HomeView user={user} onNavigate={(v) => setView(v)} activeOperator={activeOperator} />}
        {view === "stolovi" && <StoloviView user={user} showToast={showToast} onOrderForTable={(s) => { setTableForOrder(s); setView("pos"); }} activeOperator={activeOperator} />}
        {view === "pos" && <POSView user={user} showToast={showToast} tableForOrder={tableForOrder} onTableOrderDone={handleTableOrderDone} online={online} activeOperator={activeOperator} posLayout={posLayout} posTiles={posTiles} />}
        {view === "invoices" && <InvoicesView user={user} showToast={showToast} activeOperator={activeOperator} />}
        {view === "blagajna" && <BlagajnaView user={user} showToast={showToast} activeOperator={activeOperator} />}
        {view === "dashboard" && <DashboardView user={user} showToast={showToast} />}
        {view === "virman" && <VirmanView user={user} showToast={showToast} online={online} activeOperator={activeOperator} />}
        {view === "settings" && <SettingsView user={user} showToast={showToast} onTablesToggle={setTablesEnabled} activeOperator={activeOperator} posLayout={posLayout} posTiles={posTiles} onPosLayoutChanged={reloadPosLayout} />}
        </div>
        {toast && (<div style={{ position: "fixed", bottom: 24, right: 24, padding: "14px 20px", borderRadius: 10, background: toast.type === "success" ? S.green : toast.type === "error" ? S.red : S.dark, color: "white", fontSize: 15, fontWeight: 500, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 8px 30px rgba(0,0,0,0.2)", zIndex: 9999, animation: "slideUp 0.3s ease" }}>{toast.type === "success" ? <Check size={16} /> : <AlertCircle size={16} />}{toast.message}</div>)}
      </div>
    </div>
  );
}


// ═══ PIN LOCK SCREEN ═══
function PinLockScreen({ onUnlock, pinData, companyName, onForgotPin }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [time, setTime] = useState(new Date());
  const [checking, setChecking] = useState(false);

  useEffect(() => { const iv = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(iv); }, []);

  const handleDigit = async (d) => {
    if (checking) return;
    const next = pin + d;
    setPin(next);
    setError("");
    if (next.length === 4) {
      setChecking(true);
      const h = await hashPin(next);
      const found = (pinData.operators || []).find(op => op.pin_hash === h);
      if (found) {
        onUnlock(found);
      } else {
        setShake(true);
        setError("Pogrešan PIN");
        setTimeout(() => { setPin(""); setShake(false); setChecking(false); }, 600);
        return;
      }
      setChecking(false);
    }
  };

  const handleBackspace = () => { if (!checking) { setPin(p => p.slice(0, -1)); setError(""); } };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0a0a14 0%, #0d1525 40%, #0f1d35 100%)", position: "relative", overflow: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes logoGlow { 0%,100% { filter: drop-shadow(0 0 15px rgba(59,130,246,0.2)); } 50% { filter: drop-shadow(0 0 30px rgba(59,130,246,0.4)); } }
        @keyframes floatUp { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes orbitSlow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes shakeAnim { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-12px); } 40%,80% { transform: translateX(12px); } }
        @keyframes dotPop { 0% { transform: scale(0); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }
        @keyframes pinBtnPress { 0% { transform: scale(1); } 50% { transform: scale(0.92); } 100% { transform: scale(1); } }
      `}</style>

      {/* Animated background */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        <div style={{ position: "absolute", width: 500, height: 500, border: "1px solid rgba(59,130,246,0.05)", borderRadius: "50%", top: "50%", left: "50%", marginTop: -250, marginLeft: -250, animation: "orbitSlow 60s linear infinite" }}>
          <div style={{ position: "absolute", width: 5, height: 5, borderRadius: "50%", background: "#3b82f6", top: 0, left: "50%", marginLeft: -2.5, opacity: 0.3 }} />
        </div>
        <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)", top: "15%", right: "10%" }} />
        <div style={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 70%)", bottom: "20%", left: "15%" }} />
      </div>

      {/* Logo */}
      <div style={{ animation: "floatUp 4s ease-in-out infinite, logoGlow 3s ease-in-out infinite", marginBottom: 10 }}>
        <img src={process.env.PUBLIC_URL + "/efis-logo.png"} alt="EFIS.PRO" style={{ width: "min(220px, 50vw)", height: "auto", objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
      </div>

      {/* Clock */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ color: "white", fontSize: 42, fontWeight: 200, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 3, background: "linear-gradient(90deg, #60a5fa, #a78bfa, #60a5fa)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 4s linear infinite" }}>
          {time.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" })}
        </div>
        <div style={{ color: "rgba(255,255,255,0.25)", fontSize: 13, marginTop: 4 }}>
          {time.toLocaleDateString("sr-Latn-ME", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>

      {/* PIN label */}
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Unesite PIN</div>

      {/* PIN dots */}
      <div style={{ display: "flex", gap: 16, marginBottom: 8, animation: shake ? "shakeAnim 0.5s ease" : "none" }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ width: 18, height: 18, borderRadius: "50%", border: pin.length > i ? "none" : "2px solid rgba(255,255,255,0.2)", background: pin.length > i ? (error ? "#ef4444" : "#3b82f6") : "transparent", transition: "all 0.15s ease", animation: pin.length === i + 1 && !error ? "dotPop 0.2s ease" : "none", boxShadow: pin.length > i ? `0 0 12px ${error ? "rgba(239,68,68,0.4)" : "rgba(59,130,246,0.4)"}` : "none" }} />
        ))}
      </div>

      {/* Error message */}
      <div style={{ height: 24, display: "flex", alignItems: "center", marginBottom: 12 }}>
        {error && <div style={{ color: "#f87171", fontSize: 14, fontWeight: 500 }}>{error}</div>}
      </div>

      {/* Number pad */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 72px)", gap: 12 }}>
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} onClick={() => handleDigit(String(n))} style={{ width: 72, height: 72, borderRadius: 20, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "white", fontSize: 26, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s ease", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center" }}
            onMouseDown={e => { e.currentTarget.style.background = "rgba(59,130,246,0.2)"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.4)"; e.currentTarget.style.transform = "scale(0.94)"; }}
            onMouseUp={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.transform = "scale(1)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.transform = "scale(1)"; }}
          >{n}</button>
        ))}
        <div />
        <button onClick={() => handleDigit("0")} style={{ width: 72, height: 72, borderRadius: 20, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "white", fontSize: 26, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s ease", backdropFilter: "blur(10px)" }}
          onMouseDown={e => { e.currentTarget.style.background = "rgba(59,130,246,0.2)"; e.currentTarget.style.transform = "scale(0.94)"; }}
          onMouseUp={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.transform = "scale(1)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.transform = "scale(1)"; }}
        >0</button>
        <button onClick={handleBackspace} style={{ width: 72, height: 72, borderRadius: 20, border: "1px solid rgba(255,255,255,0.06)", background: "transparent", color: "rgba(255,255,255,0.4)", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s ease" }}
          onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
        >⌫</button>
      </div>

      {/* Forgot PIN */}
      {onForgotPin && (
        <button onClick={onForgotPin} style={{
          position: "absolute", bottom: 52, background: "transparent", border: "none",
          color: "rgba(255,255,255,0.35)", fontSize: 12, cursor: "pointer",
          textDecoration: "underline", fontFamily: "'DM Sans', sans-serif",
        }}>
          Zaboravili ste PIN? Odjavite se
        </button>
      )}

      {/* Company name */}
      <div style={{ position: "absolute", bottom: 24, color: "rgba(255,255,255,0.15)", fontSize: 12, letterSpacing: 1 }}>{companyName || "EFIS.PRO"}</div>
    </div>
  );
}

// ═══ PIN SETUP SCREEN (first time admin setup) ═══
function PinSetupScreen({ user, onComplete }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [step, setStep] = useState(1); // 1=enter, 2=confirm
  const [error, setError] = useState("");

  const handleDigit = async (d) => {
    if (step === 1) {
      const next = pin + d;
      setPin(next);
      if (next.length === 4) setTimeout(() => { setStep(2); }, 300);
    } else {
      const next = confirmPin + d;
      setConfirmPin(next);
      if (next.length === 4) {
        if (next !== pin) {
          setError("PIN-ovi se ne poklapaju");
          setTimeout(() => { setPin(""); setConfirmPin(""); setStep(1); setError(""); }, 1200);
        } else {
          const h = await hashPin(next);
          const data = {
            operators: [{
              id: user.id,
              name: user.name,
              operator_code: user.operator_code,
              pin_hash: h,
              permissions: ALL_PERMISSIONS.map(p => p.key),
              is_main_admin: true,
            }],
            auto_lock_minutes: 5,
          };
          savePinData(data, user.company_id);
          onComplete(data.operators[0]);
        }
      }
    }
  };
  const handleBackspace = () => {
    if (step === 1) setPin(p => p.slice(0, -1));
    else setConfirmPin(p => p.slice(0, -1));
    setError("");
  };

  const currentPin = step === 1 ? pin : confirmPin;

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0a0a14 0%, #0d1525 40%, #0f1d35 100%)", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <style>{`@keyframes dotPop { 0% { transform: scale(0); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }`}</style>

      <div style={{ marginBottom: 30 }}>
        <img src={process.env.PUBLIC_URL + "/efis-logo.png"} alt="EFIS.PRO" style={{ width: "min(180px, 40vw)", height: "auto", objectFit: "contain", opacity: 0.8 }} onError={e => { e.target.style.display = "none"; }} />
      </div>

      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "36px 40px", textAlign: "center", backdropFilter: "blur(20px)", maxWidth: 360 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Users size={24} color="#60a5fa" />
        </div>
        <h2 style={{ color: "white", fontSize: 20, fontWeight: 700, margin: "0 0 6px" }}>Postavi Admin PIN</h2>
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: "0 0 8px" }}>
          {step === 1 ? "Unesite 4-cifreni PIN za vaš nalog" : "Potvrdite PIN ponovo"}
        </p>
        <p style={{ color: "rgba(59,130,246,0.6)", fontSize: 12, margin: "0 0 24px" }}>{user.name}</p>

        {/* PIN dots */}
        <div style={{ display: "flex", gap: 14, justifyContent: "center", marginBottom: 10 }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ width: 16, height: 16, borderRadius: "50%", border: currentPin.length > i ? "none" : "2px solid rgba(255,255,255,0.2)", background: currentPin.length > i ? (error ? "#ef4444" : "#3b82f6") : "transparent", transition: "all 0.15s", animation: currentPin.length === i + 1 ? "dotPop 0.2s ease" : "none" }} />
          ))}
        </div>
        <div style={{ height: 20, marginBottom: 16 }}>{error && <span style={{ color: "#f87171", fontSize: 13 }}>{error}</span>}</div>

        {/* Numpad */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 60px)", gap: 10, justifyContent: "center" }}>
          {[1,2,3,4,5,6,7,8,9].map(n => (
            <button key={n} onClick={() => handleDigit(String(n))} style={{ width: 60, height: 60, borderRadius: 16, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "white", fontSize: 22, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
              onMouseDown={e => { e.currentTarget.style.background = "rgba(59,130,246,0.2)"; }}
              onMouseUp={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
            >{n}</button>
          ))}
          <div />
          <button onClick={() => handleDigit("0")} style={{ width: 60, height: 60, borderRadius: 16, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "white", fontSize: 22, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
            onMouseDown={e => { e.currentTarget.style.background = "rgba(59,130,246,0.2)"; }}
            onMouseUp={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
          >0</button>
          <button onClick={handleBackspace} style={{ width: 60, height: 60, borderRadius: 16, border: "none", background: "transparent", color: "rgba(255,255,255,0.4)", fontSize: 16, cursor: "pointer" }}>⌫</button>
        </div>
      </div>

      <div style={{ marginTop: 20, color: "rgba(255,255,255,0.2)", fontSize: 12 }}>Korak {step} od 2</div>
    </div>
  );
}

// ═══ STAFF PIN MANAGEMENT (inside Settings) ═══
function StaffPinManager({ user, showToast, activeOperator }) {
  const [pinData, setPinData] = useState(() => loadPinData(user.company_id));
  const [backendOps, setBackendOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(null); // operator from backend
  const [newPin, setNewPin] = useState("");
  const [confirmNewPin, setConfirmNewPin] = useState("");
  const [pinStep, setPinStep] = useState(1);
  const [selectedPreset, setSelectedPreset] = useState("cashier");
  const [customPerms, setCustomPerms] = useState([]);
  const [editOp, setEditOp] = useState(null);
  const [zones, setZones] = useState([]);
  const [allowedZones, setAllowedZones] = useState([]); // for edit modal: [] = all zones

  const isMainAdmin = activeOperator?.is_main_admin || false;

  const handleDeactivatePinSystem = () => {
    if (!window.confirm("Ovo ce obrisati sve PIN-ove i operatere.\nAplikacija ce raditi bez PIN sistema.\n\nNastaviti?")) return;
    localStorage.removeItem(getPinKey(user.company_id));
    setPinData(null);
    showToast("PIN sistem deaktiviran", "success");
    setTimeout(() => window.location.reload(), 500);
  };

  useEffect(() => {
    Promise.all([
      api("/operators/list"),
      api("/tables/zones"),
    ]).then(([r, zr]) => {
      if (r.success) setBackendOps(r.data || []);
      if (zr.success) setZones(zr.data?.zones || []);
      setLoading(false);
    });
  }, []);

  const localOps = pinData?.operators || [];
  const availableOps = backendOps.filter(bo => !localOps.find(lo => lo.operator_code === bo.operator_code));

  const handleAddOperator = (op) => {
    setAddModal(op);
    setNewPin(""); setConfirmNewPin(""); setPinStep(1);
    setSelectedPreset("cashier");
    setCustomPerms(PERMISSION_PRESETS.cashier.permissions);
    setAllowedZones([]);
  };

  const handlePinDigit = (d) => {
    if (pinStep === 1) {
      const next = newPin + d;
      setNewPin(next);
      if (next.length === 4) setTimeout(() => setPinStep(2), 300);
    } else {
      const next = confirmNewPin + d;
      setConfirmNewPin(next);
      if (next.length === 4) {
        if (next !== newPin) {
          showToast("PIN-ovi se ne poklapaju", "error");
          setNewPin(""); setConfirmNewPin(""); setPinStep(1);
        }
      }
    }
  };

  const saveOperator = async () => {
    const op = addModal || editOp;
    let pinHash;

    // If editing and no new PIN entered, keep the old one (just updating permissions)
    if (editOp && newPin.length === 0) {
      pinHash = editOp.pin_hash;
    } else {
      if (newPin.length !== 4 || confirmNewPin !== newPin) { showToast("PIN mora biti 4 cifre", "error"); return; }
      pinHash = await hashPin(newPin);
      // Check for duplicate PIN (exclude the operator being edited)
      const duplicate = localOps.find(o => o.pin_hash === pinHash && o.operator_code !== op.operator_code);
      if (duplicate) { showToast("Ovaj PIN već koristi: " + duplicate.name, "error"); return; }
    }

    const newOp = {
      id: op.id,
      name: op.full_name || op.name,
      operator_code: op.operator_code,
      pin_hash: pinHash,
      permissions: selectedPreset === "custom" ? customPerms : PERMISSION_PRESETS[selectedPreset]?.permissions || [],
      allowed_zones: allowedZones.length > 0 ? allowedZones : [], // empty = all zones
      is_main_admin: false,
    };

    const updated = pinData ? { ...pinData } : { operators: [], auto_lock_minutes: 5 };
    const isFirstOperator = !pinData || !pinData.operators || pinData.operators.length === 0;
    if (isFirstOperator) newOp.is_main_admin = true; // First operator becomes admin
    if (editOp) {
      updated.operators = updated.operators.map(o => o.operator_code === editOp.operator_code ? newOp : o);
    } else {
      updated.operators = [...(updated.operators || []), newOp];
    }
    savePinData(updated, user.company_id);
    setPinData(updated);
    setAddModal(null); setEditOp(null);
    showToast(editOp ? "Operator ažuriran" : "Operator dodat", "success");
  };

  const removeOperator = (opCode) => {
    const updated = { ...pinData, operators: pinData.operators.filter(o => o.operator_code !== opCode) };
    savePinData(updated, user.company_id);
    setPinData(updated);
    showToast("Operator uklonjen");
  };

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    if (preset !== "custom") setCustomPerms(PERMISSION_PRESETS[preset]?.permissions || []);
  };

  const togglePerm = (key) => {
    setSelectedPreset("custom");
    setCustomPerms(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const currentPerms = selectedPreset === "custom" ? customPerms : (PERMISSION_PRESETS[selectedPreset]?.permissions || []);

  if (loading) return <div style={{ padding: 20, color: S.muted }}>Učitavanje operatora...</div>;

  return (
    <div>
      {/* Current operators with PINs */}
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><Users size={18} />Operateri sa PIN-om</h3>
        {localOps.length === 0 ? (
          <p style={{ color: S.muted, fontSize: 14 }}>Nema registrovanih operatera</p>
        ) : localOps.map(op => (
          <div key={op.operator_code} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1px solid ${S.border}`, marginBottom: 8, background: op.is_main_admin ? "rgba(59,130,246,0.03)" : "white" }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: op.is_main_admin ? "rgba(59,130,246,0.1)" : "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <User size={18} color={op.is_main_admin ? "#3b82f6" : "#10b981"} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.dark }}>{op.name} {op.is_main_admin && <span style={{ fontSize: 11, color: "#3b82f6", fontWeight: 500 }}>• ADMIN</span>}</div>
              <div style={{ fontSize: 12, color: S.muted }}>{op.operator_code} • {op.is_main_admin ? "Pun pristup" : `${(op.permissions || []).length} dozvola`}</div>
            </div>
            {!op.is_main_admin && (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => { setEditOp(op); setNewPin(""); setConfirmNewPin(""); setPinStep(1); setSelectedPreset("custom"); setCustomPerms(op.permissions || []); setAllowedZones(op.allowed_zones || []); }} style={{ ...S.btnSm("rgba(59,130,246,0.1)", "#3b82f6"), padding: "6px 12px" }}><Edit3 size={13} />Izmijeni</button>
                <button onClick={() => removeOperator(op.operator_code)} style={{ ...S.btnSm("rgba(239,68,68,0.1)", "#ef4444"), padding: "6px 12px" }}><Trash2 size={13} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add operator from backend */}
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}><PlusCircle size={18} />Dodaj operatera</h3>
        {availableOps.length === 0 ? (
          <p style={{ color: S.muted, fontSize: 14 }}>Svi registrovani operateri već imaju PIN. Dodajte nove operatere putem web aplikacije (efis.cloud).</p>
        ) : availableOps.map(op => (
          <div key={op.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1px solid ${S.border}`, marginBottom: 8 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(107,114,128,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <User size={18} color="#6b7280" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.dark }}>{op.full_name}</div>
              <div style={{ fontSize: 12, color: S.muted }}>{op.operator_code}</div>
            </div>
            <button onClick={() => handleAddOperator(op)} style={{ ...S.btn(S.green), padding: "8px 16px", fontSize: 13 }}><PlusCircle size={14} />Dodaj PIN</button>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {(addModal || editOp) && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => { setAddModal(null); setEditOp(null); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 16, padding: 28, width: "100%", maxWidth: 480, maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{editOp ? "Izmijeni" : "Dodaj"} operatera</h3>
            <p style={{ color: S.muted, fontSize: 14, margin: "0 0 20px" }}>{(addModal || editOp)?.full_name || (addModal || editOp)?.name} — {(addModal || editOp)?.operator_code}</p>

            {/* PIN entry */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: S.dark, display: "block", marginBottom: 8 }}>{pinStep === 1 ? (editOp ? "Novi PIN (ostavite prazno za isti)" : "Unesite PIN (4 cifre)") : "Potvrdite PIN"}</label>
              <div style={{ display: "flex", gap: 10, marginBottom: 12, justifyContent: "center" }}>
                {[0,1,2,3].map(i => {
                  const val = pinStep === 1 ? newPin : confirmNewPin;
                  return <div key={i} style={{ width: 44, height: 44, borderRadius: 10, border: `2px solid ${val.length > i ? S.dark : S.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: S.dark }}>{val.length > i ? "•" : ""}</div>;
                })}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, maxWidth: 240, margin: "0 auto" }}>
                {[1,2,3,4,5,6,7,8,9].map(n => (
                  <button key={n} onClick={() => handlePinDigit(String(n))} style={{ height: 44, borderRadius: 8, border: `1px solid ${S.border}`, background: "white", fontSize: 18, fontWeight: 600, cursor: "pointer", fontFamily: S.font }}>{n}</button>
                ))}
                <div />
                <button onClick={() => handlePinDigit("0")} style={{ height: 44, borderRadius: 8, border: `1px solid ${S.border}`, background: "white", fontSize: 18, fontWeight: 600, cursor: "pointer", fontFamily: S.font }}>0</button>
                <button onClick={() => { if (pinStep === 1) setNewPin(p => p.slice(0,-1)); else setConfirmNewPin(p => p.slice(0,-1)); }} style={{ height: 44, borderRadius: 8, border: "none", background: "#fef2f2", fontSize: 14, cursor: "pointer", color: "#ef4444" }}>⌫</button>
              </div>
              <div style={{ textAlign: "center", marginTop: 8, fontSize: 12, color: S.muted }}>Korak {pinStep} od 2</div>
            </div>

            {/* Preset selector */}
            <label style={{ fontSize: 14, fontWeight: 600, color: S.dark, display: "block", marginBottom: 8 }}>Profil dozvola</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              {Object.entries(PERMISSION_PRESETS).map(([key, val]) => (
                <button key={key} onClick={() => handlePresetChange(key)} style={{ padding: "7px 14px", borderRadius: 7, border: `1px solid ${selectedPreset === key ? S.dark : S.border}`, background: selectedPreset === key ? S.dark : "white", color: selectedPreset === key ? "white" : S.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font }}>{val.label}</button>
              ))}
            </div>

            {/* Permission checkboxes */}
            <div style={{ maxHeight: 240, overflow: "auto", border: `1px solid ${S.border}`, borderRadius: 10, padding: 12, marginBottom: 20 }}>
              {Object.entries(ALL_PERMISSIONS.reduce((g, p) => { (g[p.group] = g[p.group] || []).push(p); return g; }, {})).map(([group, perms]) => (
                <div key={group} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{group}</div>
                  {perms.map(p => (
                    <label key={p.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer", fontSize: 14, color: S.dark }}>
                      <input type="checkbox" checked={currentPerms.includes(p.key)} onChange={() => togglePerm(p.key)} style={{ accentColor: S.dark, width: 16, height: 16 }} />
                      {p.label}
                    </label>
                  ))}
                </div>
              ))}
            </div>

            {/* Zone access — only show if zones exist and operator has sale_tables permission */}
            {zones.length > 0 && currentPerms.includes("sale_tables") && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: S.dark, display: "block", marginBottom: 8 }}>Pristup zonama (Stolovi)</label>
                <div style={{ border: `1px solid ${S.border}`, borderRadius: 10, padding: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer", fontSize: 14, color: S.dark, fontWeight: 600 }}>
                    <input type="checkbox" checked={allowedZones.length === 0} onChange={() => setAllowedZones([])} style={{ accentColor: S.dark, width: 16, height: 16 }} />
                    Sve zone
                  </label>
                  <div style={{ borderTop: `1px solid ${S.border}`, marginTop: 6, paddingTop: 6 }}>
                    {zones.map(z => (
                      <label key={z.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer", fontSize: 14, color: S.dark }}>
                        <input type="checkbox" checked={allowedZones.length === 0 || allowedZones.includes(z.id)} onChange={() => {
                          if (allowedZones.length === 0) {
                            // Switching from "all" to specific — select only this one
                            setAllowedZones([z.id]);
                          } else if (allowedZones.includes(z.id)) {
                            const next = allowedZones.filter(id => id !== z.id);
                            setAllowedZones(next.length === 0 ? [] : next); // if none left, back to "all"
                          } else {
                            setAllowedZones([...allowedZones, z.id]);
                          }
                        }} style={{ accentColor: "#059669", width: 16, height: 16 }} />
                        {z.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setAddModal(null); setEditOp(null); }} style={{ ...S.btn("#e5e7eb"), flex: 1, justifyContent: "center", color: S.dark }}>Otkaži</button>
              <button onClick={saveOperator} disabled={!editOp && (newPin.length !== 4 || confirmNewPin !== newPin)} style={{ ...S.btn(S.dark), flex: 1, justifyContent: "center", opacity: (editOp || (newPin.length === 4 && confirmNewPin === newPin)) ? 1 : 0.5 }}><Save size={15} />Sačuvaj</button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate PIN system — main admin only */}
      {isMainAdmin && pinData && pinData.operators?.length > 0 && (
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #fecaca", padding: 20, marginTop: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px", color: "#dc2626", display: "flex", alignItems: "center", gap: 8 }}><Lock size={18} />Deaktiviraj PIN sistem</h3>
          <p style={{ color: S.muted, fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>Brise sve PIN-ove i operatere. Aplikacija ce raditi bez zakljucavanja — svi imaju pun pristup.</p>
          <button onClick={handleDeactivatePinSystem} style={{ ...S.btn("#fff"), border: "1px solid #dc2626", color: "#dc2626", padding: "10px 20px", fontSize: 14, justifyContent: "center", width: "100%" }}>
            <Trash2 size={15} /> Deaktiviraj PIN sistem
          </button>
        </div>
      )}
    </div>
  );
}
function HomeView({ user, onNavigate, activeOperator }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(iv); }, []);
  const can = (p) => hasPermission(activeOperator, p);
  const buttons = [
    ...(can("sale_cash") || can("sale_noncash") ? [{ id: "pos", icon: LayoutGrid, label: "Nova Prodaja", color: "#3b82f6" }] : []),
    ...(can("sale_tables") ? [{ id: "stolovi", icon: Coffee, label: "Stolovi", color: "#059669" }] : []),
    ...(can("view_racuni") ? [{ id: "invoices", icon: FileText, label: "Računi", color: "#d97706" }] : []),
  ];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0a0a14 0%, #0d1525 40%, #0f1d35 100%)", position: "relative", overflow: "hidden" }}>
      <style>{`
        @keyframes logoGlow { 0%,100% { filter: drop-shadow(0 0 20px rgba(59,130,246,0.3)); } 50% { filter: drop-shadow(0 0 40px rgba(59,130,246,0.6)) drop-shadow(0 0 80px rgba(59,130,246,0.2)); } }
        @keyframes floatUp { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes orbitSlow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      `}</style>

      {/* Animated background elements */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        {/* Orbiting ring */}
        <div style={{ position: "absolute", width: 500, height: 500, border: "1px solid rgba(59,130,246,0.06)", borderRadius: "50%", top: "50%", left: "50%", marginTop: -250, marginLeft: -250, animation: "orbitSlow 60s linear infinite" }}>
          <div style={{ position: "absolute", width: 6, height: 6, borderRadius: "50%", background: "#3b82f6", top: 0, left: "50%", marginLeft: -3, opacity: 0.4 }} />
        </div>
        <div style={{ position: "absolute", width: 700, height: 700, border: "1px solid rgba(59,130,246,0.03)", borderRadius: "50%", top: "50%", left: "50%", marginTop: -350, marginLeft: -350, animation: "orbitSlow 90s linear infinite reverse" }}>
          <div style={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", background: "#60a5fa", top: "50%", right: 0, marginRight: -2, opacity: 0.3 }} />
        </div>
        {/* Gradient blobs */}
        <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)", top: "20%", right: "10%", animation: "pulse 6s ease-in-out infinite" }} />
        <div style={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)", bottom: "15%", left: "15%", animation: "pulse 8s ease-in-out infinite 2s" }} />
      </div>

      {/* Logo — large, animated, no background box */}
      <div style={{ animation: "floatUp 4s ease-in-out infinite, logoGlow 3s ease-in-out infinite", marginBottom: 20 }}>
        <img src={process.env.PUBLIC_URL + "/efis-logo.png"} alt="EFIS.PRO" style={{ width: "min(500px, 80vw)", height: "auto", objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
      </div>

      {/* Tagline */}
      <p style={{ color: "rgba(59,130,246,0.5)", fontSize: 13, letterSpacing: 4, textTransform: "uppercase", marginTop: 0, fontFamily: "'DM Sans', sans-serif" }}>Sistem za fiskalizaciju • Crna Gora</p>

      {/* Clock */}
      <div style={{ marginTop: 40, textAlign: "center" }}>
        <div style={{ color: "white", fontSize: 56, fontWeight: 200, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 4, background: "linear-gradient(90deg, #60a5fa, #a78bfa, #60a5fa)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 4s linear infinite" }}>
          {time.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </div>
        <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, marginTop: 6, fontFamily: "'DM Sans', sans-serif" }}>
          {time.toLocaleDateString("sr-Latn-ME", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
      </div>

      {/* Quick action buttons */}
      <div style={{ display: "flex", gap: 12, marginTop: 50, flexWrap: "wrap", justifyContent: "center" }}>
        {buttons.map(btn => (
          <button key={btn.id} onClick={() => onNavigate(btn.id)}
            style={{ padding: "12px 28px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, color: "rgba(255,255,255,0.7)", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, fontFamily: "'DM Sans', sans-serif", transition: "all 0.3s ease", backdropFilter: "blur(10px)" }}
            onMouseEnter={e => { e.currentTarget.style.background = btn.color + "22"; e.currentTarget.style.borderColor = btn.color; e.currentTarget.style.color = "white"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
          >
            <btn.icon size={18} />{btn.label}
          </button>
        ))}
      </div>

      {/* Company info */}
      <div style={{ position: "absolute", bottom: 20, color: "rgba(255,255,255,0.15)", fontSize: 12, textAlign: "center" }}>
        {user.company_name} {user.location_name ? `• ${user.location_name}` : ""}
      </div>
    </div>
  );
}

// ═══ PARTNER PICKER MODAL ═══
// Reusable modal for picking a buyer/partner — used by:
//   - Nova Prodaja "Veži za partnera" button when payment_method=card
//   - Stolovi "Zatvori & Naplati" same button when payment_method=card or split
//
// Two modes (toggleable like Virman's UX):
//   1. Search saved partners from /partners endpoint
//   2. Manual one-time entry (buyer not in the partners list)
//
// On selection, calls onSelect with a normalized object:
//   { partner_id, buyer_name, buyer_pib, buyer_address, buyer_city }
//   - For saved partners: partner_id is set, buyer_* fields are populated from the
//     partner record (so receipts and XML get full address/city even though backend
//     could re-resolve them from partner_id, this saves the round-trip).
//   - For manual entry: partner_id = null, buyer_name + buyer_pib filled from input,
//     address/city blank (user is responsible if they want them).
//
// zIndex 1100 so it stacks above the modal that opens it (parent typically uses 1000).
function PartnerPickerModal({ onSelect, onClose, currentPartner, currentManual }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Mode: "saved" or "manual". If user already has a manual partner from a previous
  // pick, default to manual mode so they see/edit their entry. Otherwise default
  // to saved.
  const [mode, setMode] = useState(currentManual ? "manual" : "saved");
  const [manualName, setManualName] = useState(currentManual?.name || "");
  const [manualPib, setManualPib] = useState(currentManual?.tax_id || "");

  useEffect(() => {
    api("/partners").then(d => {
      if (d.success) setPartners(d.data?.partners || []);
      setLoading(false);
    });
  }, []);

  const filtered = !search ? partners : partners.filter(p =>
    (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.tax_id || "").includes(search)
  );

  const pickSaved = (p) => {
    onSelect({
      partner_id: p.id,
      buyer_name: p.name,
      buyer_pib: p.tax_id || "",
      buyer_address: p.address || "",
      buyer_city: p.city || "",
      _manualMirror: null, // signals to parent: clear any manual entry
      _savedMirror: p,     // mirror for parent state if needed
    });
  };

  const applyManual = () => {
    const n = manualName.trim();
    if (!n) { return; }
    onSelect({
      partner_id: null,
      buyer_name: n,
      buyer_pib: manualPib.trim(),
      buyer_address: "",
      buyer_city: "",
      _manualMirror: { name: n, tax_id: manualPib.trim() },
      _savedMirror: null,
    });
  };

  const clearAll = () => {
    onSelect({
      partner_id: null,
      buyer_name: "",
      buyer_pib: "",
      buyer_address: "",
      buyer_city: "",
      _manualMirror: null,
      _savedMirror: null,
    });
  };

  return (
    <Modal onClose={onClose} title="Veži račun za partnera" zIndex={1100}>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button onClick={() => setMode("saved")} style={{ flex: 1, padding: "8px 10px", border: `1px solid ${mode === "saved" ? S.accent : S.border}`, background: mode === "saved" ? "#eef2ff" : "white", color: mode === "saved" ? S.accent : S.dark, borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Postojeći partner</button>
        <button onClick={() => setMode("manual")} style={{ flex: 1, padding: "8px 10px", border: `1px solid ${mode === "manual" ? S.accent : S.border}`, background: mode === "manual" ? "#eef2ff" : "white", color: mode === "manual" ? S.accent : S.dark, borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Ručni unos</button>
      </div>

      {mode === "saved" ? (
        <>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži po nazivu ili PIB-u..." style={{ ...S.input, marginBottom: 10 }} autoFocus />
          {loading ? (
            <div style={{ textAlign: "center", padding: 20, color: S.muted }}>Učitavanje...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: S.muted, fontSize: 13 }}>Nema partnera. Dodajte ih u Postavke → Partneri ili koristite ručni unos.</div>
          ) : (
            <div style={{ maxHeight: 320, overflow: "auto" }}>
              {filtered.map(p => (
                <button key={p.id} onClick={() => pickSaved(p)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: currentPartner?.id === p.id ? `2px solid ${S.green}` : `1px solid ${S.border}`, background: currentPartner?.id === p.id ? "#f0fdf4" : "white", borderRadius: 8, marginBottom: 4, cursor: "pointer", fontFamily: S.font, textAlign: "left" }}>
                  <Building size={15} color={currentPartner?.id === p.id ? S.green : S.muted} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                    {p.tax_id && <div style={{ fontSize: 11, color: S.muted }}>PIB: {p.tax_id}</div>}
                  </div>
                  {currentPartner?.id === p.id && <CheckCircle size={16} color={S.green} />}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv kupca *</label>
            <input value={manualName} onChange={e => setManualName(e.target.value)} placeholder="npr. Firma DOO" style={S.input} autoFocus />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>PIB (opciono)</label>
            <input value={manualPib} onChange={e => setManualPib(e.target.value)} placeholder="npr. 12345678" style={S.input} />
          </div>
          <button onClick={applyManual} disabled={!manualName.trim()} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", opacity: manualName.trim() ? 1 : 0.5 }}>
            <Check size={16} />Primijeni ručni unos
          </button>
        </>
      )}

      {(currentPartner || currentManual) && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${S.border}` }}>
          <button onClick={clearAll} style={{ ...S.btnSm("#fef2f2", S.red), width: "100%", justifyContent: "center" }}>
            <X size={13} />Ukloni partnera sa računa
          </button>
        </div>
      )}
    </Modal>
  );
}


// ═══ POS VIEW ═══
function POSView({ user, showToast, tableForOrder, onTableOrderDone, online, activeOperator, posLayout, posTiles }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("brzi");
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [fiscalizing, setFiscalizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [invoiceDiscount, setInvoiceDiscount] = useState({ type: "none", value: 0 });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  // qtyModal holds a cart item ref when the user taps a decimal-unit product's
  // quantity → opens QtyNumpadModal for touch-friendly entry. null = closed.
  const [qtyModal, setQtyModal] = useState(null);
  const [lastFiscalized, setLastFiscalized] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [posNumPad, setPosNumPad] = useState(null); // "splitCash" | "splitCard" | null
  // SELF integration: read the per-company toggle from localStorage. Synced with
  // Postavke → Kuhinja's "🍽️ SELF app integracija" toggle. When ON, kitchen-
  // enabled cart items get a 🍽️ button to flag them for SELF send. Re-checked
  // every render via useState initializer — toggle changes apply on POSView
  // remount (i.e., when user navigates back to Nova Prodaja).
  const [selfKasaOn] = useState(() => localStorage.getItem("pos_self_kasa_enabled_" + getCompanyId()) === "true");
  const [sendingSelf, setSendingSelf] = useState(false);
  // Partner-link state for card payments. cardPartner = saved partner from list,
  // cardManualPartner = one-time manual entry. Mutually exclusive (selecting one
  // clears the other). Only used when paymentMethod === "card". Cleared on cart
  // clear and on payment method change to non-card.
  const [cardPartner, setCardPartner] = useState(null);          // partner object from /partners
  const [cardManualPartner, setCardManualPartner] = useState(null); // { name, tax_id }
  const [showPartnerPicker, setShowPartnerPicker] = useState(false);
  // Master categories (HRANA/PIĆE) — POS-only feature, OFF by default per company.
  // When ON: top bar splits 50/50 (left = master buttons, right = search/refresh)
  // and only categories matching activeMaster show in the second row.
  // When OFF: original layout — full-width search, all categories shown.
  // activeMaster: master_category id, or "all" (the SVE button), or null (initial).
  // We default to "food" (HRANA) on first load IF feature is ON, see useEffect below.
  const [masterCategoryPos, setMasterCategoryPos] = useState(false);
  const [masterCategories, setMasterCategories] = useState([]);
  const [activeMaster, setActiveMaster] = useState(null);

  useEffect(() => { loadProducts(); loadCategories(); loadPosSettings(); }, []);
  // Clear card-partner link if the user switches away from card payment.
  // Without this: user picks partner → switches to cash → fiscalizes → cash invoice
  // gets partner data attached (still valid Poreska-wise but unintended).
  useEffect(() => {
    if (paymentMethod !== "card") {
      setCardPartner(null);
      setCardManualPartner(null);
    }
  }, [paymentMethod]);

  const loadPosSettings = async () => {
    const r = await api("/company/posSettings");
    if (r.success) {
      const enabled = !!r.data?.master_category_pos;
      const masters = r.data?.master_categories || [];
      setMasterCategoryPos(enabled);
      setMasterCategories(masters);
      // When feature is ON and we haven't picked a master yet, default to the
      // first master (sort_order 1 = HRANA per auto-seed). User can switch with
      // the toggle buttons. When feature is OFF, leave activeMaster null —
      // categories show via the original "Sve" / category-tab path.
      if (enabled && masters.length > 0) {
        setActiveMaster(prev => prev === null ? masters[0].id : prev);
      }
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    const data = await api("/products");
    if (data.success) setProducts(data.data?.products || []);
    setLoading(false);
  };
  const loadCategories = async () => {
    const data = await api("/invoices/categories");
    if (data.success) setCategories(data.data?.categories || []);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const ex = prev.find(c => c.id === product.id);
      if (ex) return prev.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: product.id, name: product.name, price: parseFloat(product.price), quantity: 1, vat_rate: Number(product.vat_rate != null ? product.vat_rate : 21), unit: product.unit || "kom", is_decimal_unit: parseInt(product.is_decimal_unit) === 1 ? 1 : 0, discount_type: "none", discount_value: 0, custom_price: null, kitchen_enabled: parseInt(product.kitchen_enabled) === 1, kitchen_tags_enabled: parseInt(product.kitchen_tags_enabled) === 1, kitchen_tags_preset: product.kitchen_tags_preset ? (typeof product.kitchen_tags_preset === "string" ? (() => { try { return JSON.parse(product.kitchen_tags_preset); } catch { return []; } })() : product.kitchen_tags_preset) : [], kitchen_tags: [] }];
    });
  };
  const updateCartItem = (id, u) => setCart(prev => prev.map(c => c.id === id ? { ...c, ...u } : c));
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearCart = () => { setCart([]); setInvoiceDiscount({ type: "none", value: 0 }); setPaymentMethod("cash"); setSplitCash(""); setSplitCard(""); setCardPartner(null); setCardManualPartner(null); };

  // Brzi meni from localStorage
  const fastMenuIds = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("pos_fast_menu_" + getCompanyId()) || "[]"); } catch { return []; }
  }, [products]);

  const cartCalc = useMemo(() => {
    let netTotal = 0, totalItemDiscount = 0;
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value;
      totalItemDiscount += disc;
      netTotal += lineNet - disc;
    });
    let invDisc = 0;
    if (invoiceDiscount.type === "percentage") invDisc = netTotal * (invoiceDiscount.value / 100);
    else if (invoiceDiscount.type === "fixed") invDisc = invoiceDiscount.value;
    const subtotal = Math.max(0, netTotal - invDisc);
    // Calculate VAT per item rate (not hardcoded 21%)
    let vatAmount = 0;
    const vatGroups = {};
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value || 0;
      const afterDisc = lineNet - disc;
      const rate = Number(item.vat_rate != null ? item.vat_rate : 21);
      const vat = afterDisc * (rate / 100);
      vatAmount += vat;
      if (!vatGroups[rate]) vatGroups[rate] = { base: 0, vat: 0 };
      vatGroups[rate].base += afterDisc;
      vatGroups[rate].vat += vat;
    });
    // Apply invoice discount proportionally to VAT
    if (invDisc > 0 && netTotal > 0) {
      const ratio = (netTotal - invDisc) / netTotal;
      vatAmount = vatAmount * ratio;
      Object.keys(vatGroups).forEach(r => { vatGroups[r].base *= ratio; vatGroups[r].vat *= ratio; });
    }
    const total = subtotal + vatAmount;
    return { netTotal, totalItemDiscount, invDisc, subtotal, vatAmount, total, vatGroups };
  }, [cart, invoiceDiscount]);

  const buildPayload = (fiscNow) => {
    // Partner-link payload extension. When user is paying by card AND has linked
    // a partner (saved or manual), include buyer fields. Backend Invoice::createInvoice
    // already accepts these on cash invoices (Virman uses the same fields). The
    // resulting invoice is invoice_type='cash', payment_method='card', with a
    // <Buyer> block in the Poreska XML — fully spec-compliant for B2B card payments.
    const partnerFields = (paymentMethod === "card" && (cardPartner || cardManualPartner)) ? {
      partner_id: cardPartner?.id || null,
      buyer_name: cardPartner?.name || cardManualPartner?.name || "",
      buyer_pib: cardPartner?.tax_id || cardManualPartner?.tax_id || "",
      buyer_address: cardPartner?.address || "",
      buyer_city: cardPartner?.city || "",
    } : {};
    return {
    location_id: user.location_id || 1,
    tcr_code: getTcr(),
    payment_method: paymentMethod,
    fiscalize_now: fiscNow,
    discount_type: invoiceDiscount.type,
    discount_value: invoiceDiscount.value,
    ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code }),
    ...(paymentMethod === "split" && { split_cash: parseFloat(splitCash) || 0, split_card: parseFloat(splitCard) || 0 }),
    ...partnerFields,
    items: cart.map(item => ({ product_id: item.id, name: item.name, quantity: item.quantity, unit_price: item.custom_price !== null ? item.custom_price : item.price, vat_rate: item.vat_rate, unit: item.unit, discount_type: item.discount_type, discount_value: item.discount_value }))
    };
  };

  // ═══ Send selected cart items to SELF app ═══
  // Cashier toggled "Pošalji na SELF" on N items in the cart. This collects
  // those items into ONE SELF order (one order number, shown in SELF admin
  // queue as 'preparing' status — no pending). Items REMAIN in cart so the
  // cashier can still fiscalize them — sending to SELF and fiscalizing are
  // independent actions. After successful send, the _send_to_self flag is
  // cleared on each item so accidentally clicking the button again doesn't
  // double-send the same items.
  //
  // Tags pass through: kitchen_tags array (preset + custom) is forwarded as
  // the SELF item.tags array, so the SELF admin queue and the kitchen card
  // both display the same tag set.
  const sendToSelf = async () => {
    const selectedItems = cart.filter(it => it._send_to_self);
    if (selectedItems.length === 0) return;
    if (!online) {
      showToast("SELF slanje zahtijeva internet vezu", "error");
      return;
    }
    setSendingSelf(true);
    try {
      const body = {
        items: selectedItems.map(it => ({
          productId: it.id,
          qty: it.quantity,
          tags: it.kitchen_tags || [],
        })),
      };
      const r = await api("/self-kasa/order", { method: "POST", body: JSON.stringify(body) });
      if (r.success) {
        const orderNum = r.data?.orderNumber || "";
        const orderId = r.data?.orderId;
        showToast(`Poslato na SELF: ${orderNum} (${selectedItems.length} ${selectedItems.length === 1 ? "stavka" : "stavki"})`);
        // Clear flags on the items we just sent — items themselves stay in
        // cart (cashier may still want to fiscalize). Match by reference: only
        // the items that were in selectedItems get cleared, so if cashier
        // added more flagged items DURING the send (unlikely but possible),
        // those keep their flags.
        const sentIds = new Set(selectedItems.map(it => it.id));
        setCart(prev => prev.map(it => sentIds.has(it.id) ? { ...it, _send_to_self: false } : it));

        // Auto-print SELF ticket on the configured printer (same printer as
        // fiscal receipts — uses WinUSB or spooler depending on settings).
        // Non-blocking: the SELF order is already created server-side, print
        // failure shouldn't unwind that. Web users (no electron) silently skip.
        if (orderId && isElectron) {
          printSelfTicket(orderId).then(printed => {
            if (!printed) {
              // Quiet warning — don't error-toast (the order DID succeed)
              console.log("SELF ticket auto-print failed (order is created though)");
            }
          });
        }
      } else {
        // Backend errors: SELF disabled at company level, or other validation
        // failures. Show the message verbatim so cashier can act on it.
        showToast(r.message || "Greška pri slanju na SELF", "error");
      }
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setSendingSelf(false);
  };

  const handleFiscalize = async () => {
    setShowConfirm(false); setFiscalizing(true);
    // Guard: operator_code is required for any fiscal operation
    if (!activeOperator?.operator_code) {
      setFiscalizing(false);
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    // Check internal day gate (cash invoices — virman is separate view)
    const tcr = getTcr();
    if (tcr) {
      try {
        const idCheck = await api("/internalday/settings");
        if (idCheck.success && idCheck.data?.internal_day_enabled) {
          const idStatus = await api(`/internalday/status/${tcr}`);
          if (idStatus.success && !idStatus.data?.day) {
            setFiscalizing(false);
            showToast("Interni dan nije otvoren! Otvorite dan u Blagajni prije izdavanja racuna.", "error");
            return;
          }
        }
      } catch(e) {}
    }
    // Quick network check first
    let isReachable = false;
    try {
      const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]);
      isReachable = test && test.ok;
    } catch(e) { isReachable = false; }
    if (!isReachable) {
      setFiscalizing(false);
      const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
      showToast(`Nema mreže! Draft sačuvan lokalno (${cnt}). Fiskalizujte kad budete online.`, "error");
      return;
    }
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(true)) });
      setFiscalizing(false);
      if (data.success) {
        const cartSnapshot = [...cart];
        setLastFiscalized({ fic: data.data.fic, invoice: data.data.invoice, total: cartCalc.total }); clearCart(); showToast("Fiskalizacija uspješna!");
        sendToKitchen(cartSnapshot, data.data.invoice?.id, data.data.invoice?.invoice_number, tableForOrder?.tableName || "");
        // Auto-print if printer configured (non-blocking)
        const invoiceId = data.data.invoice?.id;
        if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
          printReceipt(invoiceId).catch(() => {});
        }
      }
      else {
        const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
        showToast(`Greška! Draft sačuvan lokalno (${cnt}).`, "error");
      }
    } catch(e) {
      setFiscalizing(false); const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
      showToast(`Offline! Draft sačuvan lokalno (${cnt}).`, "error");
    }
  };
  const handleSaveDraft = async () => {
    if (!cart.length) return;
    // Quick network check
    let isReachable = false;
    try {
      const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]);
      isReachable = test && test.ok;
    } catch(e) { isReachable = false; }
    if (!isReachable) {
      const cnt = saveOfflineDraft(buildPayload(false)); clearCart();
      showToast(`Nema mreže! Draft sačuvan lokalno (${cnt})`, "error");
      return;
    }
    setSaving(true);
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(false)) });
      setSaving(false);
      if (data.success) { clearCart(); showToast("Draft sačuvan! #" + (data.data.invoice?.invoice_number || "")); }
      else { const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Greška! Draft sačuvan lokalno (${cnt})`, "error"); }
    } catch(e) { setSaving(false); const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Offline! Draft sačuvan lokalno (${cnt})`, "error"); }
  };

  // The "filtered" array drives the visual order of the product grid in the POS view.
  // Three modes:
  //   - "brzi"  → walk fastMenuIds in user-configured order, look up each product. This MUST
  //               iterate fastMenuIds (not products.filter), because filter() preserves the
  //               source array's order — which is the API order — and would render Brzi Meni
  //               in alphabetical/category order instead of the order the user dragged in
  //               settings. This was the bug behind the "Heineken at the bottom" report.
  //   - "all"   → every product. Server already returns them sorted by category sort_order +
  //               category_position + name (see Product::getProductsByCompany ORDER BY), so
  //               just return as-is.
  //   - <catId> → products in that category. Same reasoning — server-side sort handles it.
  //   - search  → first-class filter, beats all category modes.
  const filtered = (() => {
    if (search) {
      const q = search.toLowerCase();
      return products.filter(p => p.name.toLowerCase().includes(q));
    }
    if (activeCategory === "brzi") {
      return fastMenuIds
        .map(id => products.find(p => p.id === id))
        .filter(Boolean); // drop ids whose products were deleted/deactivated
    }
    if (activeCategory === "all") return products;
    return products.filter(p => String(p.category_id) === String(activeCategory));
  })();

  if (lastFiscalized) {
    // Auto-dismiss success — just reset to new invoice
    setTimeout(() => setLastFiscalized(null), 100);
    return null;
  }

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* PRODUCTS */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: `1px solid ${S.border}`, overflow: "hidden", minWidth: 0 }}>
        {masterCategoryPos && masterCategories.length > 0 ? (
          // ───── TOP BAR with master categories ─────
          // 50/50 split: left = HRANA/PIĆE/SVE big buttons, right = search + refresh.
          // Active master highlighted. SVE shows all categories (including unassigned).
          <div style={{ padding: "10px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 8 }}>
            <div style={{ flex: 1, display: "flex", gap: 6 }}>
              {masterCategories.map(m => (
                <button
                  key={m.id}
                  onClick={() => { setActiveMaster(m.id); /* Reset category pick when switching masters */ setActiveCategory("brzi"); }}
                  style={{
                    flex: 1,
                    padding: "10px 8px",
                    background: activeMaster === m.id ? S.accent : "white",
                    color: activeMaster === m.id ? "white" : S.dark,
                    border: `1px solid ${activeMaster === m.id ? S.accent : S.border}`,
                    borderRadius: 8,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  {m.name}
                </button>
              ))}
              <button
                onClick={() => { setActiveMaster("all"); setActiveCategory("brzi"); }}
                style={{
                  flex: 1,
                  padding: "10px 8px",
                  background: activeMaster === "all" ? S.muted : "white",
                  color: activeMaster === "all" ? "white" : S.dark,
                  border: `1px solid ${activeMaster === "all" ? S.muted : S.border}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                SVE
              </button>
            </div>
            <div style={{ flex: 1, display: "flex", gap: 8 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <Search size={17} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži..." style={{ ...S.input, paddingLeft: 38 }} />
              </div>
              <button onClick={loadProducts} style={{ padding: 10, background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><RefreshCw size={17} color={S.muted} /></button>
            </div>
          </div>
        ) : (
          // ───── ORIGINAL TOP BAR (master categories OFF) ─────
          // Identical to pre-feature behavior.
          <div style={{ padding: "10px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 8 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={17} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži proizvode..." style={{ ...S.input, paddingLeft: 38 }} />
            </div>
            <button onClick={loadProducts} style={{ padding: 10, background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><RefreshCw size={17} color={S.muted} /></button>
          </div>
        )}
        <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 5, overflowX: "auto", flexShrink: 0, flexWrap: "nowrap" }}>
          <CatTab active={activeCategory === "brzi"} onClick={() => setActiveCategory("brzi")} label="⚡ Brzi Meni" />
          {/* "Sve" button: only shown when master feature is OFF. When ON, the
              SVE button in the top row already provides the same function. */}
          {!masterCategoryPos && <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />}
          {/* Categories: filter by activeMaster when feature is ON.
                - Specific master picked → show only categories with that master_category_id
                - "all" picked → show ALL categories (including unassigned)
                - feature OFF → show all categories (legacy behavior)
              Unassigned categories (master_category_id NULL) only show under SVE. */}
          {categories.filter(c => {
            if (!masterCategoryPos) return true;
            if (activeMaster === "all") return true;
            return Number(c.master_category_id) === Number(activeMaster);
          }).map(c => <CatTab key={c.id} active={String(activeCategory) === String(c.id)} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 12, background: "#e8ecf1" }}>
          {loading ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><Loader2 size={30} className="spin" /><p style={{ marginTop: 10, fontSize: 15 }}>Učitavanje...</p></div>
          : filtered.length === 0 ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><Package size={40} /><p style={{ marginTop: 10, fontSize: 15 }}>{activeCategory === "brzi" ? "Brzi meni je prazan. Konfigurišite u Podešavanjima." : "Nema proizvoda"}</p></div>
          : (() => {
              const layout = posLayout || DEFAULT_LAYOUT;
              const tiles = posTiles || {};
              const sizeKey = layout.card_size || "srednje";
              const sizeInfo = CARD_SIZES[sizeKey] || CARD_SIZES.srednje;
              const isTabela = layout.style === "tabela";
              const cellPx = sizeInfo.px;
              const showDefaultName = layout.show_default_name !== false;
              const showDefaultPrice = layout.show_default_price !== false;
              // In tabela mode: square cells (so 2×2 tiles look square). 
              // In kartice mode: original wider-than-tall ratio (~155×110 base, scales with size).
              const cardHeight = isTabela ? cellPx : Math.round(cellPx * 0.71);
              const gridStyle = {
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${cellPx}px, 1fr))`,
                gridAutoRows: `${cardHeight}px`,
                gridAutoFlow: "dense",
                gap: isTabela ? 0 : 10,
              };
              return (
                <div style={gridStyle}>
                  {filtered.map(p => {
                    const inCart = cart.find(c => c.id === p.id);
                    const gross = parseFloat(p.price) * (1 + Number(p.vat_rate != null ? p.vat_rate : 21) / 100);
                    // Tile config — only meaningful in tabela mode
                    const tile = isTabela ? getTileConfig(p.id, tiles) : null;
                    const showName = isTabela ? tile.show_name : showDefaultName;
                    const showPrice = isTabela ? tile.show_price : showDefaultPrice;
                    const spanX = isTabela ? tile.span_x : 1;
                    const spanY = isTabela ? tile.span_y : 1;
                    const hasImg = isTabela && tile.image;
                    const bg = isTabela
                      ? (hasImg ? `url(${tile.image})` : (tile.bg_color || (inCart ? "#f0fdf4" : "white")))
                      : (inCart ? "#f0fdf4" : "white");

                    // Sizes scale with span (longer name space when wider/taller)
                    const nameSize = sizeInfo.fontName + (spanX > 1 || spanY > 1 ? 2 : 0);
                    const priceSize = sizeInfo.fontPrice + (spanX > 1 || spanY > 1 ? 2 : 0);
                    const padding = sizeInfo.padding;

                    // Border / shadow per style
                    const baseBorder = isTabela
                      ? `1px solid #c4c8ce`
                      : (inCart ? `2px solid ${S.green}` : "1px solid #d1d5db");
                    const baseRadius = isTabela ? 0 : 10;
                    const baseShadow = isTabela
                      ? "inset 0 0 0 1px rgba(255,255,255,0.5), 1px 1px 0 #b0b4ba"
                      : (inCart ? `0 2px 8px rgba(5,150,105,0.15)` : "1px 2px 0 #c4c8ce, 2px 3px 0 #d1d5db");

                    // Text color — dark always when no image; white over image (with shadow)
                    const textColor = hasImg ? "white" : S.dark;
                    const priceColor = hasImg ? "white" : S.green;
                    const textShadow = hasImg ? "0 1px 3px rgba(0,0,0,0.7), 0 0 8px rgba(0,0,0,0.5)" : "none";

                    return (
                      <button
                        key={p.id}
                        onClick={() => addToCart(p)}
                        style={{
                          position: "relative",
                          padding: hasImg ? 0 : `${padding}px ${Math.max(6, padding - 4)}px`,
                          background: bg,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          border: baseBorder,
                          borderRadius: baseRadius,
                          cursor: "pointer",
                          textAlign: "center",
                          fontFamily: S.font,
                          boxShadow: baseShadow,
                          gridColumn: `span ${spanX}`,
                          gridRow: `span ${spanY}`,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          transition: "transform 0.1s, box-shadow 0.1s",
                        }}
                        onMouseDown={e => { e.currentTarget.style.transform = "translateY(1px)"; if (!isTabela) e.currentTarget.style.boxShadow = "0 1px 0 #d1d5db"; }}
                        onMouseUp={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = baseShadow; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = baseShadow; }}
                      >
                        {/* Dark scrim over image so text stays legible */}
                        {hasImg && (showName || showPrice || inCart) && (
                          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0) 100%)", pointerEvents: "none" }} />
                        )}
                        {inCart && (
                          <div style={{ position: "absolute", top: hasImg || isTabela ? 4 : -7, right: hasImg || isTabela ? 4 : -7, width: 22, height: 22, background: S.green, borderRadius: "50%", color: "white", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.3)", zIndex: 2 }}>{inCart.quantity}</div>
                        )}
                        {showName && (
                          <div style={{
                            position: hasImg ? "absolute" : "static",
                            bottom: hasImg ? (showPrice ? "calc(50% - 4px)" : 8) : "auto",
                            left: hasImg ? 0 : "auto",
                            right: hasImg ? 0 : "auto",
                            padding: hasImg ? "0 8px" : 0,
                            fontWeight: 600,
                            fontSize: nameSize,
                            color: textColor,
                            textShadow,
                            marginBottom: !hasImg && showPrice ? 4 : 0,
                            lineHeight: 1.25,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: spanY > 1 ? 3 : 2,
                            WebkitBoxOrient: "vertical",
                            maxWidth: "100%",
                            zIndex: 1,
                          }}>
                            {p.name}
                          </div>
                        )}
                        {showPrice && (
                          <div style={{
                            position: hasImg ? "absolute" : "static",
                            bottom: hasImg ? 8 : "auto",
                            left: hasImg ? 0 : "auto",
                            right: hasImg ? 0 : "auto",
                            padding: hasImg ? "0 8px" : 0,
                            fontWeight: 700,
                            fontSize: priceSize,
                            color: priceColor,
                            textShadow,
                            zIndex: 1,
                          }}>
                            {fmt(gross)} €
                          </div>
                        )}
                        {/* "neto X.XX" small line — kartice mode only, when both name and price shown */}
                        {!isTabela && showName && showPrice && (
                          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>neto {fmt(p.price)}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })()
          }
        </div>
      </div>

      {/* CART */}
      <div style={{ flex: "0 0 43%", display: "flex", flexDirection: "column", background: "white", minWidth: 380 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShoppingCart size={18} color={S.dark} />
            <span style={{ fontWeight: 700, fontSize: 17, color: S.dark }}>Račun</span>
            <span style={{ background: "#f3f4f6", padding: "2px 8px", borderRadius: 5, fontSize: 13, fontWeight: 600, color: S.muted }}>{cart.length}</span>
          </div>
          {cart.length > 0 && <button onClick={clearCart} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={13} />Obriši</button>}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "6px 14px" }}>
          {cart.length === 0 ? <div style={{ textAlign: "center", padding: "50px 16px", color: "#9ca3af" }}><ShoppingCart size={40} strokeWidth={1.5} /><p style={{ marginTop: 12, fontSize: 15 }}>Dodajte proizvode</p></div>
          : cart.map(item => {
            const up = item.custom_price !== null ? item.custom_price : item.price;
            const lineNet = item.quantity * up;
            let disc = 0;
            if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
            else if (item.discount_type === "fixed") disc = item.discount_value;
            const lineGross = (lineNet - disc) * (1 + item.vat_rate / 100);
            return (
              <div key={item.id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                    <div style={{ fontSize: 13, color: "#9ca3af" }}>{fmt(up)} × {fmtQty(item.quantity)}{disc > 0 ? ` − ${fmt(disc)}` : ""}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <button onClick={() => updateCartItem(item.id, { quantity: Math.max(item.is_decimal_unit ? 0.01 : 1, parseFloat((item.quantity - 1).toFixed(2))) })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={16} /></button>
                    {item.is_decimal_unit ? (
                      // Tappable qty for decimal-unit products → opens numpad.
                      // Min width slightly larger to fit "0,77" without jitter.
                      <button onClick={() => setQtyModal(item)} style={{ fontSize: 15, fontWeight: 600, minWidth: 50, textAlign: "center", padding: "6px 8px", border: `1px dashed ${S.accent}`, borderRadius: 6, background: "white", cursor: "pointer", color: S.dark }} title="Unesi tačnu količinu">{fmtQty(item.quantity)}</button>
                    ) : (
                      <span style={{ fontSize: 15, fontWeight: 600, minWidth: 26, textAlign: "center" }}>{item.quantity}</span>
                    )}
                    <button onClick={() => updateCartItem(item.id, { quantity: parseFloat((item.quantity + 1).toFixed(2)) })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={16} /></button>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: S.dark, minWidth: 60, textAlign: "right" }}>{fmt(lineGross)} €</div>
                  <button onClick={() => setEditingItem(item.id === editingItem ? null : item.id)} style={{ padding: 4, background: "none", border: "none", cursor: "pointer", color: editingItem === item.id ? S.accent : S.muted }}><Edit3 size={15} /></button>
                  <button onClick={() => removeFromCart(item.id)} style={{ padding: 6, background: "#fef2f2", border: "none", borderRadius: 6, cursor: "pointer", color: "#dc2626" }}><X size={16} /></button>
                </div>
                {editingItem === item.id && (
                  <div style={{ marginTop: 8, padding: 10, background: "#f9fafb", borderRadius: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 90px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Cijena (neto)</label>
                      <input type="number" step="0.01" value={item.custom_price !== null ? item.custom_price : item.price} onChange={e => updateCartItem(item.id, { custom_price: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    <div style={{ flex: "1 1 70px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Količina</label>
                      <input type="number" step={item.is_decimal_unit ? "0.01" : "1"} value={item.quantity} onChange={e => updateCartItem(item.id, { quantity: item.is_decimal_unit ? Math.max(0.01, parseFloat(String(e.target.value).replace(",", ".")) || 1) : Math.max(1, parseInt(e.target.value) || 1) })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    {hasPermission(activeOperator, "sale_discount") && <div style={{ flex: "1 1 110px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Popust</label>
                      <div style={{ display: "flex", gap: 4 }}>
                        <select value={item.discount_type} onChange={e => updateCartItem(item.id, { discount_type: e.target.value, discount_value: 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, flex: 1 }}><option value="none">Bez</option><option value="percentage">%</option><option value="fixed">€</option></select>
                        {item.discount_type !== "none" && <input type="number" step="0.01" value={item.discount_value} onChange={e => updateCartItem(item.id, { discount_value: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, width: 60 }} />}
                      </div>
                    </div>}
                    {item.kitchen_enabled && item.kitchen_tags_enabled && (
                      <div style={{ flex: "1 1 100%", marginTop: 6 }}>
                        <label style={{ fontSize: 12, color: "#d97706", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>🍳 Tagovi za kuhinju</label>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                          {(item.kitchen_tags_preset || []).map(tag => {
                            const active = (item.kitchen_tags || []).includes(tag);
                            return <button key={tag} onClick={() => updateCartItem(item.id, { kitchen_tags: active ? (item.kitchen_tags || []).filter(t => t !== tag) : [...(item.kitchen_tags || []), tag] })} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${active ? "#f59e0b" : S.border}`, background: active ? "#fffbeb" : "white", color: active ? "#92400e" : S.muted, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{tag}</button>;
                          })}
                        </div>
                        <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                          <input id={`ctag-${item.id}`} placeholder="Prilagođeni tag..." onKeyDown={e => { if (e.key === "Enter" && e.target.value.trim()) { updateCartItem(item.id, { kitchen_tags: [...(item.kitchen_tags || []), e.target.value.trim()] }); e.target.value = ""; } }} style={{ ...S.input, flex: 1, padding: "5px 8px", fontSize: 12 }} />
                          <button onClick={() => { const el = document.getElementById(`ctag-${item.id}`); if (el?.value.trim()) { updateCartItem(item.id, { kitchen_tags: [...(item.kitchen_tags || []), el.value.trim()] }); el.value = ""; } }} style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${S.border}`, background: "#fffbeb", color: "#92400e", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+ Dodaj</button>
                        </div>
                      </div>
                    )}
                    {/* SELF flag toggle — visible when SELF integration is on AND
                        the item is kitchen-enabled. Cashier ticks this on items
                        they want to send to the SELF admin queue. The bottom-cart
                        "Pošalji na SELF" button collects all flagged items into
                        ONE order. Offline grays out the toggle (per user spec). */}
                    {item.kitchen_enabled && selfKasaOn && (
                      <div style={{ flex: "1 1 100%", marginTop: 8 }}>
                        <button
                          onClick={() => updateCartItem(item.id, { _send_to_self: !item._send_to_self })}
                          disabled={!online}
                          title={online ? "Označi za slanje na SELF" : "SELF slanje zahtijeva internet vezu"}
                          style={{
                            width: "100%",
                            padding: "7px 10px",
                            borderRadius: 7,
                            border: `1px solid ${item._send_to_self ? "#0369a1" : S.border}`,
                            background: item._send_to_self ? "#e0f2fe" : (online ? "white" : "#f9fafb"),
                            color: item._send_to_self ? "#0c4a6e" : (online ? S.dark : "#cbd5e0"),
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: online ? "pointer" : "not-allowed",
                            opacity: online ? 1 : 0.6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          🍽️ {item._send_to_self ? "Označeno za SELF" : "Pošalji na SELF"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {item.kitchen_enabled && (item.kitchen_tags || []).length > 0 && editingItem !== item.id && (
                  <div style={{ marginTop: 3, fontSize: 11, color: "#92400e", paddingLeft: 2 }}>🍳 {(item.kitchen_tags || []).join(", ")}</div>
                )}
                {/* Collapsed-state SELF badge — shown when item not being edited
                    so cashier still sees the flag even after closing the editor. */}
                {item.kitchen_enabled && selfKasaOn && item._send_to_self && editingItem !== item.id && (
                  <div style={{ marginTop: 3, fontSize: 11, color: "#0369a1", paddingLeft: 2, fontWeight: 600 }}>🍽️ Označeno za SELF</div>
                )}
              </div>
            );
          })}
        </div>

        {cart.length > 0 && (
          <div style={{ borderTop: `2px solid ${S.border}`, padding: "14px 16px" }}>
            <div style={{ marginBottom: 10, display: "flex", gap: 6 }}>
              {hasPermission(activeOperator, "sale_discount") && <button onClick={() => setShowDiscountModal(true)} style={{ ...S.btnSm(invoiceDiscount.type !== "none" ? "#fef3c7" : "#f3f4f6", invoiceDiscount.type !== "none" ? "#92400e" : S.muted), flex: 1, justifyContent: "center" }}>
                <Percent size={13} />{invoiceDiscount.type !== "none" ? `Popust: ${invoiceDiscount.value}${invoiceDiscount.type === "percentage" ? "%" : "€"}` : "Popust na račun"}
              </button>}
              {hasPermission(activeOperator, "sale_discount") && invoiceDiscount.type !== "none" && <button onClick={() => setInvoiceDiscount({ type: "none", value: 0 })} style={S.btnSm("#fef2f2", S.red)}><X size={13} /></button>}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.muted }}>Neto</span><span>{fmt(cartCalc.subtotal)} €</span></div>
            {cartCalc.vatGroups && Object.entries(cartCalc.vatGroups).map(([rate, g]) => (
              <div key={rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 13 }}><span style={{ color: S.muted }}>PDV {rate}%</span><span>{fmt(g.vat)} €</span></div>
            ))}
            {cartCalc.invDisc > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.orange }}>Popust</span><span style={{ color: S.orange }}>-{fmt(cartCalc.invDisc)} €</span></div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, paddingTop: 8, borderTop: "1px solid #f3f4f6" }}>
              <span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span>
              <span style={{ fontWeight: 700, fontSize: 22, color: S.green }}>{fmt(cartCalc.total)} €</span>
            </div>
            <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
              {[{ id: "cash", label: "Gotovina", icon: Banknote }, { id: "card", label: "Kartica", icon: CreditCard }, { id: "split", label: "Podijeli", icon: Wallet }].map(m => (
                <button key={m.id} onClick={() => setPaymentMethod(m.id)} style={{ flex: 1, padding: "12px 8px", border: "2px solid", borderColor: paymentMethod === m.id ? S.green : S.border, background: paymentMethod === m.id ? "#f0fdf4" : "white", color: paymentMethod === m.id ? S.green : S.muted, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: S.font }}><m.icon size={14} />{m.label}</button>
              ))}
            </div>
            {paymentMethod === "split" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Gotovina €</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input type="number" step="0.01" value={splitCash} onChange={e => { setSplitCash(e.target.value); setSplitCard(fmt(cartCalc.total - (parseFloat(e.target.value) || 0))); }} style={{ ...S.input, padding: "7px 10px", flex: 1 }} />
                    <button onClick={() => setPosNumPad("splitCash")} style={{ padding: "6px 10px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 16 }}>🔢</button>
                  </div>
                </div>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Kartica €</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input type="number" step="0.01" value={splitCard} onChange={e => { setSplitCard(e.target.value); setSplitCash(fmt(cartCalc.total - (parseFloat(e.target.value) || 0))); }} style={{ ...S.input, padding: "7px 10px", flex: 1 }} />
                    <button onClick={() => setPosNumPad("splitCard")} style={{ padding: "6px 10px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 16 }}>🔢</button>
                  </div>
                </div>
              </div>
            )}
            {/* SELF send button — appears above Fiskalizuj/Sačuvaj when at
                least one cart item is flagged for SELF (cashier ticked the
                🍽️ button on items they want to send). Sends as ONE order.
                Items remain in cart for fiscalization. Greyed when offline. */}
            {selfKasaOn && cart.some(it => it._send_to_self) && (() => {
              const flaggedCount = cart.filter(it => it._send_to_self).length;
              return (
                <button
                  onClick={sendToSelf}
                  disabled={sendingSelf || !online}
                  style={{
                    ...S.btn(sendingSelf || !online ? "#94a3b8" : "#0369a1"),
                    width: "100%",
                    justifyContent: "center",
                    padding: "12px",
                    fontSize: 15,
                    marginBottom: 8,
                    opacity: !online ? 0.6 : 1,
                  }}
                >
                  {sendingSelf ? <Loader2 size={17} className="spin" /> : <Send size={17} />}
                  {sendingSelf ? "Šalje..." : `🍽️ Pošalji ${flaggedCount} ${flaggedCount === 1 ? "stavku" : "stavki"} na SELF`}
                </button>
              );
            })()}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowConfirm(true)} disabled={fiscalizing} style={{ ...S.btn(fiscalizing ? "#6b7280" : S.green), flex: "0 0 63%", justifyContent: "center", padding: "13px", fontSize: 17 }}>
                {fiscalizing ? <Loader2 size={19} className="spin" /> : <Zap size={19} />}{fiscalizing ? "..." : "FISKALIZUJ"}
              </button>
              <button onClick={handleSaveDraft} disabled={saving} style={{ ...S.btn("#3b82f6"), flex: "0 0 35%", justifyContent: "center", padding: "13px", fontSize: 15 }}>
                {saving ? <Loader2 size={15} className="spin" /> : <Archive size={15} />}{saving ? "..." : "SAČUVAJ"}
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfirm && <Modal onClose={() => setShowConfirm(false)} title="Potvrda fiskalizacije">
        <p style={{ color: S.muted, fontSize: 15, marginBottom: 16 }}>Da li ste sigurni da želite fiskalizovati ovaj račun?</p>
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700 }}><span>Ukupno:</span><span style={{ color: S.green }}>{fmt(cartCalc.total)} €</span></div>
          <div style={{ fontSize: 14, color: S.muted, marginTop: 6 }}>Stavke: {cart.length} | Plaćanje: {paymentMethod === "cash" ? "Gotovina" : paymentMethod === "card" ? "Kartica" : "Podijeljeno"}</div>
        </div>
        {/* Partner-link: only available on card payment. Lets a B2B customer paying
            by card put their company on the invoice (so they can claim the expense
            in their bookkeeping). The resulting invoice is still a cash receipt
            from a fiscal standpoint — just with an identified buyer. */}
        {paymentMethod === "card" && (
          <div style={{ marginBottom: 16, padding: "10px 12px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8 }}>
            {(cardPartner || cardManualPartner) ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <Building size={15} color="#0369a1" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0c4a6e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cardPartner?.name || cardManualPartner?.name}</div>
                    {(cardPartner?.tax_id || cardManualPartner?.tax_id) && <div style={{ fontSize: 11, color: "#0369a1" }}>PIB: {cardPartner?.tax_id || cardManualPartner?.tax_id}</div>}
                  </div>
                </div>
                <button onClick={() => setShowPartnerPicker(true)} style={{ ...S.btnSm("white", "#0369a1"), border: "1px solid #bae6fd" }} title="Promijeni partnera"><Edit3 size={11} /></button>
                <button onClick={() => { setCardPartner(null); setCardManualPartner(null); }} style={{ ...S.btnSm("white", S.red), border: "1px solid #fecaca" }} title="Ukloni partnera"><X size={11} /></button>
              </div>
            ) : (
              <button onClick={() => setShowPartnerPicker(true)} style={{ width: "100%", padding: "8px 12px", background: "white", border: "1px dashed #0369a1", borderRadius: 6, cursor: "pointer", color: "#0369a1", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <Building size={14} />Veži za partnera (opciono)
              </button>
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => setShowConfirm(false)} style={S.btn("#f3f4f6", S.muted)}>Otkaži</button>
          <button onClick={handleFiscalize} style={{ ...S.btn(S.green), padding: "14px 24px", fontSize: 16 }}><Zap size={18} />Fiskalizuj</button>
        </div>
      </Modal>}
      {showPartnerPicker && (
        <PartnerPickerModal
          currentPartner={cardPartner}
          currentManual={cardManualPartner}
          onClose={() => setShowPartnerPicker(false)}
          onSelect={(sel) => {
            // sel comes from PartnerPickerModal with normalized fields. We track
            // saved partners and manual entries in separate state so we can
            // distinguish them in buildPayload (saved → partner_id set, manual → null).
            if (sel._savedMirror) {
              setCardPartner(sel._savedMirror);
              setCardManualPartner(null);
            } else if (sel._manualMirror) {
              setCardPartner(null);
              setCardManualPartner(sel._manualMirror);
            } else {
              setCardPartner(null);
              setCardManualPartner(null);
            }
            setShowPartnerPicker(false);
          }}
        />
      )}
      {showDiscountModal && <Modal onClose={() => setShowDiscountModal(false)} title="Popust na račun">
        <DiscountForm current={invoiceDiscount} onApply={d => { setInvoiceDiscount(d); setShowDiscountModal(false); }} onClose={() => setShowDiscountModal(false)} />
      </Modal>}
      {qtyModal && (
        <QtyNumpadModal
          product={qtyModal}
          onClose={() => setQtyModal(null)}
          onSave={qty => {
            updateCartItem(qtyModal.id, { quantity: qty });
            setQtyModal(null);
          }}
        />
      )}
      {posNumPad && <NumPad
        value={posNumPad === "splitCash" ? splitCash : splitCard}
        title={posNumPad === "splitCash" ? "Gotovina (€)" : "Kartica (€)"}
        onChange={v => {
          if (posNumPad === "splitCash") { setSplitCash(v); setSplitCard(fmt(cartCalc.total - (parseFloat(v) || 0))); }
          else { setSplitCard(v); setSplitCash(fmt(cartCalc.total - (parseFloat(v) || 0))); }
        }}
        onClose={() => setPosNumPad(null)}
      />}
    </div>
  );
}

// ═══ INVOICES VIEW ═══
function InvoicesView({ user, showToast, activeOperator }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pg, setPg] = useState({ total: 0, page: 1, per_page: 20, total_pages: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [stornoModal, setStornoModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => todayLocal(-7));
  const [dateTo, setDateTo] = useState(() => todayLocal());
  const [invTab, setInvTab] = useState("racuni");
  // Korekcije state
  const [corrSearch, setCorrSearch] = useState("");
  const [corrInvoice, setCorrInvoice] = useState(null);
  const [corrItems, setCorrItems] = useState([]);
  const [corrPayment, setCorrPayment] = useState("cash");
  const [corrBuyerName, setCorrBuyerName] = useState("");
  const [corrBuyerTin, setCorrBuyerTin] = useState("");
  const [corrReason, setCorrReason] = useState("CORRECTIVE");
  const [corrSubmitting, setCorrSubmitting] = useState(false);
  // Avansni state
  const [advMode, setAdvMode] = useState("create"); // create or finalize
  const [advItems, setAdvItems] = useState([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
  const [advPayment, setAdvPayment] = useState("cash");
  const [advBuyerName, setAdvBuyerName] = useState("");
  const [advBuyerTin, setAdvBuyerTin] = useState("");
  const [advSubmitting, setAdvSubmitting] = useState(false);
  const [advSearch, setAdvSearch] = useState("");
  const [advInvoice, setAdvInvoice] = useState(null);
  const [advFinalItems, setAdvFinalItems] = useState([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
  const [advFinalPayment, setAdvFinalPayment] = useState("cash");

  useEffect(() => { loadInvoices(); }, [pg.page, statusFilter, dateFrom, dateTo]);

  const loadInvoices = async () => {
    setLoading(true);
    const tcr = getTcr();
    const params = new URLSearchParams({ page: pg.page, per_page: 20 });
    if (statusFilter) params.set("status", statusFilter);
    if (tcr) params.set("tcr_code", tcr);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const data = await api(`/invoices?${params}`);
    if (data.success) { setInvoices(data.data.invoices || []); setPg(p => ({ ...p, ...data.data.pagination })); }
    setLoading(false);
  };
  const viewInvoice = async (id) => { const d = await api(`/invoices/show/${id}`); if (d.success) setDetail(d.data); };
  const fiscalizeDraft = async (id) => {
    const d = await api(`/invoices/fiscalize/${id}`, { method: "POST" });
    if (d.success) { showToast("Fiskalizovano!"); loadInvoices(); if (detail) viewInvoice(id); } else showToast(d.message, "error");
  };
  const handleStorno = async (id, reason) => {
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    const d = await api(`/invoices/storno/${id}`, { method: "POST", body: JSON.stringify({ storno_reason: reason, fiscalize_now: true, ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code }) }) });
    if (d.success) { showToast("Storno uspješan!"); setStornoModal(null); setDetail(null); loadInvoices(); } else showToast(d.message, "error");
  };
  const handleEmail = async (id, email) => {
    const d = await api(`/invoices/email/${id}`, { method: "POST", body: JSON.stringify({ email }) });
    if (d.success) showToast("Email poslat na " + email);
    else showToast(d.message || "Greška pri slanju", "error");
    setEmailModal(null);
  };

  const loadCorrInvoice = async () => {
    if (!corrSearch.trim()) return;
    const tcr = getTcr();
    // Try search by invoice number first
    let d = await api(`/invoices?search=${encodeURIComponent(corrSearch.trim())}&per_page=5${tcr ? "&tcr_code=" + tcr : ""}`);
    let found = d.success && d.data?.invoices?.length > 0 ? d.data.invoices.find(inv => inv.status === "fiscalized" && !inv.stornoed_by_invoice_id && !inv.corrected_by_invoice_id && inv.is_storno != 1) : null;
    if (!found && d.success && d.data?.invoices?.length > 0) found = d.data.invoices[0];
    if (!found) {
      // Try by ID
      const idNum = parseInt(corrSearch.trim());
      if (idNum > 0) {
        const d2 = await api(`/invoices/show/${idNum}`);
        if (d2.success && d2.data?.invoice) found = d2.data.invoice;
      }
    }
    if (found) {
      const det = await api(`/invoices/show/${found.id}`);
      if (det.success) {
        const full = det.data;
        setCorrInvoice(full);
        setCorrPayment(full.invoice?.payment_method || "cash");
        setCorrBuyerName(full.invoice?.buyer_name || "");
        setCorrBuyerTin(full.invoice?.buyer_pib || "");
        // Load items
        const items = (full.items || []).map(it => ({
          product_id: it.product_id || null,
          name: it.item_name || it.name || "Item",
          quantity: Math.abs(parseFloat(it.quantity || 1)),
          unit_price: parseFloat(it.unit_price || 0),
          vat_rate: Number(it.vat_rate != null ? it.vat_rate : 21),
          unit: it.unit || "kom",
          discount_type: it.discount_type || "none",
          discount_value: parseFloat(it.discount_value || 0)
        }));
        setCorrItems(items);
      }
    } else showToast("Račun nije pronađen", "error");
  };

  const submitCorrection = async () => {
    if (!corrInvoice || corrItems.length === 0) return;
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    setCorrSubmitting(true);
    const d = await api(`/invoices/correct/${corrInvoice.invoice.id}`, { method: "POST", body: JSON.stringify({
      items: corrItems, payment_method: corrPayment,
      buyer_name: corrBuyerName, buyer_tin: corrBuyerTin,
      correction_reason: corrReason,
      ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code })
    }) });
    setCorrSubmitting(false);
    if (d.success) {
      showToast(`Korekcija uspješna! Novi račun: ${d.data?.corrected_invoice?.invoice_number || ""}`);
      setCorrInvoice(null); setCorrItems([]); setCorrSearch("");
      loadInvoices();
    } else showToast(d.message || "Greška", "error");
  };

  const submitAdvance = async () => {
    if (advItems.length === 0 || advItems.every(i => !i.name)) return;
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    setAdvSubmitting(true);
    const tcr = getTcr();
    const d = await api("/invoices/advance", { method: "POST", body: JSON.stringify({
      items: advItems, payment_method: advPayment, tcr_code: tcr,
      buyer_name: advBuyerName, buyer_tin: advBuyerTin,
      ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code })
    }) });
    setAdvSubmitting(false);
    if (d.success) {
      showToast(`Avansni račun kreiran! ${d.data?.invoice?.invoice_number || ""}`);
      setAdvItems([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
      setAdvBuyerName(""); setAdvBuyerTin("");
      loadInvoices();
    } else showToast(d.message || "Greška", "error");
  };

  const loadAdvInvoice = async () => {
    if (!advSearch.trim()) return;
    const tcr = getTcr();
    let d = await api(`/invoices?search=${encodeURIComponent(advSearch.trim())}&per_page=5${tcr ? "&tcr_code=" + tcr : ""}`);
    let found = d.success ? (d.data?.invoices || []).find(inv => inv.is_advance == 1 && !inv.finalized_by_invoice_id) : null;
    if (found) {
      const det = await api(`/invoices/show/${found.id}`);
      if (det.success) {
        setAdvInvoice(det.data);
        const items = (det.data.items || []).map(it => ({
          name: it.item_name || it.name || "Item",
          quantity: Math.abs(parseFloat(it.quantity || 1)),
          unit_price: parseFloat(it.unit_price || 0),
          vat_rate: Number(it.vat_rate != null ? it.vat_rate : 21), unit: it.unit || "kom"
        }));
        setAdvFinalItems(items);
      }
    } else showToast("Avansni račun nije pronađen", "error");
  };

  const submitFinalizeAdvance = async () => {
    if (!advInvoice || advFinalItems.length === 0) return;
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    setAdvSubmitting(true);
    const d = await api(`/invoices/finalizeAdvance/${advInvoice.invoice.id}`, { method: "POST", body: JSON.stringify({
      items: advFinalItems, payment_method: advFinalPayment,
      buyer_name: advBuyerName, buyer_tin: advBuyerTin,
      ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code })
    }) });
    setAdvSubmitting(false);
    if (d.success) {
      showToast(`Finalni račun kreiran! Avans: ${fmt(d.data?.advance_used || 0)}€, Ostatak: ${fmt(d.data?.remaining_paid || 0)}€`);
      setAdvInvoice(null); setAdvSearch("");
      setAdvFinalItems([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
      loadInvoices();
    } else showToast(d.message || "Greška", "error");
  };

  // Detail view
  if (detail) {
    const inv = detail.invoice; const items = detail.items || [];
    const isStorno = inv.is_storno == 1;
    return (
      <div style={{ height: "100%", overflow: "auto", background: S.bg }}>
        <div style={{ padding: "14px 20px", background: isStorno ? "#fef2f2" : "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setDetail(null)} style={{ padding: 8, background: "#f3f4f6", border: "none", borderRadius: 6, cursor: "pointer", display: "flex" }}><ArrowLeft size={17} /></button>
          <div style={{ flex: 1 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{isStorno && "⊖ "}{inv.invoice_number}</h2><span style={{ fontSize: 13, color: "#9ca3af" }}>{fmtDateTime(inv.invoice_date)}</span></div>
          <Badge status={inv.status} />
          <button onClick={async () => { await printReceipt(inv.id) }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={13} />Štampaj</button>
          <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={13} />PDF</button>
          <button onClick={() => setEmailModal(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Mail size={13} />Email</button>
          {inv.status === "draft" && <button onClick={() => fiscalizeDraft(inv.id)} style={S.btnSm("#f0fdf4", "#15803d")}><Zap size={13} />Fiskalizuj</button>}
          {detail.can_storno && hasPermission(activeOperator, "sale_storno") && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} />Storniraj</button>}
        </div>
        <div style={{ padding: 20, maxWidth: 700, margin: "0 auto" }}>
          {isStorno && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 14, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}><RotateCcw size={16} color={S.red} /><span style={{ fontSize: 14, color: S.red, fontWeight: 600 }}>Ovo je storno/korektivni račun (negativne stavke).</span></div>}
          {detail.storno_invoice && detail.storno_invoice.status === "draft" && !isStorno && <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginBottom: 16 }}><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><AlertCircle size={16} color="#92400e" /><span style={{ fontSize: 14, color: "#92400e", fontWeight: 600 }}>Storno kreiran ali fiskalizacija nije uspjela!</span></div><div style={{ display: "flex", gap: 8 }}><button onClick={() => fiscalizeDraft(detail.storno_invoice.id)} style={{ ...S.btnSm(S.green, "white") }}><Zap size={14} />Ponovo fiskalizuj storno</button><button onClick={() => viewInvoice(detail.storno_invoice.id)} style={S.btnSm("#f3f4f6", S.muted)}>Pogledaj storno →</button></div></div>}
          {detail.storno_invoice && detail.storno_invoice.status !== "draft" && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 14, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><RotateCcw size={16} color={S.red} /><span style={{ fontSize: 14, color: S.red }}>Ovaj račun je storniran</span></div><button onClick={() => viewInvoice(detail.storno_invoice.id)} style={S.btnSm("#fef2f2", S.red)}>Pogledaj storno →</button></div>}
          {inv.fic && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: 18, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}><Check size={17} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 15 }}>Fiskalizovano</span></div>
              <Row label="FIC" value={<span style={{ fontFamily: S.mono, fontSize: 12, wordBreak: "break-all" }}>{inv.fic}</span>} />
              <Row label="IIC" value={<span style={{ fontFamily: S.mono, fontSize: 12, wordBreak: "break-all" }}>{inv.iic || "-"}</span>} />
              {inv.iic && detail.verification_url && (
                <div style={{ marginTop: 12, textAlign: "center" }}>
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(detail.verification_url)}`} alt="QR" style={{ width: 160 }} />
                  <div style={{ fontSize: 12, color: S.muted, marginTop: 4 }}>Verifikacioni QR kod</div>
                </div>
              )}
            </div>
          )}
          {(inv.buyer_name || inv.partner_name) && (
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Building size={16} />Kupac / Partner</div>
              <Row label="Naziv" value={inv.partner_name || inv.buyer_name} />
              {(inv.partner_tax_id || inv.buyer_pib) && <Row label="PIB" value={inv.partner_tax_id || inv.buyer_pib} />}
              {inv.buyer_address && <Row label="Adresa" value={inv.buyer_address} />}
              {inv.buyer_city && <Row label="Grad" value={inv.buyer_city} />}
              {inv.payment_due && <Row label="Rok plaćanja" value={inv.payment_due === "end_of_month" ? "Kraj mjeseca" : inv.payment_due === "avans" ? "Avans" : inv.payment_due} />}
            </div>
          )}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${S.border}`, fontWeight: 700, fontSize: 15 }}>Stavke</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={{ ...th, textAlign: "center" }}>Kol.</th><th style={{ ...th, textAlign: "right" }}>Cijena</th><th style={{ ...th, textAlign: "right" }}>Ukupno</th></tr></thead>
              <tbody>{items.map((it, i) => <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}><td style={td}>{it.item_name || it.name}</td><td style={{ ...td, textAlign: "center" }}>{it.quantity}</td><td style={{ ...td, textAlign: "right" }}>{fmt(it.unit_price)} €</td><td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(it.total_amount || it.quantity * it.unit_price)} €</td></tr>)}</tbody>
            </table>
          </div>
          {inv.is_summary_invoice == 1 && inv.summary_order_ids && (() => {
            const refIds = typeof inv.summary_order_ids === "string" ? JSON.parse(inv.summary_order_ids) : inv.summary_order_ids;
            return refIds && refIds.length > 0 ? (
              <div style={{ background: "#f0f9ff", borderRadius: 10, border: "1px solid #bae6fd", padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><FileText size={16} color="#0284c7" />Referentne narudžbe ({refIds.length})</div>
                <div style={{ fontSize: 13, color: S.muted, marginBottom: 8 }}>Ovaj zbirni račun obuhvata sljedeće narudžbe:</div>
                {refIds.map((rid, i) => (
                  <button key={rid} onClick={() => viewInvoice(rid)} style={{ display: "block", width: "100%", padding: "8px 12px", marginBottom: 4, background: "white", border: `1px solid ${S.border}`, borderRadius: 6, cursor: "pointer", textAlign: "left", fontSize: 13, fontFamily: S.font }}>
                    Narudžba #{i + 1} — ID: {rid} <span style={{ color: S.accent, marginLeft: 4 }}>→ Pogledaj</span>
                  </button>
                ))}
              </div>
            ) : null;
          })()}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 18 }}>
            <Row label="Neto" value={`${fmt(inv.subtotal)} €`} />
            {(() => { const vg = {}; (detail?.items || []).forEach(it => { const r = Number(it.vat_rate != null ? it.vat_rate : 21); const gross = parseFloat(it.total_amount || it.quantity * it.unit_price); const net = gross / (1 + r/100); if (!vg[r]) vg[r] = 0; vg[r] += gross - net; }); return Object.entries(vg).map(([r, v]) => <Row key={r} label={`PDV ${r}%`} value={`${fmt(v)} €`} />); })()}
            {parseFloat(inv.discount_amount || 0) > 0 && <Row label="Popust" value={`-${fmt(inv.discount_amount)} €`} />}
            <Row label="Plaćanje" value={inv.payment_method === "cash" ? "Gotovina" : inv.payment_method === "card" ? "Kartica" : inv.payment_method === "split" ? "Podijeljeno" : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method === "other" ? "Ostalo" : inv.payment_method} />
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 10 }}><span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span><span style={{ fontWeight: 700, fontSize: 22, color: isStorno ? S.red : S.green }}>{fmt(inv.total_amount)} €</span></div>
          </div>
        </div>
        {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
        {emailModal && <Modal onClose={() => setEmailModal(null)} title="Pošalji email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
      </div>
    );
  }

  // List view
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "14px 20px", background: "white", borderBottom: `1px solid ${S.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div><h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Računi</h2></div>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "racuni", l: "Računi" }, { id: "korekcije", l: "Korekcije" }, { id: "avansni", l: "Avansni" }].map(t => (
              <button key={t.id} onClick={() => setInvTab(t.id)} style={{ padding: "7px 16px", border: `1px solid ${invTab === t.id ? S.dark : S.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: invTab === t.id ? S.dark : "white", color: invTab === t.id ? "white" : S.muted }}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* Korekcije tab content */}
        {invTab === "korekcije" && (
          <div style={{ padding: "10px 0" }}>
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13, color: "#92400e" }}>
              Korekcija računa: sistem automatski kreira negativni (storno) račun i novi ispravljeni račun. Oba se šalju na Poresku upravu.
            </div>

            {!corrInvoice ? (
              <div>
                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  <input value={corrSearch} onChange={e => setCorrSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadCorrInvoice()} placeholder="Unesite broj računa..." style={{ ...S.input, flex: 1 }} />
                  <button onClick={loadCorrInvoice} style={S.btn(S.accent)}>Traži</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>Korekcija: {corrInvoice.invoice?.invoice_number}</div>
                    <div style={{ fontSize: 12, color: S.muted }}>{fmtDateTime(corrInvoice.invoice?.invoice_date)} · {fmt(corrInvoice.invoice?.total_amount)} €</div>
                  </div>
                  <button onClick={() => { setCorrInvoice(null); setCorrItems([]); }} style={S.btnSm("#f3f4f6", S.muted)}><X size={16} /></button>
                </div>

                {/* Reason */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Razlog korekcije</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {[{ v: "CORRECTIVE", l: "Korekcija (kupac)" }, { v: "ERROR_CORRECTIVE", l: "Greška operatera" }].map(r => (
                      <button key={r.v} onClick={() => setCorrReason(r.v)} style={{ padding: "6px 14px", border: `1px solid ${corrReason === r.v ? S.accent : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: corrReason === r.v ? S.accent : "white", color: corrReason === r.v ? "white" : S.muted }}>{r.l}</button>
                    ))}
                  </div>
                </div>

                {/* Buyer info */}
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>Kupac (ime)</label><input value={corrBuyerName} onChange={e => setCorrBuyerName(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>PIB kupca</label><input value={corrBuyerTin} onChange={e => setCorrBuyerTin(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                </div>

                {/* Payment */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Način plaćanja</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {[{ v: "cash", l: "Gotovina" }, { v: "card", l: "Kartica" }, { v: "virman", l: "Virman" }].map(p => (
                      <button key={p.v} onClick={() => setCorrPayment(p.v)} style={{ padding: "6px 14px", border: `1px solid ${corrPayment === p.v ? S.green : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: corrPayment === p.v ? S.green : "white", color: corrPayment === p.v ? "white" : S.muted }}>{p.l}</button>
                    ))}
                  </div>
                </div>

                {/* Editable items */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 700 }}>Stavke (uredite po potrebi)</label>
                  <div style={{ marginTop: 8, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", background: "#f9fafb", fontSize: 11, fontWeight: 600, color: S.muted }}>
                      <span>Naziv</span><span>Kol.</span><span>Cijena</span><span>PDV%</span><span></span>
                    </div>
                    {corrItems.map((item, idx) => (
                      <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", borderTop: `1px solid ${S.border}`, alignItems: "center" }}>
                        <input value={item.name} onChange={e => { const c = [...corrItems]; c[idx].name = e.target.value; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <input type="number" value={item.quantity} onChange={e => { const c = [...corrItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...corrItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <input type="number" value={item.vat_rate} onChange={e => { const c = [...corrItems]; c[idx].vat_rate = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                        <button onClick={() => setCorrItems(corrItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: S.red }}><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <div style={{ padding: "8px 10px", borderTop: `1px solid ${S.border}` }}>
                      <button onClick={() => setCorrItems([...corrItems, { name: "", quantity: 1, unit_price: 0, vat_rate: 21, unit: "kom", discount_type: "none", discount_value: 0 }])} style={{ ...S.btnSm("#f3f4f6", S.muted), fontSize: 11 }}>+ Dodaj stavku</button>
                    </div>
                  </div>
                </div>

                {/* Total preview */}
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  {(() => {
                    let totalGross = 0, totalVat = 0;
                    corrItems.forEach(it => {
                      const line = it.quantity * it.unit_price;
                      const vat = line * (it.vat_rate / 100);
                      totalGross += line + vat;
                      totalVat += vat;
                    });
                    return <>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: S.muted, marginBottom: 4 }}>
                        <span>Bez PDV:</span><span>{fmt(totalGross - totalVat)} €</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: S.muted, marginBottom: 6 }}>
                        <span>PDV:</span><span>{fmt(totalVat)} €</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16, borderTop: "1px solid #e5e7eb", paddingTop: 6 }}>
                        <span>Novi ukupan iznos:</span>
                        <span style={{ color: S.green }}>{fmt(totalGross)} €</span>
                      </div>
                    </>;
                  })()}
                </div>

                {/* Submit */}
                <button onClick={submitCorrection} disabled={corrSubmitting || corrItems.length === 0} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 14, fontSize: 16 }}>
                  {corrSubmitting ? <Loader2 size={17} className="spin" /> : <CheckCircle size={17} />}
                  <span style={{ marginLeft: 8 }}>{corrSubmitting ? "Obrađujem..." : "Potvrdi korekciju"}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Avansni tab content */}
        {invTab === "avansni" && (
          <div style={{ padding: "10px 0" }}>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13, color: "#1e40af" }}>
              Avansni račun: kreira se kad kupac unaprijed plati. Kada se isporuči roba/usluga, kreira se finalni račun koji referencira avansno plaćanje.
            </div>

            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[{ id: "create", l: "Kreiraj avans" }, { id: "finalize", l: "Finaliziraj avans" }].map(t => (
                <button key={t.id} onClick={() => { setAdvMode(t.id); setAdvInvoice(null); }} style={{ padding: "7px 16px", border: `1px solid ${advMode === t.id ? S.accent : S.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: advMode === t.id ? S.accent : "white", color: advMode === t.id ? "white" : S.muted }}>{t.l}</button>
              ))}
            </div>

            {advMode === "create" && (
              <div>
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>Kupac</label><input value={advBuyerName} onChange={e => setAdvBuyerName(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>PIB</label><input value={advBuyerTin} onChange={e => setAdvBuyerTin(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="opciono" /></div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>Plaćanje</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {[{ v: "cash", l: "Gotovina" }, { v: "card", l: "Kartica" }, { v: "virman", l: "Virman" }].map(p => (
                      <button key={p.v} onClick={() => setAdvPayment(p.v)} style={{ padding: "6px 14px", border: `1px solid ${advPayment === p.v ? S.green : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: advPayment === p.v ? S.green : "white", color: advPayment === p.v ? "white" : S.muted }}>{p.l}</button>
                    ))}
                  </div>
                </div>
                <label style={{ fontSize: 13, fontWeight: 700 }}>Stavke avansa</label>
                <div style={{ marginTop: 8, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", background: "#f9fafb", fontSize: 11, fontWeight: 600, color: S.muted }}>
                    <span>Naziv</span><span>Kol.</span><span>Cijena</span><span>PDV%</span><span></span>
                  </div>
                  {advItems.map((item, idx) => (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", borderTop: `1px solid ${S.border}`, alignItems: "center" }}>
                      <input value={item.name} onChange={e => { const c = [...advItems]; c[idx].name = e.target.value; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} placeholder="Opis avansa" />
                      <input type="number" value={item.quantity} onChange={e => { const c = [...advItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                      <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...advItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                      <input type="number" value={item.vat_rate} onChange={e => { const c = [...advItems]; c[idx].vat_rate = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                      <button onClick={() => setAdvItems(advItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: S.red }}><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <div style={{ padding: "8px 10px", borderTop: `1px solid ${S.border}` }}>
                    <button onClick={() => setAdvItems([...advItems, { name: "", quantity: 1, unit_price: 0, vat_rate: 21 }])} style={{ ...S.btnSm("#f3f4f6", S.muted), fontSize: 11 }}>+ Dodaj stavku</button>
                  </div>
                </div>
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginTop: 12, marginBottom: 14 }}>
                  {(() => { let tot = 0, vat = 0; advItems.forEach(i => { const n = i.quantity * i.unit_price; vat += n * (i.vat_rate / 100); tot += n + n * (i.vat_rate / 100); }); return <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16 }}><span>Avansni iznos:</span><span style={{ color: S.green }}>{fmt(tot)} €</span></div>; })()}
                </div>
                <button onClick={submitAdvance} disabled={advSubmitting} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 14, fontSize: 16 }}>
                  {advSubmitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}
                  <span style={{ marginLeft: 8 }}>{advSubmitting ? "Obrađujem..." : "Kreiraj avansni račun"}</span>
                </button>
              </div>
            )}

            {advMode === "finalize" && (
              <div>
                {!advInvoice ? (
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <input value={advSearch} onChange={e => setAdvSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadAdvInvoice()} placeholder="Unesite broj avansnog računa..." style={{ ...S.input, flex: 1 }} />
                    <button onClick={loadAdvInvoice} style={S.btn(S.accent)}>Traži</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>Avans: {advInvoice.invoice?.invoice_number}</div>
                        <div style={{ fontSize: 12, color: S.muted }}>Iznos avansa: <strong style={{ color: S.green }}>{fmt(advInvoice.invoice?.total_amount)} €</strong></div>
                      </div>
                      <button onClick={() => { setAdvInvoice(null); setAdvSearch(""); }} style={S.btnSm("#f3f4f6", S.muted)}><X size={16} /></button>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, fontWeight: 600 }}>Plaćanje ostatka</label>
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        {[{ v: "cash", l: "Gotovina" }, { v: "card", l: "Kartica" }].map(p => (
                          <button key={p.v} onClick={() => setAdvFinalPayment(p.v)} style={{ padding: "6px 14px", border: `1px solid ${advFinalPayment === p.v ? S.green : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: advFinalPayment === p.v ? S.green : "white", color: advFinalPayment === p.v ? "white" : S.muted }}>{p.l}</button>
                        ))}
                      </div>
                    </div>
                    <label style={{ fontSize: 13, fontWeight: 700 }}>Finalne stavke</label>
                    <div style={{ marginTop: 8, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", background: "#f9fafb", fontSize: 11, fontWeight: 600, color: S.muted }}>
                        <span>Naziv</span><span>Kol.</span><span>Cijena</span><span>PDV%</span><span></span>
                      </div>
                      {advFinalItems.map((item, idx) => (
                        <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 70px 90px 70px 50px", gap: 4, padding: "6px 10px", borderTop: `1px solid ${S.border}`, alignItems: "center" }}>
                          <input value={item.name} onChange={e => { const c = [...advFinalItems]; c[idx].name = e.target.value; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <input type="number" value={item.quantity} onChange={e => { const c = [...advFinalItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...advFinalItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <input type="number" value={item.vat_rate} onChange={e => { const c = [...advFinalItems]; c[idx].vat_rate = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...S.input, padding: "4px 6px", fontSize: 12 }} />
                          <button onClick={() => setAdvFinalItems(advFinalItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: S.red }}><Trash2 size={14} /></button>
                        </div>
                      ))}
                      <div style={{ padding: "8px 10px", borderTop: `1px solid ${S.border}` }}>
                        <button onClick={() => setAdvFinalItems([...advFinalItems, { name: "", quantity: 1, unit_price: 0, vat_rate: 21 }])} style={{ ...S.btnSm("#f3f4f6", S.muted), fontSize: 11 }}>+ Dodaj stavku</button>
                      </div>
                    </div>
                    <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginTop: 12, marginBottom: 14 }}>
                      {(() => {
                        let tot = 0, vat = 0;
                        advFinalItems.forEach(i => { const n = i.quantity * i.unit_price; vat += n * (i.vat_rate / 100); tot += n + n * (i.vat_rate / 100); });
                        const advAmt = parseFloat(advInvoice.invoice?.total_amount || 0);
                        const remaining = Math.max(0, tot - advAmt);
                        return <>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: S.muted, marginBottom: 4 }}><span>Ukupno:</span><span>{fmt(tot)} €</span></div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#1e40af", marginBottom: 4 }}><span>Avans:</span><span>-{fmt(advAmt)} €</span></div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16, borderTop: "1px solid #e5e7eb", paddingTop: 6 }}><span>Za naplatu:</span><span style={{ color: S.green }}>{fmt(remaining)} €</span></div>
                        </>;
                      })()}
                    </div>
                    <button onClick={submitFinalizeAdvance} disabled={advSubmitting} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 14, fontSize: 16 }}>
                      {advSubmitting ? <Loader2 size={17} className="spin" /> : <CheckCircle size={17} />}
                      <span style={{ marginLeft: 8 }}>{advSubmitting ? "Obrađujem..." : "Kreiraj finalni račun"}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Racuni filters — only show when on racuni tab */}
        {invTab === "racuni" && <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#9ca3af" }}>{pg.total} ukupno{getTcr() ? ` · TCR: ${getTcr()}` : ""}</p>
          <div style={{ display: "flex", gap: 5 }}>
            {[{ v: "", l: "Sve" }, { v: "fiscalized", l: "Fiskalizovane" }, { v: "draft", l: "Draft" }, { v: "storno", l: "Storno" }, { v: "corrective", l: "Korigovani" }, { v: "advance", l: "Avansni" }].map(s => (
              <button key={s.v} onClick={() => { setStatusFilter(s.v); setPg(p => ({ ...p, page: 1 })); }} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: S.font, borderColor: statusFilter === s.v ? S.dark : S.border, background: statusFilter === s.v ? S.dark : "white", color: statusFilter === s.v ? "white" : S.muted }}>{s.l}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: S.muted, fontWeight: 600 }}>Period:</span>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPg(p => ({ ...p, page: 1 })); }} style={{ ...S.input, padding: "5px 10px", fontSize: 13, width: 140 }} />
          <span style={{ fontSize: 13, color: S.muted }}>—</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPg(p => ({ ...p, page: 1 })); }} style={{ ...S.input, padding: "5px 10px", fontSize: 13, width: 140 }} />
          {[{ l: "Danas", d: 0 }, { l: "7 dana", d: 7 }, { l: "30 dana", d: 30 }].map(p => (
            <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setDateFrom(todayLocal(-p.d)); setDateTo(todayLocal()); setPg(pr => ({ ...pr, page: 1 })); }} style={{ padding: "5px 10px", border: `1px solid ${S.border}`, borderRadius: 5, fontSize: 12, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
          ))}
        </div>
        </>}
      </div>
      {invTab === "racuni" && <><div style={{ flex: 1, overflow: "auto", padding: "0 20px" }}>
        {loading ? <div style={{ textAlign: "center", padding: 50 }}><Loader2 size={28} className="spin" /></div>
        : invoices.length === 0 ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><FileText size={40} /><p style={{ marginTop: 10, fontSize: 15 }}>Nema faktura</p></div>
        : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, marginTop: 8 }}>
          <thead><tr><th style={th}>Broj</th><th style={th}>Datum</th><th style={{ ...th, textAlign: "center" }}>Plaćanje</th><th style={{ ...th, textAlign: "right" }}>Iznos</th><th style={{ ...th, textAlign: "center" }}>Status</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
          <tbody>{invoices.map(inv => {
            const isStorno = inv.is_storno == 1 || inv.status === "storno";
            const wasStornoed = inv.stornoed_by_invoice_id || inv.stornoed_at;
            return (
              <tr key={inv.id} style={{ borderBottom: "1px solid #f3f4f6", background: isStorno ? "#fef2f2" : wasStornoed ? "#fff7ed" : "white" }}>
                <td style={td}>
                  <span style={{ fontFamily: S.mono, fontSize: 12, fontWeight: 600, color: isStorno ? S.red : S.dark }}>
                    {isStorno && "⊖ "}{inv.invoice_number}
                    {inv.is_summary_invoice == 1 && <span style={{ marginLeft: 4, fontSize: 10, color: "#0284c7", fontWeight: 700 }}>ZBIRNI</span>}
                    {inv.is_corrective == 1 && <span style={{ marginLeft: 4, fontSize: 10, color: "#9333ea", fontWeight: 700 }}>KOREKCIJA</span>}
                    {inv.corrected_by_invoice_id && <span style={{ marginLeft: 4, fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>KORIGOVANO</span>}
                    {inv.is_advance == 1 && <span style={{ marginLeft: 4, fontSize: 10, color: "#1e40af", fontWeight: 700 }}>AVANS</span>}
                    {inv.finalized_by_invoice_id && <span style={{ marginLeft: 4, fontSize: 10, color: "#15803d", fontWeight: 700 }}>ISKORIŠTEN</span>}
                  </span>
                </td>
                <td style={{ ...td, color: S.muted }}>{fmtDate(inv.invoice_date)}</td>
                <td style={{ ...td, textAlign: "center", fontSize: 13 }}>{inv.payment_method === "cash" ? "Gotovina" : inv.payment_method === "card" ? "Kartica" : inv.payment_method === "split" ? "Podijeljeno" : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method === "other" ? "Ostalo" : inv.payment_method}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700, color: isStorno ? S.red : S.dark }}>{fmt(inv.total_amount)} €</td>
                <td style={{ ...td, textAlign: "center" }}><Badge status={isStorno ? "storno" : wasStornoed ? "cancelled" : inv.status} /></td>
                <td style={{ ...td, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button onClick={() => viewInvoice(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Eye size={13} /></button>
                    {inv.status === "draft" && <button onClick={() => fiscalizeDraft(inv.id)} style={S.btnSm("#f0fdf4", "#15803d")}><Zap size={13} /></button>}
                    {inv.status === "fiscalized" && !wasStornoed && !isStorno && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} /></button>}
                  </div>
                </td>
              </tr>
            );
          })}</tbody>
        </table>}
      </div>
      {pg.total_pages > 1 && <Pagination pg={pg} onChange={p => setPg(prev => ({ ...prev, page: p }))} />}
      </>}
      {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
    </div>
  );
}

// ═══ BLAGAJNA ═══
function BlagajnaView({ user, showToast, activeOperator }) {
  const [depositAmt, setDepositAmt] = useState("");
  const [actualCash, setActualCash] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(null);
  const [depositExists, setDepositExists] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [closingInfo, setClosingInfo] = useState(null);
  const [unclosedSession, setUnclosedSession] = useState(null);
  const [histTab, setHistTab] = useState("openings");
  const [continueMode, setContinueMode] = useState(() => !!localStorage.getItem(`pos_continued_${getTcr()}_${todayLocal()}`));
  const [todaySales, setTodaySales] = useState(null);
  const [showCloseAny, setShowCloseAny] = useState(false);
  const [internalEnabled, setInternalEnabled] = useState(false);
  const [internalDay, setInternalDay] = useState(null);
  const [internalSales, setInternalSales] = useState(null);
  const [blagajnaTab, setBlagajnaTab] = useState(() => {
    if (hasPermission(activeOperator, "deposit_declare")) return "depozit";
    if (hasPermission(activeOperator, "deposit_withdraw")) return "podizanje";
    if (hasPermission(activeOperator, "cashregister_close")) return "depozit";
    return "depozit";
  });
  const [internalHistory, setInternalHistory] = useState([]);
  const [showCloseByInvoice, setShowCloseByInvoice] = useState(false);
  const [histDateFrom, setHistDateFrom] = useState(() => todayLocal(-14));
  const [histDateTo, setHistDateTo] = useState(todayLocal());
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [todayWithdrawn, setTodayWithdrawn] = useState(0);
  const [todayDeposit, setTodayDeposit] = useState(0);
  const [numPadFor, setNumPadFor] = useState(null); // "deposit" | "actual" | "withdraw" | null
  const [dayInvoices, setDayInvoices] = useState([]);
  const [closeByInvSearch, setCloseByInvSearch] = useState("");
  const [closeAnyDate, setCloseAnyDate] = useState("");
  const [closeAnyTcr, setCloseAnyTcr] = useState("");
  const [closeAnyCash, setCloseAnyCash] = useState("");

  useEffect(() => { load(); }, []);
  const tcr = getTcr();

  const load = async () => {
    if (!tcr) { setLoading(false); return; }
    const [dep, sess, hist, unclosed, idSettings, idStatus, idHist] = await Promise.all([
      api(`/deposits/check/${tcr}`),
      api(`/cashregister/active/${tcr}`),
      api(`/cashregister?tcr_code=${tcr}`),
      api(`/cashregister/unclosed/${tcr}`),
      api("/internalday/settings"),
      api(`/internalday/status/${tcr}`),
      api(`/internalday/history/${tcr}`)
    ]);
    if (dep.success) setDepositExists(dep.data.has_deposit);
    if (sess.success) setActive(sess.data.has_active_session ? sess.data : null);
    if (hist.success) setSessions(hist.data.sessions || []);
    if (unclosed.success && unclosed.data.has_unclosed) setUnclosedSession(unclosed.data);
    else setUnclosedSession(null);
    if (idSettings.success) setInternalEnabled(!!idSettings.data?.internal_day_enabled);
    if (idStatus.success) { setInternalDay(idStatus.data?.day || null); setInternalSales(idStatus.data?.sales || null); }
    if (idHist.success) setInternalHistory(idHist.data?.days || []);
    // Load withdrawals
    const wd = await api(`/deposits/withdrawals/${tcr}`);
    if (wd.success) { setWithdrawals(wd.data?.withdrawals || []); setTodayWithdrawn(wd.data?.today_total || 0); }
    // Always load today's deposit amount
    const tdSales = await api(`/cashregister/todaySales/${tcr}`);
    if (tdSales.success) {
      setTodayDeposit(tdSales.data?.initial_deposit || 0);
    }
    // If continueMode was restored from localStorage, load today's sales
    if (localStorage.getItem(`pos_continued_${tcr}_${todayLocal()}`)) {
      const ts = await api(`/cashregister/todaySales/${tcr}`);
      if (ts.success) { setTodaySales(ts.data.sales || ts.data); setContinueMode(true); }
    }
    setLoading(false);
  };

  const doWithdraw = async () => {
    if (!withdrawAmt || parseFloat(withdrawAmt) <= 0) { showToast("Unesite iznos", "error"); return; }
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    setSubmitting(true);
    const d = await api("/deposits/withdraw", { method: "POST", body: JSON.stringify({ tcr_code: tcr, amount: parseFloat(withdrawAmt), reason: withdrawReason, operator_code: activeOperator?.operator_code || null }) });
    setSubmitting(false);
    if (d.success) {
      showToast(`Podizanje ${withdrawAmt} € registrovano! FIC: ${d.data?.fic || ""}`);
      setWithdrawAmt(""); setWithdrawReason("");
      // Auto-print withdrawal receipt (non-blocking)
      const wid = d.data?.withdraw_id;
      if (wid && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        setTimeout(async () => {
          try {
            if (isElectron && window.electronAPI) {
              const token = localStorage.getItem("pos_token");
              const r = await fetch(`${API_BASE}/cashregister/withdrawalText/${wid}`, { headers: { "Authorization": `Bearer ${token}` } });
              if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); }
            }
          } catch(e) {}
        }, 500);
      }
      load();
    } else showToast(d.message || "Greška", "error");
  };

  const doOpenInternalDay = async () => {
    setSubmitting(true);
    const d = await api("/internalday/open", { method: "POST", body: JSON.stringify({ tcr_code: tcr }) });
    setSubmitting(false);
    if (d.success) { showToast("Interni dan otvoren!"); load(); } else showToast(d.message, "error");
  };

  const doCloseInternalDay = async () => {
    setSubmitting(true);
    const d = await api("/internalday/close", { method: "POST", body: JSON.stringify({ tcr_code: tcr }) });
    setSubmitting(false);
    if (d.success) {
      showToast("Interni dan zatvoren!");
      // Auto-print internal day closing (non-blocking)
      const closedDayId = d.data?.day?.id;
      if (closedDayId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        (async () => {
          try {
            const token = localStorage.getItem("pos_token");
            if (isElectron && window.electronAPI) {
              const r = await fetch(`${API_BASE}/internalday/closingText/${closedDayId}`, { headers: { "Authorization": `Bearer ${token}` } });
              if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); }
            }
          } catch(e) {}
        })();
      }
      load();
    } else showToast(d.message, "error");
  };

  const doCloseByInvoice = async (invId) => {
    setSubmitting(true);
    const d = await api("/internalday/closeByInvoice", { method: "POST", body: JSON.stringify({ tcr_code: tcr, invoice_id: invId }) });
    setSubmitting(false);
    if (d.success) { showToast("Dan zatvoren po racunu!"); setShowCloseByInvoice(false); load(); } else showToast(d.message, "error");
  };

  const loadDayInvoices = async () => {
    const d = await api(`/internalday/invoices/${tcr}`);
    if (d.success) { setDayInvoices(d.data?.invoices || []); setShowCloseByInvoice(true); }
  };

  const doDeposit = async () => {
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    setSubmitting(true);
    const d = await api("/deposits/register", { method: "POST", body: JSON.stringify({ tcr_code: tcr, deposit_amount: parseFloat(depositAmt) || 0, operator_code: activeOperator?.operator_code || null }) });
    setSubmitting(false);
    if (d.success) {
      showToast("Depozit registrovan!");
      setDepositAmt("");
      // Auto-print deposit receipt (non-blocking, delay to ensure session exists)
      if (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width")) {
        setTimeout(async () => {
          try {
            const sess = await api(`/cashregister/active/${tcr}`);
            if (sess.success && sess.data?.session?.id) {
              await printRegisterReport(sess.data.session.id, "opening");
            }
          } catch(e) {}
        }, 500);
      }
      load();
    } else showToast(d.message, "error");
  };

  const doClose = async (targetDate) => {
    setSubmitting(true);
    const d = await api("/cashregister/close", { method: "POST", body: JSON.stringify({ tcr_code: tcr, actual_cash: parseFloat(actualCash) || 0, notes, target_date: targetDate || "today" }) });
    setSubmitting(false);
    if (d.success) {
      setClosingInfo(d.data); setActualCash(""); setNotes(""); load(); showToast("Blagajna zatvorena!");
      // Auto-print closing report (non-blocking)
      const sessId = d.data?.session?.id;
      if (sessId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        printRegisterReport(sessId, "closing").catch(() => {});
      }
    } else showToast(d.message, "error");
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;
  if (!tcr) return <div style={{ padding: 50, textAlign: "center", color: S.muted }}><AlertCircle size={40} /><p style={{ marginTop: 12, fontSize: 16 }}>Izaberite TCR kod u Podešavanjima</p></div>;

  return (
    <div style={{ height: "100%", overflow: "auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div><h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Blagajna</h2><p style={{ fontSize: 14, color: S.muted, margin: 0 }}>TCR: <span style={{ fontFamily: S.mono, fontWeight: 600 }}>{tcr}</span></p></div>
        <button onClick={load} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} />Osvježi</button>
      </div>

      {/* Tabs: Depozit / Otvaranja / Zatvaranja (if internal day enabled) */}
      {internalEnabled && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {[
            { id: "depozit", l: "Depozit (Poreska)", perm: ["deposit_declare", "cashregister_close"] },
            { id: "podizanje", l: "Podizanje", perm: ["deposit_withdraw"] },
            { id: "otvaranja", l: "Otvaranja (Interno)", perm: ["internalday_open"] },
            { id: "zatvaranja", l: "Zatvaranja (Interno)", perm: ["internalday_close"] }
          ].filter(t => t.perm.some(p => hasPermission(activeOperator, p))).map(t => (
            <button key={t.id} onClick={() => setBlagajnaTab(t.id)} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: blagajnaTab === t.id ? S.dark : S.border, background: blagajnaTab === t.id ? S.dark : "white", color: blagajnaTab === t.id ? "white" : S.muted }}>{t.l}</button>
          ))}
        </div>
      )}

      {/* Internal Day Alert (when enabled but not opened) */}
      {internalEnabled && !internalDay && blagajnaTab === "depozit" && (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><AlertCircle size={18} color="#92400e" /><span style={{ fontWeight: 700, color: "#92400e" }}>Interni dan nije otvoren!</span></div>
          <p style={{ fontSize: 13, color: "#78350f", margin: "6px 0 0" }}>Otvorite interni dan u tabu "Otvaranja" prije izdavanja racuna.</p>
        </div>
      )}

      {/* Internal Day Active Banner */}
      {internalEnabled && internalDay && blagajnaTab === "depozit" && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 14, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, color: "#15803d", fontSize: 14 }}>Interni dan otvoren</div>
            <div style={{ fontSize: 12, color: "#166534" }}>Od: {internalDay.opened_at?.slice(0, 16).replace("T"," ")} · Promet: {fmt(internalSales?.total_sales || 0)} €</div>
          </div>
        </div>
      )}

      {/* OTVARANJA TAB */}
      {internalEnabled && blagajnaTab === "otvaranja" && (
        <div style={{ maxWidth: 520 }}>
          {!internalDay ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, textAlign: "center" }}>
              <Clock size={40} color={S.muted} />
              <p style={{ fontWeight: 600, fontSize: 17, margin: "12px 0 16px" }}>Otvorite interni dan</p>
              <p style={{ fontSize: 13, color: S.muted, marginBottom: 20 }}>Svi racuni od otvaranja do zatvaranja ce se racunati kao jedan radni dan.</p>
              <button onClick={doOpenInternalDay} disabled={submitting} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>{submitting ? "..." : "Otvori dan"}</button>
            </div>
          ) : (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><CheckCircle size={20} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 16 }}>Dan je otvoren</span></div>
              <Row label="Otvoreno" value={internalDay.opened_at?.slice(0, 16).replace("T", " ")} />
              {internalSales && <>
                <Row label="Gotovina" value={`${fmt(internalSales.cash_sales)} €`} />
                <Row label="Kartica" value={`${fmt(internalSales.card_sales)} €`} />
                <Row label="Ukupno" value={`${fmt(internalSales.total_sales)} €`} />
                <Row label="Racuni" value={internalSales.transaction_count || 0} />
              </>}
            </div>
          )}
          {/* History */}
          {hasPermission(activeOperator, "view_internal_openings") && <>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "20px 0 10px", color: S.muted }}>Istorija otvaranja</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(todayLocal(-p.d)); setHistDateTo(todayLocal()); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {internalHistory.filter(d => (d.status === "open" || d.opened_at) && d.opened_at?.slice(0,10) >= histDateFrom && d.opened_at?.slice(0,10) <= histDateTo).slice(0, 30).map(d => (
            <div key={d.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{d.opened_at?.slice(0, 16).replace("T", " ")}</div>
                <div style={{ fontSize: 12, color: S.muted }}>{d.status === "open" ? "Otvoren" : `Zatvoren: ${d.closed_at?.slice(0, 16).replace("T", " ")} · ${fmt(d.total_sales)} €`}</div>
              </div>
              <button onClick={async () => {
                if (isElectron && window.electronAPI) {
                  try {
                    const token = localStorage.getItem("pos_token");
                    const r = await fetch(`${API_BASE}/internalday/closingText/${d.id}`, { headers: { "Authorization": `Bearer ${token}` } });
                    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
                  } catch(e) { showToast("Greška", "error"); }
                }
              }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
            </div>
          ))}
          </>}
        </div>
      )}

      {/* ZATVARANJA TAB */}
      {internalEnabled && blagajnaTab === "zatvaranja" && (
        <div style={{ maxWidth: 520 }}>
          {internalDay ? (
            <div>
              <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, marginBottom: 16 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>Zatvori interni dan</h3>
                {internalSales && <div style={{ background: "#f9fafb", borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <Row label="Gotovina" value={`${fmt(internalSales.cash_sales)} €`} />
                  <Row label="Kartica" value={`${fmt(internalSales.card_sales)} €`} />
                  <Row label="Ukupno" value={`${fmt(internalSales.total_sales)} €`} />
                  <Row label="Racuni" value={internalSales.transaction_count || 0} />
                  {todayWithdrawn > 0 && <Row label="Podignuto" value={`-${fmt(todayWithdrawn)} €`} />}
                </div>}
                <button onClick={doCloseInternalDay} disabled={submitting} style={{ ...S.btn(S.red), width: "100%", justifyContent: "center", padding: 13, fontSize: 16, marginBottom: 10 }}><XCircle size={17} />{submitting ? "..." : "Zatvori dan sada"}</button>
                <button onClick={loadDayInvoices} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 11, fontSize: 14 }}><Clock size={15} /><span style={{ marginLeft: 6 }}>Zatvori po racunu (zaboravljen)</span></button>
              </div>
            </div>
          ) : (
            <div style={{ background: "#f9fafb", borderRadius: 12, padding: 24, textAlign: "center" }}>
              <CheckCircle size={40} color={S.green} />
              <p style={{ fontWeight: 600, margin: "12px 0" }}>Nema otvorenog dana</p>
            </div>
          )}
          {/* History */}
          {hasPermission(activeOperator, "view_internal_closings") && <>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "20px 0 10px", color: S.muted }}>Istorija zatvaranja</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(todayLocal(-p.d)); setHistDateTo(todayLocal()); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {internalHistory.filter(d => d.status === "closed" && d.closed_at?.slice(0,10) >= histDateFrom && d.closed_at?.slice(0,10) <= histDateTo).slice(0, 30).map(d => (
            <div key={d.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{d.opened_at?.slice(0, 10)} · {fmt(d.total_sales)} €</div>
                <div style={{ fontSize: 12, color: S.muted }}>Od {d.opened_at?.slice(11, 16)} do {d.closed_at?.slice(11, 16)} · {d.transaction_count} racuna{d.is_official ? "" : " (privremeno)"}</div>
              </div>
              <button onClick={async () => {
                if (isElectron && window.electronAPI) {
                  try {
                    const token = localStorage.getItem("pos_token");
                    const r = await fetch(`${API_BASE}/internalday/closingText/${d.id}`, { headers: { "Authorization": `Bearer ${token}` } });
                    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
                  } catch(e) { showToast("Greška", "error"); }
                }
              }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
            </div>
          ))}
          </>}
        </div>
      )}

      {/* Close by Invoice Modal */}
      {showCloseByInvoice && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowCloseByInvoice(false)}>
          <div style={{ background: "white", borderRadius: 16, padding: 28, width: 440, maxHeight: "80vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}><h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Izaberite posljednji racun</h3><button onClick={() => setShowCloseByInvoice(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button></div>
            <p style={{ fontSize: 13, color: S.muted, marginBottom: 14 }}>Unesite broj racuna koji ce biti posljednji za ovaj interni dan:</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input value={closeByInvSearch} onChange={e => setCloseByInvSearch(e.target.value)} placeholder="Broj racuna..." style={{ ...S.input, flex: 1 }} />
              <button onClick={async () => { if (!closeByInvSearch) return; const d = await api(`/invoices?search=${closeByInvSearch}&per_page=5&tcr_code=${tcr}`); if (d.success) setDayInvoices(d.data?.invoices || []); }} style={S.btn(S.dark)}>Trazi</button>
            </div>
            {dayInvoices.length === 0 ? <p style={{ color: S.muted, textAlign: "center", padding: 16, fontSize: 13 }}>Unesite broj racuna i kliknite Trazi</p> : dayInvoices.map(inv => (
              <div key={inv.id} onClick={() => doCloseByInvoice(inv.id)} style={{ padding: "12px 14px", borderRadius: 8, border: `1px solid ${S.border}`, marginBottom: 6, cursor: "pointer", background: "#f9fafb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoice_number}</div><div style={{ fontSize: 12, color: S.muted }}>{fmtDateTime(inv.invoice_date)}</div></div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{fmt(inv.total_amount)} €</div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* ═══ DEPOZIT TAB CONTENT (Internal Day ON) ═══ */}
      {internalEnabled && blagajnaTab === "depozit" && (
        <div style={{ maxWidth: 520 }}>
          {!depositExists ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Registruj depozit</h3>
              <p style={{ fontSize: 14, color: S.muted, margin: "0 0 18px" }}>Poreska prijava depozita za danas</p>
              <label style={{ fontSize: 14, fontWeight: 600 }}>Iznos depozita (€)</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 16, marginTop: 6 }}>
                <input type="number" step="0.01" value={depositAmt} onChange={e => setDepositAmt(e.target.value)} placeholder="0.00" style={{ ...S.input, flex: 1 }} />
                <button onClick={() => setNumPadFor("deposit")} style={{ padding: "8px 14px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 18 }} title="Numerička tastatura">🔢</button>
              </div>
              {hasPermission(activeOperator, "deposit_declare") && <button onClick={doDeposit} disabled={submitting} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>{submitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}{submitting ? "..." : "Registruj depozit"}</button>}
            </div>
          ) : (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><CheckCircle size={20} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 16 }}>Depozit registrovan za danas</span></div>
              <div style={{ fontSize: 13, color: "#166534", marginTop: 6 }}>Depozit: {fmt(todayDeposit || active?.initial_deposit || 0)} €</div>
            </div>
          )}
          {/* Deposit History */}
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted }}>Istorija depozita</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(todayLocal(-p.d)); setHistDateTo(todayLocal()); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {sessions.filter(s => s.tcr_code === tcr && s.opened_at?.slice(0,10) >= histDateFrom && s.opened_at?.slice(0,10) <= histDateTo).slice(0, 30).map(s => (
            <div key={s.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtDate(s.opened_at)}</div>
                <div style={{ fontSize: 12, color: S.muted }}>Depozit: {fmt(s.opening_cash || 0)} €</div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={async () => { await printRegisterReport(s.id, "opening"); showToast("Štampano!"); }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
                <button onClick={() => openPdf(`/cashregister/openingPdf/${s.id}`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ PODIZANJE (WITHDRAWAL) TAB ═══ */}
      {(internalEnabled ? blagajnaTab === "podizanje" : blagajnaTab === "podizanje") && (
        <div style={{ maxWidth: 520 }}>
          {/* Withdrawal form — only if deposit exists today */}
          {depositExists ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Podizanje gotovine</h3>
              <p style={{ fontSize: 14, color: S.muted, margin: "0 0 18px" }}>Registruj podizanje gotovine iz kase (šalje se na Poresku)</p>
              {todayWithdrawn > 0 && (
                <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 13, color: "#92400e" }}>
                  Danas podignuto: <strong>{fmt(todayWithdrawn)} €</strong>
                </div>
              )}
              <label style={{ fontSize: 14, fontWeight: 600 }}>Iznos (€)</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 12, marginTop: 6 }}>
                <input type="number" step="0.01" value={withdrawAmt} onChange={e => setWithdrawAmt(e.target.value)} placeholder="0.00" style={{ ...S.input, flex: 1 }} />
                <button onClick={() => setNumPadFor("withdraw")} style={{ padding: "8px 14px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 18 }} title="Numerička tastatura">🔢</button>
              </div>
              <label style={{ fontSize: 14, fontWeight: 600 }}>Razlog (opciono)</label>
              <input value={withdrawReason} onChange={e => setWithdrawReason(e.target.value)} placeholder="npr. kupovina namirnica..." style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
              <button onClick={doWithdraw} disabled={submitting} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 13, fontSize: 16, color: "white" }}>
                {submitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}{submitting ? "..." : "Registruj podizanje"}
              </button>
            </div>
          ) : (
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: 20, textAlign: "center", marginBottom: 16 }}>
              <AlertCircle size={30} color="#92400e" />
              <p style={{ fontWeight: 600, margin: "10px 0 0", color: "#92400e" }}>Depozit nije registrovan</p>
              <p style={{ fontSize: 13, color: "#78350f", margin: "6px 0 0" }}>Prvo registrujte depozit u tab "Depozit (Poreska)"</p>
            </div>
          )}
          {/* Withdrawal History */}
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted }}>Istorija podizanja</h3>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            <span style={{ fontSize: 12, color: S.muted }}>—</span>
            <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
            {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
              <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(todayLocal(-p.d)); setHistDateTo(todayLocal()); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
            ))}
          </div>
          {withdrawals.filter(w => w.issue_date?.slice(0,10) >= histDateFrom && w.issue_date?.slice(0,10) <= histDateTo).length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>Nema podizanja u odabranom periodu</div>
          ) : withdrawals.filter(w => w.issue_date?.slice(0,10) >= histDateFrom && w.issue_date?.slice(0,10) <= histDateTo).map(w => (
            <div key={w.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtDateTime(w.issue_date)} · <span style={{ color: "#f59e0b" }}>-{fmt(w.deposit_amount)} €</span></div>
                <div style={{ fontSize: 12, color: S.muted }}>{w.fic ? `FIC: ${w.fic.slice(0,20)}...` : w.status}</div>
              </div>
              <button onClick={async () => {
                if (isElectron && window.electronAPI) {
                  try {
                    const token = localStorage.getItem("pos_token");
                    const r = await fetch(`${API_BASE}/cashregister/withdrawalText/${w.id}`, { headers: { "Authorization": `Bearer ${token}` } });
                    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
                  } catch(e) { showToast("Greška", "error"); }
                }
              }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ DEPOZIT TAB CONTENT (Internal Day OFF) ═══ */}
      {!internalEnabled && <>
      <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
        {[
          { id: "depozit", l: "Depozit (Poreska)", perm: ["deposit_declare", "cashregister_close"] },
          { id: "podizanje", l: "Podizanje", perm: ["deposit_withdraw"] }
        ].filter(t => t.perm.some(p => hasPermission(activeOperator, p))).map(t => (
          <button key={t.id} onClick={() => setBlagajnaTab(t.id)} style={{ padding: "8px 18px", border: `1px solid ${blagajnaTab === t.id ? S.dark : S.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", background: blagajnaTab === t.id ? S.dark : "white", color: blagajnaTab === t.id ? "white" : S.muted, fontFamily: S.font }}>{t.l}</button>
        ))}
      </div>
      {blagajnaTab === "depozit" && <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 380px", maxWidth: 520 }}>
          {/* Alert: unclosed previous session */}
          {unclosedSession && (
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: 18, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><AlertCircle size={18} color="#92400e" /><span style={{ fontWeight: 700, color: "#92400e", fontSize: 15 }}>Nezatvorena prethodna sesija!</span></div>
              <p style={{ fontSize: 14, color: "#78350f", margin: "0 0 12px" }}>Imate otvorenu sesiju od {fmtDate(unclosedSession.session?.opened_at)}. Morate je zatvoriti prije nastavka.</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
                <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, color: "#78350f" }}>Stvarna gotovina (€)</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="0.00" style={{ ...S.input, padding: "8px 12px", flex: 1 }} />
                    <button onClick={() => setNumPadFor("actual")} style={{ padding: "8px 14px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 18 }} title="Numerička tastatura">🔢</button>
                  </div>
                </div>
                {hasPermission(activeOperator, "cashregister_close") && <button onClick={() => doClose("oldest")} disabled={submitting} style={{ ...S.btn("#92400e"), height: 42 }}>{submitting ? "..." : "Zatvori staru sesiju"}</button>}
              </div>
            </div>
          )}

          {closingInfo && (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 16, fontWeight: 700 }}>Izvještaj zatvaranja</h3>
              <div style={{ background: "#f9fafb", borderRadius: 8, padding: 14 }}>
                <Row label="Gotovina" value={`${fmt(closingInfo.cash_sales)} €`} /><Row label="Kartica" value={`${fmt(closingInfo.card_sales)} €`} /><Row label="Ukupan promet" value={`${fmt(closingInfo.total_sales)} €`} /><Row label="Transakcije" value={closingInfo.transaction_count || 0} /><Row label="Očekivana got." value={`${fmt(closingInfo.expected_cash)} €`} /><Row label="Stvarna got." value={`${fmt(closingInfo.actual_cash)} €`} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px solid #e5e7eb" }}><span style={{ fontWeight: 700 }}>Razlika</span><span style={{ fontWeight: 700, color: parseFloat(closingInfo.difference || 0) === 0 ? S.green : S.red }}>{fmt(closingInfo.difference)} €</span></div>
              </div>
              <button onClick={() => setClosingInfo(null)} style={{ ...S.btnSm("#f3f4f6", S.muted), width: "100%", justifyContent: "center", marginTop: 12 }}>OK</button>
            </div>
          )}

          {!unclosedSession && !depositExists ? (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Otvori blagajnu</h3>
              <p style={{ fontSize: 14, color: S.muted, margin: "0 0 18px" }}>Registrujte depozit za danas</p>
              <label style={{ fontSize: 14, fontWeight: 600 }}>Iznos depozita (€)</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 16, marginTop: 6 }}>
                <input type="number" step="0.01" value={depositAmt} onChange={e => setDepositAmt(e.target.value)} placeholder="0.00" style={{ ...S.input, flex: 1 }} />
                <button onClick={() => setNumPadFor("deposit")} style={{ padding: "8px 14px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 18 }} title="Numerička tastatura">🔢</button>
              </div>
              {hasPermission(activeOperator, "deposit_declare") && <button onClick={doDeposit} disabled={submitting} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>{submitting ? <Loader2 size={17} className="spin" /> : <DollarSign size={17} />}{submitting ? "..." : "Registruj depozit"}</button>}
            </div>
          ) : !unclosedSession && active ? (
            <div>
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><CheckCircle size={20} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 16 }}>Blagajna otvorena</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <Stat label="Depozit" value={`${fmt(active.initial_deposit)} €`} /><Stat label="Gotovina" value={`${fmt(active.sales?.cash_sales || 0)} €`} /><Stat label="Kartica" value={`${fmt(active.sales?.card_sales || 0)} €`} />
                  <Stat label="Ukupan promet" value={`${fmt(active.sales?.total_sales || 0)} €`} /><Stat label="Računi" value={active.sales?.transaction_count || 0} />{todayWithdrawn > 0 && <Stat label="Podignuto" value={`-${fmt(todayWithdrawn)} €`} />}<Stat label="Očekivana got." value={`${fmt(active.expected_cash)} €`} />
                </div>
              </div>
              <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24 }}>
                {internalEnabled ? (
                  <div style={{ textAlign: "center", color: S.muted }}>
                    <Clock size={30} color={S.muted} />
                    <p style={{ fontWeight: 600, margin: "10px 0 4px" }}>Koristite interni dan</p>
                    <p style={{ fontSize: 13, margin: 0 }}>Zatvaranje blagajne se vrsi kroz tab "Zatvaranja (Interno)"</p>
                  </div>
                ) : (<>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>Zatvori blagajnu</h3>
                <label style={{ fontSize: 14, fontWeight: 600 }}>Stvarna gotovina (€)</label>
                <div style={{ display: "flex", gap: 6, marginBottom: 14, marginTop: 6 }}>
                  <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder={fmt(active.expected_cash)} style={{ ...S.input, flex: 1 }} />
                  <button onClick={() => setNumPadFor("actual")} style={{ padding: "8px 14px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 18 }} title="Numerička tastatura">🔢</button>
                </div>
                <label style={{ fontSize: 14, fontWeight: 600 }}>Napomena</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="opciono..." style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
                {hasPermission(activeOperator, "cashregister_close") && <button onClick={() => doClose("today")} disabled={submitting} style={{ ...S.btn(S.red), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}><XCircle size={17} />{submitting ? "..." : "Zatvori blagajnu"}</button>}
                </>)}
              </div>
            </div>
          ) : !unclosedSession ? (
            continueMode && todaySales ? (
              <div>
                <div style={{ background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}><RefreshCw size={18} color={S.accent} /><span style={{ fontWeight: 700, color: S.accent, fontSize: 16 }}>Nastavak rada</span></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <Stat label="Gotovina" value={`${fmt(todaySales.cash_sales || 0)} €`} />
                    <Stat label="Kartica" value={`${fmt(todaySales.card_sales || 0)} €`} />
                    <Stat label="Ukupan promet" value={`${fmt(todaySales.total_sales || 0)} €`} />
                    <Stat label="Računi" value={todaySales.transaction_count || 0} />
                  </div>
                </div>
                <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 24 }}>
                  {internalEnabled ? (
                    <div style={{ textAlign: "center", color: S.muted }}>
                      <Clock size={30} color={S.muted} />
                      <p style={{ fontWeight: 600, margin: "10px 0 4px" }}>Koristite interni dan</p>
                      <p style={{ fontSize: 13, margin: 0 }}>Zatvaranje se vrsi kroz tab "Zatvaranja (Interno)"</p>
                    </div>
                  ) : (<>
                  <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>Zatvori blagajnu ponovo</h3>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>Stvarna gotovina (€)</label>
                  <div style={{ display: "flex", gap: 6, marginBottom: 14, marginTop: 6 }}>
                    <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="0.00" style={{ ...S.input, flex: 1 }} />
                    <button onClick={() => setNumPadFor("actual")} style={{ padding: "8px 14px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 18 }} title="Numerička tastatura">🔢</button>
                  </div>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>Napomena</label>
                  <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="opciono..." style={{ ...S.input, marginBottom: 16, marginTop: 6 }} />
                  {hasPermission(activeOperator, "cashregister_close") && <button onClick={() => doClose("today")} disabled={submitting} style={{ ...S.btn(S.red), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}><XCircle size={17} />{submitting ? "..." : "Zatvori blagajnu"}</button>}
                  </>)}
                </div>
              </div>
            ) : (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 24, textAlign: "center" }}>
                <CheckCircle size={44} color={S.green} />
                <p style={{ fontWeight: 600, marginTop: 12, fontSize: 17, margin: "12px 0 16px" }}>Blagajna zatvorena za danas</p>
                {hasPermission(activeOperator, "cashregister_close") && !localStorage.getItem(`pos_continued_${tcr}_${todayLocal()}`) && <button onClick={async () => { const d = await api(`/cashregister/todaySales/${tcr}`); if (d.success) { setTodaySales(d.data.sales || d.data); setContinueMode(true); localStorage.setItem(`pos_continued_${tcr}_${todayLocal()}`, "1"); } else showToast("Greška pri učitavanju", "error"); }} style={{ ...S.btn(S.accent), justifyContent: "center" }}><RefreshCw size={15} />Nastavi opet</button>}
              </div>
            )
          ) : null}
        </div>

        {(hasPermission(activeOperator, "view_register_openings") || hasPermission(activeOperator, "view_register_closings") || hasPermission(activeOperator, "cashregister_close")) && <div style={{ flex: "1 1 300px" }}>
          {(() => {
            const tcrSessions = sessions.filter(s => s.tcr_code === tcr);
            const openings = tcrSessions.filter(s => s.opened_at?.slice(0,10) >= histDateFrom && s.opened_at?.slice(0,10) <= histDateTo);
            const closings = tcrSessions.filter(s => s.closed_at && s.closed_at?.slice(0,10) >= histDateFrom && s.closed_at?.slice(0,10) <= histDateTo);
            return (<div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
                <span style={{ fontSize: 12, color: S.muted }}>—</span>
                <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: 130 }} />
                {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
                  <button key={p.l} onClick={() => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - p.d); setHistDateFrom(todayLocal(-p.d)); setHistDateTo(todayLocal()); }} style={{ padding: "4px 8px", border: `1px solid ${S.border}`, borderRadius: 4, fontSize: 11, cursor: "pointer", background: "white", fontFamily: S.font, color: S.muted }}>{p.l}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
                {hasPermission(activeOperator, "view_register_openings") && <button onClick={() => setHistTab("openings")} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: histTab === "openings" ? S.dark : S.border, background: histTab === "openings" ? S.dark : "white", color: histTab === "openings" ? "white" : S.muted }}>Otvaranja</button>}
                {hasPermission(activeOperator, "view_register_closings") && <button onClick={() => setHistTab("closings")} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: histTab === "closings" ? S.dark : S.border, background: histTab === "closings" ? S.dark : "white", color: histTab === "closings" ? "white" : S.muted }}>Zatvaranja</button>}
              </div>
              {hasPermission(activeOperator, "view_register_openings") && histTab === "openings" && (openings.length === 0 ? <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}><BookOpen size={30} /><p style={{ fontSize: 14, marginTop: 8 }}>Nema otvaranja</p></div>
              : openings.slice(0, 20).map(s => (
                <div key={s.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "12px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div><div style={{ fontWeight: 600, fontSize: 14 }}>{fmtDate(s.opened_at)}</div><div style={{ fontSize: 12, color: S.muted }}>Depozit: {fmt(s.opening_cash || 0)} €</div></div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={async () => { await printRegisterReport(s.id, "opening"); showToast("Štampano!") }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
                      <button onClick={() => openPdf(`/cashregister/openingPdf/${s.id}`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={12} /></button>
                    </div>
                  </div>
                </div>
              )))}
              {hasPermission(activeOperator, "view_register_closings") && histTab === "closings" && (closings.length === 0 ? <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}><BookOpen size={30} /><p style={{ fontSize: 14, marginTop: 8 }}>Nema zatvaranja</p></div>
              : closings.slice(0, 20).map(s => (
                <div key={s.id} style={{ background: "white", borderRadius: 8, border: `1px solid ${S.border}`, padding: "12px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{fmtDate(s.closed_at)}</div>
                      <div style={{ fontSize: 12, color: S.muted }}>Promet: {fmt(s.total_sales)} € · Razlika: <span style={{ color: parseFloat(s.difference || 0) === 0 ? "#15803d" : S.red }}>{fmt(s.difference)} €</span></div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={async () => { await printRegisterReport(s.id, "closing"); showToast("Štampano!") }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>
                      <button onClick={() => openPdf(`/cashregister/closingPdf/${s.id}`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={12} /></button>
                    </div>
                  </div>
                </div>
              )))}
              {hasPermission(activeOperator, "cashregister_close") && <button onClick={() => setShowCloseAny(true)} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", marginTop: 12, padding: "10px 16px", fontSize: 14 }}><Clock size={15} /><span style={{ marginLeft: 6 }}>Zatvori blagajnu za bilo koji dan</span></button>}
            </div>);
          })()}
        </div>}
      </div>
      }</>}

      {/* Close Any Day Modal */}
      {showCloseAny && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowCloseAny(false)}>
          <div style={{ background: "white", borderRadius: 16, padding: 28, width: 420, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Zatvori blagajnu za odabrani dan</h3>
              <button onClick={() => setShowCloseAny(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
            </div>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Datum</label>
            <input type="date" value={closeAnyDate} onChange={e => setCloseAnyDate(e.target.value)} style={{ ...S.input, marginBottom: 14 }} />
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>TCR kod</label>
            <select value={closeAnyTcr} onChange={e => setCloseAnyTcr(e.target.value)} style={{ ...S.input, marginBottom: 14 }}>
              <option value={tcr}>{tcr} (trenutni)</option>
            </select>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Stvarna gotovina (€)</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
              <input type="number" step="0.01" value={closeAnyCash} onChange={e => setCloseAnyCash(e.target.value)} placeholder="0.00" style={{ ...S.input, flex: 1 }} />
              <button onClick={() => setNumPadFor("closeAny")} style={{ padding: "8px 14px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 18 }} title="Numerička tastatura">🔢</button>
            </div>
            <button disabled={submitting || !closeAnyDate} onClick={async () => {
              setSubmitting(true);
              const d = await api("/cashregister/closeAnyDate", { method: "POST", body: JSON.stringify({ tcr_code: closeAnyTcr || tcr, target_date: closeAnyDate, actual_cash: parseFloat(closeAnyCash) || 0, notes: "Zatvoreno retroaktivno" }) });
              setSubmitting(false);
              if (d.success) { showToast("Blagajna zatvorena za " + closeAnyDate + "!"); setShowCloseAny(false); setCloseAnyDate(""); setCloseAnyCash(""); load(); } else showToast(d.message, "error");
            }} style={{ ...S.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 13, fontSize: 16, color: "white" }}>{submitting ? "..." : "Zatvori"}</button>
          </div>
        </div>
      )}

      {/* NumPad modal */}
      {numPadFor && <NumPad
        value={numPadFor === "deposit" ? depositAmt : numPadFor === "actual" ? actualCash : numPadFor === "withdraw" ? withdrawAmt : numPadFor === "closeAny" ? closeAnyCash : ""}
        title={numPadFor === "deposit" ? "Iznos depozita (€)" : numPadFor === "actual" ? "Stvarno stanje gotovine (€)" : numPadFor === "withdraw" ? "Iznos podizanja (€)" : numPadFor === "closeAny" ? "Stvarna gotovina (€)" : "Unesite iznos"}
        onChange={v => {
          if (numPadFor === "deposit") setDepositAmt(v);
          else if (numPadFor === "actual") setActualCash(v);
          else if (numPadFor === "withdraw") setWithdrawAmt(v);
          else if (numPadFor === "closeAny") setCloseAnyCash(v);
        }}
        onClose={() => setNumPadFor(null)}
      />}
    </div>
  );
}
// ── Presjek stanja (Z-style snapshot report) on-screen renderer ─────
function PresjekRender({ data, vatByRate }) {
  if (!data) return null;
  const f = (n) => Number(n || 0).toFixed(2);
  const cf = data.cash_flow || {};
  const tc = data.turnover_cash || {};
  const tn = data.turnover_noncash || {};
  const tt = data.turnover_total || {};
  const it = data.invoice_types || {};
  const p = data.payments || {};

  const sectionStyle = { background: "#f9fafb", borderRadius: 8, padding: 14, marginBottom: 12, border: "1px solid #e5e7eb" };
  const titleStyle = { fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 8, textAlign: "center", paddingBottom: 6, borderBottom: "1px dashed #d1d5db" };
  const lineStyle = { display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 13 };
  const Line = ({ label, value, bold }) => (
    <div style={{ ...lineStyle, fontWeight: bold ? 700 : 400 }}>
      <span style={{ color: bold ? "#111" : "#374151" }}>{label}</span>
      <span style={{ color: bold ? "#059669" : "#111", fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );

  const rates = [21, 7, 0, 15];
  const byRate = {};
  vatByRate.forEach(vr => { byRate[Math.round(Number(vr.vat_rate))] = vr; });

  let totalNet = 0, totalVat = 0, totalGross = 0;
  vatByRate.forEach(vr => {
    totalNet += Number(vr.net_base || 0);
    totalVat += Number(vr.vat_total || 0);
    totalGross += Number(vr.gross_total || 0);
  });

  return (
    <div>
      {/* Cash flow */}
      <div style={sectionStyle}>
        <Line label="Gotovina u ENU:" value={f(cf.cash_in_enu)} bold />
        <Line label="Depozit:" value={f(cf.deposit)} />
        <Line label="Podignuta gotovina:" value={f(cf.withdraw)} />
        <Line label="Naplaćeno novčanice:" value={f(cf.collected_banknote)} />
        <Line label="Naplaćeno got. ostalo:" value={f(cf.collected_other_cash)} />
        <Line label="Naplaćeno bezgotovina:" value={f(cf.collected_noncash)} />
        <Line label="Naplaćeno ukupno:" value={f(cf.collected_total)} bold />
      </div>

      {/* Gotovinski promet */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Gotovinski promet</div>
        <Line label="Cijena:" value={f(tc.price)} />
        <Line label="Popust:" value={f(tc.discount)} />
        <Line label="Za uplatu:" value={f(tc.to_pay)} bold />
      </div>

      {/* Bezgotovinski promet */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Bezgotovinski promet</div>
        <Line label="Cijena:" value={f(tn.price)} />
        <Line label="Popust:" value={f(tn.discount)} />
        <Line label="Za uplatu:" value={f(tn.to_pay)} bold />
      </div>

      {/* Ukupan promet */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Ukupan promet</div>
        <Line label="Cijena:" value={f(tt.price)} />
        <Line label="Popust:" value={f(tt.discount)} />
        <Line label="Za uplatu:" value={f(tt.to_pay)} bold />
      </div>

      {/* Invoice type blocks */}
      {[
        { key: "storno", title: "Korektivni računi" },
        { key: "avans", title: "Avansni računi" },
        { key: "offline", title: "Offline računi" },
        { key: "order", title: "Order računi" },
      ].map(b => {
        const blk = it[b.key] || { count: 0, revenue: 0, tax: 0 };
        return (
          <div key={b.key} style={sectionStyle}>
            <div style={titleStyle}>{b.title}</div>
            <Line label="Broj:" value={String(blk.count)} />
            <Line label="Promet:" value={f(blk.revenue)} />
            <Line label="Porez:" value={f(blk.tax)} />
          </div>
        );
      })}

      {/* Per-rate VAT blocks */}
      {rates.map(r => {
        const vr = byRate[r] || { net_base: 0, vat_total: 0, gross_total: 0 };
        return (
          <div key={r} style={sectionStyle}>
            <div style={titleStyle}>Poreska stopa - {r.toFixed(2)}%</div>
            <Line label="Osnovica:" value={f(vr.net_base)} />
            <Line label="Iznos poreza:" value={f(vr.vat_total)} />
            <Line label="Promet:" value={f(vr.gross_total)} />
          </div>
        );
      })}

      {/* Ukupan porez */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Ukupan porez</div>
        <Line label="Osnovica:" value={f(totalNet)} />
        <Line label="Iznos poreza:" value={f(totalVat)} />
        <Line label="Promet:" value={f(totalGross)} bold />
      </div>

      {/* Plaćanja */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Plaćanja</div>
        <Line label="Novčanice i kovanice:" value={f(p.banknote)} />
        <Line label="Kreditna kartica:" value={f(p.creditcard)} />
        <Line label="Order:" value={f(p.order)} />
        <Line label="Ostalo (gotovina):" value={f(p.cash_other)} />
        <Line label="Virman:" value={f(p.virman)} />
        <Line label="Biznis kred. kartica:" value={f(p.business_card)} />
        <Line label="Kartica prodavca:" value={f(p.vendor_card)} />
        <Line label="Order:" value={f(p.order_advance)} />
        <Line label="Faktoring:" value={f(p.factoring)} />
        <Line label="Avans:" value={f(p.avans)} />
        <Line label="Vaučer:" value={f(p.voucher)} />
        <Line label="Bakšiš:" value={f(p.tip)} />
      </div>
    </div>
  );
}

function InspectorSection({ tcr, showToast, inspReport, setInspReport, inspType, setInspType, inspFrom, setInspFrom, inspTo, setInspTo, inspLoading, setInspLoading, ikofPib, setIkofPib, ikofDate, setIkofDate, ikofInvNum, setIkofInvNum, ikofBu, setIkofBu, ikofTcr, setIkofTcr, ikofSoft, setIkofSoft, ikofAmount, setIkofAmount, ikofResult, setIkofResult }) {

  // Operator report state — local to this section. Loaded once on mount.
  // selectedOperator: the operator_code being reported on (NOT the operator running the report).
  const [operatorList, setOperatorList] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState("");
  useEffect(() => {
    api("/operators/list").then(r => {
      if (r.success) setOperatorList(r.data || []);
    });
  }, []);

  const fetchInsp = async () => {
    setInspLoading(true);
    const tcrParam = tcr ? `&tcr_code=${tcr}` : "";
    let url;
    if (inspType === "operator") {
      // Per-operator report. Requires selectedOperator. Backend returns the same
      // payload shape as the journal type, so the existing renderer below works.
      if (!selectedOperator) {
        setInspLoading(false);
        showToast("Odaberite operatera", "error");
        return;
      }
      url = `/invoices/operatorReport?operator_code=${encodeURIComponent(selectedOperator)}&date_from=${inspFrom}&date_to=${inspTo}${tcrParam}`;
    } else {
      url = `/invoices/inspectorReport?type=${inspType}&date_from=${inspFrom}&date_to=${inspTo}${tcrParam}`;
    }
    const d = await api(url);
    setInspLoading(false);
    if (d.success) setInspReport(d.data);
    else showToast(d.message || "Greška", "error");
  };

  const recreateIkof = async () => {
    const d = await api("/invoices/recreateIkof", { method: "POST", body: JSON.stringify({ pib: ikofPib, date: ikofDate, inv_num: ikofInvNum, bu_code: ikofBu, tcr_code: ikofTcr, soft_code: ikofSoft, total: ikofAmount }) });
    if (d.success) setIkofResult(d.data);
    else showToast(d.message || "Greška", "error");
  };

  const printInspReport = async () => {
    if (!inspReport || !isElectron || !window.electronAPI) return;
    const token = localStorage.getItem("pos_token");
    const tcrParam = tcr ? `&tcr_code=${tcr}` : "";
    let url;
    if (inspType === "operator") {
      if (!selectedOperator) return;
      url = `${API_BASE}/invoices/operatorReportText?operator_code=${encodeURIComponent(selectedOperator)}&date_from=${inspFrom}&date_to=${inspTo}${tcrParam}`;
    } else {
      url = `${API_BASE}/invoices/inspectorReportText?type=${inspType}&date_from=${inspFrom}&date_to=${inspTo}${tcrParam}`;
    }
    const r = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
    if (r.ok) { const text = await r.text(); await window.electronAPI.printText(text); showToast("Štampano!"); }
  };

  const exportCsv = () => {
    if (!inspReport) return;
    const q = v => `"${String(v || "").replace(/"/g, '""')}"`;
    let csv = "";
    // Company header block
    if (inspReport.company_info) {
      const ci = inspReport.company_info;
      csv += "Naziv;" + q(ci.business_name) + "\n";
      csv += "Adresa;" + q((ci.address || "") + (ci.city ? ", " + ci.city : "")) + "\n";
      csv += "PIB;" + q(ci.pib) + "\n";
    }
    csv += "Izvjestaj;" + q(inspReport.title) + "\n";
    csv += "TCR;" + q(inspReport.tcr_code) + "\n";
    if (inspReport.business_unit_codes && inspReport.business_unit_codes.length > 0) {
      csv += "Poslovna jedinica;" + q(inspReport.business_unit_codes.join(", ")) + "\n";
    }
    if (inspReport.company_info?.operator_code) {
      csv += "Operater izvjestaja;" + q(inspReport.company_info.operator_code) + "\n";
    }
    csv += "Period;" + q(inspReport.period) + "\n";
    if (inspReport.company_info?.generated_at) {
      csv += "Stampano;" + q(inspReport.company_info.generated_at) + "\n";
    }
    csv += "\n";

    if (inspReport.presjek) {
      const pj = inspReport.presjek;
      const cf = pj.cash_flow || {};
      csv += "Gotovina u ENU;" + fmt(cf.cash_in_enu || 0) + "\n";
      csv += "Depozit;" + fmt(cf.deposit || 0) + "\n";
      csv += "Podignuta gotovina;" + fmt(cf.withdraw || 0) + "\n";
      csv += "Naplaćeno novčanice;" + fmt(cf.collected_banknote || 0) + "\n";
      csv += "Naplaćeno got. ostalo;" + fmt(cf.collected_other_cash || 0) + "\n";
      csv += "Naplaćeno bezgotovina;" + fmt(cf.collected_noncash || 0) + "\n";
      csv += "Naplaćeno ukupno;" + fmt(cf.collected_total || 0) + "\n\n";

      const tc = pj.turnover_cash || {}, tn = pj.turnover_noncash || {}, tt = pj.turnover_total || {};
      csv += "GOTOVINSKI PROMET\n";
      csv += "Cijena;" + fmt(tc.price || 0) + "\nPopust;" + fmt(tc.discount || 0) + "\nZa uplatu;" + fmt(tc.to_pay || 0) + "\n\n";
      csv += "BEZGOTOVINSKI PROMET\n";
      csv += "Cijena;" + fmt(tn.price || 0) + "\nPopust;" + fmt(tn.discount || 0) + "\nZa uplatu;" + fmt(tn.to_pay || 0) + "\n\n";
      csv += "UKUPAN PROMET\n";
      csv += "Cijena;" + fmt(tt.price || 0) + "\nPopust;" + fmt(tt.discount || 0) + "\nZa uplatu;" + fmt(tt.to_pay || 0) + "\n\n";

      const it = pj.invoice_types || {};
      [['storno','Korektivni računi'],['avans','Avansni računi'],['offline','Offline računi'],['order','Order računi']].forEach(([k,t]) => {
        const blk = it[k] || { count: 0, revenue: 0, tax: 0 };
        csv += t.toUpperCase() + "\n";
        csv += "Broj;" + (blk.count || 0) + "\nPromet;" + fmt(blk.revenue || 0) + "\nPorez;" + fmt(blk.tax || 0) + "\n\n";
      });

      const p = pj.payments || {};
      csv += "PLAĆANJA\n";
      csv += "Novčanice i kovanice;" + fmt(p.banknote || 0) + "\n";
      csv += "Kreditna kartica;" + fmt(p.creditcard || 0) + "\n";
      csv += "Order;" + fmt(p.order || 0) + "\n";
      csv += "Ostalo (gotovina);" + fmt(p.cash_other || 0) + "\n";
      csv += "Virman;" + fmt(p.virman || 0) + "\n";
      csv += "Biznis kred. kartica;" + fmt(p.business_card || 0) + "\n";
      csv += "Kartica prodavca;" + fmt(p.vendor_card || 0) + "\n";
      csv += "Order (advance);" + fmt(p.order_advance || 0) + "\n";
      csv += "Faktoring;" + fmt(p.factoring || 0) + "\n";
      csv += "Avans;" + fmt(p.avans || 0) + "\n";
      csv += "Vaučer;" + fmt(p.voucher || 0) + "\n";
      csv += "Bakšiš;" + fmt(p.tip || 0) + "\n\n";
    }

    if (inspReport.summary && !inspReport.presjek) {
      const s = inspReport.summary;
      csv += "Ukupno računa;" + (s.total_invoices || 0) + "\n";
      csv += "Gotovina;" + fmt(s.cash_revenue || 0) + "\n";
      csv += "Kartica;" + fmt(s.card_revenue || 0) + "\n";
      if (parseFloat(s.other_revenue || 0) > 0) csv += "Ostalo;" + fmt(s.other_revenue) + "\n";
      csv += "Bez PDV;" + fmt(s.total_without_vat || 0) + "\n";
      csv += "PDV;" + fmt(s.total_vat || 0) + "\n";
      csv += "UKUPNO;" + fmt(s.total_revenue || 0) + "\n\n";
    }

    if (inspReport.vat_by_rate && inspReport.vat_by_rate.length > 0) {
      csv += "PDV PO STOPAMA\n";
      csv += "Stopa;Br. stavki;Osnovica;PDV iznos;Bruto\n";
      inspReport.vat_by_rate.forEach(vr => {
        csv += (vr.vat_rate || 0) + "%;" + (vr.item_count || 0) + ";" + fmt(vr.net_base || 0) + ";" + fmt(vr.vat_total || 0) + ";" + fmt(vr.gross_total || 0) + "\n";
      });
      csv += "\n";
    }

    if (inspReport.invoices?.length > 0) {
      const hasItems = inspReport.invoices.some(inv => inv.items?.length > 0);
      if (hasItems) {
        csv += "Broj računa;Datum;Iznos;Način plaćanja;FIC;Operater;PJ;TCR;Storno;Stavka;Kol.;Cijena;Ukupno;PDV%;PDV iznos\n";
        inspReport.invoices.forEach(inv => {
          csv += q(inv.invoice_number) + ";" + q(inv.invoice_date) + ";" + fmt(inv.total_amount || 0) + ";" + q(inv.payment_method) + ";" + q(inv.fic) + ";" + q(inv.operator_code) + ";" + q(inv.business_unit_code) + ";" + q(inv.tcr_code) + ";" + (inv.is_storno ? "DA" : "") + ";;;;;;\n";
          if (inv.items) inv.items.forEach(item => {
            csv += ";;;;;;;;;" + q(item.name) + ";" + item.quantity + ";" + fmt(item.unit_price || 0) + ";" + fmt(item.total_price || 0) + ";" + (item.vat_rate || 0) + "%;" + fmt(item.vat_amount || 0) + "\n";
          });
        });
      } else {
        csv += "Broj računa;Datum;Iznos;Način plaćanja;FIC;Operater;PJ;TCR;Storno\n";
        inspReport.invoices.forEach(inv => {
          csv += q(inv.invoice_number) + ";" + q(inv.invoice_date) + ";" + fmt(inv.total_amount || 0) + ";" + q(inv.payment_method) + ";" + q(inv.fic) + ";" + q(inv.operator_code) + ";" + q(inv.business_unit_code) + ";" + q(inv.tcr_code) + ";" + (inv.is_storno ? "DA" : "") + "\n";
        });
      }
    }
    if (inspReport.daily_subtotals?.length > 0) {
      csv += "\nDatum;Broj računa;Promet\n";
      inspReport.daily_subtotals.forEach(ds => { csv += q(ds.day) + ";" + ds.count + ";" + fmt(ds.revenue) + "\n"; });
    }
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `inspekcija_${inspType}_${inspFrom}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: "#92400e" }}>
        Ovi izvještaji su namijenjeni za poresku inspekciju u skladu sa Zakonom o fiskalizaciji. Svi podaci uključuju oznaku ENU (TCR) uređaja.
      </div>

      <div style={{ display: "flex", gap: 5, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          { id: "presjek", l: "Presjek stanja" },
          { id: "status", l: "Trenutno stanje" },
          { id: "daily", l: "Dnevni fiskalni" },
          { id: "periodic", l: "Periodični" },
          { id: "journal", l: "Elektronski žurnal" },
          { id: "offline", l: "Offline žurnal" },
          { id: "corrective", l: "Korektivni žurnal" },
          { id: "operator", l: "📊 Po operateru" },
          { id: "ikof", l: "IKOF rekreacija" },
        ].map(t => (
          <button key={t.id} onClick={() => { setInspType(t.id); setInspReport(null); setIkofResult(null); }} style={{ padding: "6px 12px", border: `1px solid ${inspType === t.id ? "#92400e" : S.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: inspType === t.id ? "#fef3c7" : "white", color: inspType === t.id ? "#92400e" : S.muted }}>{t.l}</button>
        ))}
      </div>

      {inspType === "ikof" ? (
        <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 14px" }}>IKOF rekreacija</h3>
          <p style={{ fontSize: 13, color: S.muted, margin: "0 0 14px" }}>Unesite parametre računa za provjeru IKOF-a</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>PIB</label><input value={ikofPib} onChange={e => setIkofPib(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="PIB poreskog obveznika" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Datum i vrijeme</label><input value={ikofDate} onChange={e => setIkofDate(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="2026-03-14T10:30:00+01:00" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Broj računa</label><input value={ikofInvNum} onChange={e => setIkofInvNum(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="1/2026/ne548er229" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Kod poslovne jedinice</label><input value={ikofBu} onChange={e => setIkofBu(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="xx123xx456" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Kod ENU (TCR)</label><input value={ikofTcr} onChange={e => setIkofTcr(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="ne548er229" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Kod softvera</label><input value={ikofSoft} onChange={e => setIkofSoft(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="ss123ss456" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Ukupan iznos</label><input value={ikofAmount} onChange={e => setIkofAmount(e.target.value)} style={{ ...S.input, marginTop: 4 }} placeholder="43.56" /></div>
          </div>
          <button onClick={recreateIkof} style={{ ...S.btn(S.dark), marginTop: 14, justifyContent: "center", padding: "10px 24px" }}>Generiši IKOF</button>
          {ikofResult && (
            <div style={{ marginTop: 14, background: "#f0fdf4", borderRadius: 8, padding: 14, border: "1px solid #bbf7d0" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d", marginBottom: 6 }}>Generisani IKOF:</div>
              <div style={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all", color: S.dark }}>{ikofResult.iic}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d", marginTop: 10, marginBottom: 4 }}>IIC Potpis:</div>
              <div style={{ fontFamily: "monospace", fontSize: 10, wordBreak: "break-all", color: S.muted }}>{ikofResult.iic_signature}</div>
            </div>
          )}
        </div>
      ) : (
        <>
          {inspType === "operator" && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Operater</label>
              <select value={selectedOperator} onChange={e => { setSelectedOperator(e.target.value); setInspReport(null); }} style={{ ...S.input, padding: "8px 10px", fontSize: 13, width: 320, maxWidth: "100%" }}>
                <option value="">— Odaberite operatera —</option>
                {operatorList.map(op => (
                  <option key={op.id} value={op.operator_code}>
                    {op.operator_code}{op.full_name ? ` — ${op.full_name}` : ""}
                  </option>
                ))}
              </select>
              {operatorList.length === 0 && <div style={{ fontSize: 12, color: S.muted, marginTop: 4 }}>Učitavanje operatera...</div>}
            </div>
          )}
          {inspType !== "status" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
              <input type="date" value={inspFrom} onChange={e => setInspFrom(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
              {inspType !== "daily" && <>
                <span style={{ color: S.muted }}>—</span>
                <input type="date" value={inspTo} onChange={e => setInspTo(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
              </>}
            </div>
          )}
          <button onClick={fetchInsp} disabled={inspLoading} style={{ ...S.btn("#92400e"), marginBottom: 16, justifyContent: "center", padding: "10px 20px", color: "white" }}>
            {inspLoading ? <Loader2 size={15} className="spin" /> : <FileText size={15} />}
            <span style={{ marginLeft: 6 }}>{inspLoading ? "Učitavanje..." : "Generiši izvještaj"}</span>
          </button>

          {inspReport && (
            <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                  {inspReport.title}
                  {inspReport.reported_operator_code && (
                    <span style={{ marginLeft: 10, fontSize: 13, background: "#dbeafe", color: "#1e40af", padding: "3px 10px", borderRadius: 12, fontWeight: 600 }}>
                      {inspReport.reported_operator_code}{inspReport.reported_operator_name ? ` — ${inspReport.reported_operator_name}` : ""}
                    </span>
                  )}
                </h3>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={printInspReport} style={S.btnSm("#f3f4f6", S.muted)} title="Štampaj"><Printer size={14} /></button>
                  <button onClick={exportCsv} style={S.btnSm("#f3f4f6", S.muted)} title="Export CSV"><Download size={14} /></button>
                </div>
              </div>

              {inspReport.company_info && (
                <div style={{ background: "#fef3c7", borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, border: "1px solid #fde68a" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 4 }}>{inspReport.company_info.business_name}</div>
                  {inspReport.company_info.address && <div style={{ color: "#78350f" }}>{inspReport.company_info.address}{inspReport.company_info.city ? `, ${inspReport.company_info.city}` : ""}</div>}
                  {inspReport.company_info.pib && <div style={{ color: "#78350f" }}>PIB: {inspReport.company_info.pib}</div>}
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #fde68a", color: "#78350f" }}>
                    <span>TCR: <strong>{inspReport.tcr_code}</strong></span>
                    {inspReport.business_unit_codes && inspReport.business_unit_codes.length > 0 && <span style={{ marginLeft: 12 }}>PJ: <strong>{inspReport.business_unit_codes.join(", ")}</strong></span>}
                    {inspReport.company_info.operator_code && <span style={{ marginLeft: 12 }}>Operater: <strong>{inspReport.company_info.operator_code}</strong></span>}
                  </div>
                  <div style={{ color: "#78350f", marginTop: 4 }}>Period: {inspReport.period} · Štampano: {inspReport.company_info.generated_at}</div>
                </div>
              )}

              {!inspReport.company_info && (
                <div style={{ fontSize: 12, color: S.muted, marginBottom: 12 }}>TCR: {inspReport.tcr_code} · Period: {inspReport.period}</div>
              )}

              {inspReport.summary && !inspReport.presjek && (
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <Row label="Ukupno računa" value={inspReport.summary.total_invoices || 0} />
                  <Row label="Gotovina" value={`${fmt(inspReport.summary.cash_revenue)} €`} />
                  <Row label="Kartica" value={`${fmt(inspReport.summary.card_revenue)} €`} />
                  {parseFloat(inspReport.summary.other_revenue || 0) > 0 && <Row label="Ostalo" value={`${fmt(inspReport.summary.other_revenue)} €`} />}
                  {parseInt(inspReport.summary.storno_count || 0) > 0 && (
                    <div style={{ borderTop: "1px dashed #d1d5db", marginTop: 8, paddingTop: 8, color: S.red }}>
                      <Row label="Storno (br.)" value={inspReport.summary.storno_count} />
                      <Row label="Storno (iznos)" value={`${fmt(inspReport.summary.storno_amount)} €`} />
                    </div>
                  )}

                  {inspReport.vat_by_rate && inspReport.vat_by_rate.length > 0 && (
                    <div style={{ borderTop: "1px dashed #d1d5db", marginTop: 8, paddingTop: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: S.muted, marginBottom: 6 }}>PDV PO STOPAMA</div>
                      {inspReport.vat_by_rate.map((vr, idx) => (
                        <div key={idx} style={{ marginBottom: 4 }}>
                          <Row label={`${vr.vat_rate}% osnovica`} value={`${fmt(vr.net_base)} €`} />
                          <Row label={`${vr.vat_rate}% PDV`} value={`${fmt(vr.vat_total)} €`} />
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ borderTop: "1px dashed #d1d5db", marginTop: 8, paddingTop: 8 }}>
                    <Row label="Bez PDV" value={`${fmt(inspReport.summary.total_without_vat)} €`} />
                    <Row label="PDV" value={`${fmt(inspReport.summary.total_vat)} €`} />
                  </div>
                  <div style={{ borderTop: "2px solid #111", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 800 }}>UKUPNO</span>
                    <span style={{ fontWeight: 800, color: S.green }}>{fmt(inspReport.summary.total_revenue)} €</span>
                  </div>
                </div>
              )}

              {inspReport.presjek && <PresjekRender data={inspReport.presjek} vatByRate={inspReport.vat_by_rate || []} />}

              {inspReport.invoices?.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 8px", color: S.muted }}>Stavke ({inspReport.invoices.length})</h4>
                  <div style={{ maxHeight: 600, overflow: "auto", border: `1px solid ${S.border}`, borderRadius: 8 }}>
                    {inspReport.invoices.map((inv, idx) => (
                      <div key={idx} style={{ padding: "8px 12px", borderBottom: `1px solid ${S.border}`, fontSize: 12, background: idx % 2 === 0 ? "#f9fafb" : "white" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontWeight: 600 }}>{inv.invoice_number}{inv.is_storno ? <span style={{ marginLeft: 6, color: "#dc2626", fontWeight: 700 }}>STORNO</span> : null}</span>
                          <span style={{ fontWeight: 700 }}>{fmt(inv.total_amount)} €</span>
                        </div>
                        <div style={{ color: S.muted, fontSize: 11 }}>
                          {inv.invoice_date} · {inv.payment_method} · FIC: {inv.fic?.slice(0, 16) || "—"}{inv.fic ? "..." : ""}
                        </div>
                        {(inv.operator_code || inv.business_unit_code || inv.tcr_code) && (
                          <div style={{ color: S.muted, fontSize: 11 }}>
                            {inv.operator_code && <span>OP: <strong>{inv.operator_code}</strong></span>}
                            {inv.business_unit_code && <span style={{ marginLeft: 8 }}>PJ: <strong>{inv.business_unit_code}</strong></span>}
                            {inv.tcr_code && !tcr && <span style={{ marginLeft: 8 }}>TCR: <strong>{inv.tcr_code}</strong></span>}
                          </div>
                        )}
                        {inv.items && inv.items.map((item, j) => (
                          <div key={j} style={{ paddingLeft: 12, fontSize: 11, color: "#6b7280" }}>
                            {item.quantity}x {item.name} @ {fmt(item.unit_price)} = {fmt(item.total_price)} €{item.vat_rate > 0 ? ` (${item.vat_rate}% PDV)` : ""}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {inspReport.daily_subtotals?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 8px", color: S.muted }}>Dnevni podzbirovi</h4>
                  {inspReport.daily_subtotals.map((ds, idx) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: idx % 2 === 0 ? "#f9fafb" : "white", borderRadius: 4, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{ds.day}</span>
                      <span>{ds.count} rač. · {fmt(ds.revenue)} €</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DashboardView({ user, showToast }) {
  const [stats, setStats] = useState(null); const [monthStats, setMonthStats] = useState(null); const [yearStats, setYearStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAmounts, setShowAmounts] = useState(false);
  const [reportTab, setReportTab] = useState("pregled");
  const [internalEnabled, setInternalEnabled] = useState(false);
  const [internalHistory, setInternalHistory] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const tcr = getTcr();

  // Detailed report state
  const [detailedType, setDetailedType] = useState("daily"); // daily, monthly, yearly
  const [detailedDate, setDetailedDate] = useState(todayLocal());
  const [detailedFrom, setDetailedFrom] = useState(todayLocal());
  const [detailedTo, setDetailedTo] = useState(todayLocal());
  const [detailedMonth, setDetailedMonth] = useState(todayLocal().slice(0, 7));
  const [detailedYear, setDetailedYear] = useState(new Date().getFullYear().toString());
  const [detailedRange, setDetailedRange] = useState(false); // single day vs range
  const [detailedData, setDetailedData] = useState(null);
  const [detailedLoading, setDetailedLoading] = useState(false);

  // Inspector report state
  const [inspReport, setInspReport] = useState(null);
  const [inspType, setInspType] = useState("presjek");
  const [inspFrom, setInspFrom] = useState(todayLocal());
  const [inspTo, setInspTo] = useState(todayLocal());
  const [inspLoading, setInspLoading] = useState(false);
  const [ikofPib, setIkofPib] = useState("");
  const [ikofDate, setIkofDate] = useState("");
  const [ikofInvNum, setIkofInvNum] = useState("");
  const [ikofBu, setIkofBu] = useState("");
  const [ikofTcr, setIkofTcr] = useState(tcr || "");
  const [ikofSoft, setIkofSoft] = useState("");
  const [ikofAmount, setIkofAmount] = useState("");
  const [ikofResult, setIkofResult] = useState(null);

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    const tcrParam = tcr ? `&tcr_code=${tcr}` : '';
    const [t, m, y, idSet, idHist, invs] = await Promise.all([
      api(`/invoices/stats?period=today${tcrParam}`),
      api(`/invoices/stats?period=month${tcrParam}`),
      api(`/invoices/stats?period=year${tcrParam}`),
      api("/internalday/settings"),
      api(`/internalday/history/${tcr || "all"}`),
      api(`/invoices?per_page=10${tcr ? "&tcr_code=" + tcr : ""}`)
    ]);
    if (t.success) setStats(t.data.stats);
    if (m.success) setMonthStats(m.data.stats);
    if (y.success) setYearStats(y.data.stats);
    if (idSet.success) setInternalEnabled(!!idSet.data?.internal_day_enabled);
    if (idHist.success) setInternalHistory(idHist.data?.days || []);
    if (invs.success) setRecentInvoices(invs.data?.invoices || []);
    setLoading(false);
  };

  const fetchDetailed = async () => {
    setDetailedLoading(true);
    const tcrParam = tcr ? `&tcr_code=${tcr}` : '';
    let url = `/invoices/detailedReport?type=${detailedType}${tcrParam}`;
    if (detailedType === "daily") {
      if (detailedRange) url += `&date_from=${detailedFrom}&date_to=${detailedTo}`;
      else url += `&date=${detailedDate}`;
    } else if (detailedType === "monthly") {
      url += `&month=${detailedMonth}`;
    } else {
      url += `&year=${detailedYear}`;
    }
    const d = await api(url);
    setDetailedLoading(false);
    if (d.success) setDetailedData(d.data);
    else showToast(d.message || "Greška", "error");
  };

  const printDetailedReport = async () => {
    if (!detailedData || !isElectron || !window.electronAPI) return;
    const s = detailedData.summary;
    const w = 48;
    const lr = (l, r) => l + " ".repeat(Math.max(1, w - l.length - r.length)) + r;
    const f = n => Number(n || 0).toFixed(2).replace(".", ",");
    let out = "\x18\x1B\x61\x00\x1B\x45\x00\x1D\x21\x00\x1B\x74\x00\x1B\x4D\x00";
    out += "\x1B\x61\x01\x1B\x45\x01";
    const typeLabels = { daily: "DNEVNI IZVJESTAJ", monthly: "MJESECNI IZVJESTAJ", yearly: "GODISNJI IZVJESTAJ" };
    out += (typeLabels[detailedData.type] || "IZVJESTAJ") + "\n";
    out += "\x1B\x45\x00";
    out += detailedData.label + "\n";
    out += "=".repeat(w) + "\n\x1B\x61\x00";
    out += lr("Racuni:", (s.total_invoices || 0) + "") + "\n";
    out += lr("Gotovina:", f(s.cash_revenue) + " EUR") + "\n";
    out += lr("Kartica:", f(s.card_revenue) + " EUR") + "\n";
    if (parseFloat(s.other_revenue || 0) > 0) out += lr("Ostalo:", f(s.other_revenue) + " EUR") + "\n";
    out += "-".repeat(w) + "\n";
    out += lr("Bez PDV:", f(s.total_without_vat) + " EUR") + "\n";
    out += lr("PDV:", f(s.total_vat) + " EUR") + "\n";
    out += "=".repeat(w) + "\n";
    out += "\x1B\x45\x01\x1D\x21\x11";
    out += lr("UKUPNO:", f(s.total_revenue) + " EUR") + "\n";
    out += "\x1D\x21\x00\x1B\x45\x00";
    out += "=".repeat(w) + "\n";
    if (detailedData.storno && parseInt(detailedData.storno.cnt) > 0) {
      out += lr("Stornirano:", detailedData.storno.cnt + " (" + f(detailedData.storno.amount) + " EUR)") + "\n";
    }
    // Breakdown
    const items = detailedData.days || detailedData.months || [];
    if (items.length > 1) {
      out += "\n" + "-".repeat(w) + "\n";
      out += "\x1B\x45\x01" + "DETALJI\n" + "\x1B\x45\x00";
      items.forEach(item => {
        const key = item.day || item.month;
        out += lr(key + " (" + item.invoices + ")", f(item.revenue) + " EUR") + "\n";
        out += "  Got: " + f(item.cash) + "  Kart: " + f(item.card) + "\n";
      });
    }
    out += "\n\x1B\x61\x01\nPowered by EFIS.PRO\n\n\x1D\x56\x41\x03";
    await window.electronAPI.printText(out);
    showToast("Štampano!");
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  return (
    <div style={{ height: "100%", overflow: "auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Izvještaji</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowAmounts(!showAmounts)} style={{ ...S.btn(showAmounts ? S.dark : "#e5e7eb"), padding: "8px 14px", fontSize: 13, color: showAmounts ? "white" : S.muted }}>
            <Eye size={15} /><span style={{ marginLeft: 6 }}>{showAmounts ? "Sakrij" : "Prikaži"}</span>
          </button>
          <button onClick={loadReports} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} /></button>
        </div>
      </div>

      {/* Report Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[{ id: "pregled", l: "Pregled" }, { id: "detaljni", l: "Detaljni izvještaji" }, ...(internalEnabled ? [{ id: "interni", l: "Interni dani" }] : []), { id: "inspekcija", l: "Poreski inspektor" }].map(t => (
          <button key={t.id} onClick={() => setReportTab(t.id)} style={{ padding: "7px 16px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: reportTab === t.id ? S.dark : S.border, background: reportTab === t.id ? S.dark : "white", color: reportTab === t.id ? "white" : S.muted }}>{t.l}</button>
        ))}
      </div>

      {/* PREGLED TAB — Overview */}
      {reportTab === "pregled" && <>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Danas</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
          <StatCard icon={Receipt} label="Računi" value={stats?.total_invoices || 0} color={S.accent} />
          <StatCard icon={Euro} label="Promet" value={`${fmt(stats?.total_revenue || 0)} €`} color={S.green} hidden={!showAmounts} />
          <StatCard icon={Banknote} label="Gotovina" value={`${fmt(stats?.cash_revenue || 0)} €`} color="#ea580c" hidden={!showAmounts} />
          <StatCard icon={CreditCard} label="Kartica" value={`${fmt(stats?.card_revenue || 0)} €`} color="#7c3aed" hidden={!showAmounts} />
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Ovaj mjesec</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
          <StatCard icon={Receipt} label="Računi" value={monthStats?.total_invoices || 0} color={S.accent} />
          <StatCard icon={TrendingUp} label="Promet" value={`${fmt(monthStats?.total_revenue || 0)} €`} color={S.green} hidden={!showAmounts} />
          <StatCard icon={Banknote} label="Gotovina" value={`${fmt(monthStats?.cash_revenue || 0)} €`} color="#ea580c" hidden={!showAmounts} />
          <StatCard icon={CreditCard} label="Kartica" value={`${fmt(monthStats?.card_revenue || 0)} €`} color="#7c3aed" hidden={!showAmounts} />
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Ova godina</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <StatCard icon={Receipt} label="Računi" value={yearStats?.total_invoices || 0} color={S.accent} />
          <StatCard icon={TrendingUp} label="Promet" value={`${fmt(yearStats?.total_revenue || 0)} €`} color={S.green} hidden={!showAmounts} />
          <StatCard icon={Banknote} label="Gotovina" value={`${fmt(yearStats?.cash_revenue || 0)} €`} color="#ea580c" hidden={!showAmounts} />
          <StatCard icon={CreditCard} label="Kartica" value={`${fmt(yearStats?.card_revenue || 0)} €`} color="#7c3aed" hidden={!showAmounts} />
        </div>
      </>}

      {/* DETALJNI TAB — Detailed reports */}
      {reportTab === "detaljni" && (
        <div>
          {/* Type selector */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {[{ id: "daily", l: "Dnevni" }, { id: "monthly", l: "Mjesečni" }, { id: "yearly", l: "Godišnji" }].map(t => (
              <button key={t.id} onClick={() => { setDetailedType(t.id); setDetailedData(null); }} style={{ padding: "7px 16px", border: `1px solid ${detailedType === t.id ? S.accent : S.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, background: detailedType === t.id ? S.accent : "white", color: detailedType === t.id ? "white" : S.muted }}>{t.l}</button>
            ))}
          </div>

          {/* Date pickers */}
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 18, marginBottom: 16 }}>
            {detailedType === "daily" && <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>
                  <input type="checkbox" checked={detailedRange} onChange={e => setDetailedRange(e.target.checked)} style={{ marginRight: 6 }} />
                  Period (raspon dana)
                </label>
              </div>
              {detailedRange ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input type="date" value={detailedFrom} onChange={e => setDetailedFrom(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
                  <span style={{ color: S.muted }}>—</span>
                  <input type="date" value={detailedTo} onChange={e => setDetailedTo(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 150 }} />
                </div>
              ) : (
                <input type="date" value={detailedDate} onChange={e => setDetailedDate(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 170 }} />
              )}
            </>}
            {detailedType === "monthly" && (
              <input type="month" value={detailedMonth} onChange={e => setDetailedMonth(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 170 }} />
            )}
            {detailedType === "yearly" && (
              <select value={detailedYear} onChange={e => setDetailedYear(e.target.value)} style={{ ...S.input, padding: "6px 10px", fontSize: 13, width: 120 }}>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
            <button onClick={fetchDetailed} disabled={detailedLoading} style={{ ...S.btn(S.accent), marginTop: 12, justifyContent: "center", padding: "10px 24px", fontSize: 14 }}>
              {detailedLoading ? <Loader2 size={15} className="spin" /> : <BarChart3 size={15} />}
              <span style={{ marginLeft: 6 }}>{detailedLoading ? "Učitavanje..." : "Generiši izvještaj"}</span>
            </button>
          </div>

          {/* Report Results */}
          {detailedData && (() => {
            const s = detailedData.summary;
            return (
              <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
                    {detailedData.type === "daily" ? "Dnevni" : detailedData.type === "monthly" ? "Mjesečni" : "Godišnji"} izvještaj — {detailedData.label}
                  </h3>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={printDetailedReport} style={S.btnSm("#f3f4f6", S.muted)} title="Štampaj"><Printer size={14} /></button>
                    <button onClick={() => {
                      const s = detailedData.summary;
                      let csv = `Izvještaj;${detailedData.type === "daily" ? "Dnevni" : detailedData.type === "monthly" ? "Mjesečni" : "Godišnji"}\nPeriod;${detailedData.label}\n\n`;
                      csv += `Ukupno računa;${s.total_invoices || 0}\nGotovina;${fmt(s.cash_revenue || 0)}\nKartica;${fmt(s.card_revenue || 0)}\n`;
                      if (parseFloat(s.other_revenue || 0) > 0) csv += `Ostalo;${fmt(s.other_revenue)}\n`;
                      csv += `Bez PDV;${fmt(s.total_without_vat || 0)}\nPDV;${fmt(s.total_vat || 0)}\nUKUPNO;${fmt(s.total_revenue || 0)}\n`;
                      if (detailedData.storno && parseInt(detailedData.storno.cnt) > 0) csv += `\nStornirano;${detailedData.storno.cnt};${fmt(detailedData.storno.amount || 0)}\n`;
                      const items = detailedData.days || detailedData.months || [];
                      if (items.length > 0) {
                        csv += "\nDatum;Računi;Gotovina;Kartica;Ukupno\n";
                        items.forEach(i => { csv += `${i.day || i.month};${i.invoices};${fmt(i.cash)};${fmt(i.card)};${fmt(i.revenue)}\n`; });
                      }
                      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
                      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `izvjestaj_${detailedData.type}_${detailedData.label}.csv`; a.click(); URL.revokeObjectURL(url);
                    }} style={S.btnSm("#f3f4f6", S.muted)} title="Export CSV"><Download size={14} /></button>
                  </div>
                </div>

                {/* Summary */}
                <div style={{ background: "#f9fafb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <Row label="Ukupno računa" value={s.total_invoices || 0} />
                  <Row label="Gotovina" value={showAmounts ? `${fmt(s.cash_revenue)} €` : "••••••"} />
                  <Row label="Kartica" value={showAmounts ? `${fmt(s.card_revenue)} €` : "••••••"} />
                  {parseFloat(s.other_revenue || 0) > 0 && <Row label="Ostalo (virman)" value={showAmounts ? `${fmt(s.other_revenue)} €` : "••••••"} />}
                  <div style={{ borderTop: `1px solid ${S.border}`, marginTop: 8, paddingTop: 8 }}>
                    <Row label="Bez PDV" value={showAmounts ? `${fmt(s.total_without_vat)} €` : "••••••"} />
                    <Row label="PDV" value={showAmounts ? `${fmt(s.total_vat)} €` : "••••••"} />
                  </div>
                  <div style={{ borderTop: "2px solid #111", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>UKUPNO</span>
                    <span style={{ fontWeight: 800, fontSize: 16, color: S.green }}>{showAmounts ? `${fmt(s.total_revenue)} €` : "••••••"}</span>
                  </div>
                </div>

                {/* Storno info */}
                {detailedData.storno && parseInt(detailedData.storno.cnt) > 0 && (
                  <div style={{ background: "#fef2f2", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: "#991b1b" }}>
                    Stornirano: <strong>{detailedData.storno.cnt} računa</strong> ({fmt(detailedData.storno.amount)} €)
                  </div>
                )}

                {/* Daily breakdown (for monthly/yearly/range) */}
                {(detailedData.days?.length > 0 || detailedData.months?.length > 0) && (
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 10px", color: S.muted }}>
                      {detailedData.months ? "Po mjesecima" : "Po danima"}
                    </h4>
                    <div style={{ fontSize: 12, color: S.muted, display: "grid", gridTemplateColumns: "1fr 60px 80px 80px 80px", gap: 4, padding: "4px 8px", fontWeight: 600 }}>
                      <span>Datum</span><span style={{ textAlign: "right" }}>Rač.</span><span style={{ textAlign: "right" }}>Gotovina</span><span style={{ textAlign: "right" }}>Kartica</span><span style={{ textAlign: "right" }}>Ukupno</span>
                    </div>
                    {(detailedData.days || detailedData.months || []).map((item, idx) => (
                      <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 60px 80px 80px 80px", gap: 4, padding: "8px", borderRadius: 6, background: idx % 2 === 0 ? "#f9fafb" : "white", fontSize: 13 }}>
                        <span style={{ fontWeight: 600 }}>{item.day || item.month}</span>
                        <span style={{ textAlign: "right" }}>{item.invoices}</span>
                        <span style={{ textAlign: "right" }}>{showAmounts ? `${fmt(item.cash)} €` : "••"}</span>
                        <span style={{ textAlign: "right" }}>{showAmounts ? `${fmt(item.card)} €` : "••"}</span>
                        <span style={{ textAlign: "right", fontWeight: 700 }}>{showAmounts ? `${fmt(item.revenue)} €` : "••"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* INTERNI DANI TAB */}
      {reportTab === "interni" && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 12px", color: S.muted }}>Istorija internih dana</h3>
          {internalHistory.length === 0 ? <div style={{ textAlign: "center", padding: 40, color: S.muted }}><Clock size={36} /><p style={{ marginTop: 10 }}>Nema podataka</p></div>
          : internalHistory.filter(d => d.status === "closed" && d.is_official).map(d => (
            <div key={d.id} style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{d.opened_at?.slice(0, 10)}</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: S.green }}>{showAmounts ? `${fmt(d.total_sales)} €` : "••••••"}</div>
              </div>
              <div style={{ fontSize: 12, color: S.muted }}>
                {d.opened_at?.slice(11, 16)} - {d.closed_at?.slice(11, 16)} · {d.transaction_count} računa ·
                Got: {showAmounts ? `${fmt(d.cash_sales)} €` : "••"} · Kart: {showAmounts ? `${fmt(d.card_sales)} €` : "••"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PORESKI INSPEKTOR TAB */}
      {reportTab === "inspekcija" && <InspectorSection tcr={tcr} showToast={showToast}
        inspReport={inspReport} setInspReport={setInspReport} inspType={inspType} setInspType={setInspType}
        inspFrom={inspFrom} setInspFrom={setInspFrom} inspTo={inspTo} setInspTo={setInspTo}
        inspLoading={inspLoading} setInspLoading={setInspLoading}
        ikofPib={ikofPib} setIkofPib={setIkofPib} ikofDate={ikofDate} setIkofDate={setIkofDate}
        ikofInvNum={ikofInvNum} setIkofInvNum={setIkofInvNum} ikofBu={ikofBu} setIkofBu={setIkofBu}
        ikofTcr={ikofTcr} setIkofTcr={setIkofTcr} ikofSoft={ikofSoft} setIkofSoft={setIkofSoft}
        ikofAmount={ikofAmount} setIkofAmount={setIkofAmount} ikofResult={ikofResult} setIkofResult={setIkofResult}
      />}
    </div>
  );
}
// ═══ PRINTER SETTINGS COMPONENT ═══
function PrinterSettingsTab({ showToast }) {
  const [printerType, setPrinterType] = useState(() => localStorage.getItem("pos_printer_type") || (isElectron ? "usb" : "system"));
  const [printerAddr, setPrinterAddr] = useState(() => localStorage.getItem("pos_printer_addr") || "");
  const [paperWidth, setPaperWidth] = useState(() => localStorage.getItem("pos_paper_width") || "80");
  const [serverStatus, setServerStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [systemPrinters, setSystemPrinters] = useState([]);

  // ═══ WinUSB direct printing state (Windows only) ═══
  // Detect Windows runtime — only show WinUSB UI there. macOS/Linux Electron
  // builds ignore this entire feature (the toggle is hidden).
  const isWindowsElectron = isElectron && /windows nt/i.test(navigator.userAgent || "");

  // useWinUsb: master toggle. When ON, all print jobs route through libusb
  // bypassing node-thermal-printer entirely. Default OFF (existing flow runs).
  const [useWinUsb, setUseWinUsb] = useState(false);
  // winUsbVid/Pid: when null → auto-detect first printer-class device.
  // When set → use that exact device. Saved as integers in settings.
  const [winUsbVid, setWinUsbVid] = useState(null);
  const [winUsbPid, setWinUsbPid] = useState(null);
  const [detectedDevice, setDetectedDevice] = useState(null);     // result of last detect/test
  const [usbDevices, setUsbDevices] = useState([]);                // for advanced picker
  const [showAdvancedUsb, setShowAdvancedUsb] = useState(false);   // toggle Napredno section
  const [winUsbBusy, setWinUsbBusy] = useState(false);

  useEffect(() => {
    if (isElectron && window.electronAPI?.listPrinters) {
      window.electronAPI.listPrinters().then(p => setSystemPrinters(p || []));
      window.electronAPI.getSettings().then(s => {
        if (s.interface) setPrinterAddr(s.interface);
        // Load WinUSB settings — defaults applied by main.js getSettings()
        setUseWinUsb(!!s.useWinUsb);
        setWinUsbVid(s.winUsbVid ?? null);
        setWinUsbPid(s.winUsbPid ?? null);
        // Load paperWidth from settings if set there too (settings is the source
        // of truth on Electron; localStorage is for web/Capacitor compat).
        if (s.paperWidth) setPaperWidth(String(s.paperWidth));
      });
    }
  }, []);

  const savePrinter = async () => {
    localStorage.setItem("pos_printer_type", printerType);
    localStorage.setItem("pos_printer_addr", printerAddr);
    localStorage.setItem("pos_paper_width", paperWidth);

    // If Electron, also save to main process
    if (isElectron && window.electronAPI) {
      const widthChars = paperWidth === "58" || paperWidth === "57" ? 32 : 48;
      await window.electronAPI.saveSettings({
        type: "epson",
        interface: printerAddr,
        printerName: printerAddr,
        width: widthChars,
        // WinUSB fields — saved every time so toggling is persisted.
        useWinUsb: !!useWinUsb,
        winUsbVid: winUsbVid,
        winUsbPid: winUsbPid,
        paperWidth: parseInt(paperWidth) || 80,
      });
    }
    showToast("Postavke printera sacuvane!");
  };

  // ═══ WinUSB action handlers ═══

  // Auto-detect: scan USB and find a printer-class device. Saves found VID/PID
  // to state but does NOT persist until user clicks Sačuvaj.
  const winUsbAutoDetect = async () => {
    setWinUsbBusy(true);
    setDetectedDevice(null);
    try {
      const r = await window.electronAPI.detectUsbPrinter();
      if (r.success) {
        setDetectedDevice(r.device);
        setWinUsbVid(r.device.vid);
        setWinUsbPid(r.device.pid);
        showToast(`Pronađen: ${r.device.product || r.device.vendor || "USB printer"}`);
      } else {
        showToast(r.message || "USB printer nije pronađen", "error");
      }
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setWinUsbBusy(false);
  };

  // Test: verify the saved/picked VID/PID resolves to a connected device.
  // Doesn't print — just checks libusb can find the device.
  const winUsbTestConnection = async () => {
    setWinUsbBusy(true);
    try {
      // Save first so the test uses the current selection
      await window.electronAPI.saveSettings({
        type: "epson",
        interface: printerAddr,
        printerName: printerAddr,
        width: paperWidth === "58" || paperWidth === "57" ? 32 : 48,
        useWinUsb: true,
        winUsbVid: winUsbVid,
        winUsbPid: winUsbPid,
        paperWidth: parseInt(paperWidth) || 80,
      });
      const r = await window.electronAPI.winUsbTest();
      if (r.success) showToast(r.message || "Povezan!");
      else showToast(r.message || "Greška", "error");
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setWinUsbBusy(false);
  };

  // Refresh USB device list for the advanced picker
  const winUsbRefreshList = async () => {
    setWinUsbBusy(true);
    try {
      const r = await window.electronAPI.listUsbDevices();
      if (r.success) {
        setUsbDevices(r.devices || []);
        if (!r.usbAvailable) {
          showToast("USB modul nije dostupan u ovoj instalaciji", "error");
        }
      } else {
        showToast(r.message || "Greška pri čitanju USB uređaja", "error");
      }
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setWinUsbBusy(false);
  };

  const winUsbPickDevice = (dev) => {
    setWinUsbVid(dev.vid);
    setWinUsbPid(dev.pid);
    setDetectedDevice(dev);
    showToast(`Izabran: ${dev.product || dev.vendor || "USB device"}`);
  };

  const testConnection = async () => {
    setTesting(true);
    setServerStatus(null);

    if (isElectron && window.electronAPI) {
      const r = await window.electronAPI.testConnection();
      setServerStatus({ ok: r.success, msg: r.message || (r.success ? "Povezan!" : "Greska") });
    } else {
      // Browser — just check if system print is available
      setServerStatus({ ok: true, msg: "Sistem za stampanje je dostupan. Koristice se sistemski dijalog za izbor printera." });
    }
    setTesting(false);
  };

  const testPrint = async () => {
    setTesting(true);
    const testText = "\n================================\n     EFIS.PRO - Test Print\n================================\nPrinter: OK | Sirina: " + paperWidth + "mm\n" + new Date().toLocaleString("sr-Latn-ME") + "\n================================\n\n\n";
    if (isElectron && window.electronAPI) {
      const r = await window.electronAPI.printText(testText);
      if (r.success) showToast("Stampano!"); else showToast(r.message || "Greska", "error");
    } else if (printerType === "network" && printerAddr) {
      try {
        const printUrl = (printerAddr || "").endsWith("/") ? printerAddr.slice(0,-1) + "/print/text" : printerAddr + "/print/text";
        const r = await fetch(printUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: testText }) });
        const result = await r.json();
        if (result.success) showToast("Stampano!"); else showToast(result.message || "Greska", "error");
      } catch (e) { showToast("Ne mogu se povezati na print server: " + e.message, "error"); }
    } else {
      const w = window.open("", "test", "width=350,height=400");
      if (w) {
        w.document.write('<html><head><style>@page{size:' + paperWidth + 'mm auto;margin:2mm;}body{font-family:monospace;text-align:center;padding:10mm;}</style></head><body><h2>EFIS.PRO</h2><p>Test Print</p><p>Sirina: ' + paperWidth + 'mm</p><p>' + new Date().toLocaleString("sr-Latn-ME") + '</p><hr><p>Stampanje radi!</p><script>setTimeout(function(){window.print();},500);<\/script></body></html>');
        w.document.close();
      }
    }
    setTesting(false);
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginBottom: 16 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 16px" }}>Termalni printer</h3>

        {/* Platform indicator */}
        <div style={{ padding: "8px 14px", background: "#f0fdf4", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#15803d", display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle size={16} />
          {isElectron ? "Windows desktop — direktno stampanje" : isCapacitor ? "Mobilna aplikacija — sistemski printer" : "Web pretraživač — sistemski dijalog za štampu"}
        </div>

        {isElectron ? <>
          <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Printer adresa / interfejs</label>
          <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="tcp://192.168.1.100:9100 ili USB printer ime" style={{ ...S.input, marginBottom: 8, fontFamily: S.mono }} />
          <div style={{ fontSize: 12, color: S.muted, marginBottom: 16 }}>
            Mrežni: tcp://IP:9100 · USB (Windows): \\localhost\ImeStampaca · USB (direktno): /dev/usb/lp0
          </div>
          {systemPrinters.length > 0 && <>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Ili izaberite sistemski printer</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {systemPrinters.map(p => (
                <button key={p.name} onClick={() => setPrinterAddr(p.name)} style={{ padding: "10px 14px", borderRadius: 8, border: `2px solid ${printerAddr === p.name ? S.green : S.border}`, background: printerAddr === p.name ? S.green + "10" : "white", textAlign: "left", cursor: "pointer", fontFamily: S.font, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{p.name}</span>
                  {p.isDefault && <span style={{ marginLeft: 8, fontSize: 11, color: S.green }}>(default)</span>}
                </button>
              ))}
            </div>
          </>}
        </> : <>
          <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Način štampanja</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => { setPrinterType("system"); localStorage.setItem("pos_printer_type", "system"); }} style={{ flex: 1, padding: "12px", borderRadius: 8, border: `2px solid ${printerType === "system" ? S.green : S.border}`, background: printerType === "system" ? "#f0fdf4" : "white", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: S.font, color: printerType === "system" ? S.green : S.muted }}>Sistemski dijalog</button>
            <button onClick={() => { setPrinterType("network"); localStorage.setItem("pos_printer_type", "network"); }} style={{ flex: 1, padding: "12px", borderRadius: 8, border: `2px solid ${printerType === "network" ? S.green : S.border}`, background: printerType === "network" ? "#f0fdf4" : "white", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: S.font, color: printerType === "network" ? S.green : S.muted }}>Mrežni print server</button>
          </div>
          {printerType === "network" ? <>
            <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Print server URL</label>
            <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="http://192.168.1.100:9100" style={{ ...S.input, marginBottom: 8, fontFamily: S.mono }} />
            <div style={{ fontSize: 12, color: S.muted, marginBottom: 16 }}>
              URL vašeg print servera. Emulator: http://10.0.2.2:9100
            </div>
          </> : <>
            <div style={{ padding: 16, background: "#f0f9ff", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#1d4ed8" }}>
              Stampanje koristi sistemski dijalog za izbor printera. Kad kliknete "Stampaj", otvara se prozor gdje birate printer.
            </div>
          </>}
        </>}

        <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Sirina papira</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {["57", "58", "76", "80"].map(w => (
            <button key={w} onClick={() => setPaperWidth(w)} style={{ padding: "10px 20px", borderRadius: 8, border: `2px solid ${paperWidth === w ? S.green : S.border}`, background: paperWidth === w ? S.green + "15" : "white", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: S.mono, color: paperWidth === w ? S.green : S.muted }}>{w}mm</button>
          ))}
        </div>

        {/* ═══ WinUSB direct printing (Windows desktop only) ═══
            When toggle is ON, all print jobs go directly to the USB printer
            via libusb — no Windows printer driver needed. Bypasses the
            spooler entirely. Useful for tablets/kiosks where users can't
            install drivers. NOTE: requires the printer to expose libusb-
            compatible drivers (WinUSB or libusb-win32 via Zadig). Most
            cheap thermal printers work; some vendor-branded units don't. */}
        {isWindowsElectron && (
          <div style={{ marginBottom: 20, padding: 16, background: useWinUsb ? "#f0f9ff" : "#f9fafb", border: `1px solid ${useWinUsb ? "#bae6fd" : S.border}`, borderRadius: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: useWinUsb ? 14 : 4 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: useWinUsb ? "#0c4a6e" : S.dark }}>WinUSB direktno štampanje</div>
                <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                  Šalje ESC/POS direktno preko USB-a, bez Windows drajvera za printer.
                </div>
              </div>
              <button
                onClick={() => setUseWinUsb(v => !v)}
                style={{
                  padding: "8px 14px",
                  background: useWinUsb ? S.green : "#f3f4f6",
                  color: useWinUsb ? "white" : S.muted,
                  border: "none",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {useWinUsb ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                {useWinUsb ? "ON" : "OFF"}
              </button>
            </div>

            {useWinUsb && (
              <>
                {/* Status / detected device info */}
                {(detectedDevice || (winUsbVid && winUsbPid)) ? (
                  <div style={{ padding: "10px 12px", background: "white", border: `1px solid ${S.border}`, borderRadius: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: S.muted, marginBottom: 2 }}>Aktivni printer:</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {detectedDevice?.product || detectedDevice?.vendor || "USB Printer"}
                    </div>
                    <div style={{ fontSize: 10, color: S.muted, fontFamily: S.mono }}>
                      VID:{(winUsbVid ?? 0).toString(16).padStart(4, "0").toUpperCase()} PID:{(winUsbPid ?? 0).toString(16).padStart(4, "0").toUpperCase()}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: "10px 12px", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, marginBottom: 10, fontSize: 12, color: "#92400e" }}>
                    Printer još nije izabran. Kliknite "Auto-detekcija" ili izaberite ručno u Naprednim postavkama.
                  </div>
                )}

                <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  <button onClick={winUsbAutoDetect} disabled={winUsbBusy} style={{ ...S.btnSm(S.accent, "white"), flex: "1 1 calc(50% - 3px)", justifyContent: "center", opacity: winUsbBusy ? 0.5 : 1 }}>
                    {winUsbBusy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
                    Auto-detekcija
                  </button>
                  <button onClick={winUsbTestConnection} disabled={winUsbBusy || !winUsbVid} style={{ ...S.btnSm(S.green, "white"), flex: "1 1 calc(50% - 3px)", justifyContent: "center", opacity: (winUsbBusy || !winUsbVid) ? 0.5 : 1 }}>
                    <CheckCircle size={13} />
                    Testiraj
                  </button>
                </div>

                <button
                  onClick={() => { const next = !showAdvancedUsb; setShowAdvancedUsb(next); if (next && usbDevices.length === 0) winUsbRefreshList(); }}
                  style={{ width: "100%", padding: "8px 12px", background: "transparent", border: `1px dashed ${S.border}`, borderRadius: 7, cursor: "pointer", fontSize: 12, color: S.muted, fontWeight: 600 }}
                >
                  {showAdvancedUsb ? "▾" : "▸"} Napredno (ručni izbor uređaja)
                </button>

                {showAdvancedUsb && (
                  <div style={{ marginTop: 10, padding: 10, background: "white", border: `1px solid ${S.border}`, borderRadius: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: S.muted }}>Svi USB uređaji ({usbDevices.length})</div>
                      <button onClick={winUsbRefreshList} disabled={winUsbBusy} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={11} />Osvježi</button>
                    </div>
                    {usbDevices.length === 0 && !winUsbBusy && (
                      <div style={{ fontSize: 12, color: S.muted, padding: 12, textAlign: "center" }}>
                        Nema dostupnih USB uređaja. Provjerite drajver (WinUSB / libusb).
                      </div>
                    )}
                    {usbDevices.map((d, i) => (
                      <button
                        key={`${d.vid}-${d.pid}-${i}`}
                        onClick={() => winUsbPickDevice(d)}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          background: (winUsbVid === d.vid && winUsbPid === d.pid) ? "#f0fdf4" : "white",
                          border: `1px solid ${(winUsbVid === d.vid && winUsbPid === d.pid) ? S.green : S.border}`,
                          borderRadius: 6,
                          marginBottom: 4,
                          cursor: "pointer",
                          textAlign: "left",
                          fontFamily: S.font,
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {d.isPrinter && <span title="Printer-class device" style={{ fontSize: 11 }}>🖨️</span>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.product || d.vendor || "Unknown device"}
                          </div>
                          <div style={{ fontSize: 10, color: S.muted, fontFamily: S.mono }}>
                            VID:{d.vidHex.toUpperCase()} PID:{d.pidHex.toUpperCase()}
                            {d.isPrinter && <span style={{ marginLeft: 6, color: S.green }}>· printer-class</span>}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 10, fontSize: 11, color: S.muted, lineHeight: 1.4 }}>
                  ⚠️ Za WinUSB potreban je libusb drajver. Ako auto-detekcija ne nalazi printer, instalirajte WinUSB drajver pomoću alata <b>Zadig</b> (zadig.akeo.ie) i pokušajte ponovo.
                </div>
              </>
            )}
          </div>
        )}

        <button onClick={savePrinter} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 12, fontSize: 15, marginBottom: 10 }}><Save size={16} /><span style={{ marginLeft: 8 }}>Sacuvaj postavke</span></button>
      </div>

      {/* Test Connection */}
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>Testiranje</h3>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <button onClick={testConnection} disabled={testing} style={{ ...S.btn("#f3f4f6", S.dark), flex: 1, justifyContent: "center", padding: 11 }}>{testing ? "..." : "Provjeri konekciju"}</button>
          <button onClick={testPrint} disabled={testing} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center", padding: 11 }}><Printer size={15} /><span style={{ marginLeft: 6 }}>Test print</span></button>
        </div>
        {serverStatus && (
          <div style={{ padding: 14, borderRadius: 8, background: serverStatus.ok ? "#f0fdf4" : "#fef2f2", border: `1px solid ${serverStatus.ok ? "#bbf7d0" : "#fecaca"}`, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
            {serverStatus.ok ? <CheckCircle size={18} color={S.green} /> : <XCircle size={18} color={S.red} />}
            <span style={{ color: serverStatus.ok ? "#15803d" : S.red }}>{serverStatus.msg}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ KASA SETTINGS COMPONENT ═══
function KasaSettingsTab({ showToast }) {
  const [idEnabled, setIdEnabled] = useState(false);
  const [openDay, setOpenDay] = useState(null);
  const [liveReport, setLiveReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const tcr = getTcr();

  useEffect(() => {
    api("/internalday/settings").then(r => { if (r.success) setIdEnabled(!!r.data?.internal_day_enabled); });
    // Check if there's an open internal day
    if (tcr) api(`/internalday/status/${tcr}`).then(r => { if (r.success && r.data?.day) setOpenDay(r.data.day); });
  }, [tcr]);

  const fetchLive = async () => {
    if (!tcr) return showToast("Izaberite TCR", "error");
    setLoadingReport(true);
    const d = await api(`/internalday/liveReport/${tcr}`);
    setLoadingReport(false);
    if (d.success) setLiveReport(d.data);
    else showToast(d.message, "error");
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Interni pocetak/kraj dana</div>
            <div style={{ fontSize: 13, color: S.muted }}>Za objekte koji rade preko ponoci</div>
          </div>
          <button onClick={async () => {
            const nv = !idEnabled;
            // Prevent turning off if there's an open day
            if (!nv && openDay) {
              showToast("Zatvorite otvoreni interni dan prije isključivanja! Idite u Blagajna → Otvaranja.", "error");
              return;
            }
            setIdEnabled(nv);
            await api("/internalday/settings", { method: "POST", body: JSON.stringify({ internal_day_enabled: nv ? 1 : 0 }) });
            showToast(nv ? "Interni dan uključen" : "Interni dan isključen");
          }} style={{ width: 52, height: 28, borderRadius: 14, border: "none", cursor: "pointer", background: idEnabled ? S.green : "#d1d5db", position: "relative", transition: "background 0.2s" }}>
            <div style={{ width: 22, height: 22, borderRadius: 11, background: "white", position: "absolute", top: 3, left: idEnabled ? 27 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </button>
        </div>
        {idEnabled && openDay && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e" }}>Interni dan je otvoren od {openDay.opened_at?.slice(0, 16).replace("T", " ")}</div>
            <div style={{ fontSize: 12, color: "#78350f", marginTop: 4 }}>Zatvorite interni dan u Blagajna → Otvaranja prije isključivanja.</div>
          </div>
        )}
      </div>

      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Trenutno stanje kase</div>
        <button onClick={fetchLive} disabled={loadingReport} style={{ ...S.btn(S.dark), width: "100%", justifyContent: "center", padding: 12, fontSize: 15 }}>
          {loadingReport ? <Loader2 size={16} className="spin" /> : <BarChart3 size={16} />}
          <span style={{ marginLeft: 8 }}>{loadingReport ? "Ucitavanje..." : "Prikazi stanje"}</span>
        </button>
        {liveReport && (
          <div style={{ marginTop: 16, background: "#f9fafb", borderRadius: 10, padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>TCR: {tcr} · {liveReport.date}</div>
            <Row label="Depozit" value={`${fmt(liveReport.deposit)} €`} />
            {liveReport.total_withdrawn > 0 && <Row label="Podignuto" value={`-${fmt(liveReport.total_withdrawn)} €`} />}
            <Row label="Gotovina" value={`${fmt(liveReport.today_sales?.cash_sales || 0)} €`} />
            <Row label="Kartica" value={`${fmt(liveReport.today_sales?.card_sales || 0)} €`} />
            <Row label="Ukupan promet" value={`${fmt(liveReport.today_sales?.total_sales || 0)} €`} />
            <Row label="Racuni" value={liveReport.today_sales?.transaction_count || 0} />
            <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700 }}>Ocekivana gotovina</span>
              <span style={{ fontWeight: 700, color: S.green }}>{fmt(liveReport.expected_cash)} €</span>
            </div>
            {liveReport.internal_day && (
              <div style={{ marginTop: 10, padding: 10, background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>Interni dan otvoren od {liveReport.internal_day.opened_at?.slice(11, 16)}</div>
                <div style={{ fontSize: 13 }}>Promet internog dana: {fmt(liveReport.internal_day.sales?.total_sales || 0)} €</div>
              </div>
            )}
            <button onClick={async () => {
              if (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width")) {
                try {
                  const token = localStorage.getItem("pos_token");
                  const r = await fetch(`${API_BASE}/cashregister/liveReportText/${tcr}`, { headers: { "Authorization": `Bearer ${token}` } });
                  if (r.ok && isElectron && window.electronAPI) {
                    const text = await r.text();
                    await window.electronAPI.printText(text);
                    showToast("Štampano!");
                  } else {
                    showToast("Koristite Electron za štampu", "error");
                  }
                } catch(e) { showToast("Greška pri štampi", "error"); }
              } else showToast("Podesite štampač prvo", "error");
            }} style={{ ...S.btn("#f3f4f6", S.muted), width: "100%", justifyContent: "center", marginTop: 10 }}><Printer size={14} />Štampaj stanje</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ SETTINGS VIEW ═══
// ═══ KITCHEN SETTINGS TAB ═══
function KitchenSettingsTab({ showToast }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTags, setEditingTags] = useState(null);
  const [newTag, setNewTag] = useState("");
  const [kitchenOn, setKitchenOn] = useState(() => localStorage.getItem("pos_kitchen_enabled_" + getCompanyId()) !== "false");
  // SELF app integration toggle. When ON, kitchen-enabled cart items in Nova
  // Prodaja get a "🍽️ SELF" toggle next to the kitchen tags. Cashier ticks
  // selected items, then clicks "Pošalji X stavki na SELF" at the bottom of
  // the cart. Sends as one order to the SELF admin queue (status='preparing'
  // immediately — no pending). Items stay in cart so cashier can fiscalize.
  // Default OFF — must be explicitly enabled by admin (and SELF must also be
  // enabled at company level on the backend self_settings.enabled).
  const [selfKasaOn, setSelfKasaOn] = useState(() => localStorage.getItem("pos_self_kasa_enabled_" + getCompanyId()) === "true");
  const [apiError, setApiError] = useState(null);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    setApiError(null);
    // Test kitchen API first
    const test = await api("/kitchen/sectors");
    if (!test.success) { setApiError("Kitchen API nedostupan: " + (test.message || "Provjerite da li je KitchenApiController.php uploadovan na server")); setLoading(false); return; }
    const d = await api("/products");
    if (d.success) {
      const prods = (d.data?.products || []).map(p => ({
        ...p,
        kitchen_enabled: parseInt(p.kitchen_enabled) === 1,
        kitchen_tags_enabled: parseInt(p.kitchen_tags_enabled) === 1,
        kitchen_tags_preset: p.kitchen_tags_preset ? (typeof p.kitchen_tags_preset === "string" ? (() => { try { return JSON.parse(p.kitchen_tags_preset); } catch { return []; } })() : p.kitchen_tags_preset) : []
      }));
      setProducts(prods);
    }
    setLoading(false);
  };

  const toggleGlobal = (val) => {
    setKitchenOn(val);
    localStorage.setItem("pos_kitchen_enabled_" + getCompanyId(), val ? "true" : "false");
    showToast(val ? "Kuhinja UKLJUČENA" : "Kuhinja ISKLJUČENA");
  };

  const toggleSelfKasa = (val) => {
    setSelfKasaOn(val);
    localStorage.setItem("pos_self_kasa_enabled_" + getCompanyId(), val ? "true" : "false");
    showToast(val ? "SELF integracija UKLJUČENA" : "SELF integracija ISKLJUČENA");
  };

  const toggleKitchen = async (product) => {
    const newVal = product.kitchen_enabled ? 0 : 1;
    const d = await api(`/kitchen/updateProduct/${product.id}`, { method: "POST", body: JSON.stringify({ kitchen_enabled: newVal, kitchen_tags_enabled: newVal ? (product.kitchen_tags_enabled ? 1 : 0) : 0, kitchen_tags_preset: product.kitchen_tags_preset || [] }) });
    console.log("toggleKitchen response:", d);
    if (d.success) {
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, kitchen_enabled: !!newVal, kitchen_tags_enabled: newVal ? p.kitchen_tags_enabled : false } : p));
      showToast(newVal ? `${product.name} → Kuhinja ON` : `${product.name} → Kuhinja OFF`);
    } else {
      showToast("GREŠKA: " + (d.message || "Ne mogu sačuvati"), "error");
    }
  };

  const toggleTags = async (product) => {
    const newVal = product.kitchen_tags_enabled ? 0 : 1;
    const d = await api(`/kitchen/updateProduct/${product.id}`, { method: "POST", body: JSON.stringify({ kitchen_enabled: 1, kitchen_tags_enabled: newVal, kitchen_tags_preset: product.kitchen_tags_preset || [] }) });
    console.log("toggleTags response:", d);
    if (d.success) {
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, kitchen_tags_enabled: !!newVal } : p));
    } else { showToast("GREŠKA: " + (d.message || ""), "error"); }
  };

  const addTag = async (product) => {
    if (!newTag.trim()) return;
    const tags = [...(product.kitchen_tags_preset || []), newTag.trim()];
    const d = await api(`/kitchen/updateProduct/${product.id}`, { method: "POST", body: JSON.stringify({ kitchen_enabled: 1, kitchen_tags_enabled: 1, kitchen_tags_preset: tags }) });
    console.log("addTag response:", d);
    if (d.success) {
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, kitchen_tags_preset: tags, kitchen_tags_enabled: true } : p));
      setNewTag("");
      showToast(`Tag "${newTag.trim()}" dodat`);
    } else { showToast("GREŠKA: " + (d.message || ""), "error"); }
  };

  const removeTag = async (product, tag) => {
    const tags = (product.kitchen_tags_preset || []).filter(t => t !== tag);
    const d = await api(`/kitchen/updateProduct/${product.id}`, { method: "POST", body: JSON.stringify({ kitchen_enabled: 1, kitchen_tags_enabled: tags.length > 0 ? 1 : 0, kitchen_tags_preset: tags }) });
    if (d.success) {
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, kitchen_tags_preset: tags, kitchen_tags_enabled: tags.length > 0 ? p.kitchen_tags_enabled : false } : p));
    } else { showToast("GREŠKA: " + (d.message || ""), "error"); }
  };

  if (loading) return <div style={{ padding: 20, color: S.muted }}>Učitavanje...</div>;

  const kitchenProducts = products.filter(p => p.kitchen_enabled);
  const otherProducts = products.filter(p => !p.kitchen_enabled);

  return (
    <div>
      {apiError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "14px 18px", marginBottom: 16, color: "#dc2626", fontSize: 14, fontWeight: 600 }}>
          ⚠️ {apiError}
        </div>
      )}

      {/* Global on/off */}
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>🍳 Kuhinjski displej</h3>
          <p style={{ color: S.muted, fontSize: 13, margin: 0 }}>Kada je uključeno, proizvodi sa kuhinjom se šalju na kuhinjski displej pri fiskalizaciji</p>
        </div>
        <button onClick={() => toggleGlobal(!kitchenOn)} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: kitchenOn ? S.green : "#e5e7eb", color: kitchenOn ? "white" : S.muted, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: S.font, minWidth: 80 }}>{kitchenOn ? "ON" : "OFF"}</button>
      </div>

      {/* SELF app integration — only meaningful when Kitchen is ON, since SELF
          orders go to the kitchen too (via SelfKitchenBridge). When OFF, the
          cart UI in Nova Prodaja shows no SELF buttons — default behavior. */}
      {kitchenOn && (
        <div style={{ background: "white", borderRadius: 12, border: `1px solid ${selfKasaOn ? "#bae6fd" : S.border}`, padding: 20, marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>🍽️ SELF app integracija</h3>
            <p style={{ color: S.muted, fontSize: 13, margin: 0 }}>Slanje porudžbi iz Nove Prodaje na SELF admin (aktivne porudžbe). Bira se pojedinačno po stavki u kartici.</p>
          </div>
          <button onClick={() => toggleSelfKasa(!selfKasaOn)} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: selfKasaOn ? S.green : "#e5e7eb", color: selfKasaOn ? "white" : S.muted, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: S.font, minWidth: 80 }}>{selfKasaOn ? "ON" : "OFF"}</button>
        </div>
      )}

      {!kitchenOn ? (
        <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 30, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 10, opacity: 0.3 }}>🍳</div>
          <p style={{ color: S.muted, fontSize: 15 }}>Kuhinja je isključena. Uključite da biste konfigurisali proizvode.</p>
        </div>
      ) : (<>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 6px", display: "flex", alignItems: "center", gap: 8 }}>🍳 Proizvodi za kuhinju ({kitchenProducts.length})</h3>
        <p style={{ color: S.muted, fontSize: 13, margin: "0 0 16px" }}>Uključite proizvode koji se šalju na kuhinjski displej pri kreiranju računa</p>

        {kitchenProducts.length === 0 && <p style={{ color: S.muted, fontSize: 14, fontStyle: "italic" }}>Nijedan proizvod nije uključen za kuhinju</p>}

        {kitchenProducts.map(p => (
          <div key={p.id} style={{ padding: "12px 14px", borderRadius: 10, border: `1px solid ${S.border}`, marginBottom: 8, background: "#fffbeb" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: S.dark }}>{p.name}</div>
                <div style={{ fontSize: 12, color: S.muted }}>{p.category_name || "Bez kategorije"} • {fmt(p.price)} €</div>
              </div>
              <button onClick={() => toggleTags(p)} style={{ ...S.btnSm(p.kitchen_tags_enabled ? "#dbeafe" : "#f3f4f6", p.kitchen_tags_enabled ? "#2563eb" : S.muted), padding: "5px 10px", fontSize: 12 }}>🏷 Tagovi {p.kitchen_tags_enabled ? "ON" : "OFF"}</button>
              <button onClick={() => setEditingTags(editingTags === p.id ? null : p.id)} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "5px 10px", fontSize: 12 }}><Edit3 size={12} /></button>
              <button onClick={() => toggleKitchen(p)} style={{ ...S.btnSm("#fef2f2", "#dc2626"), padding: "5px 10px", fontSize: 12 }}><X size={12} /></button>
            </div>

            {p.kitchen_tags_enabled && (p.kitchen_tags_preset || []).length > 0 && editingTags !== p.id && (
              <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                {(p.kitchen_tags_preset || []).map(tag => (
                  <span key={tag} style={{ padding: "2px 8px", borderRadius: 5, background: "#fef3c7", color: "#92400e", fontSize: 11, fontWeight: 600 }}>{tag}</span>
                ))}
              </div>
            )}

            {editingTags === p.id && (
              <div style={{ marginTop: 10, padding: 10, background: "white", borderRadius: 8, border: `1px solid ${S.border}` }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: S.dark, marginBottom: 6, display: "block" }}>Tagovi za {p.name}</label>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                  {(p.kitchen_tags_preset || []).map(tag => (
                    <span key={tag} style={{ padding: "4px 8px", borderRadius: 6, background: "#fef3c7", color: "#92400e", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                      {tag} <button onClick={() => removeTag(p, tag)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#dc2626", fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === "Enter" && addTag(p)} placeholder="Novi tag (npr. well done)" style={{ ...S.input, flex: 1, padding: "7px 10px", fontSize: 13 }} />
                  <button onClick={() => addTag(p)} style={{ ...S.btn("#f59e0b"), padding: "7px 14px", fontSize: 13, color: "white" }}><Plus size={13} />Dodaj</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>Svi proizvodi</h3>
        {otherProducts.map(p => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, border: `1px solid ${S.border}`, marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.dark }}>{p.name}</div>
              <div style={{ fontSize: 12, color: S.muted }}>{p.category_name || "Bez kategorije"}</div>
            </div>
            <button onClick={() => toggleKitchen(p)} style={{ ...S.btn("#f59e0b"), padding: "6px 14px", fontSize: 12, color: "white" }}>🍳 Uključi</button>
          </div>
        ))}
      </div>
      </>)}
    </div>
  );
}

// ═══ IZGLED PRODAJE — Nova prodaja layout customization ═══
function IzgledProdaje({ showToast, products, posLayout, posTiles, onLayoutChanged }) {
  const [layout, setLayoutLocal] = useState(posLayout);
  const [tiles, setTilesLocal] = useState(posTiles);
  const [storageBytes, setStorageBytes] = useState(estimateImageStorageBytes());
  const [editingTile, setEditingTile] = useState(null); // product object

  // Recompute storage usage display whenever tiles change
  useEffect(() => { setStorageBytes(estimateImageStorageBytes()); }, [tiles]);

  const updateLayout = (patch) => {
    const next = { ...layout, ...patch };
    setLayoutLocal(next);
    saveLayoutSettings(next);
    onLayoutChanged && onLayoutChanged();
  };

  const refreshTiles = () => {
    const fresh = loadTileConfigs();
    setTilesLocal(fresh);
    setStorageBytes(estimateImageStorageBytes());
    onLayoutChanged && onLayoutChanged();
  };

  const resetLayout = () => {
    if (!window.confirm("Vratiti sve postavke izgleda na podrazumijevano? (Tile konfiguracija ostaje sačuvana.)")) return;
    setLayoutLocal({ ...DEFAULT_LAYOUT });
    saveLayoutSettings({ ...DEFAULT_LAYOUT });
    onLayoutChanged && onLayoutChanged();
    showToast("Vraćeno na podrazumijevano");
  };

  const clearAllTiles = () => {
    if (!window.confirm("Obrisati SVE prilagođene postavke proizvoda (slike, veličine, prikaz)? Ova akcija ne može se poništiti.")) return;
    try { localStorage.removeItem(POS_TILES_KEY + getCompanyId()); } catch {}
    refreshTiles();
    showToast("Sve prilagođene postavke obrisane");
  };

  const usagePct = Math.min(100, Math.round((storageBytes / POS_IMG_BUDGET_BYTES) * 100));
  const usageMb = (storageBytes / 1024 / 1024).toFixed(2);
  const budgetMb = (POS_IMG_BUDGET_BYTES / 1024 / 1024).toFixed(0);

  const customizedCount = Object.keys(tiles).length;

  return (
    <div>
      <div style={{ background: "#f9fafb", borderRadius: 10, padding: 16, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Veličina kartica</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {Object.entries(CARD_SIZES).map(([key, info]) => (
            <button key={key} onClick={() => updateLayout({ card_size: key })}
              style={{
                padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: `1.5px solid ${layout.card_size === key ? S.green : S.border}`,
                background: layout.card_size === key ? "#f0fdf4" : "white",
                color: layout.card_size === key ? S.green : S.dark,
                borderRadius: 8, fontFamily: S.font,
              }}>
              {info.label}
              <div style={{ fontSize: 10, color: S.muted, marginTop: 2 }}>{info.px}px</div>
            </button>
          ))}
        </div>
        <p style={{ fontSize: 12, color: S.muted, margin: "10px 0 0" }}>
          Veličina svake ploče u Novoj prodaji. Broj kolona se automatski prilagođava širini ekrana.
        </p>
      </div>

      <div style={{ background: "#f9fafb", borderRadius: 10, padding: 16, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Stil prikaza</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => updateLayout({ style: "kartice" })}
            style={{
              flex: 1, padding: "14px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer",
              border: `1.5px solid ${layout.style === "kartice" ? S.green : S.border}`,
              background: layout.style === "kartice" ? "#f0fdf4" : "white",
              color: layout.style === "kartice" ? S.green : S.dark,
              borderRadius: 8, textAlign: "left", fontFamily: S.font,
            }}>
            <div style={{ fontSize: 15, marginBottom: 4 }}>📦 Kartice</div>
            <div style={{ fontSize: 11, color: S.muted, fontWeight: 400 }}>
              Razmak između ploča, zaobljeni rubovi. Sve ploče iste veličine.
            </div>
          </button>
          <button onClick={() => updateLayout({ style: "tabela" })}
            style={{
              flex: 1, padding: "14px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer",
              border: `1.5px solid ${layout.style === "tabela" ? S.green : S.border}`,
              background: layout.style === "tabela" ? "#f0fdf4" : "white",
              color: layout.style === "tabela" ? S.green : S.dark,
              borderRadius: 8, textAlign: "left", fontFamily: S.font,
            }}>
            <div style={{ fontSize: 15, marginBottom: 4 }}>🧱 Tabela (custom)</div>
            <div style={{ fontSize: 11, color: S.muted, fontWeight: 400 }}>
              Bez razmaka, oštri rubovi. Po proizvodu birate veličinu, sliku, prikaz.
            </div>
          </button>
        </div>
      </div>

      {layout.style === "kartice" && (
        <div style={{ background: "#f9fafb", borderRadius: 10, padding: 16, marginBottom: 14, border: `1px solid ${S.border}` }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Prikaz na kartici</h3>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 8, fontSize: 14 }}>
            <input type="checkbox" checked={layout.show_default_name} onChange={e => updateLayout({ show_default_name: e.target.checked })} style={{ width: 18, height: 18, accentColor: S.green }} />
            <span style={{ fontWeight: 600 }}>Prikaži naziv proizvoda</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
            <input type="checkbox" checked={layout.show_default_price} onChange={e => updateLayout({ show_default_price: e.target.checked })} style={{ width: 18, height: 18, accentColor: S.green }} />
            <span style={{ fontWeight: 600 }}>Prikaži cijenu (Bruto)</span>
          </label>
        </div>
      )}

      {layout.style === "tabela" && (
        <div style={{ background: "#f9fafb", borderRadius: 10, padding: 16, marginBottom: 14, border: `1px solid ${S.border}` }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Prilagođavanje proizvoda</h3>
          <p style={{ fontSize: 13, color: S.muted, margin: "0 0 12px" }}>
            Kliknite proizvod da podesite veličinu, sliku i prikaz.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 8 }}>
            {products.map(p => {
              const tile = getTileConfig(p.id, tiles);
              const isCustom = tile.span_x !== 1 || tile.span_y !== 1 || tile.image || tile.bg_color || !tile.show_name || !tile.show_price;
              return (
                <button key={p.id} onClick={() => setEditingTile(p)} style={{
                  padding: "10px 12px",
                  border: `1px solid ${isCustom ? S.green : S.border}`,
                  borderRadius: 8,
                  background: isCustom ? "#f0fdf4" : "white",
                  cursor: "pointer", textAlign: "left", fontFamily: S.font,
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  {tile.image ? (
                    <img src={tile.image} alt="" style={{ width: 36, height: 36, borderRadius: 4, objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: 4, background: tile.bg_color || "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: S.muted, fontWeight: 700 }}>
                      {tile.span_x}×{tile.span_y}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: isCustom ? S.green : S.muted }}>{isCustom ? "Prilagođeno" : "Standardno"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ background: "#f9fafb", borderRadius: 10, padding: 16, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Skladište slika</h3>
        <div style={{ fontSize: 13, color: S.muted, marginBottom: 6 }}>
          Koristite: <strong style={{ color: usagePct >= 80 ? S.red : S.dark }}>{usageMb} MB</strong> / {budgetMb} MB
          {customizedCount > 0 && <span> · {customizedCount} prilagođen{customizedCount === 1 ? "" : "ih"} proizvod{customizedCount === 1 ? "" : "a"}</span>}
        </div>
        <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden", marginBottom: 10 }}>
          <div style={{ width: `${usagePct}%`, height: "100%", background: usagePct >= 80 ? S.red : usagePct >= 60 ? "#f59e0b" : S.green, transition: "width 0.3s" }} />
        </div>
        {usagePct >= 80 && (
          <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 6, padding: "8px 10px", fontSize: 12, color: "#92400e", marginBottom: 8 }}>
            ⚠️ Skladište skoro puno. Razmislite o brisanju nekih slika ili korištenju manjih.
          </div>
        )}
        <button onClick={clearAllTiles} style={S.btnSm("#fef2f2", S.red)}>
          <Trash2 size={12} />Obriši sve prilagođene postavke
        </button>
      </div>

      <button onClick={resetLayout} style={{ ...S.btn("#f3f4f6", S.muted) }}>
        Vrati izgled na podrazumijevano
      </button>

      {editingTile && (
        <TileConfigModal
          product={editingTile}
          cardSizeKey={layout.card_size}
          tiles={tiles}
          onClose={() => setEditingTile(null)}
          onSaved={() => { refreshTiles(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// Per-product tile customization modal (only shown in tabela mode)
function TileConfigModal({ product, cardSizeKey, tiles, onClose, onSaved, showToast }) {
  const initial = getTileConfig(product.id, tiles);
  const [tile, setTile] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const save = () => {
    const { saved } = setTileConfig(product.id, tile);
    if (!saved) {
      showToast("Greška pri čuvanju (skladište puno?). Pokušajte obrisati neke slike.", "error");
      return;
    }
    onSaved();
    showToast("Sačuvano!");
    onClose();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await processProductImage(file, tile.span_x, tile.span_y, cardSizeKey);
      // Quick quota pre-check: estimate after-add bytes
      const currentBytes = estimateImageStorageBytes();
      const oldImageBytes = initial.image ? Math.round(initial.image.length * 0.75) : 0;
      const projected = currentBytes - oldImageBytes + result.bytes;
      if (projected > POS_IMG_BUDGET_BYTES) {
        const projMb = (projected / 1024 / 1024).toFixed(2);
        const budgetMb = (POS_IMG_BUDGET_BYTES / 1024 / 1024).toFixed(0);
        showToast(`Skladište bi prelazilo limit (${projMb} MB / ${budgetMb} MB). Obrišite neke slike.`, "error");
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setTile(t => ({ ...t, image: result.dataUrl }));
      showToast(`Slika obrađena (${(result.bytes / 1024).toFixed(1)} KB, ${result.width}×${result.height})`);
    } catch (err) {
      showToast(err.message || "Greška pri obradi slike", "error");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = () => setTile(t => ({ ...t, image: null }));

  return (
    <Modal onClose={onClose} title={`Prikaz: ${product.name}`}>
      {/* Span size picker */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Veličina ploče</label>
        <div style={{ display: "flex", gap: 8 }}>
          {SPAN_PRESETS.map(s => {
            const isActive = tile.span_x === s.x && tile.span_y === s.y;
            return (
              <button key={s.label} onClick={() => setTile(t => ({ ...t, span_x: s.x, span_y: s.y }))}
                style={{
                  flex: 1, padding: "12px 8px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                  border: `1.5px solid ${isActive ? S.green : S.border}`,
                  background: isActive ? "#f0fdf4" : "white",
                  color: isActive ? S.green : S.dark,
                  borderRadius: 8, textAlign: "center", fontFamily: S.font,
                }}>
                <div style={{ fontSize: 16 }}>{s.label}</div>
                <div style={{ fontSize: 10, color: S.muted, fontWeight: 400, marginTop: 2 }}>{s.desc}</div>
              </button>
            );
          })}
        </div>
        {tile.image && (tile.span_x !== initial.span_x || tile.span_y !== initial.span_y) && (
          <div style={{ fontSize: 11, color: "#b45309", marginTop: 6 }}>
            ⚠ Promjena veličine — ako želite oštru sliku, ponovo učitajte sliku.
          </div>
        )}
      </div>

      {/* Display toggles */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Prikaz na ploči</label>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 6, fontSize: 14 }}>
          <input type="checkbox" checked={tile.show_name} onChange={e => setTile(t => ({ ...t, show_name: e.target.checked }))} style={{ width: 18, height: 18, accentColor: S.green }} />
          <span>Prikaži naziv</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
          <input type="checkbox" checked={tile.show_price} onChange={e => setTile(t => ({ ...t, show_price: e.target.checked }))} style={{ width: 18, height: 18, accentColor: S.green }} />
          <span>Prikaži cijenu (Bruto)</span>
        </label>
      </div>

      {/* Image upload */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Pozadinska slika</label>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ width: 80, height: 80, borderRadius: 8, border: `1px dashed ${S.border}`, background: tile.bg_color || "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
            {tile.image ? (
              <img src={tile.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <span style={{ fontSize: 11, color: S.muted }}>Bez slike</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} style={{ display: "none" }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ ...S.btnSm("#3b82f6", "white"), marginRight: 6 }}>
              {uploading ? "Obrađujem..." : <><Upload size={12} />{tile.image ? "Promijeni" : "Učitaj"}</>}
            </button>
            {tile.image && (
              <button onClick={removeImage} style={S.btnSm("#fef2f2", S.red)}>
                <Trash2 size={12} />Ukloni
              </button>
            )}
            <div style={{ fontSize: 11, color: S.muted, marginTop: 6 }}>
              JPEG, PNG ili WebP. Slika se automatski smanjuje na potrebnu veličinu.
            </div>
          </div>
        </div>
      </div>

      {/* Background color */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>
          Boja pozadine {tile.image && <span style={{ fontSize: 11, color: S.muted, fontWeight: 400 }}>(ignorisano kad ima slike)</span>}
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="color" value={tile.bg_color || "#ffffff"} onChange={e => setTile(t => ({ ...t, bg_color: e.target.value }))} style={{ width: 50, height: 36, border: `1px solid ${S.border}`, borderRadius: 6, cursor: "pointer", padding: 2 }} />
          <input type="text" value={tile.bg_color || ""} placeholder="#ffffff" onChange={e => setTile(t => ({ ...t, bg_color: e.target.value || null }))} style={{ ...S.input, flex: 1, fontFamily: S.mono, fontSize: 13 }} />
          {tile.bg_color && (
            <button onClick={() => setTile(t => ({ ...t, bg_color: null }))} style={S.btnSm("#fef2f2", S.red)}>
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
        <button onClick={save} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>
          <Save size={15} />Sačuvaj
        </button>
      </div>
    </Modal>
  );
}

function SettingsView({ user, showToast, onTablesToggle, activeOperator, posLayout, posTiles, onPosLayoutChanged }) {
  const [tcrs, setTcrs] = useState([]);
  const [selectedTcr, setSelectedTcr] = useState(getTcr());
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("general");
  const [fastMenu, setFastMenu] = useState(() => { try { return JSON.parse(localStorage.getItem("pos_fast_menu_" + getCompanyId()) || "[]"); } catch { return []; } });
  const [editProd, setEditProd] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [editPartner, setEditPartner] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnerDetail, setPartnerDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  // Tables settings
  const [tablesEnabled, setTablesEnabled] = useState(false);
  const [rTables, setRTables] = useState([]);
  const [editTable, setEditTable] = useState(null);
  const [zones, setZones] = useState([]);
  const [newZoneName, setNewZoneName] = useState("");
  // Master categories (HRANA/PIĆE) — POS-only feature, OFF by default per company.
  // masterCategoryPos: company-level toggle.
  // masterCategories: list of master categories for this company (only populated
  //   after first opt-in; empty until then).
  // The toggle's state is loaded alongside other settings; on save POST we
  // refresh both the flag and the list so the UI reflects auto-seeded rows.
  const [masterCategoryPos, setMasterCategoryPos] = useState(false);
  const [masterCategories, setMasterCategories] = useState([]);
  // Kategorije → "Redoslijed proizvoda" subview state. null = show normal categories list.
  // When set, the Kategorije tab renders a subview showing all products in that category
  // with up/down buttons, and a "Sačuvaj redoslijed" button to persist via /products/reorder.
  // reorderIds holds the working copy (array of product ids) so cancel-without-save just
  // navigates back without touching the server.
  const [reorderingCategory, setReorderingCategory] = useState(null); // category object or null
  const [reorderIds, setReorderIds] = useState([]);
  const [reorderSaving, setReorderSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const [cfgData, prodData, catData, partData, tablesSettings, tablesData, posSettingsData] = await Promise.all([
      api("/auth/company-config"),
      api("/products"),
      api("/products/categories"),
      api("/partners"),
      api("/tables/settings"),
      api("/tables"),
      api("/company/posSettings"),
    ]);
    if (cfgData.success) {
      const allTcrs = cfgData.data.all_tcrs || [];
      setTcrs(allTcrs);
      const storedTcr = localStorage.getItem("pos_tcr_" + getCompanyId());
      const validTcr = allTcrs.find(t => t.tcr_code === storedTcr);
      if (validTcr) {
        setSelectedTcr(validTcr.tcr_code);
      } else if (allTcrs.length > 0) {
        setSelectedTcr(allTcrs[0].tcr_code);
        localStorage.setItem("pos_tcr_" + getCompanyId(), allTcrs[0].tcr_code);
      } else {
        setSelectedTcr("");
        localStorage.removeItem("pos_tcr_" + getCompanyId());
      }
    }
    if (prodData.success) setProducts(prodData.data?.products || []);
    if (catData.success) setCategories(catData.data?.categories || []);
    if (partData.success) setPartners(partData.data?.partners || []);
    if (tablesSettings.success) setTablesEnabled(!!tablesSettings.data?.tables_enabled);
    if (tablesData.success) setRTables(tablesData.data?.tables || []);
    if (posSettingsData.success) {
      setMasterCategoryPos(!!posSettingsData.data?.master_category_pos);
      setMasterCategories(posSettingsData.data?.master_categories || []);
    }
    // Load zones - auto-create "Glavni" if none exist
    let zonesData = await api("/tables/zones");
    if (zonesData.success && (!zonesData.data?.zones || zonesData.data.zones.length === 0)) {
      await api("/tables/zones", { method: "POST", body: JSON.stringify({ name: "Glavni" }) });
      zonesData = await api("/tables/zones");
    }
    if (zonesData.success) setZones(zonesData.data?.zones || []);
    setLoading(false);
  };

  const handleTcrChange = (tcr) => { setSelectedTcr(tcr); localStorage.setItem("pos_tcr_" + getCompanyId(), tcr); showToast("TCR: " + tcr); };
  const toggleFastMenu = (pid) => { setFastMenu(prev => { const n = prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]; localStorage.setItem("pos_fast_menu_" + getCompanyId(), JSON.stringify(n)); return n; }); };
  const moveFastItem = (pid, dir) => { setFastMenu(prev => { const idx = prev.indexOf(pid); if (idx === -1) return prev; const n = [...prev]; const si = dir === "up" ? idx - 1 : idx + 1; if (si < 0 || si >= n.length) return prev; [n[idx], n[si]] = [n[si], n[idx]]; localStorage.setItem("pos_fast_menu_" + getCompanyId(), JSON.stringify(n)); return n; }); };

  // ───── Category reorder (subview inside Kategorije tab) ─────
  // openReorder: enter the subview for a given category. Seeds reorderIds from
  // products already sorted by category_position on the server (NULL last → matches grid).
  const openReorder = (cat) => {
    const inCat = products
      .filter(p => String(p.category_id) === String(cat.id))
      .slice() // products is sorted by API in the order we want; copy so we can reorder locally
      .map(p => p.id);
    setReorderIds(inCat);
    setReorderingCategory(cat);
  };
  const closeReorder = () => { setReorderingCategory(null); setReorderIds([]); };
  // Local up/down — touches working copy only. Persist happens on saveReorder.
  const moveReorderItem = (pid, dir) => {
    setReorderIds(prev => {
      const idx = prev.indexOf(pid);
      if (idx === -1) return prev;
      const swap = dir === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= prev.length) return prev;
      const n = prev.slice();
      [n[idx], n[swap]] = [n[swap], n[idx]];
      return n;
    });
  };
  const saveReorder = async () => {
    if (!reorderingCategory) return;
    setReorderSaving(true);
    const d = await api("/products/reorder", {
      method: "POST",
      body: JSON.stringify({ category_id: reorderingCategory.id, ordered_ids: reorderIds })
    });
    setReorderSaving(false);
    if (d.success) {
      showToast("Redoslijed sačuvan");
      // Reload products so the local 'products' array also reflects the new positions.
      // (Otherwise the subview would be in sync but the main POS grid would still show
      // pre-save order until next page load.)
      await loadAll();
      closeReorder();
    } else {
      showToast(d.message || "Greška pri snimanju", "error");
    }
  };

  // Product CRUD
  const saveProduct = async () => {
    if (!editProd.name || !editProd.price) return showToast("Naziv i cijena su obavezni", "error");
    setSaving(true);
    const isNew = !editProd.id;
    const url = isNew ? "/products" : `/products/update/${editProd.id}`;
    // Strip internal-only fields used for Neto/Bruto sync (not persisted)
    const { _netoTyped, _brutoTyped, _priceMode, _brutoSnizen, ...rest } = editProd;
    const payload = { ...rest, track_stock: editProd.track_stock ? 1 : 0 };
    const d = await api(url, { method: "POST", body: JSON.stringify(payload) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Proizvod kreiran!" : "Proizvod ažuriran!"); setEditProd(null); loadAll(); }
    else showToast(d.message || "Greška", "error");
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Deaktivirati ovaj proizvod?")) return;
    const d = await api(`/products/delete/${id}`, { method: "POST" });
    if (d.success) { showToast("Proizvod deaktiviran"); loadAll(); } else showToast(d.message, "error");
  };

  // Category CRUD
  const saveCategory = async () => {
    if (!editCat.name) return showToast("Naziv kategorije je obavezan", "error");
    setSaving(true);
    const isNew = !editCat.id;
    const url = isNew ? "/products/categories" : `/products/updateCategory/${editCat.id}`;
    const d = await api(url, { method: "POST", body: JSON.stringify(editCat) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Kategorija kreirana!" : "Kategorija ažurirana!"); setEditCat(null); loadAll(); }
    else showToast(d.message, "error");
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Obrisati kategoriju?")) return;
    const d = await api(`/products/deleteCategory/${id}`, { method: "POST" });
    if (d.success) { showToast("Kategorija obrisana"); loadAll(); } else showToast(d.message, "error");
  };

  // Partner CRUD
  const savePartner = async () => {
    if (!editPartner.name) return showToast("Naziv partnera je obavezan", "error");
    setSaving(true);
    const isNew = !editPartner.id;
    const url = isNew ? "/partners" : `/partners/update/${editPartner.id}`;
    const d = await api(url, { method: "POST", body: JSON.stringify(editPartner) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Partner kreiran!" : "Partner ažuriran!"); setEditPartner(null); loadAll(); }
    else showToast(d.message || "Greška", "error");
  };
  const deletePartner = async (id) => {
    if (!window.confirm("Obrisati partnera?")) return;
    const d = await api(`/partners/delete/${id}`, { method: "POST" });
    if (d.success) { showToast("Partner obrisan"); loadAll(); } else showToast(d.message, "error");
  };

  const loadPartnerDetail = async (partner) => {
    const d = await api(`/partners/stats/${partner.id}`);
    if (d.success) setPartnerDetail({ ...partner, stats: d.data });
    else setPartnerDetail({ ...partner, stats: null });
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  const inputRow = (label, field, type = "text", placeholder = "") => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: S.dark, display: "block", marginBottom: 4 }}>{label}</label>
      <input type={type} step={type === "number" ? "0.01" : undefined} value={editProd?.[field] ?? ""} onChange={e => setEditProd(prev => ({ ...prev, [field]: type === "number" ? e.target.value : e.target.value }))} placeholder={placeholder} style={S.input} />
    </div>
  );

  return (
    <div style={{ height: "100%", overflow: "auto", padding: 20 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 16px" }}>Podešavanja</h2>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {[{ id: "general", l: "TCR Kasa" }, { id: "kasa", l: "💰 Kasa" }, { id: "products", l: "Proizvodi" }, { id: "categories", l: "Kategorije" }, { id: "partners", l: "Partneri" }, { id: "fastmenu", l: "⚡ Brzi Meni" }, { id: "izgled", l: "🎨 Izgled prodaje" }, { id: "stolovi", l: "🍽 Stolovi" }, { id: "printer", l: "🖨 Printer" }, { id: "kitchen", l: "🍳 Kuhinja" }, ...(hasPermission(activeOperator, "manage_pins") ? [{ id: "operators", l: "🔐 Operateri" }] : [])].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 18px", border: "1px solid", borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: tab === t.id ? S.dark : S.border, background: tab === t.id ? S.dark : "white", color: tab === t.id ? "white" : S.muted }}>{t.l}</button>
        ))}
      </div>

      {/* ═══ TCR TAB ═══ */}
      {tab === "general" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>TCR Kod (Elektronska Kasa)</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tcrs.map(t => (
                <label key={t.tcr_code} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 8, cursor: "pointer", background: selectedTcr === t.tcr_code ? "#f0fdf4" : "#f9fafb", border: `2px solid ${selectedTcr === t.tcr_code ? S.green : S.border}` }}>
                  <input type="radio" name="tcr" checked={selectedTcr === t.tcr_code} onChange={() => handleTcrChange(t.tcr_code)} style={{ accentColor: S.green, width: 18, height: 18 }} />
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontFamily: S.mono, fontSize: 15 }}>{t.tcr_code}</div>{t.location_name && <div style={{ fontSize: 13, color: S.muted }}>{t.location_name}</div>}</div>
                  {selectedTcr === t.tcr_code && <CheckCircle size={20} color={S.green} />}
                </label>
              ))}
              {tcrs.length === 0 && <div style={{ padding: 20, textAlign: "center", color: S.muted }}>Nema TCR kodova. Dodajte ih u web aplikaciji.</div>}
            </div>
          </div>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginTop: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>Info</h3>
            <Row label="Operater" value={user.name} /><Row label="Kompanija" value={user.company_name} /><Row label="Email" value={user.email} last />
          </div>
        </div>
      )}

      {/* ═══ PRODUCTS TAB ═══ */}
      {/* ═══ KASA SETTINGS TAB ═══ */}
      {tab === "kasa" && <KasaSettingsTab showToast={showToast} />}

      {/* ═══ PRINTER TAB ═══ */}
      {tab === "printer" && <PrinterSettingsTab showToast={showToast} />}

      {tab === "products" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{products.length} proizvoda</span>
            <button onClick={() => setEditProd({ name: "", price: "", vat_rate: 21, unit: "kom", is_decimal_unit: 0, category_id: "", sku: "", barcode: "", description: "", track_stock: true, stock_quantity: 0 })} style={S.btn(S.green)}><PlusCircle size={15} />Novi proizvod</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, background: "white", borderRadius: 10, overflow: "hidden", border: `1px solid ${S.border}` }}>
            <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={{ ...th, textAlign: "right" }}>Neto cijena</th><th style={{ ...th, textAlign: "right" }}>Bruto cijena</th><th style={{ ...th, textAlign: "center" }}>PDV</th><th style={{ ...th, textAlign: "center" }}>Kategorija</th><th style={{ ...th, textAlign: "center" }}>Stanje</th><th style={{ ...th, textAlign: "center" }}>Brzi</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
            <tbody>{products.map(p => {
              const cat = categories.find(c => String(c.id) === String(p.category_id));
              const bruto = parseFloat(p.price) * (1 + (Number(p.vat_rate) || 0) / 100);
              return (
                <tr key={p.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ ...td, fontWeight: 600 }}>{p.name}<br/><span style={{ fontSize: 12, color: S.muted, fontFamily: S.mono }}>{p.sku || ""}</span></td>
                  <td style={{ ...td, textAlign: "right" }}>{fmt(p.price)} €</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600, color: S.green }}>{fmt(bruto)} €</td>
                  <td style={{ ...td, textAlign: "center" }}>{p.vat_rate}%</td>
                  <td style={{ ...td, textAlign: "center", fontSize: 13 }}>{cat ? cat.name : "-"}</td>
                  <td style={{ ...td, textAlign: "center", fontSize: 13 }}>{p.track_stock == 1 ? <span style={{ fontWeight: 600, color: parseFloat(p.stock_quantity || 0) <= parseFloat(p.low_stock_alert || 10) ? S.red : S.green }}>{p.stock_quantity ?? 0}</span> : <span style={{ color: "#d1d5db" }}>—</span>}</td>
                  <td style={{ ...td, textAlign: "center" }}><button onClick={() => toggleFastMenu(p.id)} style={{ ...S.btnSm(fastMenu.includes(p.id) ? "#f0fdf4" : "#f3f4f6", fastMenu.includes(p.id) ? "#15803d" : S.muted), padding: "3px 8px" }}>{fastMenu.includes(p.id) ? <Check size={12} /> : <Plus size={12} />}</button></td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button onClick={() => setEditProd({ id: p.id, name: p.name, price: p.price, vat_rate: p.vat_rate, unit: p.unit, is_decimal_unit: parseInt(p.is_decimal_unit) === 1 ? 1 : 0, category_id: p.category_id || "", sku: p.sku || "", barcode: p.barcode || "", description: p.description || "", stock_quantity: p.stock_quantity || 0, track_stock: p.track_stock == 1 })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} /></button>
                      <button onClick={() => deleteProduct(p.id)} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      {/* ═══ CATEGORIES TAB ═══ */}
      {tab === "categories" && !reorderingCategory && (
        <div style={{ maxWidth: 560 }}>
          {/* Master categories toggle (POS-only feature). When enabled, the POS
              shows HRANA/PIĆE master buttons in Nova Prodaja and a HRANA/PIĆE
              toggle in Stolovi. Each simple category can be linked to a master
              via the dropdown that appears below when toggle is ON. */}
          <div style={{ marginBottom: 14, padding: "12px 14px", background: masterCategoryPos ? "#eef2ff" : "#f9fafb", border: `1px solid ${masterCategoryPos ? "#c7d2fe" : S.border}`, borderRadius: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: masterCategoryPos ? "#3730a3" : S.dark }}>Master kategorije u POS-u</div>
                <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                  Grupiše kategorije pod HRANA / PIĆE u Nova Prodaja i Stolovi sektoru.
                </div>
              </div>
              <button
                onClick={async () => {
                  const newVal = !masterCategoryPos;
                  // Optimistically update UI for snappy feel; revert on backend error.
                  setMasterCategoryPos(newVal);
                  const r = await api("/company/posSettings", { method: "POST", body: JSON.stringify({ master_category_pos: newVal ? 1 : 0 }) });
                  if (!r.success) {
                    setMasterCategoryPos(!newVal);
                    showToast(r.message || "Greška pri spremanju", "error");
                    return;
                  }
                  // Refresh both flag and master list (auto-seeded HRANA/PIĆE on first ON)
                  setMasterCategoryPos(!!r.data?.master_category_pos);
                  setMasterCategories(r.data?.master_categories || []);
                  showToast(newVal ? "Master kategorije omogućene" : "Master kategorije onemogućene");
                }}
                style={{
                  padding: "8px 14px",
                  background: masterCategoryPos ? S.green : "#f3f4f6",
                  color: masterCategoryPos ? "white" : S.muted,
                  border: "none",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {masterCategoryPos ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                {masterCategoryPos ? "Omogućeno" : "Onemogućeno"}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{categories.length} kategorija</span>
            <button onClick={() => setEditCat({ name: "", description: "", sort_order: 0 })} style={S.btn(S.green)}><PlusCircle size={15} />Nova kategorija</button>
          </div>
          {categories.map(c => {
            const productCount = products.filter(p => String(p.category_id) === String(c.id)).length;
            return (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "white", borderRadius: 8, border: `1px solid ${S.border}`, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                  {c.description && <div style={{ fontSize: 13, color: S.muted }}>{c.description}</div>}
                  <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>{productCount} {productCount === 1 ? "proizvod" : "proizvoda"}</div>
                  {/* Master picker — only visible when feature is ON. Saves on change.
                      "Bez master kategorije" maps to NULL (category visible only under SVE).
                      Master list comes from auto-seeded HRANA + PIĆE rows. */}
                  {masterCategoryPos && (
                    <div style={{ marginTop: 6 }}>
                      <select
                        value={c.master_category_id || ""}
                        onChange={async (e) => {
                          const newMaster = e.target.value === "" ? null : parseInt(e.target.value);
                          // Optimistic local update
                          setCategories(prev => prev.map(x => x.id === c.id ? { ...x, master_category_id: newMaster } : x));
                          const r = await api(`/products/updateCategory/${c.id}`, { method: "POST", body: JSON.stringify({ master_category_id: newMaster }) });
                          if (!r.success) {
                            // Revert on error
                            setCategories(prev => prev.map(x => x.id === c.id ? { ...x, master_category_id: c.master_category_id } : x));
                            showToast(r.message || "Greška", "error");
                          }
                        }}
                        style={{ padding: "4px 8px", fontSize: 12, border: `1px solid ${S.border}`, borderRadius: 5, background: "white", cursor: "pointer" }}
                      >
                        <option value="">— Bez master kategorije —</option>
                        {masterCategories.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {/* "Redoslijed" — opens subview to drag products into desired display order
                    within this category. Only shown when category has 2+ products (1 product
                    has nothing to reorder, 0 products would be confusing). */}
                {productCount >= 2 && (
                  <button onClick={() => openReorder(c)} style={S.btnSm("#eff6ff", "#1d4ed8")} title="Redoslijed proizvoda u ovoj kategoriji"><LayoutGrid size={12} />Redoslijed</button>
                )}
                <button onClick={() => setEditCat({ id: c.id, name: c.name, description: c.description || "", sort_order: c.sort_order || 0, is_active: c.is_active })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} />Uredi</button>
                <button onClick={() => deleteCategory(c.id)} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* ───── Kategorije → Redoslijed subview ─────
           Replaces the categories list when reorderingCategory is set. Shows every product
           in the chosen category with up/down arrows. The list reflects local working copy
           (reorderIds), persisted only on Save. Back button discards local changes. */}
      {tab === "categories" && reorderingCategory && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <button onClick={closeReorder} style={S.btnSm("#f3f4f6", S.muted)}><ArrowLeft size={13} />Nazad</button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Redoslijed: {reorderingCategory.name}</div>
              <div style={{ fontSize: 12, color: S.muted }}>Pomjerite proizvode strelicama. Sačuvajte da bi se primijenilo u POS-u.</div>
            </div>
            <button onClick={saveReorder} disabled={reorderSaving} style={{ ...S.btn(S.green), opacity: reorderSaving ? 0.6 : 1 }}><Save size={14} />{reorderSaving ? "..." : "Sačuvaj"}</button>
          </div>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
            {reorderIds.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: S.muted, background: "#f9fafb", borderRadius: 8 }}>
                <Package size={30} /><p style={{ marginTop: 8 }}>Nema proizvoda u ovoj kategoriji.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {reorderIds.map((id, idx) => {
                  const p = products.find(pr => pr.id === id);
                  if (!p) return null; // product was deleted while subview open — skip silently
                  const gross = parseFloat(p.price) * (1 + (parseFloat(p.vat_rate || 21) / 100));
                  return (
                    <div key={id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, border: `1px solid ${S.border}` }}>
                      <span style={{ fontSize: 14, color: S.muted, fontWeight: 600, minWidth: 28 }}>#{idx + 1}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                        <div style={{ fontSize: 13, color: S.muted }}>{fmt(gross)} €</div>
                      </div>
                      <button onClick={() => moveReorderItem(id, "up")} disabled={idx === 0} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "4px 8px", opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
                      <button onClick={() => moveReorderItem(id, "down")} disabled={idx === reorderIds.length - 1} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "4px 8px", opacity: idx === reorderIds.length - 1 ? 0.3 : 1 }}>↓</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ FAST MENU TAB ═══ */}
      {tab === "fastmenu" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 4px" }}>⚡ Brzi Meni</h3>
            <p style={{ fontSize: 14, color: S.muted, margin: "0 0 16px" }}>Proizvodi koji se pojavljuju na prvom tabu POS ekrana</p>
            {fastMenu.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: S.muted, background: "#f9fafb", borderRadius: 8 }}><Package size={30} /><p style={{ marginTop: 8 }}>Prazan. Dodajte iz tab-a Proizvodi.</p></div>
            : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {fastMenu.map((id, idx) => { const p = products.find(pr => pr.id === id); if (!p) return null; const gross = parseFloat(p.price) * 1.21; return (
                <div key={id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, border: `1px solid ${S.border}` }}>
                  <span style={{ fontSize: 14, color: S.muted, fontWeight: 600, minWidth: 24 }}>#{idx + 1}</span>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div><div style={{ fontSize: 13, color: S.muted }}>{fmt(gross)} €</div></div>
                  <button onClick={() => moveFastItem(id, "up")} disabled={idx === 0} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "4px 8px", opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveFastItem(id, "down")} disabled={idx === fastMenu.length - 1} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "4px 8px", opacity: idx === fastMenu.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => toggleFastMenu(id)} style={S.btnSm("#fef2f2", S.red)}><X size={13} /></button>
                </div>
              ); })}
            </div>}
          </div>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 22, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>Dodaj u brzi meni</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {products.filter(p => !fastMenu.includes(p.id)).map(p => <button key={p.id} onClick={() => toggleFastMenu(p.id)} style={{ ...S.btnSm("#f3f4f6", S.dark), borderRadius: 20 }}><PlusCircle size={13} />{p.name}</button>)}
              {products.filter(p => !fastMenu.includes(p.id)).length === 0 && <span style={{ fontSize: 14, color: S.muted }}>Svi su dodani</span>}
            </div>
          </div>
        </div>
      )}

      {/* ═══ PARTNERS TAB ═══ */}
      {tab === "partners" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{partners.length} partnera</span>
            <button onClick={() => setEditPartner({ name: "", tax_id: "", partner_type: "customer", email: "", phone: "", address: "", city: "", postal_code: "", country: "Montenegro", bank_name: "", bank_account: "", notes: "" })} style={S.btn(S.green)}><PlusCircle size={15} />Novi partner</button>
          </div>

          {partnerDetail && (
            <div style={{ background: "white", border: `1px solid ${S.border}`, borderRadius: 10, padding: 16, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div><span style={{ fontWeight: 700, fontSize: 16 }}>{partnerDetail.name}</span>{partnerDetail.tax_id && <span style={{ marginLeft: 8, fontSize: 13, color: S.muted, fontFamily: S.mono }}>PIB: {partnerDetail.tax_id}</span>}</div>
                <button onClick={() => setPartnerDetail(null)} style={S.btnSm("#f3f4f6", S.muted)}><X size={14} /></button>
              </div>
              {partnerDetail.stats ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  {[
                    { l: "Ovaj mjesec", c: partnerDetail.stats.month_count, v: partnerDetail.stats.month_total },
                    { l: "Ova godina", c: partnerDetail.stats.year_count, v: partnerDetail.stats.year_total },
                    { l: "Sve vrijeme", c: partnerDetail.stats.all_count, v: partnerDetail.stats.all_total }
                  ].map((s, i) => (
                    <div key={i} style={{ background: "#f9fafb", borderRadius: 8, padding: 12, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: S.muted, fontWeight: 600, marginBottom: 4 }}>{s.l}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: S.green }}>{fmt(s.v || 0)} €</div>
                      <div style={{ fontSize: 12, color: S.muted }}>{s.c || 0} računa</div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ textAlign: "center", color: S.muted, fontSize: 13 }}>Nema podataka</div>}
            </div>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, background: "white", borderRadius: 10, overflow: "hidden", border: `1px solid ${S.border}` }}>
            <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={th}>PIB</th><th style={th}>Grad</th><th style={th}>Email</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
            <tbody>{partners.map(p => (
              <tr key={p.id} onClick={() => loadPartnerDetail(p)} style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: partnerDetail?.id === p.id ? "#eff6ff" : "white" }}>
                <td style={{ ...td, fontWeight: 600 }}>{p.name}</td>
                <td style={{ ...td, fontFamily: S.mono, fontSize: 13 }}>{p.tax_id || "-"}</td>
                <td style={{ ...td, color: S.muted }}>{p.city || "-"}</td>
                <td style={{ ...td, color: S.muted, fontSize: 13 }}>{p.email || "-"}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button onClick={() => setEditPartner({ id: p.id, name: p.name, tax_id: p.tax_id || "", partner_type: p.partner_type || "customer", email: p.email || "", phone: p.phone || "", address: p.address || "", city: p.city || "", postal_code: p.postal_code || "", country: p.country || "Montenegro", bank_name: p.bank_name || "", bank_account: p.bank_account || "", notes: p.notes || "" })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} /></button>
                    <button onClick={() => deletePartner(p.id)} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* ═══ PRODUCT EDIT MODAL ═══ */}
      {editProd && <Modal onClose={() => setEditProd(null)} title={editProd.id ? "Uredi proizvod" : "Novi proizvod"}>
        {inputRow("Naziv *", "name", "text", "npr. Coca Cola 0.33L")}
        {/* Price input — user picks Neto or Bruto entry mode.
            Storage is always Neto at 2 decimals (DB column decimal(10,2)).
            
            When user types Bruto and exact value can't be achieved at 2-dec
            Neto (e.g. Bruto=4.00 with PDV=21% has no clean Neto), we
            ALWAYS ROUND DOWN — customer pays less, never more than expected.
            
            Example: Bruto typed 4.00 with PDV=21% →
              Neto stored = 3.30 (floor of 4.00/1.21 = 3.305...)
              Final Bruto = 3.30 * 1.21 = 3.99 (notification shown to user)
            For PDV=7% or 15%: Bruto 4.00 IS achievable (Neto 3.74 or 3.48).
        */}
        <div style={{ display: "flex", gap: 10, marginBottom: 6, alignItems: "center" }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Unos cijene:</label>
          <div style={{ display: "flex", gap: 0, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setEditProd(p => ({ ...p, _priceMode: "neto", _brutoSnizen: undefined }))}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 600,
                background: (editProd._priceMode || "neto") === "neto" ? S.green : "white",
                color: (editProd._priceMode || "neto") === "neto" ? "white" : S.muted,
                border: "none",
                cursor: "pointer",
              }}
            >Neto</button>
            <button
              type="button"
              onClick={() => setEditProd(p => ({ ...p, _priceMode: "bruto", _brutoSnizen: undefined }))}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 600,
                background: editProd._priceMode === "bruto" ? S.green : "white",
                color: editProd._priceMode === "bruto" ? "white" : S.muted,
                border: "none",
                borderLeft: `1px solid ${S.border}`,
                cursor: "pointer",
              }}
            >Bruto</button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          {(editProd._priceMode || "neto") === "neto" ? (
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Neto cijena (€) *</label>
              <input
                type="number"
                step="0.01"
                value={editProd.price}
                onChange={e => setEditProd(p => ({ ...p, price: e.target.value, _brutoSnizen: undefined }))}
                style={S.input}
              />
              {editProd.price !== "" && editProd.price != null && !isNaN(parseFloat(editProd.price)) && (
                <div style={{ fontSize: 11, color: S.muted, marginTop: 4 }}>
                  Bruto: <strong style={{ color: S.green, fontSize: 13 }}>{(parseFloat(editProd.price) * (1 + (Number(editProd.vat_rate) || 0) / 100)).toFixed(2)} €</strong>
                </div>
              )}
            </div>
          ) : (
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Bruto cijena (€) *</label>
              <input
                type="number"
                step="0.01"
                value={editProd._brutoTyped !== undefined ? editProd._brutoTyped : (editProd.price === "" || editProd.price == null ? "" : (parseFloat(editProd.price) * (1 + (Number(editProd.vat_rate) || 0) / 100)).toFixed(2))}
                onChange={e => {
                  const v = e.target.value;
                  const r = Number(editProd.vat_rate) || 0;
                  const typedBruto = parseFloat(v);
                  if (v === "" || isNaN(typedBruto)) {
                    setEditProd(p => ({ ...p, price: "", _brutoTyped: v, _brutoSnizen: undefined }));
                    return;
                  }
                  const factor = 1 + r / 100;
                  const exactNeto = typedBruto / factor;
                  // Try standard candidates: round, floor, ceil
                  const candidates = [
                    Math.round(exactNeto * 100) / 100,
                    Math.floor(exactNeto * 100) / 100,
                    Math.ceil(exactNeto * 100) / 100,
                  ];
                  let chosenNeto = null;
                  for (const n of candidates) {
                    const brutoBack = Math.round(n * factor * 100) / 100;
                    if (brutoBack === typedBruto) { chosenNeto = n; break; }
                  }
                  if (chosenNeto !== null) {
                    // Exact match
                    setEditProd(p => ({ ...p, price: chosenNeto.toFixed(2), _brutoTyped: v, _brutoSnizen: undefined }));
                  } else {
                    // No exact match — round DOWN (customer-friendly)
                    const neto = Math.floor(exactNeto * 100) / 100;
                    const finalBruto = Math.round(neto * factor * 100) / 100;
                    setEditProd(p => ({ ...p, price: neto.toFixed(2), _brutoTyped: v, _brutoSnizen: finalBruto }));
                  }
                }}
                style={S.input}
              />
              {editProd._brutoSnizen !== undefined ? (
                <div style={{ fontSize: 11, color: "#b45309", marginTop: 4, padding: "4px 8px", background: "#fef3c7", borderRadius: 6, border: "1px solid #fbbf24" }}>
                  ⚠️ Bruto cijena nije moguća zbog 2-decimalnog PDV zaokruživanja. Snižena na <strong>{editProd._brutoSnizen.toFixed(2)} €</strong> (Neto: {parseFloat(editProd.price).toFixed(2)} €)
                </div>
              ) : (editProd.price !== "" && editProd.price != null && !isNaN(parseFloat(editProd.price)) && (
                <div style={{ fontSize: 11, color: S.muted, marginTop: 4 }}>
                  Neto: <strong style={{ color: S.dark, fontSize: 13 }}>{parseFloat(editProd.price).toFixed(2)} €</strong>
                </div>
              ))}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>PDV %</label>
            <input
              type="number"
              value={editProd.vat_rate}
              onChange={e => {
                const newR = e.target.value;
                // When PDV changes, recompute Bruto-side hint and re-check round-trip
                setEditProd(p => {
                  const r = Number(newR) || 0;
                  const factor = 1 + r / 100;
                  if (p._priceMode === "bruto" && p._brutoTyped !== undefined && !isNaN(parseFloat(p._brutoTyped))) {
                    // User in Bruto mode — recompute Neto from typed Bruto
                    const typedBruto = parseFloat(p._brutoTyped);
                    const exactNeto = typedBruto / factor;
                    const candidates = [
                      Math.round(exactNeto * 100) / 100,
                      Math.floor(exactNeto * 100) / 100,
                      Math.ceil(exactNeto * 100) / 100,
                    ];
                    let chosenNeto = null;
                    for (const n of candidates) {
                      const brutoBack = Math.round(n * factor * 100) / 100;
                      if (brutoBack === typedBruto) { chosenNeto = n; break; }
                    }
                    if (chosenNeto !== null) {
                      return { ...p, vat_rate: newR, price: chosenNeto.toFixed(2), _brutoSnizen: undefined };
                    } else {
                      const neto = Math.floor(exactNeto * 100) / 100;
                      const finalBruto = Math.round(neto * factor * 100) / 100;
                      return { ...p, vat_rate: newR, price: neto.toFixed(2), _brutoSnizen: finalBruto };
                    }
                  }
                  // Neto mode — Neto stays, Bruto display recalculates automatically
                  return { ...p, vat_rate: newR, _brutoSnizen: undefined };
                });
              }}
              style={S.input}
            />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Jedinica mjere</label>
          <select value={editProd.unit} onChange={e => setEditProd(p => ({ ...p, unit: e.target.value }))} style={S.input}>
            <option value="kom">kom</option>
            <option value="kg">kg</option>
            <option value="l">l</option>
            <option value="m">m</option>
            <option value="sub">sub</option>
          </select>
        </div>
        {/* is_decimal_unit: when checked, the POS cart shows a tappable quantity that
            opens a decimal numpad (e.g. for fish sold by 0.77 kg). When unchecked
            (default), only integer quantity via +/- stepper. The flag is per-product
            so a Coca-Cola can't accidentally be sold as "0.5". */}
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
            <input type="checkbox" checked={!!editProd.is_decimal_unit} onChange={e => setEditProd(p => ({ ...p, is_decimal_unit: e.target.checked ? 1 : 0 }))} style={{ width: 18, height: 18, accentColor: S.accent }} />
            <span style={{ fontWeight: 600 }}>Decimalna količina</span>
          </label>
          <span style={{ fontSize: 12, color: S.muted }}>Dozvoli npr. 0.77 (kg, l, m)</span>
        </div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Kategorija</label><select value={editProd.category_id} onChange={e => setEditProd(p => ({ ...p, category_id: e.target.value }))} style={S.input}><option value="">-- Bez kategorije --</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
            <input type="checkbox" checked={!!editProd.track_stock} onChange={e => setEditProd(p => ({ ...p, track_stock: e.target.checked }))} style={{ width: 18, height: 18, accentColor: S.accent }} />
            <span style={{ fontWeight: 600 }}>Praćenje zaliha</span>
          </label>
          <span style={{ fontSize: 12, color: S.muted }}>Automatski oduzima sa lagera</span>
        </div>
        {editProd.track_stock && <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Količina na stanju</label><input type="number" step="1" value={editProd.stock_quantity ?? 0} onChange={e => setEditProd(p => ({ ...p, stock_quantity: e.target.value }))} style={S.input} /></div>}
        {inputRow("SKU", "sku", "text")}
        {inputRow("Barkod", "barcode", "text")}
        {inputRow("Opis", "description", "text")}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditProd(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={saveProduct} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editProd.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ CATEGORY EDIT MODAL ═══ */}
      {editCat && <Modal onClose={() => setEditCat(null)} title={editCat.id ? "Uredi kategoriju" : "Nova kategorija"}>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editCat.name} onChange={e => setEditCat(p => ({ ...p, name: e.target.value }))} style={S.input} /></div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Opis</label><input value={editCat.description} onChange={e => setEditCat(p => ({ ...p, description: e.target.value }))} style={S.input} /></div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Redoslijed</label><input type="number" value={editCat.sort_order} onChange={e => setEditCat(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditCat(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={saveCategory} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editCat.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ PARTNER EDIT MODAL ═══ */}
      {editPartner && <Modal onClose={() => setEditPartner(null)} title={editPartner.id ? "Uredi partnera" : "Novi partner"}>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editPartner.name} onChange={e => setEditPartner(p => ({ ...p, name: e.target.value }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>PIB</label><input value={editPartner.tax_id} onChange={e => setEditPartner(p => ({ ...p, tax_id: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip</label><select value={editPartner.partner_type} onChange={e => setEditPartner(p => ({ ...p, partner_type: e.target.value }))} style={S.input}><option value="customer">Kupac</option><option value="supplier">Dobavljač</option><option value="both">Oba</option></select></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label><input value={editPartner.email} onChange={e => setEditPartner(p => ({ ...p, email: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Telefon</label><input value={editPartner.phone} onChange={e => setEditPartner(p => ({ ...p, phone: e.target.value }))} style={S.input} /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 2 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Adresa</label><input value={editPartner.address} onChange={e => setEditPartner(p => ({ ...p, address: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Grad</label><input value={editPartner.city} onChange={e => setEditPartner(p => ({ ...p, city: e.target.value }))} style={S.input} /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Banka</label><input value={editPartner.bank_name} onChange={e => setEditPartner(p => ({ ...p, bank_name: e.target.value }))} style={S.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Žiro račun</label><input value={editPartner.bank_account} onChange={e => setEditPartner(p => ({ ...p, bank_account: e.target.value }))} style={S.input} /></div>
        </div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Napomena</label><input value={editPartner.notes} onChange={e => setEditPartner(p => ({ ...p, notes: e.target.value }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditPartner(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={savePartner} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editPartner.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ STOLOVI (TABLES) TAB ═══ */}
      {tab === "stolovi" && (
        <div>
          {/* Toggle */}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div><div style={{ fontWeight: 700, fontSize: 16 }}>Koristi stolove</div><div style={{ fontSize: 13, color: S.muted }}>Prikaži Stolovi sektor u navigaciji</div></div>
              <button onClick={async () => {
                const nv = !tablesEnabled;
                if (!nv) {
                  if (!window.confirm("Isključivanjem stolova izgubićete sve informacije o stolovima. Da li ste sigurni?")) return;
                }
                setTablesEnabled(nv);
                onTablesToggle && onTablesToggle(nv);
                await api("/tables/settings", { method: "POST", body: JSON.stringify({ tables_enabled: nv ? 1 : 0 }) });
                showToast(nv ? "Stolovi uključeni" : "Stolovi isključeni");
              }} style={{ width: 52, height: 28, borderRadius: 14, border: "none", cursor: "pointer", background: tablesEnabled ? S.green : "#d1d5db", position: "relative", transition: "background 0.2s" }}>
                <div style={{ width: 22, height: 22, borderRadius: 11, background: "white", position: "absolute", top: 3, left: tablesEnabled ? 27 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
              </button>
            </div>
          </div>

          {tablesEnabled && <>
            {/* Zones */}
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 20, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>🗺 Zone</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {zones.map(z => (
                  <div key={z.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "#f3f4f6", border: `1px solid ${S.border}` }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{z.name}</span>
                    <span style={{ fontSize: 11, color: S.muted }}>({rTables.filter(t => String(t.zone_id) === String(z.id)).length})</span>
                    {zones.length > 1 && <button onClick={async () => { if (!window.confirm(`Obrisati zonu "${z.name}"?`)) return; await api(`/tables/deleteZone/${z.id}`, { method: "POST" }); loadAll(); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 0, fontSize: 14 }}>×</button>}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input value={newZoneName} onChange={e => setNewZoneName(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && newZoneName.trim()) { api("/tables/zones", { method: "POST", body: JSON.stringify({ name: newZoneName.trim() }) }).then(() => { setNewZoneName(""); loadAll(); }); } }} placeholder="Nova zona (Terasa, Unutra, Plaža...)" style={{ ...S.input, flex: 1, padding: "8px 12px", fontSize: 13 }} />
                <button onClick={async () => { if (!newZoneName.trim()) return; await api("/tables/zones", { method: "POST", body: JSON.stringify({ name: newZoneName.trim() }) }); setNewZoneName(""); loadAll(); }} style={{ ...S.btn(S.accent), padding: "8px 16px", fontSize: 13 }}><Plus size={13} />Dodaj</button>
              </div>
            </div>

            {/* Quick add */}
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Brzo dodaj stolove</div>
                {zones.length > 0 && <select id="quick-add-zone" style={{ ...S.input, width: "auto", padding: "6px 10px", fontSize: 13 }}><option value="">Bez zone</option>{zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}</select>}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { type: "table", label: "Sto", icon: "🟫", seats: 4 },
                  { type: "bar", label: "Bar", icon: "🍸", seats: 2 },
                  { type: "couch", label: "Kauč", icon: "🛋", seats: 3 },
                  { type: "vip", label: "VIP", icon: "⭐", seats: 6 },
                  { type: "outdoor", label: "Terasa", icon: "☀", seats: 4 },
                  { type: "barber", label: "Berberska", icon: "💈", seats: 1 },
                  { type: "beach_bed", label: "Ležaljke", icon: "🏖", seats: 2 },
                  { type: "chair", label: "Stolica", icon: "🪑", seats: 0 },
                  { type: "wall", label: "Zid/Pregrada", icon: "🧱", seats: 0 },
                ].map(t => (
                  <button key={t.type} onClick={async () => {
                    const count = rTables.filter(tb => tb.table_type === t.type).length;
                    const name = t.type === "wall" ? `Zid ${count + 1}` : t.type === "chair" ? `Stolica ${count + 1}` : t.type === "barber" ? `Berberska ${count + 1}` : t.type === "beach_bed" ? `Ležaljka ${count + 1}` : `${t.label} ${count + 1}`;
                    const row = Math.floor(count / 5);
                    const col = count % 5;
                    const isWall = t.type === "wall";
                    const isChair = t.type === "chair";
                    const isBarber = t.type === "barber";
                    const isBeach = t.type === "beach_bed";
                    const sizes = { S: { w: 8, h: 7 }, M: { w: 14, h: 12 }, L: { w: 20, h: 16 } };
                    const sz = sizes.M;
                    const zoneEl = document.getElementById("quick-add-zone");
                    const zoneId = zoneEl?.value ? parseInt(zoneEl.value) : null;
                    const d = await api("/tables", { method: "POST", body: JSON.stringify({ name, table_type: t.type, seats: t.seats, x: 5 + col * 18, y: 5 + row * 16, width: isWall ? 22 : isChair ? 5 : isBarber ? 8 : isBeach ? 20 : sz.w, height: isWall ? 3 : isChair ? 5 : isBarber ? 8 : isBeach ? 10 : sz.h, shape: t.type === "outdoor" ? "circle" : isBarber ? "circle" : "rect", color: t.type === "vip" ? "#eab308" : t.type === "bar" ? "#8b5cf6" : t.type === "outdoor" ? "#22c55e" : t.type === "couch" ? "#f97316" : t.type === "wall" ? "#78716c" : t.type === "chair" ? "#a1a1aa" : t.type === "barber" ? "#ec4899" : t.type === "beach_bed" ? "#06b6d4" : "#4f46e5", zone_id: zoneId }) });
                    if (d.success) { showToast(`${name} dodat`); loadAll(); }
                  }} style={{ ...S.btn("#f3f4f6", S.dark), fontSize: 13 }}>{t.icon} + {t.label}</button>
                ))}
              </div>
            </div>

            {/* Tables list */}
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{rTables.length} stolova</span>
              </div>
              {rTables.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: S.muted }}>Nema stolova. Koristite dugmad iznad za brzo dodavanje.</div>
              : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={th}>Tip</th><th style={th}>Mjesta</th><th style={th}>Boja</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
                <tbody>{rTables.map(t => (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ ...td, fontWeight: 600 }}>{t.name}</td>
                    <td style={td}>{t.table_type === "table" ? "Sto" : t.table_type === "bar" ? "Bar" : t.table_type === "couch" ? "Kauč" : t.table_type === "vip" ? "VIP" : t.table_type === "wall" ? "Zid" : t.table_type === "chair" ? "Stolica" : "Terasa"}</td>
                    <td style={td}>{t.seats}</td>
                    <td style={td}><div style={{ width: 22, height: 22, borderRadius: 6, background: t.color || "#4f46e5" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button onClick={() => setEditTable({ ...t })} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} /></button>
                        <button onClick={async () => { if (!window.confirm("Obrisati " + t.name + "?")) return; const d = await api(`/tables/delete/${t.id}`, { method: "POST" }); if (d.success) { showToast("Obrisano"); loadAll(); } else showToast(d.message, "error"); }} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
            </div>
          </>}
        </div>
      )}

      {/* ═══ KITCHEN TAB ═══ */}
      {tab === "kitchen" && <KitchenSettingsTab showToast={showToast} />}

      {/* ═══ OPERATORS TAB ═══ */}
      {tab === "operators" && <StaffPinManager user={user} showToast={showToast} activeOperator={activeOperator} />}

      {/* ═══ IZGLED PRODAJE TAB (Nova prodaja layout customization) ═══ */}
      {tab === "izgled" && (
        <IzgledProdaje
          showToast={showToast}
          products={products}
          posLayout={posLayout}
          posTiles={posTiles}
          onLayoutChanged={onPosLayoutChanged}
        />
      )}

      {/* ═══ TABLE EDIT MODAL ═══ */}
      {editTable && <Modal onClose={() => setEditTable(null)} title="Uredi sto">
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editTable.name} onChange={e => setEditTable(p => ({ ...p, name: e.target.value }))} style={S.input} /></div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip</label><select value={editTable.table_type} onChange={e => setEditTable(p => ({ ...p, table_type: e.target.value }))} style={S.input}><option value="table">Sto</option><option value="bar">Bar</option><option value="couch">Kauč</option><option value="vip">VIP</option><option value="outdoor">Terasa</option><option value="barber">Berberska</option><option value="beach_bed">Ležaljke</option><option value="chair">Stolica</option><option value="wall">Zid/Pregrada</option></select></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Mjesta</label><input type="number" value={editTable.seats} onChange={e => setEditTable(p => ({ ...p, seats: parseInt(e.target.value) || 0 }))} style={S.input} /></div>
        </div>
        {zones.length > 0 && <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Zona</label><select value={editTable.zone_id || ""} onChange={e => setEditTable(p => ({ ...p, zone_id: e.target.value ? parseInt(e.target.value) : null }))} style={S.input}><option value="">Bez zone</option>{zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}</select></div>}
        {/* Size S/M/L */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Veličina</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "S", l: "S — Mali", w: 8, h: 7 }, { id: "M", l: "M — Srednji", w: 14, h: 12 }, { id: "L", l: "L — Veliki", w: 20, h: 16 }, { id: "XL", l: "XL — Extra", w: 28, h: 22 }, { id: "XXL", l: "XXL — Max", w: 38, h: 30 }].map(sz => {
              const isH = (editTable.width || 14) > (editTable.height || 12);
              const curW = parseFloat(editTable.width) || 14;
              const curH = parseFloat(editTable.height) || 12;
              const isActive = (Math.abs(curW - (isH ? sz.w : sz.h)) < 3 && Math.abs(curH - (isH ? sz.h : sz.w)) < 3) || (Math.abs(curW - sz.w) < 3 && Math.abs(curH - sz.h) < 3);
              return <button key={sz.id} onClick={() => {
                const isHoriz = (editTable.width || 14) >= (editTable.height || 12);
                setEditTable(p => ({ ...p, width: isHoriz ? sz.w : sz.h, height: isHoriz ? sz.h : sz.w }));
              }} style={{ flex: 1, padding: "8px 6px", border: "2px solid", borderColor: isActive ? S.accent : S.border, background: isActive ? "#eef2ff" : "white", color: isActive ? S.accent : S.dark, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "center" }}>{sz.id}<div style={{ fontSize: 10, fontWeight: 400, color: S.muted, marginTop: 2 }}>{sz.l}</div></button>;
            })}
          </div>
        </div>
        {/* Direction H/V */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Smjer</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "H", l: "↔ Horizontalno" }, { id: "V", l: "↕ Vertikalno" }].map(dir => {
              const curW = parseFloat(editTable.width) || 14;
              const curH = parseFloat(editTable.height) || 12;
              const isH = curW >= curH;
              const isActive = (dir.id === "H" && isH) || (dir.id === "V" && !isH);
              return <button key={dir.id} onClick={() => {
                const newRot = dir.id === "H" ? 0 : 90;
                setEditTable(p => ({ ...p, width: p.height, height: p.width, rotation: newRot }));
              }} style={{ flex: 1, padding: "9px 8px", border: "2px solid", borderColor: isActive ? S.accent : S.border, background: isActive ? "#eef2ff" : "white", color: isActive ? S.accent : S.dark, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{dir.l}</button>;
            })}
          </div>
        </div>
        {/* Facing direction (for couch, chair, bar) */}
        {(editTable.table_type === "couch" || editTable.table_type === "chair" || editTable.table_type === "bar" || editTable.table_type === "barber" || editTable.table_type === "beach_bed") && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Okretanje (strana)</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ rot: 0, l: "⬆ Gore" }, { rot: 90, l: "➡ Desno" }, { rot: 180, l: "⬇ Dole" }, { rot: 270, l: "⬅ Lijevo" }].map(f => {
                const curRot = parseInt(editTable.rotation) || 0;
                const isActive = curRot === f.rot;
                return <button key={f.rot} onClick={() => {
                  setEditTable(p => ({ ...p, rotation: f.rot }));
                }} style={{ flex: 1, padding: "8px 4px", border: "2px solid", borderColor: isActive ? S.accent : S.border, background: isActive ? "#eef2ff" : "white", color: isActive ? S.accent : S.dark, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{f.l}</button>;
              })}
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Oblik</label><select value={editTable.shape} onChange={e => setEditTable(p => ({ ...p, shape: e.target.value }))} style={S.input}><option value="rect">Pravougaonik</option><option value="circle">Krug</option></select></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Boja</label><input type="color" value={editTable.color || "#4f46e5"} onChange={e => setEditTable(p => ({ ...p, color: e.target.value }))} style={{ ...S.input, padding: 4, height: 42 }} /></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditTable(null)} style={{ ...S.btn("#f3f4f6", S.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={async () => {
            setSaving(true);
            const d = await api(`/tables/update/${editTable.id}`, { method: "POST", body: JSON.stringify(editTable) });
            setSaving(false);
            if (d.success) { showToast("Sto ažuriran"); setEditTable(null); loadAll(); } else showToast(d.message, "error");
          }} disabled={saving} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />Sačuvaj</>}</button>
        </div>
      </Modal>}
    </div>
  );
}

// ═══ STOLOVI VIEW (Restaurant tables) ═══
function StoloviView({ user, showToast, onOrderForTable, activeOperator }) {
  const [tables, setTables] = useState([]);
  const [zones, setZones] = useState([]);
  const [activeZone, setActiveZone] = useState(null); // zone id, set on load
  const zoneInitRef = useRef(false);

  // Filter zones by operator's allowed_zones (empty = all, main admin = all)
  const visibleZones = (!activeOperator || activeOperator.is_main_admin || !activeOperator.allowed_zones || activeOperator.allowed_zones.length === 0)
    ? zones
    : zones.filter(z => activeOperator.allowed_zones.includes(z.id));
  const [loading, setLoading] = useState(true);
  const [subView, setSubView] = useState("layout"); // layout | editor | history
  const [selectedTable, setSelectedTable] = useState(null);
  const [openModal, setOpenModal] = useState(null); // table to open session
  const [guestCount, setGuestCount] = useState(2);
  const [addItemsSession, setAddItemsSession] = useState(null); // session for adding items
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [prodSearch, setProdSearch] = useState("");
  // Master categories (HRANA/PIĆE) — same feature as POSView. Per user spec:
  // Stolovi has HRANA/PIĆE toggle only, no SVE button. Categories without a
  // master assignment are INVISIBLE in Stolovi (admin must assign them).
  const [masterCategoryPos, setMasterCategoryPos] = useState(false);
  const [masterCategories, setMasterCategories] = useState([]);
  const [activeMaster, setActiveMaster] = useState(null);
  const [sessionCart, setSessionCart] = useState([]);
  // qtyModal — same as other carts: opens decimal numpad for is_decimal_unit products.
  const [qtyModal, setQtyModal] = useState(null);
  const [editTagsFor, setEditTagsFor] = useState(null);
  const [customTag, setCustomTag] = useState(""); // custom tag input value
  const [closeModal, setCloseModal] = useState(null);
  const [payMethod, setPayMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  // Partner-link state for closeFiscalize when card or split. Same shape as POSView's
  // cardPartner/cardManualPartner. Cleared when closeModal closes.
  const [closePartner, setClosePartner] = useState(null);
  const [closeManualPartner, setCloseManualPartner] = useState(null);
  const [showClosePartnerPicker, setShowClosePartnerPicker] = useState(false);
  const [stoloviNumPad, setStoloviNumPad] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [transferModal, setTransferModal] = useState(null);
  // mergeModal: holds the source session when user clicks Spoji. When set, modal
  // shows other busy tables to pick a destination. Same shape as transferModal.
  const [mergeModal, setMergeModal] = useState(null);
  // partialPayMode: when truthy, the order list in the side panel renders with
  // checkboxes + a "Naplati odabrano" footer button. Picked order IDs go to
  // closeFiscalizePartial. null/false = normal view.
  const [partialPayMode, setPartialPayMode] = useState(false);
  const [partialPayIds, setPartialPayIds] = useState([]);
  const [partialPayProcessing, setPartialPayProcessing] = useState(false);
  // New: order-based system
  const [sessionOrders, setSessionOrders] = useState([]); // fiscalized orders for selected table
  const [expandedOrder, setExpandedOrder] = useState(null); // expanded order in sidebar
  const [closedHistory, setClosedHistory] = useState([]); // closed tables last 24h
  const [orderDiscount, setOrderDiscount] = useState({ type: "none", value: 0 });
  const [showCloseDiscount, setShowCloseDiscount] = useState(false);
  // Editor drag state
  const [dragging, setDragging] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const gridRef = useRef(null);

  const loadTables = async () => {
    const [d, zd] = await Promise.all([api("/tables"), api("/tables/zones")]);
    let zoneList = zd.success ? (zd.data?.zones || []) : [];

    // Auto-create "Glavni" zone if no zones exist
    if (zoneList.length === 0) {
      const cr = await api("/tables/zones", { method: "POST", body: JSON.stringify({ name: "Glavni" }) });
      if (cr.success) {
        const zd2 = await api("/tables/zones");
        zoneList = zd2.success ? (zd2.data?.zones || []) : [];
      }
    }

    if (d.success) {
      const newTables = d.data?.tables || [];

      // Auto-assign unassigned tables to first zone
      if (zoneList.length > 0) {
        const firstZoneId = zoneList[0].id;
        const unassigned = newTables.filter(t => !t.zone_id);
        for (const t of unassigned) {
          await api(`/tables/update/${t.id}`, { method: "POST", body: JSON.stringify({ zone_id: firstZoneId }) });
          t.zone_id = firstZoneId;
        }
      }

      setTables(newTables);
      // Update selectedTable with fresh data if one is selected
      if (selectedTable) {
        const updated = newTables.find(t => t.id === selectedTable.id);
        if (updated) setSelectedTable(updated);
        else setSelectedTable(null);
      }
    }
    if (zd.success) {
      const zoneList = zd.data?.zones || [];
      setZones(zoneList);
      if (!zoneInitRef.current && zoneList.length > 0) {
        // Select first visible zone based on operator permissions
        const opZones = (!activeOperator || activeOperator.is_main_admin || !activeOperator.allowed_zones || activeOperator.allowed_zones.length === 0)
          ? zoneList
          : zoneList.filter(z => activeOperator.allowed_zones.includes(z.id));
        if (opZones.length > 0) setActiveZone(opZones[0].id);
        else if (zoneList.length > 0) setActiveZone(zoneList[0].id);
        zoneInitRef.current = true;
      }
    }
    setLoading(false);
  };

  const loadProducts = async () => {
    const [p, c] = await Promise.all([api("/products"), api("/products/categories")]);
    if (p.success) setProducts(p.data?.products || []);
    if (c.success) setCategories(c.data?.categories || []);
  };

  useEffect(() => { loadTables(); loadProducts(); loadPosSettings(); }, []);

  const loadPosSettings = async () => {
    const r = await api("/company/posSettings");
    if (r.success) {
      const enabled = !!r.data?.master_category_pos;
      const masters = r.data?.master_categories || [];
      setMasterCategoryPos(enabled);
      setMasterCategories(masters);
      // When ON, default to first master (HRANA per auto-seed sort_order=1).
      if (enabled && masters.length > 0) {
        setActiveMaster(prev => prev === null ? masters[0].id : prev);
      }
    }
  };
  // Auto refresh only in layout mode (NOT in editor mode — would reset drag positions)
  useEffect(() => {
    if (subView === "editor") return;
    const iv = setInterval(loadTables, 15000);
    return () => clearInterval(iv);
  }, [subView]);
  // Reset partial-pay mode when navigating away from a table. Prevents the
  // selection from leaking into a different table the user clicks next.
  useEffect(() => {
    setPartialPayMode(false);
    setPartialPayIds([]);
  }, [selectedTable?.id]);

  const openSession = async (table) => {
    setProcessing(true);
    const d = await api("/tables/openSession", { method: "POST", body: JSON.stringify({ table_id: table.id, guest_count: guestCount, tcr_code: getTcr() }) });
    setProcessing(false);
    if (d.success) { showToast(`${table.name} otvoren`); setOpenModal(null); loadTables(); }
    else showToast(d.message || "Greška", "error");
  };

  const addItemsToSession = async (sessionId) => {
    if (sessionCart.length === 0) return;
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    setProcessing(true);
    // Check internal day gate
    const orderTcr = getTcr();
    if (orderTcr) {
      try {
        const idCheck = await api("/internalday/settings");
        if (idCheck.success && idCheck.data?.internal_day_enabled) {
          const idStatus = await api(`/internalday/status/${orderTcr}`);
          if (idStatus.success && !idStatus.data?.day) {
            setProcessing(false);
            showToast("Interni dan nije otvoren! Otvorite dan u Blagajni.", "error");
            return;
          }
        }
      } catch(e) {}
    }
    const items = sessionCart.map(c => ({
      product_id: c.product_id || c.id,
      name: c.name,
      quantity: c.quantity,
      unit_price: c.custom_price !== null ? c.custom_price : c.price,
      vat_rate: Number(c.vat_rate != null ? c.vat_rate : 21),
      unit: c.unit || "kom",
      discount_type: c.discount_type || "none",
      discount_value: c.discount_value || 0,
    }));
    const d = await api(`/tables/orderFiscalize/${sessionId}`, { method: "POST", body: JSON.stringify({
      items,
      location_id: user?.location_id || 1,
      tcr_code: getTcr(),
      discount_type: orderDiscount.type,
      discount_value: orderDiscount.value,
      ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code })
    }) });
    setProcessing(false);
    if (d.success) {
      showToast("Narudžba #" + (d.data?.order_number || "") + " fiskalizovana!");
      const cartSnap = [...sessionCart];
      setSessionCart([]);
      setAddItemsSession(null);
      setOrderDiscount({ type: "none", value: 0 });
      // Send kitchen items
      sendToKitchen(cartSnap, d.data?.invoice?.id, d.data?.invoice?.invoice_number, selectedTable?.name || "");
      // Auto-print the order receipt (non-blocking)
      const invoiceId = d.data?.invoice?.id;
      if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        printReceipt(invoiceId).catch(() => {});
      }
      // Reload tables and orders
      loadTables();
      if (selectedTable?.session) loadSessionOrders(sessionId);
    }
    else showToast(d.message || "Greška pri fiskalizaciji narudžbe", "error");
  };

  const loadSessionOrders = async (sessionId) => {
    const d = await api(`/tables/orderHistory/${sessionId}`);
    if (d.success) setSessionOrders(d.data?.orders || []);
  };

  const loadClosedHistory = async () => {
    const d = await api("/tables/closedHistory");
    if (d.success) setClosedHistory(d.data?.sessions || []);
  };

  const removeItem = async (sessionId, itemId) => {
    const d = await api(`/tables/sessionRemove/${sessionId}`, { method: "POST", body: JSON.stringify({ item_id: itemId }) });
    if (d.success) loadTables();
    else showToast(d.message, "error");
  };

  const closeSession = async (sessionId) => {
    if (!activeOperator?.operator_code) {
      showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error");
      return;
    }
    setProcessing(true);
    try {
      // Partner fields — only sent when payment is card or split AND a partner is
      // linked. Backend defaults to NULL/empty when not provided (backwards compat).
      const partnerFields = ((payMethod === "card" || payMethod === "split") && (closePartner || closeManualPartner)) ? {
        partner_id: closePartner?.id || null,
        buyer_name: closePartner?.name || closeManualPartner?.name || "",
        buyer_pib: closePartner?.tax_id || closeManualPartner?.tax_id || "",
        buyer_address: closePartner?.address || "",
        buyer_city: closePartner?.city || "",
      } : {};

      // Use new closeFiscalize endpoint — creates summary invoice with SumInv refs
      const d = await api(`/tables/closeFiscalize/${sessionId}`, { method: "POST", body: JSON.stringify({
        payment_method: payMethod,
        ...(payMethod === "split" && { split_cash: parseFloat(splitCash) || 0, split_card: parseFloat(splitCard) || 0 }),
        ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code }),
        discount_type: orderDiscount.type,
        discount_value: orderDiscount.value,
        ...partnerFields,
      }) });

      if (!d.success) {
        showToast(d.message || "Greška pri zatvaranju stola", "error");
        setProcessing(false);
        return;
      }

      const inv = d.data?.invoice;
      showToast(`Sto zatvoren! Zbirni račun ${inv?.invoice_number || ""} fiskalizovan!`);

      // Auto-print summary receipt (non-blocking)
      const invoiceId = inv?.id;
      if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
        printReceipt(invoiceId).catch(() => {});
      }

      // Clear partner link so it doesn't leak to the next close
      setClosePartner(null); setCloseManualPartner(null);
      setCloseModal(null); setSelectedTable(null); setSessionOrders([]); loadTables();
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setProcessing(false);
  };

  const transferSession = async (sessionId, newTableId) => {
    const d = await api(`/tables/sessionTransfer/${sessionId}`, { method: "POST", body: JSON.stringify({ new_table_id: newTableId }) });
    if (d.success) { showToast("Premješteno!"); setTransferModal(null); setSelectedTable(null); loadTables(); }
    else showToast(d.message, "error");
  };

  // Spoji (merge): rebinds source session's items + order receipts to target.
  // Backend: POST /tables/sessionMerge/<sourceSessionId>  body { target_session_id }
  // After success: source table goes free, target table holds combined orders.
  // Existing fiscalized order receipts keep their original IIC/FIC/TCR/BU codes —
  // only their session FK changes (operational, not fiscal).
  const mergeSession = async (sourceSessionId, targetSessionId) => {
    if (!window.confirm("Spojiti narudžbe? Izvorni sto će postati slobodan, a sve narudžbe će se prebaciti na ciljni sto.")) return;
    const d = await api(`/tables/sessionMerge/${sourceSessionId}`, { method: "POST", body: JSON.stringify({ target_session_id: targetSessionId }) });
    if (d.success) {
      showToast("Spojeno!");
      setMergeModal(null);
      setSelectedTable(null);
      loadTables();
    } else {
      showToast(d.message || "Greška pri spajanju", "error");
    }
  };

  // Naplati djelimično: fiscalize a summary covering only the picked order receipts.
  // Backend: POST /tables/closeFiscalizePartial/<sessionId> body { order_invoice_ids, ... }
  // Selected orders get summary_invoice_id=<new id> server-side; remaining orders
  // stay open on the session for a later round of payment. If picked = all unpaid,
  // session auto-closes server-side.
  const closePartial = async (sessionId, orderIds, paymentMethod) => {
    if (!orderIds || orderIds.length === 0) {
      showToast("Odaberite barem jednu narudžbu", "error");
      return;
    }
    const opCode = activeOperator?.operator_code || "";
    setPartialPayProcessing(true);
    try {
      const body = {
        order_invoice_ids: orderIds,
        payment_method: paymentMethod,
        operator_code: opCode,
        // Discount intentionally omitted for partial pay v1 — discount UX would
        // need its own modal + recalc preview. Existing closeFiscalize keeps full
        // discount support; partial pay starts simple. Can be extended later.
        discount_type: "none",
        discount_value: 0,
      };
      const d = await api(`/tables/closeFiscalizePartial/${sessionId}`, { method: "POST", body: JSON.stringify(body) });
      if (d.success) {
        const closedNow = d.data?.session_closed;
        showToast(closedNow ? "Sve naplaćeno, sto zatvoren!" : `Naplaćeno (preostalo: ${d.data?.remaining_orders ?? 0})`);
        // Try to print the new summary invoice (best-effort, don't block)
        const invId = d.data?.invoice?.id;
        if (invId && isElectron && window.electronAPI) {
          printReceipt(invId).catch(() => {});
        }
        setPartialPayMode(false);
        setPartialPayIds([]);
        if (closedNow) {
          // Session is gone — go back to grid
          setSelectedTable(null);
          setSessionOrders([]);
        } else {
          // Session still active — reload its order list to refresh UI
          // (paid orders should now appear greyed-out with NAPLAĆENO badge).
          if (sessionId) loadSessionOrders(sessionId);
          loadTables();
        }
      } else {
        showToast(d.message || "Greška pri naplati", "error");
      }
    } finally {
      setPartialPayProcessing(false);
    }
  };

  const cancelSession = async (sessionId) => {
    if (!window.confirm("Otkazati sesiju? Svi artikli će biti izgubljeni.")) return;
    const d = await api(`/tables/sessionCancel/${sessionId}`, { method: "POST" });
    if (d.success) { showToast("Sesija otkazana"); setSelectedTable(null); loadTables(); }
    else showToast(d.message || "Greška", "error");
  };

  // Editor: drag handlers
  const handleEditorMouseDown = (e, table) => {
    if (subView !== "editor") return;
    e.preventDefault();
    setDragging(table.id);
    const rect = gridRef.current.getBoundingClientRect();
    setDragStart({ mx: e.clientX, my: e.clientY, ox: table.x, oy: table.y });
  };
  const handleEditorMouseMove = (e) => {
    if (!dragging || !dragStart || !gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragStart.mx) / rect.width) * 100;
    const dy = ((e.clientY - dragStart.my) / rect.height) * 100;
    setTables(prev => prev.map(t => t.id == dragging ? { ...t, x: Math.max(0, Math.min(88, dragStart.ox + dx)), y: Math.max(0, Math.min(88, dragStart.oy + dy)) } : t));
  };
  const handleEditorMouseUp = () => { setDragging(null); setDragStart(null); };
  const saveLayout = async () => {
    const payload = tables.map(t => ({ id: t.id, x: t.x, y: t.y, width: t.width, height: t.height, rotation: t.rotation || 0 }));
    const d = await api("/tables/bulkUpdate", { method: "POST", body: JSON.stringify({ tables: payload }) });
    if (d.success) showToast("Raspored sačuvan!"); else showToast("Greška", "error");
  };

  // Calc session total
  const sessionTotal = (session) => {
    if (!session?.items) return 0;
    return session.items.filter(i => i.status !== "cancelled").reduce((sum, i) => {
      const net = i.quantity * i.unit_price;
      let disc = 0;
      if (i.discount_type === "percentage") disc = net * (i.discount_value / 100);
      else if (i.discount_type === "fixed") disc = i.discount_value || 0;
      return sum + (net - disc) * (1 + (i.vat_rate != null ? Number(i.vat_rate) : 21) / 100);
    }, 0);
  };

  const getColor = (table) => {
    if (table.session) return table.session.status === "active" ? "#3b82f6" : "#f59e0b";
    return "#22c55e";
  };
  const getStatus = (table) => {
    if (table.session) return "Zauzet";
    return "Slobodan";
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  // Product picker for adding items
  const filteredProducts = products.filter(p => {
    // Master category gate (when feature ON): if activeCategory="all", scope to
    // the current master's categories. Without this, switching to HRANA tab
    // would still show drinks because activeCategory defaulted to "all".
    if (masterCategoryPos && activeCategory === "all" && activeMaster) {
      const cat = categories.find(c => c.id == p.category_id);
      if (!cat || Number(cat.master_category_id) !== Number(activeMaster)) return false;
    }
    if (activeCategory !== "all" && p.category_id != activeCategory) return false;
    if (prodSearch && !p.name.toLowerCase().includes(prodSearch.toLowerCase())) return false;
    return true;
  });

  const addToSessionCart = (product) => {
    setSessionCart(prev => {
      const exists = prev.find(c => c.id === product.id);
      if (exists) return prev.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { ...product, quantity: 1, custom_price: null, discount_type: "none", discount_value: 0, kitchen_tags: [], kitchen_enabled: parseInt(product.kitchen_enabled) === 1, kitchen_tags_enabled: parseInt(product.kitchen_tags_enabled) === 1, kitchen_tags_preset: product.kitchen_tags_preset ? (typeof product.kitchen_tags_preset === "string" ? (() => { try { return JSON.parse(product.kitchen_tags_preset); } catch { return []; } })() : product.kitchen_tags_preset) : [] }];
    });
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Grid area */}
      <div style={{ flex: 1, overflow: "hidden", padding: 12, position: "relative" }}>
        {tables.length === 0 || zones.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 20px", color: S.muted }}>
            <Coffee size={48} strokeWidth={1.5} />
            <p style={{ marginTop: 16, fontSize: 16 }}>{zones.length === 0 ? "Kreirajte zone u Podešavanja → Stolovi." : "Nema stolova. Dodajte stolove u Podešavanja → Stolovi."}</p>
          </div>
        ) : (
          <div ref={gridRef} onMouseMove={handleEditorMouseMove} onMouseUp={handleEditorMouseUp} onMouseLeave={handleEditorMouseUp}
            style={{ position: "relative", width: "100%", height: "100%", background: subView === "editor" ? "#faf9f7" : "#f4f2ee", borderRadius: 12, border: `2px ${subView === "editor" ? "dashed" : "solid"} #d6d0c4`, overflow: "hidden" }}>
            {/* Floor texture */}
            <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
              <defs>
                <pattern id="floor" width="40" height="40" patternUnits="userSpaceOnUse">
                  <rect width="40" height="40" fill={subView === "editor" ? "#faf9f7" : "#f4f2ee"} />
                  <path d="M0 20h40M20 0v40" stroke="#e8e2d8" strokeWidth="0.5" opacity="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#floor)" />
            </svg>

            {tables.filter(t => activeZone && String(t.zone_id) === String(activeZone)).map(table => {
              const isOccupied = !!table.session;
              const statusColor = getColor(table);
              const isSelected = selectedTable?.id === table.id;
              const total = isOccupied ? sessionTotal(table.session) : 0;
              const tp = table.table_type || "table";
              const w = table.width || 12;
              const h = table.height || 10;
              const rot = parseInt(table.rotation) || 0; // 0, 90, 180, 270

              // Realistic furniture renderer
              const renderFurniture = () => {
                const fw = "100%", fh = "100%";
                // Apply rotation transform to SVG for direction/facing
                const svgStyle = { width: fw, height: fh, position: "absolute", inset: 0, transform: rot ? `rotate(${rot}deg)` : "none" };
                if (tp === "table") {
                  // Round or rect table with chairs around it
                  const seats = parseInt(table.seats) || 4;
                  const isRound = table.shape === "circle";
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Chairs */}
                      {Array.from({ length: Math.min(seats, 8) }).map((_, i) => {
                        const positions = seats <= 2 ? [[30,4],[90,4]] : seats <= 4 ? [[30,2],[90,2],[30,82],[90,82]] : seats <= 6 ? [[20,2],[60,2],[100,2],[20,82],[60,82],[100,82]] : [[20,2],[50,2],[80,2],[108,50],[80,82],[50,82],[20,82],[2,50]];
                        const pos = positions[i] || [60,50];
                        return <rect key={i} x={pos[0]-8} y={pos[1]} width="16" height="14" rx="4" fill={isOccupied ? "#93c5fd" : "#a8b5a0"} opacity="0.6" />;
                      })}
                      {/* Table surface */}
                      {isRound
                        ? <ellipse cx="60" cy="50" rx="32" ry="26" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="2" />
                        : <rect x="18" y="18" width="84" height="64" rx="8" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="2" />
                      }
                      {/* Wood grain */}
                      {!isRound && <>
                        <line x1="30" y1="30" x2="30" y2="70" stroke={isOccupied ? "#bfdbfe" : "#d4cbb5"} strokeWidth="0.5" opacity="0.5" />
                        <line x1="60" y1="22" x2="60" y2="78" stroke={isOccupied ? "#bfdbfe" : "#d4cbb5"} strokeWidth="0.5" opacity="0.5" />
                        <line x1="90" y1="30" x2="90" y2="70" stroke={isOccupied ? "#bfdbfe" : "#d4cbb5"} strokeWidth="0.5" opacity="0.5" />
                      </>}
                    </svg>
                  );
                }
                if (tp === "bar") {
                  // Bar counter with stools
                  const seats = parseInt(table.seats) || 3;
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Bar counter */}
                      <rect x="10" y="10" width="100" height="45" rx="6" fill={isOccupied ? "#c7d2fe" : "#8b7355"} stroke={isOccupied ? "#6366f1" : "#6b5740"} strokeWidth="2" />
                      {/* Counter top shine */}
                      <rect x="14" y="14" width="92" height="8" rx="3" fill="white" opacity="0.15" />
                      {/* Foot rail */}
                      <rect x="15" y="48" width="90" height="3" rx="1" fill={isOccupied ? "#a5b4fc" : "#7a6548"} opacity="0.6" />
                      {/* Bar stools */}
                      {Array.from({ length: Math.min(seats, 6) }).map((_, i) => {
                        const spacing = 100 / (seats + 1);
                        const cx = 10 + spacing * (i + 1);
                        return <g key={i}>
                          <circle cx={cx} cy="75" r="10" fill={isOccupied ? "#93c5fd" : "#a0937c"} stroke={isOccupied ? "#60a5fa" : "#8a7d68"} strokeWidth="1.5" />
                          <line x1={cx} y1="65" x2={cx} y2="58" stroke={isOccupied ? "#93c5fd" : "#a0937c"} strokeWidth="3" strokeLinecap="round" />
                        </g>;
                      })}
                    </svg>
                  );
                }
                if (tp === "couch") {
                  // Booth / couch seating
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Back cushion */}
                      <rect x="5" y="5" width="110" height="25" rx="10" fill={isOccupied ? "#93c5fd" : "#a67c52"} opacity="0.8" />
                      {/* Seat cushions */}
                      <rect x="8" y="28" width="34" height="30" rx="6" fill={isOccupied ? "#bfdbfe" : "#c49a6c"} stroke={isOccupied ? "#60a5fa" : "#a67c52"} strokeWidth="1" />
                      <rect x="44" y="28" width="34" height="30" rx="6" fill={isOccupied ? "#bfdbfe" : "#c49a6c"} stroke={isOccupied ? "#60a5fa" : "#a67c52"} strokeWidth="1" />
                      <rect x="80" y="28" width="34" height="30" rx="6" fill={isOccupied ? "#bfdbfe" : "#c49a6c"} stroke={isOccupied ? "#60a5fa" : "#a67c52"} strokeWidth="1" />
                      {/* Armrests */}
                      <rect x="2" y="8" width="10" height="52" rx="5" fill={isOccupied ? "#60a5fa" : "#8b6440"} opacity="0.7" />
                      <rect x="108" y="8" width="10" height="52" rx="5" fill={isOccupied ? "#60a5fa" : "#8b6440"} opacity="0.7" />
                      {/* Coffee table in front */}
                      <rect x="30" y="68" width="60" height="26" rx="5" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="1.5" />
                    </svg>
                  );
                }
                if (tp === "vip") {
                  // VIP booth - u-shaped seating
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* U-shaped sofa */}
                      <path d="M10,10 L10,85 Q10,95 20,95 L100,95 Q110,95 110,85 L110,10" fill="none" stroke={isOccupied ? "#eab308" : "#7c3aed"} strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
                      <path d="M10,10 L10,85 Q10,95 20,95 L100,95 Q110,95 110,85 L110,10" fill="none" stroke={isOccupied ? "#fbbf24" : "#8b5cf6"} strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
                      {/* Center table */}
                      <rect x="30" y="25" width="60" height="50" rx="8" fill={isOccupied ? "#fef3c7" : "#ede9fe"} stroke={isOccupied ? "#f59e0b" : "#a78bfa"} strokeWidth="2" />
                      {/* Star / VIP indicator */}
                      <text x="60" y="55" textAnchor="middle" fontSize="18" fill={isOccupied ? "#d97706" : "#7c3aed"} opacity="0.4">★</text>
                    </svg>
                  );
                }
                if (tp === "outdoor") {
                  // Outdoor table with umbrella
                  const seats = parseInt(table.seats) || 4;
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      {/* Umbrella shadow */}
                      <ellipse cx="60" cy="50" rx="48" ry="40" fill="#22c55e" opacity="0.08" />
                      {/* Outdoor chairs */}
                      {Array.from({ length: Math.min(seats, 6) }).map((_, i) => {
                        const positions = seats <= 2 ? [[30,8],[90,8]] : seats <= 4 ? [[30,6],[90,6],[30,76],[90,76]] : [[20,6],[60,6],[100,6],[20,76],[60,76],[100,76]];
                        const pos = positions[i] || [60,50];
                        return <rect key={i} x={pos[0]-7} y={pos[1]} width="14" height="16" rx="3" fill={isOccupied ? "#86efac" : "#4ade80"} opacity="0.5" stroke={isOccupied ? "#22c55e" : "#16a34a"} strokeWidth="1" />;
                      })}
                      {/* Table */}
                      <circle cx="60" cy="48" r="22" fill={isOccupied ? "#bbf7d0" : "#dcfce7"} stroke={isOccupied ? "#22c55e" : "#86efac"} strokeWidth="2" />
                      {/* Umbrella pole */}
                      <line x1="60" y1="26" x2="60" y2="70" stroke="#854d0e" strokeWidth="2" opacity="0.3" />
                      {/* Umbrella top */}
                      <path d="M35,28 Q60,10 85,28" fill="none" stroke="#dc2626" strokeWidth="3" opacity="0.4" />
                    </svg>
                  );
                }
                if (tp === "barber") {
                  // Barber/salon chair
                  return (
                    <svg viewBox="0 0 100 100" style={svgStyle}>
                      {/* Base */}
                      <ellipse cx="50" cy="88" rx="24" ry="6" fill={isOccupied ? "#f9a8d4" : "#9ca3af"} opacity="0.5" />
                      <rect x="44" y="70" width="12" height="18" rx="2" fill={isOccupied ? "#ec4899" : "#6b7280"} />
                      {/* Seat */}
                      <rect x="20" y="40" width="60" height="32" rx="10" fill={isOccupied ? "#fbcfe8" : "#d1d5db"} stroke={isOccupied ? "#ec4899" : "#9ca3af"} strokeWidth="2" />
                      {/* Back rest */}
                      <rect x="25" y="8" width="50" height="35" rx="12" fill={isOccupied ? "#f9a8d4" : "#9ca3af"} stroke={isOccupied ? "#db2777" : "#6b7280"} strokeWidth="2" />
                      {/* Armrests */}
                      <rect x="10" y="38" width="14" height="22" rx="5" fill={isOccupied ? "#ec4899" : "#6b7280"} opacity="0.6" />
                      <rect x="76" y="38" width="14" height="22" rx="5" fill={isOccupied ? "#ec4899" : "#6b7280"} opacity="0.6" />
                      {/* Barber pole indicator */}
                      <rect x="86" y="10" width="6" height="30" rx="3" fill="#ef4444" opacity="0.4" />
                      <rect x="86" y="15" width="6" height="5" rx="2" fill="#3b82f6" opacity="0.4" />
                      <rect x="86" y="25" width="6" height="5" rx="2" fill="#3b82f6" opacity="0.4" />
                    </svg>
                  );
                }
                if (tp === "beach_bed") {
                  // Beach beds with umbrella
                  return (
                    <svg viewBox="0 0 160 100" style={svgStyle}>
                      {/* Left bed */}
                      <rect x="5" y="45" width="55" height="22" rx="4" fill={isOccupied ? "#a5f3fc" : "#e0f2fe"} stroke={isOccupied ? "#06b6d4" : "#7dd3fc"} strokeWidth="1.5" />
                      <rect x="5" y="35" width="20" height="14" rx="4" fill={isOccupied ? "#67e8f9" : "#bae6fd"} stroke={isOccupied ? "#06b6d4" : "#7dd3fc"} strokeWidth="1" />
                      {/* Bed legs */}
                      <line x1="10" y1="67" x2="10" y2="75" stroke={isOccupied ? "#0891b2" : "#94a3b8"} strokeWidth="2" />
                      <line x1="55" y1="67" x2="55" y2="75" stroke={isOccupied ? "#0891b2" : "#94a3b8"} strokeWidth="2" />
                      {/* Right bed */}
                      <rect x="100" y="45" width="55" height="22" rx="4" fill={isOccupied ? "#a5f3fc" : "#e0f2fe"} stroke={isOccupied ? "#06b6d4" : "#7dd3fc"} strokeWidth="1.5" />
                      <rect x="135" y="35" width="20" height="14" rx="4" fill={isOccupied ? "#67e8f9" : "#bae6fd"} stroke={isOccupied ? "#06b6d4" : "#7dd3fc"} strokeWidth="1" />
                      {/* Bed legs */}
                      <line x1="105" y1="67" x2="105" y2="75" stroke={isOccupied ? "#0891b2" : "#94a3b8"} strokeWidth="2" />
                      <line x1="150" y1="67" x2="150" y2="75" stroke={isOccupied ? "#0891b2" : "#94a3b8"} strokeWidth="2" />
                      {/* Umbrella pole */}
                      <line x1="80" y1="18" x2="80" y2="78" stroke="#854d0e" strokeWidth="3" opacity="0.5" />
                      {/* Umbrella canopy */}
                      <path d="M50,22 Q80,2 110,22" fill={isOccupied ? "#fbbf24" : "#f59e0b"} opacity="0.5" stroke={isOccupied ? "#d97706" : "#b45309"} strokeWidth="1.5" />
                      <path d="M55,22 Q80,8 105,22" fill="none" stroke="white" strokeWidth="1" opacity="0.3" />
                    </svg>
                  );
                }
                if (tp === "chair") {
                  // Decorative chair - no sessions
                  return (
                    <svg viewBox="0 0 60 60" style={svgStyle}>
                      {/* Seat */}
                      <rect x="10" y="20" width="40" height="30" rx="6" fill="#d4d4d8" stroke="#a1a1aa" strokeWidth="1.5" />
                      {/* Back rest */}
                      <rect x="12" y="5" width="36" height="18" rx="6" fill="#a1a1aa" stroke="#78716c" strokeWidth="1.5" />
                      {/* Legs */}
                      <line x1="16" y1="50" x2="14" y2="58" stroke="#78716c" strokeWidth="2.5" strokeLinecap="round" />
                      <line x1="44" y1="50" x2="46" y2="58" stroke="#78716c" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  );
                }
                if (tp === "wall") {
                  // Wall / divider
                  return (
                    <svg viewBox="0 0 120 100" style={svgStyle}>
                      <rect x="2" y="35" width="116" height="30" rx="2" fill="#78716c" stroke="#57534e" strokeWidth="2" />
                      <line x1="20" y1="38" x2="20" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="40" y1="38" x2="40" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="60" y1="38" x2="60" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="80" y1="38" x2="80" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                      <line x1="100" y1="38" x2="100" y2="62" stroke="#a8a29e" strokeWidth="0.5" opacity="0.4" />
                    </svg>
                  );
                }
                // Default fallback
                return (
                  <svg viewBox="0 0 120 100" style={svgStyle}>
                    <rect x="15" y="15" width="90" height="70" rx="8" fill={isOccupied ? "#dbeafe" : "#e8e0d0"} stroke={isOccupied ? "#3b82f6" : "#c4b99a"} strokeWidth="2" />
                  </svg>
                );
              };

              const isWall = tp === "wall";
              const isChair = tp === "chair";
              const isDecor = isWall || isChair;
              return (
                <div key={table.id}
                  onMouseDown={subView === "editor" ? (e) => handleEditorMouseDown(e, table) : undefined}
                  onClick={() => {
                    if (subView === "editor" || isDecor) return;
                    if (isOccupied) { setSelectedTable(table); if (table.session?.id) loadSessionOrders(table.session.id); }
                    else {
                      // Open session immediately and go to add-items
                      (async () => {
                        setProcessing(true);
                        const d = await api("/tables/openSession", { method: "POST", body: JSON.stringify({ table_id: table.id, guest_count: 1, tcr_code: getTcr() }) });
                        setProcessing(false);
                        if (d.success) {
                          showToast(`${table.name} otvoren`);
                          // Reload and open add-items for this table
                          const td = await api("/tables");
                          if (td.success) {
                            const newTables = td.data?.tables || [];
                            setTables(newTables);
                            const updated = newTables.find(t => t.id === table.id);
                            if (updated) {
                              setSelectedTable(updated);
                              if (updated.session) { setAddItemsSession(updated.session); loadSessionOrders(updated.session.id); }
                            }
                          }
                        } else showToast(d.message || "Greška", "error");
                      })();
                    }
                  }}
                  style={{
                    position: "absolute",
                    left: `${table.x}%`, top: `${table.y}%`,
                    width: `${w}%`, height: `${h}%`,
                    minWidth: isDecor ? 20 : 80, minHeight: isDecor ? 12 : 65,
                    cursor: subView === "editor" ? "grab" : isDecor ? "default" : "pointer",
                    transition: dragging == table.id ? "none" : "transform 0.15s, box-shadow 0.2s",
                    transform: isSelected && !isDecor ? "scale(1.03)" : "scale(1)",
                    filter: isSelected && !isDecor ? "drop-shadow(0 4px 12px rgba(79,70,229,0.25))" : dragging == table.id ? "drop-shadow(0 6px 20px rgba(0,0,0,0.2))" : "none",
                    zIndex: dragging == table.id ? 100 : isSelected ? 50 : isDecor ? 0 : 1,
                    userSelect: "none",
                  }}>
                  {renderFurniture()}
                  {/* Label overlay */}
                  {!isWall && tp !== "chair" && (
                    <div style={{ position: "absolute", left: "50%", top: -2, transform: "translateX(-50%) translateY(-100%)", pointerEvents: "none", zIndex: 2 }}>
                      <div style={{ background: isOccupied ? "rgba(220,38,38,0.15)" : (table.color ? table.color + "33" : "rgba(0,0,0,0.07)"), borderRadius: 5, padding: "2px 6px", display: "flex", flexDirection: "column", alignItems: "center", border: isOccupied ? "1px solid rgba(220,38,38,0.3)" : "1px solid rgba(0,0,0,0.12)", whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 10, color: isOccupied ? "#dc2626" : S.dark, lineHeight: 1.3 }}>{table.name}</div>
                        {subView !== "editor" && isOccupied && table.session && <div style={{ fontSize: 9, fontWeight: 600, color: "#dc2626", lineHeight: 1.2 }}>{fmt(total)} €</div>}
                      </div>
                    </div>
                  )}
                  {/* Editor label for walls */}
                  {isWall && subView === "editor" && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <span style={{ fontSize: 9, color: "white", fontWeight: 600, textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>{table.name}</span>
                    </div>
                  )}
                  {/* Selection ring */}
                  {isSelected && !isDecor && <div style={{ position: "absolute", inset: -4, border: `2px solid ${S.accent}`, borderRadius: 14, pointerEvents: "none" }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom toolbar */}
      <div style={{ padding: "8px 12px", borderTop: `1px solid ${S.border}`, background: "white", display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
        {/* Zone tabs */}
        {visibleZones.length > 0 && (
          <div style={{ display: "flex", gap: 4, overflowX: "auto" }}>
            {visibleZones.map(z => (
              <button key={z.id} onClick={() => setActiveZone(z.id)} style={{ padding: "5px 14px", border: "1px solid", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: activeZone == z.id ? S.dark : S.border, background: activeZone == z.id ? S.dark : "white", color: activeZone == z.id ? "white" : S.muted, whiteSpace: "nowrap" }}>
                {z.name} <span style={{ fontSize: 10, opacity: 0.7 }}>({tables.filter(t => String(t.zone_id) === String(z.id) && t.session).length}/{tables.filter(t => String(t.zone_id) === String(z.id) && t.table_type !== "wall" && t.table_type !== "chair").length})</span>
              </button>
            ))}
          </div>
        )}
        {/* Controls */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => setSubView("layout")} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: subView === "layout" ? S.accent : S.border, background: subView === "layout" ? S.accent : "white", color: subView === "layout" ? "white" : S.muted }}>
            <Coffee size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Raspored
          </button>
          <button onClick={() => { setSubView("history"); loadClosedHistory(); }} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: subView === "history" ? S.accent : S.border, background: subView === "history" ? S.accent : "white", color: subView === "history" ? "white" : S.muted }}>
            <Clock size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Istorija
          </button>
          <button onClick={() => setSubView("editor")} style={{ padding: "6px 14px", border: "1px solid", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font, borderColor: subView === "editor" ? S.accent : S.border, background: subView === "editor" ? S.accent : "white", color: subView === "editor" ? "white" : S.muted }}>
            <Move size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Uredi raspored
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ fontSize: 13, color: S.muted }}>{tables.filter(t => t.session && String(t.zone_id) === String(activeZone) && t.table_type !== "wall" && t.table_type !== "chair").length}/{tables.filter(t => String(t.zone_id) === String(activeZone) && t.table_type !== "wall" && t.table_type !== "chair").length} zauzeto</div>
          <button onClick={loadTables} style={S.btnSm("#f3f4f6", S.muted)}><RefreshCw size={13} /></button>
          {subView === "editor" && <button onClick={saveLayout} style={S.btn(S.green)}><Save size={14} />Sacuvaj raspored</button>}
        </div>
        </div>
      </div>

      {/* History view */}
      {subView === "history" && (
        <div style={{ position: "absolute", inset: 0, background: "white", zIndex: 800, overflow: "auto", padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Zatvoreni stolovi (24h)</h3>
            <button onClick={() => setSubView("layout")} style={S.btnSm("#f3f4f6", S.muted)}><X size={14} />Zatvori</button>
          </div>
          {closedHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: S.muted }}><Clock size={30} strokeWidth={1.5} /><p style={{ marginTop: 8 }}>Nema zatvorenih stolova u posljednjih 24h</p></div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {closedHistory.map(s => (
                <div key={s.id} style={{ background: "#f9fafb", borderRadius: 10, padding: 14, border: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: S.green + "15", display: "flex", alignItems: "center", justifyContent: "center" }}><Check size={20} color={S.green} /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{s.table_name}</div>
                    <div style={{ fontSize: 12, color: S.muted }}>Otvoreno: {fmtDateTime(s.opened_at)} • Zatvoreno: {fmtDateTime(s.closed_at)}</div>
                    <div style={{ fontSize: 12, color: S.muted }}>{s.operator_name || ""} • {s.total_orders || 0} narudžbi</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: S.green }}>{fmt(s.summary_total || s.total_amount || 0)} €</div>
                    {s.summary_invoice_number && <div style={{ fontSize: 11, color: S.muted }}>{s.summary_invoice_number}</div>}
                  </div>
                  {s.summary_invoice_id && <button onClick={async () => { await printReceipt(s.summary_invoice_id); }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={12} /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Session detail panel (right side overlay when table selected) */}
      {selectedTable && selectedTable.session && (
        <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: 400, background: "white", boxShadow: "-4px 0 20px rgba(0,0,0,0.1)", zIndex: 999, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div><div style={{ fontWeight: 700, fontSize: 17 }}>{selectedTable.name}</div><div style={{ fontSize: 13, color: S.muted }}>Gostiju: {selectedTable.session.guest_count} • {fmtDateTime(selectedTable.session.opened_at)}</div></div>
            <button onClick={() => { setSelectedTable(null); setSessionOrders([]); }} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} color={S.muted} /></button>
          </div>

          {/* Orders list */}
          <div style={{ flex: 1, overflow: "auto", padding: "12px 16px" }}>
            {sessionOrders.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px 0", color: S.muted }}><Package size={30} strokeWidth={1.5} /><p style={{ marginTop: 8 }}>Nema narudžbi</p><p style={{ fontSize: 12 }}>Dodajte prvu narudžbu</p></div>
            ) : sessionOrders.filter(o => !o.storno_of_invoice_id).map((order, idx) => {
              const isStorno = order.is_storno == 1 || order.stornoed_by_invoice_id || order.status === "storno";
              const isPaid = !!order.summary_invoice_id && !isStorno;
              const isUnpaidFiscalized = order.status === "fiscalized" && !isStorno && !isPaid;
              const items = order.items_json ? (typeof order.items_json === "string" ? JSON.parse(order.items_json) : order.items_json) : [];
              const checked = partialPayMode && partialPayIds.includes(order.id);
              return (
                <div key={order.id} onClick={() => {
                    // In partial-pay mode, click toggles checkbox (only for unpaid fiscalized).
                    // Otherwise expand/collapse for details (existing behavior).
                    if (partialPayMode && isUnpaidFiscalized) {
                      setPartialPayIds(prev => prev.includes(order.id) ? prev.filter(x => x !== order.id) : [...prev, order.id]);
                    } else {
                      setExpandedOrder(expandedOrder === order.id ? null : order.id);
                    }
                  }} style={{
                    background: isStorno ? "#fef2f2" : isPaid ? "#f3f4f6" : checked ? "#fef3c7" : "#f9fafb",
                    borderRadius: 10,
                    padding: 12,
                    marginBottom: 10,
                    border: `1px solid ${isStorno ? "#fecaca" : checked ? "#92400e" : isPaid ? "#d1d5db" : S.border}`,
                    borderWidth: checked ? 2 : 1,
                    cursor: "pointer",
                    opacity: isPaid ? 0.65 : 1
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {/* Checkbox shown only in partial-pay mode and only for unpaid fiscalized orders.
                          Storno + already-paid orders are not selectable. */}
                      {partialPayMode && isUnpaidFiscalized && (
                        <input type="checkbox" checked={checked} onChange={() => {}} onClick={e => e.stopPropagation()} style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#92400e" }} />
                      )}
                      <span style={{ fontWeight: 700, fontSize: 14 }}>Narudžba #{order.order_number || idx + 1}</span>
                      {isStorno && <span style={{ fontSize: 11, color: S.red, fontWeight: 600 }}>STORNO</span>}
                      {isPaid && <span style={{ fontSize: 11, color: "#059669", fontWeight: 600 }}>✓ NAPLAĆENO</span>}
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 15, color: isStorno ? S.red : isPaid ? S.muted : S.green, textDecoration: isStorno ? "line-through" : "none" }}>{fmt(order.total_amount)} €</span>
                  </div>
                  <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>{fmtDateTime(order.invoice_date)} • {order.status === "fiscalized" ? "✓ Fiskalizovano" : order.status}</div>
                  {expandedOrder === order.id && <>
                    {items.length > 0 && <div style={{ margin: "8px 0", padding: "8px 0", borderTop: `1px solid ${isStorno ? "#fecaca" : "#e5e7eb"}` }}>
                      {items.map((it, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
                        <span style={{ color: isStorno ? S.red : S.dark }}>{it.quantity}x {it.name}</span>
                        <span style={{ fontWeight: 600, color: isStorno ? S.red : S.dark }}>{fmt((it.quantity * it.unit_price * (1 + (it.vat_rate != null ? Number(it.vat_rate) : 21) / 100)).toFixed(2))} €</span>
                      </div>)}
                    </div>}
                    {order.fic && <div style={{ fontSize: 10, color: S.muted, marginBottom: 6, wordBreak: "break-all" }}>FIC: {order.fic}</div>}
                    <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button onClick={async () => { await printReceipt(order.id); showToast("Štampano!"); }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={11} /></button>
                      <button onClick={() => openPdf(`/invoices/pdf/${order.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={11} /></button>
                      {!isStorno && !isPaid && order.status === "fiscalized" && <button onClick={async () => { if (!activeOperator?.operator_code) { showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error"); return; } if (!window.confirm("Stornirati narudžbu #" + (order.order_number || idx+1) + "?")) return; const d = await api(`/invoices/storno/${order.id}`, { method: "POST", body: JSON.stringify({ storno_reason: "STORNO_OP", fiscalize_now: true, ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code }) }) }); if (d.success) { showToast("Narudžba stornirana!"); loadSessionOrders(selectedTable.session.id); loadTables(); } else showToast(d.message, "error"); }} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={11} />Storno</button>}
                    </div>
                  </>}
                </div>
              );
            })}
          </div>

          {/* Total + actions */}
          <div style={{ borderTop: `2px solid ${S.border}`, padding: 16 }}>
            {partialPayMode ? (
              // ───── PARTIAL PAY BAR ─────
              // Replaces the normal total/actions when user is in selection mode.
              // Shows count + total of selected orders, plus Cancel + 2 pay buttons.
              (() => {
                const eligibleUnpaid = sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1 && !o.summary_invoice_id);
                const selected = eligibleUnpaid.filter(o => partialPayIds.includes(o.id));
                const selTotal = selected.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
                return (
                  <>
                    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                      <div style={{ fontSize: 12, color: "#92400e", fontWeight: 600, marginBottom: 4 }}>📋 Odaberite narudžbe za djelimičnu naplatu</div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, color: S.dark }}>Odabrano: <b>{selected.length}</b> od {eligibleUnpaid.length}</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: "#92400e" }}>{fmt(selTotal)} €</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button onClick={() => { setPartialPayMode(false); setPartialPayIds([]); }} style={{ ...S.btnSm("#f3f4f6", S.muted), flex: "1 1 30%", justifyContent: "center" }} disabled={partialPayProcessing}><X size={12} />Otkaži</button>
                      <button onClick={() => closePartial(selectedTable.session.id, partialPayIds, "cash")} disabled={partialPayProcessing || selected.length === 0} style={{ ...S.btn(S.green), flex: "1 1 30%", justifyContent: "center", fontSize: 13, opacity: (partialPayProcessing || selected.length === 0) ? 0.5 : 1 }} title="Naplati odabrane narudžbe gotovinom"><Banknote size={14} />Gotovina</button>
                      <button onClick={() => closePartial(selectedTable.session.id, partialPayIds, "card")} disabled={partialPayProcessing || selected.length === 0} style={{ ...S.btn(S.accent), flex: "1 1 30%", justifyContent: "center", fontSize: 13, opacity: (partialPayProcessing || selected.length === 0) ? 0.5 : 1 }} title="Naplati odabrane narudžbe karticom"><CreditCard size={14} />Kartica</button>
                    </div>
                  </>
                );
              })()
            ) : (
              // ───── NORMAL BAR ─────
              // For total + count: filter to UNPAID orders only when any have been
              // partial-paid. This way "Ukupno" shows what's still due, not the
              // historical total. If no partial pay yet, behavior identical to before.
              (() => {
                const allActive = sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1);
                const unpaid = allActive.filter(o => !o.summary_invoice_id);
                const hasPaid = allActive.length > unpaid.length;
                return (
              <>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: S.muted }}>
                Narudžbe: {hasPaid ? `${unpaid.length} (od ${allActive.length})` : allActive.length}
              </span>
              <span style={{ fontSize: 13, color: S.muted }}>{fmtDateTime(selectedTable.session.opened_at)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{hasPaid ? "Preostalo" : "Ukupno"}</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: S.green }}>{fmt(unpaid.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0))} €</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button onClick={() => { setAddItemsSession(selectedTable.session); setSessionCart([]); setOrderDiscount({ type: "none", value: 0 }); }} style={{ ...S.btn(S.accent), flex: "1 1 45%", justifyContent: "center", fontSize: 13 }}><Plus size={14} />Nova narudžba</button>
              {sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1 && !o.summary_invoice_id).length > 0
                ? <button onClick={() => { setCloseModal(selectedTable.session); setPayMethod("cash"); setOrderDiscount({ type: "none", value: 0 }); setShowCloseDiscount(false); }} style={{ ...S.btn(S.green), flex: "1 1 45%", justifyContent: "center", fontSize: 13 }}><Zap size={14} />Zatvori & Naplati</button>
                : null}
              {/* Naplati djelimično: only useful when 2+ unpaid orders. With 1 order
                  the user should just use Zatvori & Naplati. With 0 there's nothing to pay. */}
              {sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1 && !o.summary_invoice_id).length >= 2
                && !partialPayMode
                ? <button onClick={() => { setPartialPayMode(true); setPartialPayIds([]); }} style={{ ...S.btn("#fef3c7", "#92400e"), flex: "1 1 45%", justifyContent: "center", fontSize: 13 }} title="Odaberite koje narudžbe naplatiti zajedno"><CheckCircle size={14} />Djelimično</button>
                : null}
              <button onClick={() => setTransferModal(selectedTable.session)} style={{ ...S.btnSm("#f3f4f6", S.muted), flex: "1 1 30%", justifyContent: "center" }}><Move size={12} />Premjesti</button>
              {/* Spoji: merge this session into another busy table. Only useful if we have
                  something to merge (orders or cart items). Hidden in cancel-only state. */}
              <button onClick={() => setMergeModal(selectedTable.session)} style={{ ...S.btnSm("#eef2ff", "#4f46e5"), flex: "1 1 30%", justifyContent: "center" }} title="Spoji ovaj sto sa drugim zauzetim stolom"><LayoutGrid size={12} />Spoji</button>
              {sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1 && !o.summary_invoice_id).length === 0 &&
                <button onClick={() => cancelSession(selectedTable.session.id)} style={{ ...S.btnSm("#fef2f2", S.red), flex: "1 1 30%", justifyContent: "center" }}><XCircle size={12} />Otkaži</button>
              }
            </div>
              </>
                );
              })()
            )}
          </div>
        </div>
      )}

      {/* Open table modal */}
      {openModal && <Modal onClose={() => setOpenModal(null)} title={`Otvori ${openModal.name}`}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#dcfce7", margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center" }}><Coffee size={28} color={S.green} /></div>
          <div style={{ fontSize: 15, color: S.muted }}>{openModal.seats} mjesta • {openModal.table_type}</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Broj gostiju</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[1,2,3,4,5,6,7,8].map(n => (
              <button key={n} onClick={() => setGuestCount(n)} style={{ width: 38, height: 38, borderRadius: 8, border: "1px solid", borderColor: guestCount === n ? S.accent : S.border, background: guestCount === n ? S.accent : "white", color: guestCount === n ? "white" : S.dark, fontWeight: 600, fontSize: 15, cursor: "pointer" }}>{n}</button>
            ))}
          </div>
        </div>
        <button onClick={() => openSession(openModal)} disabled={processing} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: "12px" }}>
          {processing ? <Loader2 size={16} className="spin" /> : <Check size={16} />}Otvori sto
        </button>
      </Modal>}

      {/* Add items modal (product picker) */}
      {addItemsSession && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", justifyContent: "center" }} onClick={() => {
        // If session has no items and user dismisses without adding, cancel the session
        if (addItemsSession && sessionCart.length === 0) {
          const sess = addItemsSession;
          const tbl = tables.find(t => t.session?.id === sess.id);
          const hasItems = tbl?.session?.items?.filter(i => i.status !== "cancelled")?.length > 0;
          if (!hasItems) {
            api(`/tables/sessionCancel/${sess.id}`, { method: "POST" }).then(() => loadTables());
          }
        }
        setAddItemsSession(null);
      }}>
        <div style={{ background: "white", width: "75%", maxWidth: 1000, margin: "30px auto", borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 60px)" }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: 17 }}>Dodaj stavke</span>
            <button onClick={() => setAddItemsSession(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} color={S.muted} /></button>
          </div>
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* Products */}
            <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
              <input placeholder="Pretraži..." value={prodSearch} onChange={e => setProdSearch(e.target.value)} style={{ ...S.input, marginBottom: 8 }} />
              {/* Master category toggle (HRANA / PIĆE) — only when feature is enabled.
                  Per user spec: Stolovi has the toggle only, no SVE button. Unassigned
                  categories are INVISIBLE in Stolovi until admin assigns them. */}
              {masterCategoryPos && masterCategories.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  {masterCategories.map(m => (
                    <button
                      key={m.id}
                      onClick={() => { setActiveMaster(m.id); setActiveCategory("all"); }}
                      style={{
                        flex: 1,
                        padding: "9px 8px",
                        background: activeMaster === m.id ? S.accent : "white",
                        color: activeMaster === m.id ? "white" : S.dark,
                        border: `1px solid ${activeMaster === m.id ? S.accent : S.border}`,
                        borderRadius: 7,
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                {/* SVE button only when master feature is OFF (legacy behavior).
                    When ON, "Sve" doesn't make sense — user picks HRANA or PIĆE. */}
                {!masterCategoryPos && <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />}
                {/* Categories: when master feature ON, filter by activeMaster.
                    Categories without a master_category_id are NOT shown — admin
                    must assign one for them to appear in Stolovi. */}
                {categories.filter(c => {
                  if (!masterCategoryPos) return true;
                  return Number(c.master_category_id) === Number(activeMaster);
                }).map(c => <CatTab key={c.id} active={activeCategory == c.id} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 10 }}>
                {filteredProducts.map(p => (
                  <button key={p.id} onClick={() => addToSessionCart(p)} style={{ padding: 12, background: sessionCart.find(c => c.id === p.id) ? "#eef2ff" : "white", border: `1px solid ${sessionCart.find(c => c.id === p.id) ? S.accent : S.border}`, borderRadius: 8, cursor: "pointer", textAlign: "left" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: S.green, marginTop: 4 }}>{fmt((p.price * (1 + (p.vat_rate != null ? Number(p.vat_rate) : 21) / 100)))} €</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Cart */}
            <div style={{ width: 240, borderLeft: `1px solid ${S.border}`, padding: 12, overflow: "auto", display: "flex", flexDirection: "column" }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Korpa ({sessionCart.reduce((s,c) => s + c.quantity, 0)})</div>
              <div style={{ flex: 1, overflow: "auto" }}>
              {sessionCart.map(c => (
                <div key={c.id} style={{ padding: "6px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{c.name}</div>
                    <button onClick={() => setSessionCart(prev => prev.map(x => x.id === c.id ? { ...x, quantity: Math.max(x.is_decimal_unit ? 0.01 : 1, parseFloat((x.quantity - 1).toFixed(2))) } : x))} style={{ width: 22, height: 22, border: `1px solid ${S.border}`, borderRadius: 4, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={10} /></button>
                    {c.is_decimal_unit ? (
                      // Decimal-unit qty: small tappable button → opens numpad
                      <button onClick={() => setQtyModal(c)} style={{ fontWeight: 700, minWidth: 36, textAlign: "center", padding: "2px 4px", border: `1px dashed ${S.accent}`, borderRadius: 3, background: "white", cursor: "pointer", color: S.dark, fontSize: 11 }}>{fmtQty(c.quantity)}</button>
                    ) : (
                      <span style={{ fontWeight: 700, minWidth: 18, textAlign: "center" }}>{c.quantity}</span>
                    )}
                    <button onClick={() => setSessionCart(prev => prev.map(x => x.id === c.id ? { ...x, quantity: parseFloat((x.quantity + 1).toFixed(2)) } : x))} style={{ width: 22, height: 22, border: `1px solid ${S.border}`, borderRadius: 4, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={10} /></button>
                    {c.kitchen_enabled && c.kitchen_tags_enabled && <button onClick={() => { setEditTagsFor(editTagsFor === c.id ? null : c.id); setCustomTag(""); }} style={{ width: 22, height: 22, border: `1px solid ${editTagsFor === c.id ? "#f59e0b" : S.border}`, borderRadius: 4, background: editTagsFor === c.id ? "#fffbeb" : "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>🍳</button>}
                    <button onClick={() => setSessionCart(prev => prev.filter(x => x.id !== c.id))} style={{ padding: 0, background: "none", border: "none", cursor: "pointer" }}><X size={12} color="#d1d5db" /></button>
                  </div>
                  {c.kitchen_enabled && (c.kitchen_tags || []).length > 0 && editTagsFor !== c.id && (
                    <div style={{ fontSize: 10, color: "#92400e", marginTop: 2 }}>🍳 {(c.kitchen_tags || []).join(", ")}</div>
                  )}
                  {editTagsFor === c.id && c.kitchen_enabled && c.kitchen_tags_enabled && (
                    <div style={{ marginTop: 4, padding: 6, background: "#fffbeb", borderRadius: 6 }}>
                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 4 }}>
                        {(c.kitchen_tags_preset || []).map(tag => {
                          const active = (c.kitchen_tags || []).includes(tag);
                          return <button key={tag} onClick={() => setSessionCart(prev => prev.map(x => x.id === c.id ? { ...x, kitchen_tags: active ? (x.kitchen_tags || []).filter(t => t !== tag) : [...(x.kitchen_tags || []), tag] } : x))} style={{ padding: "2px 7px", borderRadius: 4, border: `1px solid ${active ? "#f59e0b" : "#e5e7eb"}`, background: active ? "#fef3c7" : "white", color: active ? "#92400e" : "#9ca3af", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{tag}</button>;
                        })}
                      </div>
                      <div style={{ display: "flex", gap: 3 }}>
                        <input value={editTagsFor === c.id ? customTag : ""} onChange={e => setCustomTag(e.target.value)} placeholder="Custom..." onKeyDown={e => { if (e.key === "Enter" && customTag.trim()) { setSessionCart(prev => prev.map(x => x.id === c.id ? { ...x, kitchen_tags: [...(x.kitchen_tags || []), customTag.trim()] } : x)); setCustomTag(""); } }} style={{ ...S.input, flex: 1, padding: "3px 6px", fontSize: 11 }} />
                        <button onClick={() => { if (customTag.trim()) { setSessionCart(prev => prev.map(x => x.id === c.id ? { ...x, kitchen_tags: [...(x.kitchen_tags || []), customTag.trim()] } : x)); setCustomTag(""); } }} style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #f59e0b", background: "#fffbeb", color: "#92400e", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>+</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              </div>
              {sessionCart.length > 0 && <button onClick={() => addItemsToSession(addItemsSession.id)} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", marginTop: 10, padding: 10 }}><Check size={14} />Potvrdi ({sessionCart.length})</button>}
            </div>
          </div>
        </div>
      </div>}

      {/* Close session / payment modal */}
      {closeModal && <Modal onClose={() => { setCloseModal(null); setClosePartner(null); setCloseManualPartner(null); }} title="Zatvori račun">
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: S.muted, marginBottom: 6 }}>Ukupno za naplatu:</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: S.green }}>{fmt(sessionOrders.filter(o => o.status === "fiscalized" && !o.storno_of_invoice_id && !o.stornoed_by_invoice_id && o.is_storno != 1 && !o.summary_invoice_id).reduce((s, o) => s + parseFloat(o.total_amount || 0), 0))} €</div>
        </div>
        {hasPermission(activeOperator, "sale_discount") && <div style={{ marginBottom: 12 }}>
          <button onClick={() => setShowCloseDiscount(!showCloseDiscount)} style={{ ...S.btnSm(orderDiscount.type !== "none" ? "#fef3c7" : "#f3f4f6", orderDiscount.type !== "none" ? "#92400e" : S.muted), width: "100%", justifyContent: "center" }}>
            <Percent size={13} />{orderDiscount.type !== "none" ? `Popust: ${orderDiscount.value}${orderDiscount.type === "percentage" ? "%" : "€"}` : "Popust na račun"}
          </button>
          {orderDiscount.type !== "none" && <button onClick={() => setOrderDiscount({ type: "none", value: 0 })} style={{ ...S.btnSm("#fef2f2", S.red), marginTop: 4, width: "100%", justifyContent: "center" }}><X size={13} />Ukloni popust</button>}
        </div>}
        {hasPermission(activeOperator, "sale_discount") && showCloseDiscount && <div style={{ marginBottom: 14, padding: 12, background: "#f9fafb", borderRadius: 10, border: `1px solid ${S.border}` }}>
          <DiscountForm current={orderDiscount} onApply={d => { setOrderDiscount(d); setShowCloseDiscount(false); }} onClose={() => setShowCloseDiscount(false)} />
        </div>}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Način plaćanja</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "cash", l: "Gotovina", icon: Banknote }, { id: "card", l: "Kartica", icon: CreditCard }, { id: "split", l: "Split", icon: Wallet }].map(m => (
              <button key={m.id} onClick={() => { setPayMethod(m.id); if (m.id === "cash") { setClosePartner(null); setCloseManualPartner(null); } }} style={{ flex: 1, padding: 10, border: "1px solid", borderColor: payMethod === m.id ? S.green : S.border, background: payMethod === m.id ? "#f0fdf4" : "white", color: payMethod === m.id ? S.green : S.muted, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}><m.icon size={14} />{m.l}</button>
            ))}
          </div>
        </div>
        {payMethod === "split" && (
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Gotovina (€)</label>
              <div style={{ display: "flex", gap: 4 }}>
                <input type="number" step="0.01" value={splitCash} onChange={e => setSplitCash(e.target.value)} style={{ ...S.input, flex: 1 }} />
                <button onClick={() => setStoloviNumPad("splitCash")} style={{ padding: "6px 10px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 16 }}>🔢</button>
              </div>
            </div>
            <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Kartica (€)</label>
              <div style={{ display: "flex", gap: 4 }}>
                <input type="number" step="0.01" value={splitCard} onChange={e => setSplitCard(e.target.value)} style={{ ...S.input, flex: 1 }} />
                <button onClick={() => setStoloviNumPad("splitCard")} style={{ padding: "6px 10px", background: "#f3f4f6", border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", fontSize: 16 }}>🔢</button>
              </div>
            </div>
          </div>
        )}
        {/* Partner-link: B2B card/split summary. Same UX pattern as Nova Prodaja. */}
        {(payMethod === "card" || payMethod === "split") && (
          <div style={{ marginBottom: 16, padding: "10px 12px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8 }}>
            {(closePartner || closeManualPartner) ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <Building size={15} color="#0369a1" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0c4a6e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{closePartner?.name || closeManualPartner?.name}</div>
                    {(closePartner?.tax_id || closeManualPartner?.tax_id) && <div style={{ fontSize: 11, color: "#0369a1" }}>PIB: {closePartner?.tax_id || closeManualPartner?.tax_id}</div>}
                  </div>
                </div>
                <button onClick={() => setShowClosePartnerPicker(true)} style={{ ...S.btnSm("white", "#0369a1"), border: "1px solid #bae6fd" }} title="Promijeni partnera"><Edit3 size={11} /></button>
                <button onClick={() => { setClosePartner(null); setCloseManualPartner(null); }} style={{ ...S.btnSm("white", S.red), border: "1px solid #fecaca" }} title="Ukloni partnera"><X size={11} /></button>
              </div>
            ) : (
              <button onClick={() => setShowClosePartnerPicker(true)} style={{ width: "100%", padding: "8px 12px", background: "white", border: "1px dashed #0369a1", borderRadius: 6, cursor: "pointer", color: "#0369a1", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <Building size={14} />Veži za partnera (opciono)
              </button>
            )}
          </div>
        )}
        <button onClick={() => closeSession(closeModal.id)} disabled={processing} style={{ ...S.btn(S.green), width: "100%", justifyContent: "center", padding: 13, fontSize: 16 }}>
          {processing ? <Loader2 size={17} className="spin" /> : <Zap size={17} />}Fiskalizuj i zatvori
        </button>
      </Modal>}

      {showClosePartnerPicker && (
        <PartnerPickerModal
          currentPartner={closePartner}
          currentManual={closeManualPartner}
          onClose={() => setShowClosePartnerPicker(false)}
          onSelect={(sel) => {
            if (sel._savedMirror) { setClosePartner(sel._savedMirror); setCloseManualPartner(null); }
            else if (sel._manualMirror) { setClosePartner(null); setCloseManualPartner(sel._manualMirror); }
            else { setClosePartner(null); setCloseManualPartner(null); }
            setShowClosePartnerPicker(false);
          }}
        />
      )}

      {/* Merge modal — Spoji ovaj sto sa drugim zauzetim stolom.
          Filters destination tables to ONLY busy ones (have an active session)
          excluding self. Shows order count to help waiter pick the right destination. */}
      {mergeModal && <Modal onClose={() => setMergeModal(null)} title="Spoji sa drugim stolom">
        <div style={{ fontSize: 13, color: S.muted, marginBottom: 12 }}>
          Sve narudžbe sa stola <b>{selectedTable?.name}</b> će se prebaciti na odabrani sto. Sto <b>{selectedTable?.name}</b> će postati slobodan.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
          {tables.filter(t => t.session && t.id !== selectedTable?.id && t.table_type !== "wall" && t.table_type !== "chair").map(t => (
            <button key={t.id} onClick={() => mergeSession(mergeModal.id, t.session.id)} style={{ padding: 14, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
              <div style={{ fontSize: 11, color: S.muted }}>{t.seats} mj.</div>
              {t.session.total_orders ? <div style={{ fontSize: 11, color: S.green, fontWeight: 600, marginTop: 2 }}>{t.session.total_orders} narudžbi</div> : null}
            </button>
          ))}
          {tables.filter(t => t.session && t.id !== selectedTable?.id && t.table_type !== "wall" && t.table_type !== "chair").length === 0 && (
            <div style={{ gridColumn: "span 2", textAlign: "center", padding: 20, color: S.muted }}>Nema drugih zauzetih stolova</div>
          )}
        </div>
      </Modal>}

      {transferModal && <Modal onClose={() => setTransferModal(null)} title="Premjesti na drugi sto">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {tables.filter(t => !t.session && t.id !== selectedTable?.id && t.table_type !== "wall" && t.table_type !== "chair").map(t => (
            <button key={t.id} onClick={() => transferSession(transferModal.id, t.id)} style={{ padding: 14, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
              <div style={{ fontSize: 11, color: S.muted }}>{t.seats} mj.</div>
            </button>
          ))}
          {tables.filter(t => !t.session && t.id !== selectedTable?.id && t.table_type !== "wall" && t.table_type !== "chair").length === 0 && (
            <div style={{ gridColumn: "span 3", textAlign: "center", padding: 20, color: S.muted }}>Nema slobodnih stolova</div>
          )}
        </div>
      </Modal>}
      {qtyModal && (
        <QtyNumpadModal
          product={qtyModal}
          onClose={() => setQtyModal(null)}
          onSave={qty => {
            setSessionCart(prev => prev.map(x => x.id === qtyModal.id ? { ...x, quantity: qty } : x));
            setQtyModal(null);
          }}
        />
      )}
      {stoloviNumPad && <NumPad
        value={stoloviNumPad === "splitCash" ? splitCash : splitCard}
        title={stoloviNumPad === "splitCash" ? "Gotovina (€)" : "Kartica (€)"}
        onChange={v => {
          if (stoloviNumPad === "splitCash") setSplitCash(v);
          else setSplitCard(v);
        }}
        onClose={() => setStoloviNumPad(null)}
      />}
    </div>
  );
}

function VirmanView({ user, showToast, online, activeOperator }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [fiscalizing, setFiscalizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [manualPartner, setManualPartner] = useState(null); // { name, tax_id }
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentDue, setPaymentDue] = useState("end_of_month");
  const [invoiceDiscount, setInvoiceDiscount] = useState({ type: "none", value: 0 });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  // qtyModal — same as Nova Prodaja: opens decimal numpad for is_decimal_unit products.
  const [qtyModal, setQtyModal] = useState(null);
  const [lastFiscalized, setLastFiscalized] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [subView, setSubView] = useState("create"); // create | list
  // Non-cash invoices list
  const [ncInvoices, setNcInvoices] = useState([]);
  const [ncLoading, setNcLoading] = useState(false);
  const [ncPg, setNcPg] = useState({ total: 0, page: 1, per_page: 20, total_pages: 0 });
  const [ncDetail, setNcDetail] = useState(null);
  const [stornoModal, setStornoModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [p, c, pa] = await Promise.all([api("/products"), api("/invoices/categories"), api("/partners")]);
    if (p.success) setProducts(p.data?.products || []);
    if (c.success) setCategories(c.data?.categories || []);
    if (pa.success) setPartners(pa.data?.partners || []);
    setLoading(false);
  };

  const loadNcInvoices = async () => {
    setNcLoading(true);
    const tcr = getTcr();
    const params = new URLSearchParams({ page: ncPg.page, per_page: 20 });
    if (tcr) params.set("tcr_code", tcr);
    const data = await api(`/invoices?${params}`);
    if (data.success) {
      const all = data.data.invoices || [];
      const filtered = all.filter(inv => inv.invoice_type === "non_cash" || inv.type_of_inv === "NONCASH" || inv.payment_method === "bank_transfer" || inv.payment_method === "other");
      setNcInvoices(filtered);
      setNcPg(p => ({ ...p, ...data.data.pagination }));
    }
    setNcLoading(false);
  };

  useEffect(() => { if (subView === "list") loadNcInvoices(); }, [subView, ncPg.page]);

  const addToCart = (product) => {
    setCart(prev => {
      const ex = prev.find(c => c.id === product.id);
      if (ex) return prev.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: product.id, name: product.name, price: parseFloat(product.price), quantity: 1, vat_rate: Number(product.vat_rate != null ? product.vat_rate : 21), unit: product.unit || "kom", is_decimal_unit: parseInt(product.is_decimal_unit) === 1 ? 1 : 0, discount_type: "none", discount_value: 0, custom_price: null }];
    });
  };
  const updateCartItem = (id, u) => setCart(prev => prev.map(c => c.id === id ? { ...c, ...u } : c));
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearAll = () => { setCart([]); setInvoiceDiscount({ type: "none", value: 0 }); setSelectedPartner(null); setManualPartner(null); setPaymentDue("end_of_month"); setPaymentMethod("bank_transfer"); };

  const cartCalc = useMemo(() => {
    let netTotal = 0;
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value;
      netTotal += lineNet - disc;
    });
    let invDisc = 0;
    if (invoiceDiscount.type === "percentage") invDisc = netTotal * (invoiceDiscount.value / 100);
    else if (invoiceDiscount.type === "fixed") invDisc = invoiceDiscount.value;
    const subtotal = Math.max(0, netTotal - invDisc);
    let vatAmount = 0;
    const vatGroups = {};
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value || 0;
      const afterDisc = lineNet - disc;
      const rate = Number(item.vat_rate != null ? item.vat_rate : 21);
      const vat = afterDisc * (rate / 100);
      vatAmount += vat;
      if (!vatGroups[rate]) vatGroups[rate] = { base: 0, vat: 0 };
      vatGroups[rate].base += afterDisc;
      vatGroups[rate].vat += vat;
    });
    if (invDisc > 0 && netTotal > 0) {
      const ratio = (netTotal - invDisc) / netTotal;
      vatAmount *= ratio;
    }
    const total = subtotal + vatAmount;
    return { netTotal, invDisc, subtotal, vatAmount, total, vatGroups };
  }, [cart, invoiceDiscount]);

  const buyerName = selectedPartner ? selectedPartner.name : manualPartner?.name || "";
  const buyerPib = selectedPartner ? selectedPartner.tax_id : manualPartner?.tax_id || "";

  const buildPayload = (fiscNow) => ({
    location_id: user.location_id || 1,
    tcr_code: getTcr(),
    payment_method: paymentMethod,
    invoice_type: "non_cash",
    type_of_inv: "NONCASH",
    fiscalize_now: fiscNow,
    discount_type: invoiceDiscount.type,
    discount_value: invoiceDiscount.value,
    ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code }),
    partner_id: selectedPartner?.id || null,
    buyer_name: buyerName,
    buyer_pib: buyerPib,
    buyer_address: selectedPartner?.address || "",
    buyer_city: selectedPartner?.city || "",
    payment_due: paymentDue,
    items: cart.map(item => ({ product_id: item.id, name: item.name, quantity: item.quantity, unit_price: item.custom_price !== null ? item.custom_price : item.price, vat_rate: item.vat_rate, unit: item.unit, discount_type: item.discount_type, discount_value: item.discount_value }))
  });

  const handleFiscalize = async () => {
    if (!buyerName) { showToast("Izaberite partnera ili unesite naziv", "error"); return; }
    if (!activeOperator?.operator_code) { showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error"); return; }
    setShowConfirm(false); setFiscalizing(true);
    let isReachable = false;
    try { const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]); isReachable = test && test.ok; } catch(e) { isReachable = false; }
    if (!isReachable) { setFiscalizing(false); const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt}). Fiskalizujte kad budete online.`, "error"); return; }
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(true)) });
      setFiscalizing(false);
      if (data.success) {
        setLastFiscalized({ fic: data.data.fic, invoice: data.data.invoice, total: cartCalc.total }); clearAll(); showToast("Fiskalizacija uspješna!");
        const invoiceId = data.data.invoice?.id;
        if (invoiceId && (localStorage.getItem("pos_printer_addr") || localStorage.getItem("pos_paper_width"))) {
          try { await printReceipt(invoiceId); } catch(e) { console.log("Auto-print failed:", e); }
        }
      }
      else { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Greška! Draft sačuvan lokalno (${cnt}).`, "error"); }
    } catch(e) { setFiscalizing(false); const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Offline! Draft sačuvan lokalno (${cnt}).`, "error"); }
  };
  const handleSaveDraft = async () => {
    if (!cart.length) return;
    if (!buyerName) { showToast("Izaberite partnera ili unesite naziv", "error"); return; }
    let isReachable = false;
    try { const test = await Promise.race([fetch(`${API_BASE}/products`, { headers: { "Authorization": `Bearer ${localStorage.getItem("pos_token")}` } }), new Promise((_, rej) => setTimeout(() => rej("timeout"), 3000))]); isReachable = test && test.ok; } catch(e) { isReachable = false; }
    if (!isReachable) { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt})`, "error"); return; }
    setSaving(true);
    try {
      const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(false)) });
      setSaving(false);
      if (data.success) { clearAll(); showToast("Virman sačuvan! #" + (data.data.invoice?.invoice_number || "")); }
      else { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Greška! Draft sačuvan lokalno (${cnt})`, "error"); }
    } catch(e) { setSaving(false); const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Offline! Draft sačuvan lokalno (${cnt})`, "error"); }
  };

  const viewNcInvoice = async (id) => { const d = await api(`/invoices/show/${id}`); if (d.success) setNcDetail(d.data); };
  const handleStorno = async (id, reason) => {
    if (!activeOperator?.operator_code) { showToast("Operater nije konfiguriran. Postavke → Operateri za podešavanje.", "error"); return; }
    const d = await api(`/invoices/storno/${id}`, { method: "POST", body: JSON.stringify({ storno_reason: reason, fiscalize_now: true, ...(activeOperator?.operator_code && { operator_code: activeOperator.operator_code }) }) });
    if (d.success) { showToast("Storno uspješan!"); setStornoModal(null); setNcDetail(null); loadNcInvoices(); } else showToast(d.message, "error");
  };
  const handleEmail = async (id, email) => {
    const d = await api(`/invoices/email/${id}`, { method: "POST", body: JSON.stringify({ email }) });
    if (d.success) showToast("Email poslat!"); else showToast(d.message || "Greška", "error");
    setEmailModal(null);
  };

  const filtered = products.filter(p => {
    if (search) return p.name.toLowerCase().includes(search.toLowerCase());
    if (activeCategory === "all") return true;
    return String(p.category_id) === String(activeCategory);
  });

  const filteredPartners = partners.filter(p => !partnerSearch || p.name.toLowerCase().includes(partnerSearch.toLowerCase()) || (p.tax_id && p.tax_id.includes(partnerSearch)));

  const payDueLabel = (v) => ({ end_of_month: "Kraj mjeseca", avans: "AVANS", unspecified: "Neodređeno" }[v] || v);

  if (lastFiscalized) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: 40 }}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ width: 70, height: 70, background: "#ecfdf5", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}><Check size={36} color={S.green} /></div>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 6px" }}>Virman fiskalizovan!</h2>
        <p style={{ color: S.muted, fontSize: 16, marginBottom: 22 }}>Bezgotovinsi račun registrovan kod Poreske Uprave</p>
        <div style={{ background: "white", borderRadius: 12, border: `1px solid ${S.border}`, padding: 20, marginBottom: 22, textAlign: "left" }}>
          <Row label="FIC" value={<span style={{ fontFamily: S.mono, fontSize: 12, color: S.green, wordBreak: "break-all" }}>{lastFiscalized.fic}</span>} />
          <Row label="Ukupno" value={<span style={{ fontWeight: 700, fontSize: 22 }}>{fmt(lastFiscalized.total)} €</span>} />
          <Row label="Broj" value={<span style={{ fontFamily: S.mono, fontSize: 13 }}>{lastFiscalized.invoice?.invoice_number || "-"}</span>} last />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={() => { const id = lastFiscalized.invoice?.id; if (id) openPdf(`/invoices/pdf/${id}?type=a4`); }} style={S.btn("#f3f4f6", S.muted)}><Download size={17} />PDF</button>
          <button onClick={async () => { const id = lastFiscalized.invoice?.id; if (id) { const ok = await printReceipt(id); if (ok) showToast("Štampano!"); else showToast("Štampa nije uspjela", "error"); } }} style={S.btn("#f3f4f6", S.muted)}><Printer size={17} />Štampaj</button>
          <button onClick={() => setLastFiscalized(null)} style={S.btn(S.dark)}><Plus size={17} />Novi virman</button>
        </div>
      </div>
    </div>
  );

  // NC invoice detail
  if (ncDetail) {
    const inv = ncDetail.invoice; const items = ncDetail.items || [];
    const isStorno = inv.is_storno == 1;
    return (
      <div style={{ height: "100%", overflow: "auto", background: S.bg }}>
        <div style={{ padding: "14px 20px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setNcDetail(null)} style={{ padding: 8, background: "#f3f4f6", border: "none", borderRadius: 6, cursor: "pointer", display: "flex" }}><ArrowLeft size={17} /></button>
          <div style={{ flex: 1 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{isStorno && "⊖ "}{inv.invoice_number}</h2><span style={{ fontSize: 13, color: "#9ca3af" }}>{fmtDateTime(inv.invoice_date)}</span></div>
          <Badge status={inv.status} />
          <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={13} />PDF</button>
          <button onClick={async () => { await printReceipt(inv.id) }} style={S.btnSm("#f3f4f6", S.muted)}><Printer size={13} />Štampaj</button>
          <button onClick={() => setEmailModal(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Mail size={13} />Email</button>
          {inv.status === "draft" && <button onClick={async () => { const d = await api(`/invoices/fiscalize/${inv.id}`, { method: "POST" }); if (d.success) { showToast("Fiskalizovano!"); loadNcInvoices(); viewNcInvoice(inv.id); } else showToast(d.message, "error"); }} style={S.btnSm("#f0fdf4", "#15803d")}><Zap size={13} />Fiskalizuj</button>}
          {ncDetail.can_storno && hasPermission(activeOperator, "sale_storno") && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} />Storniraj</button>}
        </div>
        <div style={{ padding: 20, maxWidth: 700, margin: "0 auto" }}>
          {inv.buyer_name && <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Kupac</div>
            <Row label="Naziv" value={inv.buyer_name} />
            {inv.buyer_pib && <Row label="PIB" value={inv.buyer_pib} />}
            {inv.payment_due && <Row label="Rok plaćanja" value={payDueLabel(inv.payment_due)} last />}
          </div>}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${S.border}`, fontWeight: 700, fontSize: 15 }}>Stavke</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={{ ...th, textAlign: "center" }}>Kol.</th><th style={{ ...th, textAlign: "right" }}>Cijena</th><th style={{ ...th, textAlign: "right" }}>Popust</th><th style={{ ...th, textAlign: "right" }}>Ukupno</th></tr></thead>
              <tbody>{items.map((it, i) => <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}><td style={td}>{it.item_name || it.name}</td><td style={{ ...td, textAlign: "center" }}>{it.quantity}</td><td style={{ ...td, textAlign: "right" }}>{fmt(it.unit_price)} €</td><td style={{ ...td, textAlign: "right", color: parseFloat(it.discount_amount || 0) > 0 ? S.orange : S.muted }}>{parseFloat(it.discount_amount || 0) > 0 ? `-${fmt(it.discount_amount)} €` : "-"}</td><td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(it.total_amount || it.quantity * it.unit_price)} €</td></tr>)}</tbody>
            </table>
          </div>
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${S.border}`, padding: 18 }}>
            <Row label="Neto" value={`${fmt(inv.subtotal)} €`} />
            {(() => { const vg = {}; (ncDetail?.items || []).forEach(it => { const r = Number(it.vat_rate != null ? it.vat_rate : 21); const gross = parseFloat(it.total_amount || it.quantity * it.unit_price); const net = gross / (1 + r/100); if (!vg[r]) vg[r] = 0; vg[r] += gross - net; }); return Object.entries(vg).map(([r, v]) => <Row key={r} label={`PDV ${r}%`} value={`${fmt(v)} €`} />); })()}
            {parseFloat(inv.discount_amount || 0) > 0 && <Row label="Popust" value={<span style={{ color: S.orange }}>-{fmt(inv.discount_amount)} €</span>} />}
            <Row label="Plaćanje" value={inv.payment_method === "cash" ? "Gotovina" : inv.payment_method === "card" ? "Kartica" : inv.payment_method === "split" ? "Podijeljeno" : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method === "other" ? "Ostalo" : inv.payment_method} />
            {inv.payment_due && <Row label="Rok plaćanja" value={payDueLabel(inv.payment_due)} />}
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 10 }}><span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span><span style={{ fontWeight: 700, fontSize: 22, color: isStorno ? S.red : S.green }}>{fmt(inv.total_amount)} €</span></div>
          </div>
        </div>
        {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
        {emailModal && <Modal onClose={() => setEmailModal(null)} title="Pošalji email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* LEFT: Products top, Partners bottom (or full-width history table in list mode) */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: subView === "list" ? "none" : `1px solid ${S.border}`, overflow: subView === "list" ? "hidden" : "auto", minWidth: 0 }}>
        {/* Tab: Create | List */}
        <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 6 }}>
          <button onClick={() => setSubView("create")} style={{ ...S.btnSm(subView === "create" ? S.dark : "#f3f4f6", subView === "create" ? "white" : S.muted) }}><PlusCircle size={13} />Novi virman</button>
          <button onClick={() => setSubView("list")} style={{ ...S.btnSm(subView === "list" ? S.dark : "#f3f4f6", subView === "list" ? "white" : S.muted) }}><FileText size={13} />Virmani ({getTcr() ? `TCR: ${getTcr().slice(-6)}` : "svi"})</button>
        </div>

        {subView === "list" ? (
          <div style={{ flex: 1, overflow: "auto", padding: "0 14px" }}>
            {ncLoading ? <div style={{ textAlign: "center", padding: 50 }}><Loader2 size={28} className="spin" /></div>
            : ncInvoices.length === 0 ? <div style={{ textAlign: "center", padding: 50, color: "#9ca3af" }}><Building size={40} /><p style={{ marginTop: 10, fontSize: 15 }}>Nema bezgotovinskih faktura</p></div>
            : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, marginTop: 8 }}>
              <thead><tr><th style={th}>Broj</th><th style={th}>Kupac</th><th style={{ ...th, textAlign: "right" }}>Iznos</th><th style={{ ...th, textAlign: "center" }}>Status</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
              <tbody>{ncInvoices.map(inv => {
                const isStorno = inv.is_storno == 1;
                return (
                  <tr key={inv.id} style={{ borderBottom: "1px solid #f3f4f6", background: isStorno ? "#fef2f2" : "white" }}>
                    <td style={td}><span style={{ fontFamily: S.mono, fontSize: 12, fontWeight: 600 }}>{inv.invoice_number}</span><br/><span style={{ fontSize: 11, color: S.muted }}>{fmtDate(inv.invoice_date)}</span></td>
                    <td style={{ ...td, fontSize: 13 }}>{inv.buyer_name || "-"}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(inv.total_amount)} €</td>
                    <td style={{ ...td, textAlign: "center" }}><Badge status={isStorno ? "storno" : inv.status} /></td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button onClick={() => viewNcInvoice(inv.id)} style={S.btnSm("#f3f4f6", S.muted)}><Eye size={13} /></button>
                        <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={S.btnSm("#f3f4f6", S.muted)}><Download size={13} /></button>
                        {inv.status === "fiscalized" && !inv.stornoed_by_invoice_id && !isStorno && <button onClick={() => setStornoModal(inv.id)} style={S.btnSm("#fef2f2", S.red)}><RotateCcw size={13} /></button>}
                      </div>
                    </td>
                  </tr>);
              })}</tbody>
            </table>}
          </div>
        ) : (<>
          {/* Products */}
          <div style={{ flex: "1 1 50%", display: "flex", flexDirection: "column", minHeight: 220, overflow: "hidden" }}>
            <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 6 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži..." style={{ ...S.input, paddingLeft: 34, padding: "7px 10px 7px 34px", fontSize: 14 }} />
              </div>
            </div>
            <div style={{ padding: "5px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 4, overflowX: "auto" }}>
              <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />
              {categories.map(c => <CatTab key={c.id} active={String(activeCategory) === String(c.id)} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 10, background: S.bg }}>
              {loading ? <div style={{ textAlign: "center", padding: 30 }}><Loader2 size={24} className="spin" /></div>
              : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                {filtered.map(p => {
                  const inCart = cart.find(c => c.id === p.id);
                  const gross = parseFloat(p.price) * (1 + Number(p.vat_rate != null ? p.vat_rate : 21) / 100);
                  return (
                    <button key={p.id} onClick={() => addToCart(p)} style={{ position: "relative", padding: "12px 8px", background: inCart ? "#f0fdf4" : "white", border: inCart ? `2px solid ${S.green}` : `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", textAlign: "center", fontFamily: S.font }}>
                      {inCart && <div style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, background: S.green, borderRadius: "50%", color: "white", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{inCart.quantity}</div>}
                      <div style={{ fontWeight: 600, fontSize: 13, color: S.dark, marginBottom: 4, lineHeight: 1.2 }}>{p.name}</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: S.green }}>{fmt(gross)} €</div>
                    </button>
                  );
                })}
              </div>}
            </div>
          </div>

          {/* Partners */}
          <div style={{ flex: "1 1 50%", display: "flex", flexDirection: "column", borderTop: `2px solid ${S.border}`, minHeight: 220, overflow: "hidden" }}>
            <div style={{ padding: "8px 14px", background: "white", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 8 }}>
              <Users size={15} color={S.muted} />
              <span style={{ fontWeight: 700, fontSize: 14, color: S.dark }}>Partner</span>
              {selectedPartner && <span style={{ fontSize: 12, background: "#f0fdf4", color: S.green, padding: "2px 8px", borderRadius: 5, fontWeight: 600 }}>{selectedPartner.name}</span>}
              {!selectedPartner && !manualPartner && <span style={{ fontSize: 12, color: S.red }}>Obavezno</span>}
              <div style={{ flex: 1 }} />
              <button onClick={() => { setSelectedPartner(null); setManualPartner({ name: "", tax_id: "" }); }} style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={12} />Ručni unos</button>
            </div>
            {manualPartner ? (
              <div style={{ padding: 14, background: "#fffbeb", borderBottom: `1px solid ${S.border}` }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 2 }}><label style={{ fontSize: 12, fontWeight: 600 }}>Naziv *</label><input value={manualPartner.name} onChange={e => setManualPartner(p => ({ ...p, name: e.target.value }))} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: 12, fontWeight: 600 }}>PIB</label><input value={manualPartner.tax_id} onChange={e => setManualPartner(p => ({ ...p, tax_id: e.target.value }))} placeholder="Opcionalno" style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} /></div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setManualPartner(null)} style={S.btnSm("#f3f4f6", S.muted)}><X size={12} />Otkaži</button>
                  {manualPartner.name && <span style={{ fontSize: 12, color: S.green, display: "flex", alignItems: "center", gap: 4 }}><Check size={12} />Ručni partner aktivan</span>}
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, overflow: "auto", padding: "6px 14px" }}>
                <input value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} placeholder="Pretraži partnere..." style={{ ...S.input, padding: "7px 10px", fontSize: 13, marginBottom: 8 }} />
                {filteredPartners.length === 0 ? <div style={{ textAlign: "center", padding: 20, color: "#9ca3af", fontSize: 14 }}>Nema partnera</div>
                : filteredPartners.map(p => (
                  <button key={p.id} onClick={() => { setSelectedPartner(p); setManualPartner(null); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: selectedPartner?.id === p.id ? `2px solid ${S.green}` : `1px solid ${S.border}`, background: selectedPartner?.id === p.id ? "#f0fdf4" : "white", borderRadius: 8, marginBottom: 4, cursor: "pointer", fontFamily: S.font, textAlign: "left" }}>
                    <Building size={15} color={selectedPartner?.id === p.id ? S.green : S.muted} />
                    <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div><div style={{ fontSize: 12, color: S.muted }}>{p.tax_id || "Bez PIB-a"} · {p.city || ""}</div></div>
                    {selectedPartner?.id === p.id && <CheckCircle size={16} color={S.green} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>)}
      </div>

      {/* RIGHT: Cart — only shown in CREATE mode (hidden when viewing virman history) */}
      {subView === "create" && (
      <div style={{ flex: "0 0 43%", display: "flex", flexDirection: "column", background: "white", minWidth: 380 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Building size={18} color={S.dark} />
            <span style={{ fontWeight: 700, fontSize: 17, color: S.dark }}>Virman</span>
            <span style={{ background: "#f3f4f6", padding: "2px 8px", borderRadius: 5, fontSize: 13, fontWeight: 600, color: S.muted }}>{cart.length}</span>
          </div>
          {cart.length > 0 && <button onClick={clearAll} style={S.btnSm("#fef2f2", S.red)}><Trash2 size={13} />Obriši</button>}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "6px 14px" }}>
          {cart.length === 0 ? <div style={{ textAlign: "center", padding: "50px 16px", color: "#9ca3af" }}><Building size={40} strokeWidth={1.5} /><p style={{ marginTop: 12, fontSize: 15 }}>Dodajte stavke</p></div>
          : cart.map(item => {
            const up = item.custom_price !== null ? item.custom_price : item.price;
            const lineNet = item.quantity * up;
            let disc = 0;
            if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
            else if (item.discount_type === "fixed") disc = item.discount_value;
            const lineGross = (lineNet - disc) * (1 + item.vat_rate / 100);
            return (
              <div key={item.id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div><div style={{ fontSize: 13, color: "#9ca3af" }}>{fmt(up)} × {fmtQty(item.quantity)}{disc > 0 ? ` − ${fmt(disc)}` : ""}</div></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <button onClick={() => updateCartItem(item.id, { quantity: Math.max(item.is_decimal_unit ? 0.01 : 1, parseFloat((item.quantity - 1).toFixed(2))) })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={16} /></button>
                    {item.is_decimal_unit ? (
                      <button onClick={() => setQtyModal(item)} style={{ fontSize: 15, fontWeight: 600, minWidth: 50, textAlign: "center", padding: "6px 8px", border: `1px dashed ${S.accent}`, borderRadius: 6, background: "white", cursor: "pointer", color: S.dark }} title="Unesi tačnu količinu">{fmtQty(item.quantity)}</button>
                    ) : (
                      <span style={{ fontSize: 15, fontWeight: 600, minWidth: 26, textAlign: "center" }}>{item.quantity}</span>
                    )}
                    <button onClick={() => updateCartItem(item.id, { quantity: parseFloat((item.quantity + 1).toFixed(2)) })} style={{ width: 36, height: 36, border: `1px solid ${S.border}`, borderRadius: 8, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={16} /></button>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, minWidth: 60, textAlign: "right" }}>{fmt(lineGross)} €</div>
                  <button onClick={() => setEditingItem(item.id === editingItem ? null : item.id)} style={{ padding: 4, background: "none", border: "none", cursor: "pointer", color: editingItem === item.id ? S.accent : S.muted }}><Edit3 size={15} /></button>
                  <button onClick={() => removeFromCart(item.id)} style={{ padding: 6, background: "#fef2f2", border: "none", borderRadius: 6, cursor: "pointer", color: "#dc2626" }}><X size={16} /></button>
                </div>
                {editingItem === item.id && (
                  <div style={{ marginTop: 8, padding: 10, background: "#f9fafb", borderRadius: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 90px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Cijena (neto)</label>
                      <input type="number" step="0.01" value={item.custom_price !== null ? item.custom_price : item.price} onChange={e => updateCartItem(item.id, { custom_price: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    <div style={{ flex: "1 1 70px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Količina</label>
                      <input type="number" step={item.is_decimal_unit ? "0.01" : "1"} value={item.quantity} onChange={e => updateCartItem(item.id, { quantity: item.is_decimal_unit ? Math.max(0.01, parseFloat(String(e.target.value).replace(",", ".")) || 1) : Math.max(1, parseInt(e.target.value) || 1) })} style={{ ...S.input, padding: "7px 10px", fontSize: 14 }} />
                    </div>
                    {hasPermission(activeOperator, "sale_discount") && <div style={{ flex: "1 1 110px" }}>
                      <label style={{ fontSize: 12, color: S.muted, fontWeight: 600 }}>Popust</label>
                      <div style={{ display: "flex", gap: 4 }}>
                        <select value={item.discount_type} onChange={e => updateCartItem(item.id, { discount_type: e.target.value, discount_value: 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, flex: 1 }}><option value="none">Bez</option><option value="percentage">%</option><option value="fixed">€</option></select>
                        {item.discount_type !== "none" && <input type="number" step="0.01" value={item.discount_value} onChange={e => updateCartItem(item.id, { discount_value: parseFloat(e.target.value) || 0 })} style={{ ...S.input, padding: "7px 6px", fontSize: 13, width: 60 }} />}
                      </div>
                    </div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {cart.length > 0 && (
          <div style={{ borderTop: `2px solid ${S.border}`, padding: "14px 16px" }}>
            {/* Payment method */}
            <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
              {[{ id: "bank_transfer", label: "Virman", icon: Building }, { id: "other", label: "Ostalo", icon: Wallet }].map(m => (
                <button key={m.id} onClick={() => setPaymentMethod(m.id)} style={{ flex: 1, padding: "12px 8px", border: "2px solid", borderColor: paymentMethod === m.id ? S.green : S.border, background: paymentMethod === m.id ? "#f0fdf4" : "white", color: paymentMethod === m.id ? S.green : S.muted, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: S.font }}><m.icon size={14} />{m.label}</button>
              ))}
            </div>
            {/* Payment due */}
            <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
              {[{ id: "end_of_month", l: "Kraj mjeseca" }, { id: "avans", l: "AVANS" }, { id: "unspecified", l: "Neodređeno" }].map(d => (
                <button key={d.id} onClick={() => setPaymentDue(d.id)} style={{ flex: 1, padding: "7px", border: "1px solid", borderColor: paymentDue === d.id ? S.accent : S.border, background: paymentDue === d.id ? "#eef2ff" : "white", color: paymentDue === d.id ? S.accent : S.muted, borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: S.font }}><Clock size={11} style={{ marginRight: 3 }} />{d.l}</button>
              ))}
            </div>
            {/* Invoice discount */}
            <div style={{ marginBottom: 10, display: "flex", gap: 6 }}>
              {hasPermission(activeOperator, "sale_discount") && <button onClick={() => setShowDiscountModal(true)} style={{ ...S.btnSm(invoiceDiscount.type !== "none" ? "#fef3c7" : "#f3f4f6", invoiceDiscount.type !== "none" ? "#92400e" : S.muted), flex: 1, justifyContent: "center" }}>
                <Percent size={13} />{invoiceDiscount.type !== "none" ? `Popust: ${invoiceDiscount.value}${invoiceDiscount.type === "percentage" ? "%" : "€"}` : "Popust na račun"}
              </button>}
              {hasPermission(activeOperator, "sale_discount") && invoiceDiscount.type !== "none" && <button onClick={() => setInvoiceDiscount({ type: "none", value: 0 })} style={S.btnSm("#fef2f2", S.red)}><X size={13} /></button>}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.muted }}>Neto</span><span>{fmt(cartCalc.subtotal)} €</span></div>
            {cartCalc.vatGroups && Object.entries(cartCalc.vatGroups).map(([rate, g]) => (
              <div key={rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 13 }}><span style={{ color: S.muted }}>PDV {rate}%</span><span>{fmt(g.vat)} €</span></div>
            ))}
            {cartCalc.invDisc > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 14 }}><span style={{ color: S.orange }}>Popust</span><span style={{ color: S.orange }}>-{fmt(cartCalc.invDisc)} €</span></div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, paddingTop: 8, borderTop: "1px solid #f3f4f6" }}>
              <span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span>
              <span style={{ fontWeight: 700, fontSize: 22, color: S.green }}>{fmt(cartCalc.total)} €</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { if (!buyerName) { showToast("Izaberite partnera", "error"); return; } setShowConfirm(true); }} disabled={fiscalizing} style={{ ...S.btn(fiscalizing ? "#6b7280" : S.green), flex: "0 0 63%", justifyContent: "center", padding: "13px", fontSize: 17 }}>
                {fiscalizing ? <Loader2 size={19} className="spin" /> : <Zap size={19} />}{fiscalizing ? "..." : "FISKALIZUJ"}
              </button>
              <button onClick={handleSaveDraft} disabled={saving} style={{ ...S.btn("#3b82f6"), flex: "0 0 35%", justifyContent: "center", padding: "13px", fontSize: 15 }}>
                {saving ? <Loader2 size={15} className="spin" /> : <Archive size={15} />}{saving ? "..." : "SAČUVAJ"}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {showConfirm && <Modal onClose={() => setShowConfirm(false)} title="Potvrda fiskalizacije virmana">
        <p style={{ color: S.muted, fontSize: 15, marginBottom: 16 }}>Bezgotovinsi račun za: <strong>{buyerName}</strong> {buyerPib && `(PIB: ${buyerPib})`}</p>
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700 }}><span>Ukupno:</span><span style={{ color: S.green }}>{fmt(cartCalc.total)} €</span></div>
          <div style={{ fontSize: 14, color: S.muted, marginTop: 6 }}>Stavke: {cart.length} | Rok: {payDueLabel(paymentDue)}</div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => setShowConfirm(false)} style={S.btn("#f3f4f6", S.muted)}>Otkaži</button>
          <button onClick={handleFiscalize} style={{ ...S.btn(S.green), padding: "14px 24px", fontSize: 16 }}><Zap size={18} />Fiskalizuj</button>
        </div>
      </Modal>}
      {showDiscountModal && <Modal onClose={() => setShowDiscountModal(false)} title="Popust na račun">
        <DiscountForm current={invoiceDiscount} onApply={d => { setInvoiceDiscount(d); setShowDiscountModal(false); }} onClose={() => setShowDiscountModal(false)} />
      </Modal>}
      {qtyModal && (
        <QtyNumpadModal
          product={qtyModal}
          onClose={() => setQtyModal(null)}
          onSave={qty => {
            updateCartItem(qtyModal.id, { quantity: qty });
            setQtyModal(null);
          }}
        />
      )}
      {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
      {emailModal && <Modal onClose={() => setEmailModal(null)} title="Pošalji email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(() => {
    try {
      const t = localStorage.getItem("pos_token"); const u = localStorage.getItem("pos_user");
      if (t && u && u !== "undefined") return { token: t, user: JSON.parse(u) };
    } catch(e) { localStorage.removeItem("pos_token"); localStorage.removeItem("pos_user"); }
    return null;
  });
  const [pinState, setPinState] = useState("checking"); // "checking" | "locked" | "setup" | "unlocked"
  const [activeOperator, setActiveOperator] = useState(null);
  const [showResetOffer, setShowResetOffer] = useState(() => {
    const flag = sessionStorage.getItem("pos_pin_reset_offer");
    if (flag === "1") { sessionStorage.removeItem("pos_pin_reset_offer"); return true; }
    return false;
  });

  // Check PIN system state after login
  useEffect(() => {
    if (!auth) { setPinState("checking"); setActiveOperator(null); return; }
    const pinData = loadPinData(auth.user.company_id);
    if (!pinData || !pinData.operators || pinData.operators.length === 0) {
      // No PIN system — use the logged-in user's own operator_code.
      // This is the cleanest behavior: whoever logs in is the operator
      // for fiscalization purposes. Real CIS-registered operator_code
      // comes from the login response.
      setPinState("unlocked");
      setActiveOperator({
        name: auth.user.name,
        operator_code: auth.user.operator_code || "",
        is_main_admin: true,
        permissions: ALL_PERMISSIONS.map(p => p.key)
      });
    } else {
      setPinState("locked");
    }
  }, [auth]);

  const handleLogin = (token, user) => { localStorage.setItem("pos_token", token); localStorage.setItem("pos_user", JSON.stringify(user)); setAuth({ token, user }); };
  const handleLogout = () => { localStorage.removeItem("pos_token"); localStorage.removeItem("pos_user"); setAuth(null); setActiveOperator(null); setPinState("checking"); };

  const handlePinUnlock = (operator) => { setActiveOperator(operator); setPinState("unlocked"); };
  const handlePinSetup = (operator) => { setActiveOperator(operator); setPinState("unlocked"); };
  const handleLock = () => { setActiveOperator(null); setPinState("locked"); };
  const handleForgotPin = () => { sessionStorage.setItem("pos_pin_reset_offer", "1"); handleLogout(); };
  const handleResetPinSystem = () => {
    if (!window.confirm("Ovo će obrisati sve PIN-ove i operatere. Nastaviti?")) return;
    localStorage.removeItem(getPinKey(auth.user.company_id));
    setShowResetOffer(false);
    setPinState("unlocked");
    // Use logged-in user's own operator_code (same logic as no-PIN system)
    setActiveOperator({
      name: auth.user.name,
      operator_code: auth.user.operator_code || "",
      is_main_admin: true,
      permissions: ALL_PERMISSIONS.map(p => p.key)
    });
  };

  if (!auth) return <LoginScreen onLogin={handleLogin} />;

  if (pinState === "setup") return <PinSetupScreen user={auth.user} onComplete={handlePinSetup} />;

  if (pinState === "locked") {
    const pinData = loadPinData(auth.user.company_id);
    // If admin just re-logged-in after forgot-PIN, offer reset
    if (showResetOffer && pinData && pinData.operators?.length > 0) {
      return (
        <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", padding: 20, fontFamily: "'DM Sans', sans-serif" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 30, maxWidth: 440, width: "100%", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}>
            <div style={{ display: "inline-flex", width: 56, height: 56, borderRadius: 14, background: "rgba(245,158,11,0.1)", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
              <AlertCircle size={28} color="#f59e0b" />
            </div>
            <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>PIN sistem je aktivan</h2>
            <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
              Mozete nastaviti unosom PIN-a, ili ako ste zaboravili PIN — resetovati cijeli sistem. Reset brise sve operatere i PIN-ove. Nakon reseta postavicete novi Admin PIN.
            </p>
            <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
              <button onClick={() => setShowResetOffer(false)} style={{ ...S.btn(S.accent), justifyContent: "center", padding: "12px" }}>
                <Lock size={16} /> Nastavi — imam PIN
              </button>
              <button onClick={handleResetPinSystem} style={{ ...S.btn("#fff"), border: "1px solid #dc2626", justifyContent: "center", padding: "12px", color: "#dc2626" }}>
                <Trash2 size={16} /> Resetuj PIN sistem
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 14, textAlign: "center" }}>
              Ova opcija je dostupna jer ste se upravo prijavili sa email-om i lozinkom (dokaz identiteta).
            </div>
          </div>
        </div>
      );
    }
    return <PinLockScreen onUnlock={handlePinUnlock} pinData={pinData} companyName={auth.user.company_name} onForgotPin={handleForgotPin} />;
  }

  if (pinState === "unlocked") {
    const hasPinSystem = !!loadPinData(auth.user.company_id);
    return <POSApp user={auth.user} onLogout={handleLogout} activeOperator={activeOperator} onLock={hasPinSystem ? handleLock : null} />;
  }

  // "checking" state — brief loading
  return <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0a14" }}><Loader2 size={32} color="#3b82f6" className="spin" /></div>;
}
